/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// --- Aggregator Operations Module -----------------------------------------------

import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { usePersistentState } from '../../hooks/usePersistentState';
import { apiFetch } from '../../data/ApiModuleRepository';
import {
  Megaphone, Plus, Edit2, Trash2, X, Check, AlertTriangle, Info,
  ChevronRight, ChevronUp, ChevronDown, Tag, Calendar, Users, BarChart2,
  Building2, Percent, DollarSign, Globe, ToggleLeft, ToggleRight, Filter,
  Send, ClipboardList, ArrowRight, ArrowLeft, RotateCcw, Eye, Clock, CheckCircle2,
  XCircle, Layers, Package, Settings, ImageIcon, FileEdit, RefreshCw, Link2,
  MessageSquare, Unlock, Lock, AlertCircle, Ban, Paperclip, FileText, Upload,
  Rocket, Radio, Zap, Download, ShieldCheck, Hash, Archive, BarChart3, Target,
  Search, History, BookOpen, MapPin,
} from 'lucide-react';
import { cn } from '../../ui/utils';
import { hasDataChanged } from '../../core/utils';
import { ConfirmDeleteDialog, type DeleteConfirmState } from '../../ui/ConfirmDeleteDialog';
import { ExcelExportService, computeCampaignReadiness, detectItemChanges, compareTarget, TARGET_STATUS_COLORS, getApprovedItems, type CampaignReadiness, type BrandReadinessEntry } from './utils';
import { DatePicker } from '../../ui/DatePicker';
import { TimePicker } from '../../ui/TimePicker';
import { useToast } from '../../ui/Toast';
import { CustomSelect } from '../../components/shared/CustomSelect';
import { getRepository } from '../../config/repository';
import { uploadFile, getMediaUrl } from '../../core/fileUpload';
import { User, Department, DEPARTMENT_STAGES, DepartmentStage, NotificationType, MODULE_IDS } from '../../domain/entities';
import { ModuleTabs, TabGate } from '../shared/ModuleTabs';
import { PLATFORM_MODULES } from '../../core/appRegistry';
import { hasTabVariant } from '../../core/security';
import { useCampaignWatchdog } from '../../hooks/useCampaignWatchdog';
import {
  Campaign, CampaignSettings, CampaignStatus, DiscountType, FundingType,
  Brand, DEFAULT_AGGREGATORS,
  CampaignSubmission, SubmissionStatus, SubmissionItem, ReviewAction, DiscussionMessage,
} from './types';
import { ReviewTimeline } from './ReviewTimeline';
import { HistoryAuditPopup } from './components/HistoryAuditPopup';
import { MenuSelectorModal } from './components/MenuSelectorModal';
import { hasMenuAccess } from './services/MenuIntegrationService';
import { pushItemsToConnectedMenus, revokeCampaignItems, type InjectedItem } from '../../modules/menu-manage/services/ModuleOrchestrationService';
import { DispatchService, DEFAULT_DISPATCH_CONFIG, ALL_COLUMNS_ON, ALL_READINESS_COLUMNS_ON, ALL_AUDIT_COLUMNS_ON, type DispatchConfig, type ColumnSelection, type ReadinessColumnSelection, type AuditColumnSelection } from './services/DispatchService';
import { ProfessionalExcelFormatter } from './services/ProfessionalExcelFormatter';
import { useFilteredSubmissions, computeTimeInStage, formatDuration, type ReviewSubTab } from './hooks/useFilteredSubmissions';
import * as XLSX from 'xlsx-js-style';

// --- Props --------------------------------------------------------------------

interface CampaignManagerModuleProps {
  user: User | null;
}

// --- Helpers -----------------------------------------------------------------

const STATUS_STYLES: Record<CampaignStatus, { pill: string; dot: string; label: string }> = {
  [CampaignStatus.DRAFT]:   { pill: 'bg-amber-100   text-amber-700   border-amber-200',    dot: 'bg-amber-500',    label: 'Draft'   },
  [CampaignStatus.ACTIVE]:  { pill: 'bg-emerald-100 text-emerald-700 border-emerald-200',  dot: 'bg-emerald-500',  label: 'Active'  },
  [CampaignStatus.PLANNED]: { pill: 'bg-indigo-100  text-indigo-700  border-indigo-200',   dot: 'bg-indigo-500',   label: 'Planned' },
  [CampaignStatus.CLOSED]:  { pill: 'bg-zinc-100    text-zinc-500    border-zinc-200',      dot: 'bg-zinc-400',     label: 'Closed'  },
};

const FUNDING_LABELS: Record<FundingType, string> = {
  [FundingType.NONE]:         'No Funding',
  [FundingType.CO_FUNDED]:    'Co-Funded',
  [FundingType.FULLY_FUNDED]: 'Fully Funded',
};

const FUNDING_STYLES: Record<FundingType, string> = {
  [FundingType.NONE]:         'bg-zinc-100 text-zinc-500',
  [FundingType.CO_FUNDED]:    'bg-teal-100 text-teal-700',
  [FundingType.FULLY_FUNDED]: 'bg-emerald-100 text-emerald-700',
};

function fmtDate(ts: number) {
  return new Date(ts).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

// --- Module Root --------------------------------------------------------------

export function AggregatorModule({ user }: CampaignManagerModuleProps) {
  const base = '/apps/aggregator';

  // -- Watchdog engine · checks every 60s for PLANNED?ACTIVE auto-posts --
  useCampaignWatchdog();

  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  useEffect(() => {
    getRepository().getCampaignSubmissions().then(subs => {
      setPendingReviewCount(subs.filter(s => s.status === SubmissionStatus.PENDING || s.status === SubmissionStatus.IN_REVIEW).length);
    });
  }, []);

  const moduleTabs = [
    { key: 'dashboard',  label: 'Dashboard',           to: base,                     exact: true  },
    { key: 'management', label: 'Management',          to: `${base}/management`,     exact: false },
    { key: 'submit',     label: 'Submit Items',        to: `${base}/submit`,         exact: false },
    { key: 'review',     label: 'Submission Reviews',  to: `${base}/review`,         exact: false, badge: pendingReviewCount },
    { key: 'deployment', label: 'Launch Readiness',    to: `${base}/deployment`,     exact: false },
    { key: 'live',       label: 'Live Campaigns',      to: `${base}/live`,           exact: false },
    { key: 'analytics', label: 'Analytics',            to: `${base}/analytics`,      exact: false },
    { key: 'archive',    label: 'Campaign Archive',    to: `${base}/archive`,        exact: false },
    { key: 'settings', label: 'Settings', to: `${base}/settings`, exact: false },
  ];

  return (
    <div className="space-y-0">
      <ModuleTabs moduleId="aggregator" tabs={moduleTabs} user={user} />
      <div className="pt-4">
        <Routes>
          <Route index element={<TabGate moduleId="aggregator" tabId="dashboard" user={user}><DashboardTab user={user} /></TabGate>} />
          <Route path="management" element={<TabGate moduleId="aggregator" tabId="management" user={user}><ManagementTab user={user} /></TabGate>} />
          <Route path="submit" element={<TabGate moduleId="aggregator" tabId="submit" user={user}><SubmitItemsTab user={user} /></TabGate>} />
          <Route path="review/*" element={<TabGate moduleId="aggregator" tabId="review" user={user}><ReviewInboxTab user={user} /></TabGate>} />
          <Route path="deployment" element={<TabGate moduleId="aggregator" tabId="deployment" user={user}><DeploymentCenterTab user={user} /></TabGate>} />
          <Route path="live" element={<TabGate moduleId="aggregator" tabId="live" user={user}><LiveOperationsTab user={user} /></TabGate>} />
          <Route path="analytics" element={<TabGate moduleId="aggregator" tabId="analytics" user={user}><AnalyticsDashboardTab user={user} /></TabGate>} />
          <Route path="archive" element={<TabGate moduleId="aggregator" tabId="archive" user={user}><CampaignArchiveTab user={user} /></TabGate>} />
          <Route path="settings" element={<TabGate moduleId="aggregator" tabId="settings" user={user}><CampaignSettingsTab user={user} /></TabGate>} />
        </Routes>
      </div>
    </div>
  );
}

// --- Dashboard Tab ------------------------------------------------------------

function DashboardTab({ user }: { user: User | null }) {
  const canEdit = hasTabVariant(user, 'aggregator', 'dashboard', 'admin');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [submissions, setSubmissions] = useState<CampaignSubmission[]>([]);
  const [aggLogos, setAggLogos] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  // -- Filters --
  const [searchQ, setSearchQ] = usePersistentState('search');
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'ALL'>('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    Promise.all([
      getRepository().getCampaigns(),
      getRepository().getBrands(),
      getRepository().getCampaignSubmissions(),
      getRepository().getCampaignSettings(),
    ]).then(([c, b, s, settings]) => {
      setCampaigns(c);
      setBrands(b.filter(br => br.enabledModules?.includes('aggregator')));
      setSubmissions(s);
      setAggLogos(settings.aggregatorLogos ?? {});
    });
  }, []);

  // Store Manager filtering · managers only see ACTIVE campaigns within valid date range
  const now = Date.now();
  const userBrands = user?.assignedBrands;
  const hasBrands = userBrands && userBrands.length > 0;
  const baseVisible = canEdit
    ? campaigns
    : !hasBrands
      ? []
      : campaigns.filter(c =>
          c.status === CampaignStatus.ACTIVE &&
          c.validFrom <= now && c.validTo >= now &&
          (c.targetAllBrands || c.brandIds.some(bid => userBrands!.includes(bid)))
        );

  // Apply search + status + date filters
  const visible = baseVisible.filter(c => {
    // Search filter
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      const brandNames = c.targetAllBrands ? 'all brands' : c.brandIds.map(id => brands.find(b => b.id === id)?.name ?? '').join(' ').toLowerCase();
      if (
        !c.name.toLowerCase().includes(q) &&
        !(c.description ?? '').toLowerCase().includes(q) &&
        !brandNames.includes(q) &&
        !c.aggregators.some(a => a.toLowerCase().includes(q))
      ) return false;
    }
    // Status filter
    if (statusFilter !== 'ALL' && c.status !== statusFilter) return false;
    // Date range filter
    if (dateFrom) {
      const from = new Date(dateFrom).getTime();
      if (c.validTo < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo).setHours(23, 59, 59, 999);
      if (c.validFrom > to) return false;
    }
    return true;
  });

  const draft   = baseVisible.filter(c => c.status === CampaignStatus.DRAFT).length;
  const active  = baseVisible.filter(c => c.status === CampaignStatus.ACTIVE).length;
  const planned = baseVisible.filter(c => c.status === CampaignStatus.PLANNED).length;
  const closed  = baseVisible.filter(c => c.status === CampaignStatus.CLOSED).length;

  // Manager's submissions
  const mySubs = user ? submissions.filter(s => s.submittedBy === user.id) : [];
  const unlockedSubs = mySubs.filter(s => s.items.some(it => it.locked === false));
  const unlockedItemsCount = mySubs.reduce((acc, s) => acc + s.items.filter(it => it.locked === false).length, 0);

  const hasActiveFilters = searchQ.trim() || statusFilter !== 'ALL' || dateFrom || dateTo;
  const clearFilters = () => { setSearchQ(''); setStatusFilter('ALL'); setDateFrom(''); setDateTo(''); };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900">Campaign Dashboard</h2>
          <p className="text-sm text-slate-500 mt-1">Overview of all promotional campaigns across your brands.</p>
        </div>
        {canEdit && (
          <button
            onClick={() => navigate('/apps/aggregator/management')}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-all"
          >
            <Megaphone size={16} /> Manage Campaigns
          </button>
        )}
      </div>

      {/* Manager Submission Process Ring · only for Store Managers with submissions */}
      {!canEdit && mySubs.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Your Submission Status</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <button onClick={() => navigate('/apps/aggregator/submit?tab=in-review')} className="bg-white border border-blue-200 rounded-2xl p-4 text-center relative group hover:border-blue-400 hover:shadow-sm transition-all cursor-pointer">
              <div className="w-14 h-14 mx-auto rounded-full border-4 border-blue-400 flex items-center justify-center animate-timeline-pulse mb-2">
                <Clock size={20} className="text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-zinc-900">{mySubs.filter(s => s.status === SubmissionStatus.PENDING || s.status === SubmissionStatus.IN_REVIEW).length}</p>
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">In Review</p>
            </button>
            <button onClick={() => navigate('/apps/aggregator/submit?tab=ready')} className="bg-white border border-violet-200 rounded-2xl p-4 text-center hover:border-violet-400 hover:shadow-sm transition-all cursor-pointer">
              <div className="w-14 h-14 mx-auto rounded-full border-4 border-violet-400 flex items-center justify-center mb-2">
                <Rocket size={20} className="text-violet-600" />
              </div>
              <p className="text-2xl font-bold text-zinc-900">{mySubs.filter(s => s.status === SubmissionStatus.DEPLOYMENT_READY).length}</p>
              <p className="text-[10px] font-bold text-violet-600 uppercase tracking-widest">Deploy Ready</p>
            </button>
            <button onClick={() => navigate('/apps/aggregator/submit?tab=live')} className="bg-white border border-emerald-200 rounded-2xl p-4 text-center hover:border-emerald-400 hover:shadow-sm transition-all cursor-pointer">
              <div className="w-14 h-14 mx-auto rounded-full border-4 border-emerald-400 flex items-center justify-center mb-2">
                <Radio size={20} className="text-emerald-600" />
              </div>
              <p className="text-2xl font-bold text-zinc-900">{mySubs.filter(s => s.status === SubmissionStatus.COMPLETED).length}</p>
              <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Live</p>
            </button>
            <div className="bg-white border border-amber-200 rounded-2xl p-4 text-center relative">
              <div className="w-14 h-14 mx-auto rounded-full border-4 border-amber-400 flex items-center justify-center mb-2 relative">
                <RotateCcw size={20} className="text-amber-600" />
                {unlockedItemsCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-bounce">
                    {unlockedItemsCount}
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-zinc-900">{unlockedSubs.length}</p>
              <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Action Required</p>
              {unlockedItemsCount > 0 && (
                <button
                  onClick={() => navigate('/apps/aggregator/submit')}
                  className="mt-2 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-lg hover:bg-red-100 transition-all"
                >
                  Fix {unlockedItemsCount} Item{unlockedItemsCount > 1 ? 's' : ''}
                </button>
              )}
            </div>
            <button onClick={() => navigate('/apps/aggregator/submit?tab=rejected')} className="bg-white border border-zinc-300 rounded-2xl p-4 text-center hover:border-zinc-500 hover:shadow-sm transition-all cursor-pointer">
              <div className="w-14 h-14 mx-auto rounded-full border-4 border-zinc-400 flex items-center justify-center mb-2">
                <Ban size={20} className="text-zinc-600" />
              </div>
              <p className="text-2xl font-bold text-zinc-900">{mySubs.filter(s => s.status === SubmissionStatus.REJECTED).length}</p>
              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Rejected</p>
            </button>
          </div>
        </div>
      )}

      {/* -- KPI Row -- */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {canEdit && (
          <button onClick={() => setStatusFilter(statusFilter === CampaignStatus.DRAFT ? 'ALL' : CampaignStatus.DRAFT)} className={cn(
            "bg-white border p-4 rounded-xl flex items-center gap-3 transition-all hover:shadow-sm text-left",
            statusFilter === CampaignStatus.DRAFT ? "border-amber-400 ring-1 ring-amber-200" : "border-zinc-200"
          )}>
            <div className="w-9 h-9 bg-amber-500 rounded-lg flex items-center justify-center text-white shrink-0"><FileEdit size={16} /></div>
            <div><p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Draft</p><p className="text-xl font-bold text-zinc-900">{draft}</p></div>
          </button>
        )}
        <button onClick={() => setStatusFilter(statusFilter === CampaignStatus.ACTIVE ? 'ALL' : CampaignStatus.ACTIVE)} className={cn(
          "bg-white border p-4 rounded-xl flex items-center gap-3 transition-all hover:shadow-sm text-left",
          statusFilter === CampaignStatus.ACTIVE ? "border-emerald-400 ring-1 ring-emerald-200" : "border-zinc-200"
        )}>
          <div className="w-9 h-9 bg-emerald-500 rounded-lg flex items-center justify-center text-white shrink-0"><Megaphone size={16} /></div>
          <div><p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Active</p><p className="text-xl font-bold text-zinc-900">{active}</p></div>
        </button>
        {canEdit && (
          <button onClick={() => setStatusFilter(statusFilter === CampaignStatus.PLANNED ? 'ALL' : CampaignStatus.PLANNED)} className={cn(
            "bg-white border p-4 rounded-xl flex items-center gap-3 transition-all hover:shadow-sm text-left",
            statusFilter === CampaignStatus.PLANNED ? "border-indigo-400 ring-1 ring-indigo-200" : "border-zinc-200"
          )}>
            <div className="w-9 h-9 bg-indigo-500 rounded-lg flex items-center justify-center text-white shrink-0"><Calendar size={16} /></div>
            <div><p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Planned</p><p className="text-xl font-bold text-zinc-900">{planned}</p></div>
          </button>
        )}
        <button onClick={() => setStatusFilter(statusFilter === CampaignStatus.CLOSED ? 'ALL' : CampaignStatus.CLOSED)} className={cn(
          "bg-white border p-4 rounded-xl flex items-center gap-3 transition-all hover:shadow-sm text-left",
          statusFilter === CampaignStatus.CLOSED ? "border-zinc-500 ring-1 ring-zinc-300" : "border-zinc-200"
        )}>
          <div className="w-9 h-9 bg-zinc-400 rounded-lg flex items-center justify-center text-white shrink-0"><BarChart2 size={16} /></div>
          <div><p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Closed</p><p className="text-xl font-bold text-zinc-900">{closed}</p></div>
        </button>
      </div>

      {/* -- Expiring Today & Activating Tomorrow -- */}
      {(() => {
        const todayStart = new Date(); todayStart.setHours(0,0,0,0);
        const todayEnd   = new Date(); todayEnd.setHours(23,59,59,999);
        const tomorrowStart = new Date(todayStart); tomorrowStart.setDate(tomorrowStart.getDate() + 1);
        const tomorrowEnd   = new Date(todayEnd);   tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);

        const expiringToday = campaigns.filter(c =>
          c.status === CampaignStatus.ACTIVE && c.validTo >= todayStart.getTime() && c.validTo <= todayEnd.getTime()
        );
        const activatingTomorrow = campaigns.filter(c =>
          c.status === CampaignStatus.PLANNED && c.validFrom >= tomorrowStart.getTime() && c.validFrom <= tomorrowEnd.getTime()
        );
        if (expiringToday.length === 0 && activatingTomorrow.length === 0) return null;

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {expiringToday.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center text-white"><AlertTriangle size={16} /></div>
                  <div>
                    <p className="text-sm font-bold text-red-700">Expiring Today</p>
                    <p className="text-[10px] text-red-500">{expiringToday.length} campaign{expiringToday.length > 1 ? 's' : ''} ending today</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {expiringToday.map(c => {
                    const bNames = c.targetAllBrands ? ['All Brands'] : c.brandIds.map(id => brands.find(b => b.id === id)?.name ?? id);
                    return <ExpiryAlertCard key={c.id} campaign={c} brandNames={bNames} variant="expiring" onNavigate={() => navigate('/apps/aggregator/live')} />;
                  })}
                </div>
              </div>
            )}
            {activatingTomorrow.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white"><Rocket size={16} /></div>
                  <div>
                    <p className="text-sm font-bold text-blue-700">Activating Tomorrow</p>
                    <p className="text-[10px] text-blue-500">{activatingTomorrow.length} campaign{activatingTomorrow.length > 1 ? 's' : ''} going live</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {activatingTomorrow.map(c => {
                    const bNames = c.targetAllBrands ? ['All Brands'] : c.brandIds.map(id => brands.find(b => b.id === id)?.name ?? id);
                    return <ExpiryAlertCard key={c.id} campaign={c} brandNames={bNames} variant="activating" onNavigate={() => navigate('/apps/aggregator/management')} />;
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* -- Analytics Charts (Admin Only) -- */}
      {canEdit && campaigns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Status Distribution</p>
            <div className="space-y-2">
              {([
                { label: 'Draft',   count: draft,   color: 'bg-amber-500' },
                { label: 'Planned', count: planned, color: 'bg-indigo-500' },
                { label: 'Active',  count: active,  color: 'bg-emerald-500' },
                { label: 'Closed',  count: closed,  color: 'bg-zinc-400' },
              ] as const).map(s => (
                <div key={s.label} className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-zinc-500 w-14">{s.label}</span>
                  <div className="flex-1 h-4 bg-zinc-100 rounded-full overflow-hidden">
                    <div className={cn("h-full rounded-full transition-all", s.color)} style={{ width: `${campaigns.length > 0 ? (s.count / campaigns.length) * 100 : 0}%` }} />
                  </div>
                  <span className="text-xs font-bold text-zinc-700 w-6 text-right">{s.count}</span>
                </div>
              ))}
            </div>
          </div>

          {(() => {
            const pending   = submissions.filter(s => s.status === SubmissionStatus.PENDING || s.status === SubmissionStatus.IN_REVIEW).length;
            const approved  = submissions.filter(s => s.status === SubmissionStatus.APPROVED || s.status === SubmissionStatus.DEPLOYMENT_READY).length;
            const completed = submissions.filter(s => s.status === SubmissionStatus.COMPLETED).length;
            const rejected  = submissions.filter(s => s.status === SubmissionStatus.REJECTED).length;
            const total     = submissions.length || 1;
            const pPct = (pending / total) * 100;
            const aPct = (approved / total) * 100;
            const cPct = (completed / total) * 100;
            const gradient = `conic-gradient(#3b82f6 0% ${pPct}%, #8b5cf6 ${pPct}% ${pPct + aPct}%, #10b981 ${pPct + aPct}% ${pPct + aPct + cPct}%, #ef4444 ${pPct + aPct + cPct}% 100%)`;
            return (
              <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Submission Pipeline</p>
                {submissions.length === 0 ? (
                  <p className="text-xs text-zinc-400 italic text-center py-6">No submissions yet</p>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full shrink-0" style={{ background: gradient }}>
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-zinc-900">{submissions.length}</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1 text-[11px]">
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500" /><span className="text-zinc-600">Review: {pending}</span></div>
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-violet-500" /><span className="text-zinc-600">Ready: {approved}</span></div>
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-zinc-600">Done: {completed}</span></div>
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" /><span className="text-zinc-600">Reject: {rejected}</span></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {(() => {
            const completedSubs = submissions.filter(s => s.status === SubmissionStatus.COMPLETED && s.reviewHistory.length > 0);
            const speeds = completedSubs.map(s => {
              const lastApproval = s.reviewHistory.filter(r => r.action === 'APPROVE').at(-1);
              return lastApproval ? Math.ceil((lastApproval.timestamp - s.createdAt) / (1000 * 60 * 60 * 24)) : 0;
            }).filter(d => d > 0);
            const avg = speeds.length > 0 ? (speeds.reduce((a, b) => a + b, 0) / speeds.length).toFixed(1) : '·';
            const fastest = speeds.length > 0 ? Math.min(...speeds) : 0;
            const slowest = speeds.length > 0 ? Math.max(...speeds) : 0;
            return (
              <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Review Speed</p>
                {speeds.length === 0 ? (
                  <p className="text-xs text-zinc-400 italic text-center py-6">No completed reviews yet</p>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-zinc-900">{avg}</p>
                      <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Avg Days</p>
                    </div>
                    <div className="flex justify-between text-xs">
                      <div className="text-center flex-1"><p className="text-lg font-bold text-emerald-600">{fastest}</p><p className="text-[9px] font-bold text-zinc-400 uppercase">Fast</p></div>
                      <div className="w-px bg-zinc-200" />
                      <div className="text-center flex-1"><p className="text-lg font-bold text-amber-600">{slowest}</p><p className="text-[9px] font-bold text-zinc-400 uppercase">Slow</p></div>
                      <div className="w-px bg-zinc-200" />
                      <div className="text-center flex-1"><p className="text-lg font-bold text-zinc-700">{speeds.length}</p><p className="text-[9px] font-bold text-zinc-400 uppercase">Samples</p></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* -- Search + Filter Bar -- */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search campaigns, brands, aggregators·"
              className="w-full pl-9 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500 transition-colors"
            />
          </div>
          {/* Date range */}
          <div className="flex items-center gap-2">
            <DatePicker
              value={dateFrom}
              onChange={v => setDateFrom(v)}
              max={dateTo || undefined}
              placeholder="From date"
              className="w-[140px]"
            />
            <span className="text-xs text-zinc-400">?</span>
            <DatePicker
              value={dateTo}
              onChange={v => setDateTo(v)}
              min={dateFrom || undefined}
              placeholder="To date"
              className="w-[140px]"
            />
          </div>
        </div>

        {/* Status filter pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mr-1">Status:</span>
          {(['ALL', CampaignStatus.ACTIVE, CampaignStatus.PLANNED, CampaignStatus.DRAFT, CampaignStatus.CLOSED] as const).map(s => {
            if (!canEdit && (s === CampaignStatus.DRAFT || s === CampaignStatus.PLANNED || s === CampaignStatus.CLOSED)) return null;
            const isActive = statusFilter === s;
            const colors: Record<string, string> = {
              ALL: isActive ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-zinc-400',
              [CampaignStatus.ACTIVE]: isActive ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-400',
              [CampaignStatus.PLANNED]: isActive ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:border-indigo-400',
              [CampaignStatus.DRAFT]: isActive ? 'bg-amber-600 text-white border-amber-600' : 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400',
              [CampaignStatus.CLOSED]: isActive ? 'bg-zinc-600 text-white border-zinc-600' : 'bg-zinc-100 text-zinc-500 border-zinc-200 hover:border-zinc-400',
            };
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s === statusFilter ? 'ALL' : s)}
                className={cn("px-3 py-1 rounded-lg text-[10px] font-bold border transition-all", colors[s])}
              >
                {s === 'ALL' ? `All (${baseVisible.length})` : `${s} (${baseVisible.filter(cc => cc.status === s).length})`}
              </button>
            );
          })}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="px-2.5 py-1 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all flex items-center gap-1">
              <X size={10} /> Clear
            </button>
          )}
          <span className="ml-auto text-[10px] text-zinc-400 font-semibold">{visible.length} result{visible.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* -- Campaign Cards Grid -- */}
      {visible.length === 0 ? (
        <div className="py-16 text-center border border-zinc-200 rounded-xl bg-white">
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center">
              <Megaphone size={28} className="text-zinc-300" />
            </div>
            <p className="text-sm font-bold text-zinc-500">
              {hasActiveFilters ? 'No campaigns match your filters.' : canEdit ? 'No campaigns to display.' : 'No Active Campaigns'}
            </p>
            <p className="text-xs text-zinc-400 max-w-sm mx-auto">
              {hasActiveFilters
                ? 'Try adjusting your search or date range.'
                : canEdit
                  ? 'Create your first campaign from the Management tab.'
                  : 'No active campaigns found for your assigned brands. Check back later.'}
            </p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all">
                <X size={12} /> Clear Filters
              </button>
            )}
            {!hasActiveFilters && canEdit && (
              <button onClick={() => navigate('/apps/aggregator/management')} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all shadow-[0_2px_8px_rgba(0,0,0,0.12)]">
                <Plus size={14} /> Create Campaign
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {visible.map(c => {
            const campSubs = mySubs.filter(s => s.campaignId === c.id);
            return (
              <CampaignCard
                key={c.id}
                campaign={c}
                brands={brands}
                submissions={campSubs}
                isManager={!canEdit}
                aggregatorLogos={aggLogos}
                onNavigateSubmit={() => navigate(campSubs.length > 0 ? `/apps/aggregator/submit?campaign=${c.id}&tab=all` : `/apps/aggregator/submit?campaign=${c.id}`)}
              />
            );
          })}
        </div>
      )}

      {/* -- Quick Actions (Admin: Launch Readiness / Dispatch / Archive) -- */}
      {canEdit && (
        <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
          <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Quick Actions</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate('/apps/aggregator/deployment')} className="flex items-center gap-1.5 px-3 py-2 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg text-xs font-bold hover:bg-violet-100 transition-all">
              <Rocket size={13} /> Launch Readiness
            </button>
            <button onClick={() => navigate('/apps/aggregator/live')} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-bold hover:bg-emerald-100 transition-all">
              <Radio size={13} /> Live Operations
            </button>
            <button onClick={() => navigate('/apps/aggregator/archive')} className="flex items-center gap-1.5 px-3 py-2 bg-zinc-50 text-zinc-700 border border-zinc-200 rounded-lg text-xs font-bold hover:bg-zinc-100 transition-all">
              <Archive size={13} /> Campaign Archive
            </button>
            <button onClick={() => navigate('/apps/aggregator/analytics')} className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold hover:bg-blue-100 transition-all">
              <BarChart2 size={13} /> Analytics
            </button>
          </div>
        </div>
      )}

      {/* -- Campaign History · visible to ALL users -- */}
      {(canEdit ? submissions.length > 0 : mySubs.length > 0) && (
        <CampaignHistorySection
          submissions={canEdit ? submissions : mySubs}
          campaigns={campaigns}
          brands={brands}
          user={user}
          canEdit={canEdit}
          navigate={navigate}
        />
      )}
    </div>
  );
}

// --- Campaign History Section (all users) ------------------------------------

const CampaignHistorySection: React.FC<{
  submissions: CampaignSubmission[];
  campaigns: Campaign[];
  brands: Brand[];
  user: User | null;
  canEdit: boolean;
  navigate: (path: string) => void;
}> = ({ submissions, campaigns, brands, canEdit, navigate }) => {
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<SubmissionStatus | 'ALL'>('ALL');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const toast = useToast();

  // Build enriched history entries
  const entries = submissions.map(sub => {
    const campaign = campaigns.find(c => c.id === sub.campaignId);
    const brand = brands.find(b => b.id === sub.brandId);
    return { ...sub, campaign, brand };
  }).sort((a, b) => b.updatedAt - a.updatedAt);

  const filtered = entries.filter(e => {
    if (historyStatusFilter !== 'ALL' && e.status !== historyStatusFilter) return false;
    if (historySearch.trim()) {
      const q = historySearch.toLowerCase();
      if (
        !e.campaignName.toLowerCase().includes(q) &&
        !e.brandName.toLowerCase().includes(q) &&
        !(e.campaign?.aggregators ?? []).some(a => a.toLowerCase().includes(q))
      ) return false;
    }
    return true;
  });

  const handleBrandDownload = async (sub: CampaignSubmission & { campaign?: Campaign; brand?: Brand }) => {
    if (!sub.campaign) return;
    setDownloadingId(sub.id);
    try {
      const c = sub.campaign;
      const liveItems = sub.items.filter(it => it.itemStatus !== 'DISCARDED');
      if (liveItems.length === 0) {
        toast.warning('No active items to export.');
        setDownloadingId(null);
        return;
      }
      // Build brand-wise Excel with new column order
      const XLSX = await import('xlsx-js-style');
      const none = 'None';
      const durationText = (() => {
        const from = new Date(c.validFrom);
        const to = new Date(c.validTo);
        const dayF = from.getDate();
        const dayT = to.getDate();
        const monthT = to.toLocaleDateString('en-GB', { month: 'long' });
        if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear())
          return `${dayF} ${monthT} to ${dayT} ${monthT}`;
        const monthF = from.toLocaleDateString('en-GB', { month: 'long' });
        return `${dayF} ${monthF} to ${dayT} ${monthT}`;
      })();
      const calcDiscount = (price: number) => {
        if (c.discountType === DiscountType.PERCENTAGE)
          return Math.round(price * (1 - c.discountValue / 100) * 100) / 100;
        return Math.max(0, Math.round((price - c.discountValue) * 100) / 100);
      };

      const headers = ['Item EN', 'Item AR', 'Description EN', 'Description AR', 'Option Groups EN', 'Option Groups AR', 'Original Price', 'After Discount', 'Duration'];
      const rows = liveItems.map(item => {
        const ogEN = (item.importedOptions ?? []).map(g => {
          const opts = g.options.map(o => o.nameEN || none).join(', ');
          return `${g.nameEN || none}: ${opts || none}`;
        }).join(' | ') || none;
        const ogAR = (item.importedOptions ?? []).map(g => {
          const opts = g.options.map(o => o.nameAR || none).join(', ');
          return `${g.nameAR || none}: ${opts || none}`;
        }).join(' | ') || none;
        return [
          item.name || none,
          item.nameAR || none,
          item.description || none,
          item.descriptionAR || none,
          ogEN,
          ogAR,
          item.price,
          calcDiscount(item.price),
          durationText,
        ];
      });

      const aoa = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // Style headers
      for (let ci = 0; ci < headers.length; ci++) {
        const cell = ws[XLSX.utils.encode_cell({ r: 0, c: ci })];
        if (cell) {
          cell.s = { font: { bold: true, color: { rgb: '000000' } }, fill: { fgColor: { rgb: 'FFD700' } }, alignment: { horizontal: 'center' } };
        }
      }
      // Auto-width
      ws['!cols'] = headers.map((_, i) => {
        let max = headers[i].length;
        rows.forEach(r => { const v = String(r[i] ?? ''); if (v.length > max) max = v.length; });
        return { wch: Math.min(Math.max(max + 2, 10), 55) };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sub.brandName.slice(0, 31));
      const safeFileName = `${sub.brandName.replace(/[^a-zA-Z0-9 ]/g, '')}_${c.name.replace(/[^a-zA-Z0-9 ]/g, '')}_Items.xlsx`;
      XLSX.writeFile(wb, safeFileName);
      toast.success(`Downloaded ${liveItems.length} items for ${sub.brandName}`);
    } catch (err) {
      toast.error('Download failed');
    }
    setDownloadingId(null);
  };

  const statusList = [SubmissionStatus.PENDING, SubmissionStatus.IN_REVIEW, SubmissionStatus.APPROVED, SubmissionStatus.DEPLOYMENT_READY, SubmissionStatus.COMPLETED, SubmissionStatus.REJECTED] as const;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <History size={14} className="text-zinc-500" />
            <h3 className="text-sm font-bold text-zinc-800">My Campaign History</h3>
            <span className="text-[9px] font-bold text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">{submissions.length}</span>
          </div>
          {canEdit && (
            <button onClick={() => navigate('/apps/aggregator/archive')} className="text-[10px] font-bold text-zinc-500 hover:text-zinc-700 transition-colors">
              View Full Archive ?
            </button>
          )}
        </div>
        {/* History search + filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={historySearch}
              onChange={e => setHistorySearch(e.target.value)}
              placeholder="Search history·"
              className="w-full pl-8 pr-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-[11px] focus:outline-none focus:border-emerald-500 transition-colors"
            />
          </div>
          <CustomSelect
            value={historyStatusFilter}
            onChange={v => setHistoryStatusFilter(v as SubmissionStatus | 'ALL')}
            options={[
              { value: 'ALL', label: 'All Status' },
              ...statusList.map(s => ({ value: s, label: s })),
            ]}
            placeholder="All Status"
            className="min-w-[130px]"
          />
        </div>
      </div>

      {/* History list */}
      <div className="divide-y divide-zinc-100 max-h-[400px] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-xs text-zinc-400">No submissions match your filters.</p>
          </div>
        ) : filtered.map(e => {
          const sc = STATUS_COLOR[e.status];
          const expanded = expandedId === e.id;
          const liveItems = e.items.filter(it => it.itemStatus !== 'DISCARDED');

          return (
            <div key={e.id} className="hover:bg-zinc-50/50 transition-colors">
              <button onClick={() => setExpandedId(expanded ? null : e.id)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
                {/* Status dot */}
                <span className={cn("w-2 h-2 rounded-full shrink-0", sc.dot)} />
                {/* Campaign + Brand */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-zinc-800 truncate">{e.campaignName}</p>
                  <p className="text-[10px] text-zinc-400">{e.brandName}</p>
                </div>
                {/* Status pill */}
                <span className={cn("px-2 py-0.5 rounded-full text-[8px] font-bold border shrink-0", sc.pill)}>{e.status}</span>
                {/* Items count */}
                <span className="text-[9px] font-bold text-zinc-400 shrink-0">{liveItems.length} items</span>
                {/* Date */}
                <span className="text-[9px] text-zinc-400 shrink-0 w-16 text-right">{new Date(e.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                <ChevronDown size={12} className={cn("text-zinc-400 transition-transform shrink-0", expanded && 'rotate-180')} />
              </button>

              {/* Expanded details */}
              {expanded && (
                <div className="px-4 pb-3 space-y-2">
                  {/* Campaign info */}
                  {e.campaign && (
                    <div className="flex items-center gap-3 flex-wrap text-[10px]">
                      <span className="flex items-center gap-1 text-zinc-500">
                        <Calendar size={10} />
                        {new Date(e.campaign.validFrom).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} · {new Date(e.campaign.validTo).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </span>
                      <span className="flex items-center gap-1 text-teal-600 font-bold">
                        {e.campaign.discountType === DiscountType.PERCENTAGE ? <><Percent size={10} />{e.campaign.discountValue}%</> : <><DollarSign size={10} />{e.campaign.discountValue}</>}
                      </span>
                      {e.campaign.aggregators.map(a => (
                        <span key={a} className="px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded border border-teal-100 text-[9px] font-semibold">{a}</span>
                      ))}
                    </div>
                  )}

                  {/* Items preview */}
                  <div className="bg-zinc-50 rounded-lg p-2 space-y-1">
                    {liveItems.slice(0, 5).map(it => (
                      <div key={it.id} className="flex items-center justify-between text-[10px]">
                        <span className="text-zinc-700 font-medium truncate flex-1">{it.name}</span>
                        {it.nameAR && <span className="text-zinc-400 font-arabic text-[9px] mx-2 truncate max-w-[100px]">{it.nameAR}</span>}
                        <span className="text-zinc-500 font-bold shrink-0">{it.price}</span>
                      </div>
                    ))}
                    {liveItems.length > 5 && (
                      <p className="text-[9px] text-zinc-400 italic">+ {liveItems.length - 5} more items·</p>
                    )}
                  </div>

                  {/* Review history summary */}
                  {e.reviewHistory.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Reviews:</span>
                      {e.reviewHistory.slice(-3).map((rh, i) => (
                        <span key={i} className={cn("px-1.5 py-0.5 rounded text-[8px] font-bold",
                          rh.action === 'APPROVE' ? 'bg-emerald-50 text-emerald-700' :
                          rh.action === 'REJECT' ? 'bg-red-50 text-red-700' :
                          rh.action === 'UNLOCK' ? 'bg-amber-50 text-amber-700' :
                          'bg-blue-50 text-blue-700'
                        )}>
                          {rh.action} by {rh.reviewerName}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => handleBrandDownload(e)}
                      disabled={downloadingId === e.id || liveItems.length === 0}
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all",
                        liveItems.length === 0
                          ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      )}
                    >
                      <Download size={10} /> {downloadingId === e.id ? 'Downloading·' : 'Download Items (Excel)'}
                    </button>
                    {e.campaign && (
                      <button
                        onClick={() => navigate(`/apps/aggregator/submit?submission=${e.id}&tab=all`)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-lg text-[10px] font-bold hover:bg-zinc-200 transition-all"
                      >
                        <Eye size={10} /> View Submission
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Expiry / Activation Alert Card ------------------------------------------

const ExpiryAlertCard: React.FC<{
  campaign: Campaign;
  brandNames: string[];
  variant: 'expiring' | 'activating';
  onNavigate: () => void;
}> = ({ campaign: c, brandNames, variant, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const isExpiring = variant === 'expiring';
  const borderColor = isExpiring ? 'border-red-200 hover:border-red-300' : 'border-blue-200 hover:border-blue-300';
  const headerBg = isExpiring ? 'bg-red-100' : 'bg-blue-100';
  const accentText = isExpiring ? 'text-red-700' : 'text-blue-700';
  const accentBg = isExpiring ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className={cn('bg-white border rounded-xl overflow-hidden transition-all cursor-pointer', borderColor)} onClick={() => setOpen(o => !o)}>
      <div className={cn('flex items-center justify-between px-4 py-3', headerBg)}>
        <div className="flex items-center gap-3 min-w-0">
          <span className={cn('text-sm font-bold truncate', accentText)}>{c.name}</span>
          <span className="text-[10px] font-semibold text-zinc-500">{fmtDate(isExpiring ? c.validTo : c.validFrom)}</span>
        </div>
        <ChevronDown size={14} className={cn('text-zinc-400 transition-transform', open && 'rotate-180')} />
      </div>
      {open && (
        <div className="px-4 py-3 space-y-3 border-t border-zinc-100">
          {/* Discount info */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5 font-bold text-zinc-700">
              {c.discountType === DiscountType.PERCENTAGE
                ? <><Percent size={13} className="text-teal-600" /> {c.discountValue}% Off</>
                : <><DollarSign size={13} className="text-teal-600" /> {c.discountValue} Flat</>
              }
            </div>
            <span className="text-[10px] text-zinc-400">{fmtDate(c.validFrom)} · {fmtDate(c.validTo)}</span>
          </div>
          {/* Brands */}
          <div className="flex flex-wrap gap-1.5">
            {brandNames.map(name => (
              <span key={name} className="px-2 py-0.5 bg-zinc-100 text-zinc-600 text-[10px] font-semibold rounded-lg border border-zinc-200">{name}</span>
            ))}
          </div>
          {/* Aggregators */}
          <div className="flex flex-wrap gap-1.5">
            {c.aggregators.map(agg => (
              <span key={agg} className="px-2 py-0.5 bg-teal-50 text-teal-700 text-[10px] font-semibold rounded-lg border border-teal-100">{agg}</span>
            ))}
          </div>
          {/* Funding + Target */}
          <div className="flex items-center gap-3 text-[10px]">
            <span className={cn('px-2 py-0.5 rounded-lg font-bold uppercase tracking-widest', FUNDING_STYLES[c.fundingType])}>{FUNDING_LABELS[c.fundingType]}</span>
            {!!(c.requiredItemCount && c.requiredItemCount > 0) && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full font-bold"><Target size={10} /> Target: {c.requiredItemCount} items / brand</span>
            )}
          </div>
          {/* Navigate button */}
          <button
            onClick={e => { e.stopPropagation(); onNavigate(); }}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-xs font-bold transition-all', accentBg)}
          >
            <Eye size={12} /> {isExpiring ? 'View in Live Operations' : 'View in Management'}
          </button>
        </div>
      )}
    </div>
  );
};

const CampaignCard: React.FC<{
  campaign: Campaign;
  brands: Brand[];
  submissions?: CampaignSubmission[];
  isManager?: boolean;
  aggregatorLogos?: Record<string, string>;
  onNavigateSubmit?: () => void;
}> = ({ campaign: c, brands, submissions = [], isManager, aggregatorLogos = {}, onNavigateSubmit }) => {
  const st = STATUS_STYLES[c.status];
  const brandNames = c.targetAllBrands
    ? ['All Brands']
    : c.brandIds.map(id => brands.find(b => b.id === id)?.name ?? id);

  const hasUnlockedItems = submissions.some(s => s.items.some(it => it.locked === false));

  // Time remaining calculation
  const msLeft = c.validTo - Date.now();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  const isExpiringSoon = c.status === CampaignStatus.ACTIVE && daysLeft <= 3 && daysLeft > 0;
  const isExpired = msLeft <= 0;
  const isNotStarted = c.validFrom > Date.now();

  return (
    <div className={cn(
      "bg-white border rounded-xl p-4 hover:shadow-md transition-all relative group",
      hasUnlockedItems ? "border-amber-300 ring-1 ring-amber-100" : isExpiringSoon ? "border-red-200" : "border-zinc-200"
    )}>
      {/* Urgent badge */}
      {hasUnlockedItems && (
        <div className="absolute -top-2 right-3 px-2 py-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full shadow-sm animate-bounce flex items-center gap-1">
          <AlertCircle size={8} /> Fix Required
        </div>
      )}

      {/* Row 1: Status + Name + Discount + Aggregator Logos */}
      <div className="flex items-center gap-2 mb-2">
        <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border shrink-0', st.pill)}>
          <span className={cn('w-1.5 h-1.5 rounded-full', st.dot)} />
          {st.label}
        </span>
        <h3 className="text-sm font-bold text-zinc-900 truncate flex-1">{c.name}</h3>
        <div className="flex items-center gap-1 shrink-0">
          {c.aggregators.slice(0, 4).map(agg => {
            const logo = aggregatorLogos[agg];
            return logo
              ? <img key={agg} src={logo} alt={agg} title={agg} className="w-5 h-5 rounded object-contain border border-zinc-100" />
              : <span key={agg} title={agg} className="w-5 h-5 rounded bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[7px] font-bold text-indigo-600">{agg.charAt(0)}</span>;
          })}
          {c.aggregators.length > 4 && (
            <span className="text-[8px] font-bold text-zinc-400">+{c.aggregators.length - 4}</span>
          )}
        </div>
        <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 bg-teal-50 border border-teal-200 rounded-lg text-[10px] font-bold text-teal-700">
          {c.discountType === DiscountType.PERCENTAGE
            ? <><Percent size={10} /> {c.discountValue}%</>
            : <><DollarSign size={10} /> {c.discountValue}</>
          }
        </span>
      </div>

      {/* Row 2: Date range + Time remaining + Funding */}
      <div className="flex items-center gap-2 flex-wrap text-[10px] mb-2.5">
        <span className="flex items-center gap-1 text-zinc-400">
          <Calendar size={10} />
          {fmtDate(c.validFrom)} · {fmtDate(c.validTo)}
        </span>
        {c.status === CampaignStatus.ACTIVE && !isExpired && (
          <span className={cn(
            "px-1.5 py-0.5 rounded font-bold",
            daysLeft <= 1 ? "bg-red-100 text-red-700" : daysLeft <= 3 ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"
          )}>
            {daysLeft}d left
          </span>
        )}
        {isNotStarted && c.status === CampaignStatus.PLANNED && (
          <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-bold">
            Starts {fmtDate(c.validFrom)}
          </span>
        )}
        <span className={cn('px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider', FUNDING_STYLES[c.fundingType])}>
          {FUNDING_LABELS[c.fundingType]}
        </span>
        {!!(c.requiredItemCount && c.requiredItemCount > 0) && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full text-[9px] font-bold">
            <Target size={9} /> Target: {c.requiredItemCount} items / brand
          </span>
        )}
      </div>

      {/* Row 3: Brands (with status) + Aggregators inline */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        {(() => {
          // Build brand list with submission status
          const brandList = c.targetAllBrands
            ? brands.map(b => ({ id: b.id, name: b.name }))
            : c.brandIds.map(id => ({ id, name: brands.find(b => b.id === id)?.name ?? id }));
          const shown = brandList.slice(0, 4);
          const overflow = brandList.length - shown.length;

          return (
            <>
              {shown.map(b => {
                const sub = submissions.find(s => s.brandId === b.id);
                const done = !!sub;
                const rejected = sub?.status === SubmissionStatus.REJECTED;
                const pillCls = rejected
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : done
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-red-50 text-red-500 border-red-200';
                const dotCls = rejected ? 'bg-red-500' : done ? 'bg-emerald-500' : 'bg-red-400';
                return (
                  <span key={b.id} className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded border", pillCls)}>
                    <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", dotCls)} />
                    {b.name}
                  </span>
                );
              })}
              {overflow > 0 && (
                <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-500 text-[9px] font-bold rounded">+{overflow}</span>
              )}
            </>
          );
        })()}
        <span className="text-zinc-300">|</span>
        {c.aggregators.slice(0, 3).map(agg => {
          const logo = aggregatorLogos[agg];
          return (
            <span key={agg} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 text-indigo-700 text-[9px] font-semibold rounded border border-indigo-100">
              {logo && <img src={logo} alt={agg} className="w-3.5 h-3.5 rounded object-contain" />}
              {agg}
            </span>
          );
        })}
        {c.aggregators.length > 3 && (
          <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-600 text-[9px] font-bold rounded">+{c.aggregators.length - 3}</span>
        )}
      </div>

      {/* Row 4: Manager submission bar */}
      {isManager && (
        <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
          <div className="flex items-center gap-1.5 flex-wrap">
            {submissions.length > 0 ? (
              submissions.map(s => {
                const sc = STATUS_COLOR[s.status];
                return (
                  <span key={s.id} className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold border", sc.pill)}>
                    <span className={cn("w-1 h-1 rounded-full", sc.dot)} />
                    {s.brandName}
                  </span>
                );
              })
            ) : (
              <span className="text-[10px] text-zinc-400 italic">Not started</span>
            )}
          </div>
          {onNavigateSubmit && (
            <button
              onClick={onNavigateSubmit}
              className={cn(
                "flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all shrink-0",
                submissions.length > 0
                  ? "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 border border-zinc-200"
                  : "bg-zinc-900 text-white hover:bg-zinc-800"
              )}
            >
              <Send size={10} /> {submissions.length > 0 ? 'Continue' : 'Submit'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// --- Management Tab -----------------------------------------------------------

function ManagementTab({ user }: { user: User | null }) {
  const canEdit = hasTabVariant(user, 'aggregator', 'management', 'admin');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [settings, setSettings] = useState<CampaignSettings>({ brands: [], aggregators: [...DEFAULT_AGGREGATORS] });
  const [showForm, setShowForm] = useState(false);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'ALL'>('ALL');
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const toast = useToast();

  // -- Soft-Delete Restore --
  const isAdmin = user?.role === 'ADMIN';
  const isSA    = user?.role === 'SUPER_ADMIN';
  const isPO    = user?.role === 'PLATFORM_OWNER';
  const canRestore = isAdmin || isSA || isPO;
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedCampaigns, setDeletedCampaigns] = useState<Campaign[]>([]);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const loadDeletedCampaigns = async () => {
    try {
      const res = await apiFetch<{ data: any[] }>('/api/aggregator/campaigns/deleted');
      setDeletedCampaigns((res.data ?? []).map((r: any) => ({
        id: r.id,
        name: r.name,
        description: r.description ?? '',
        status: r.status as CampaignStatus,
        discountType: r.discount_type as DiscountType,
        discountValue: r.discount_value ?? 0,
        fundingType: (r.funding_type ?? 'NONE') as FundingType,
        sterlingSharePct: r.sterling_share_pct ?? 50,
        partnerSharePct: r.partner_share_pct ?? 50,
        targetAllBrands: !!r.target_all_brands,
        brandIds: r.brand_ids ? JSON.parse(r.brand_ids) : [],
        aggregators: r.aggregators ?? [],
        validFrom: r.valid_from,
        validTo: r.valid_to,
        createdAt: r.created_at,
        createdBy: r.created_by ?? '',
        deletedAt: r.deleted_at,
      } as Campaign & { deletedAt?: number })));
    } catch { setDeletedCampaigns([]); }
  };

  const handleRestoreCampaign = async (id: string) => {
    setRestoringId(id);
    try {
      await apiFetch(`/api/aggregator/campaigns/${id}/restore`, { method: 'POST' });
      toast.success('Campaign Restored', 'The campaign and its submissions have been restored.');
      await loadDeletedCampaigns();
      await load();
    } catch { toast.error('Restore Failed', 'Could not restore campaign.'); }
    setRestoringId(null);
  };

  const load = async () => {
    const [c, b, s] = await Promise.all([
      getRepository().getCampaigns(),
      getRepository().getBrands(),
      getRepository().getCampaignSettings(),
    ]);
    setCampaigns(c);
    setBrands(b.filter(br => br.enabledModules?.includes('aggregator')));
    setSettings(s);
  };

  useEffect(() => { load(); const h = (e: any) => { if (import.meta.env.DEV && e && e.type === 'PULSE_UPDATE') console.log('💓 Heartbeat: Data Refreshed (Aggregator) ' + e.type); load(); }; window.addEventListener('PULSE_UPDATE', h); return () => window.removeEventListener('PULSE_UPDATE', h); }, []);

  const handleDelete = (id: string) => {
    setDeleteConfirm({
      message: 'Delete this campaign permanently?',
      onConfirm: async () => {
        await getRepository().deleteCampaign(id);
        await load();
        toast.success('Campaign Deleted', 'The campaign has been permanently removed.');
      },
    });
  };

  const handleSave = async (campaign: Campaign) => {
    const isNew = !editCampaign;
    await getRepository().saveCampaign(campaign);
    // Notify only users whose brands match the campaign's target brands
    if (isNew && user) {
      const recipientId = campaign.targetAllBrands
        ? '*'
        : `brands:${campaign.brandIds.join(',')}`;
      await getRepository().saveNotification({
        id: crypto.randomUUID(),
        recipientId,
        actorId: user.id,
        type: NotificationType.INFO,
        title: 'New Campaign Released',
        message: `${user.name} created a new campaign: "${campaign.name}". Check your submissions.`,
        targetPath: '/apps/aggregator/submit',
        isRead: false,
        createdAt: Date.now(),
      });
    }
    await load();
    setShowForm(false);
    setEditCampaign(null);
    toast.success(editCampaign ? 'Campaign Updated' : 'Campaign Created', `"${campaign.name}" saved successfully.`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900">Campaign Management</h2>
          <p className="text-sm text-slate-500 mt-1">Create, edit and manage all promotional campaigns.</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditCampaign(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-all"
          >
            <Plus size={16} /> Create New Campaign
          </button>
        )}
      </div>

      {/* Inline Create/Edit Form */}
      {showForm && canEdit && (
        <CampaignForm
          campaign={editCampaign}
          brands={brands}
          settings={settings}
          user={user}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditCampaign(null); }}
        />
      )}

      {/* Status Filter Pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-zinc-400 mr-1">
          <Filter size={13} />
          <span className="text-[9px] font-bold uppercase tracking-widest">Filter</span>
        </div>
        {(['ALL', CampaignStatus.DRAFT, CampaignStatus.PLANNED, CampaignStatus.ACTIVE, CampaignStatus.CLOSED] as const).map(f => {
          const isAll = f === 'ALL';
          const count = isAll ? campaigns.length : campaigns.filter(c => c.status === f).length;
          const st = isAll ? null : STATUS_STYLES[f];
          return (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all',
                statusFilter === f
                  ? isAll
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : cn(st!.pill, 'shadow-sm')
                  : 'bg-white text-zinc-400 border-zinc-200 hover:border-zinc-400'
              )}
            >
              {!isAll && <span className={cn('w-1.5 h-1.5 rounded-full', statusFilter === f ? st!.dot : 'bg-zinc-300')} />}
              {isAll ? 'All' : st!.label}
              <span className="ml-0.5 text-[10px] font-mono opacity-60">{count}</span>
            </button>
          );
        })}
        {canRestore && (
          <button
            type="button"
            onClick={() => { setShowDeleted(!showDeleted); if (!showDeleted) loadDeletedCampaigns(); }}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ml-2',
              showDeleted
                ? 'bg-red-50 text-red-600 border-red-300 shadow-sm'
                : 'bg-white text-zinc-400 border-zinc-200 hover:border-zinc-400'
            )}
          >
            <Trash2 size={13} />
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
            {showDeleted && deletedCampaigns.length > 0 && (
              <span className="ml-0.5 text-[10px] font-mono bg-red-200 text-red-700 px-1.5 py-0.5 rounded">{deletedCampaigns.length}</span>
            )}
          </button>
        )}
      </div>

      {/* Deleted Campaigns Panel */}
      {showDeleted && canRestore && (
        <div className="bg-red-50/50 border border-red-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-red-100/60 border-b border-red-200 flex items-center gap-2">
            <Trash2 size={14} className="text-red-500" />
            <span className="text-xs font-bold text-red-700 uppercase tracking-widest">Deleted Campaigns</span>
            <span className="text-[10px] font-mono bg-red-200 text-red-700 px-1.5 py-0.5 rounded ml-1">{deletedCampaigns.length}</span>
          </div>
          {deletedCampaigns.length === 0 ? (
            <div className="p-8 text-center text-red-400 text-sm italic">No deleted campaigns found.</div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-red-200">
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest">Name</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest">Status</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest">Brands</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest">Deleted</th>
                  <th className="px-4 py-2.5 text-[10px] font-bold text-red-400 uppercase tracking-widest text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100">
                {deletedCampaigns.map((c: any) => {
                  const brandNames = c.targetAllBrands
                    ? ['All Brands']
                    : c.brandIds.map((id: string) => brands.find(b => b.id === id)?.name ?? id);
                  return (
                    <tr key={c.id} className="hover:bg-red-100/40 transition-colors">
                      <td className="px-4 py-2.5">
                        <p className="text-sm font-semibold text-zinc-600">{c.name}</p>
                        {c.description && <p className="text-[10px] text-zinc-400 truncate max-w-[180px]">{c.description}</p>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-100 text-zinc-500 border border-zinc-200">
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {brandNames.slice(0, 2).map((n: string) => (
                            <span key={n} className="px-1.5 py-0.5 bg-red-100 text-red-500 text-[10px] font-semibold rounded">{n}</span>
                          ))}
                          {brandNames.length > 2 && <span className="text-[10px] text-red-400">+{brandNames.length - 2}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-zinc-400">
                        {c.deletedAt ? new Date(c.deletedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '·'}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => handleRestoreCampaign(c.id)}
                          disabled={restoringId === c.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-[11px] font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50"
                        >
                          <RotateCcw size={12} /> {restoringId === c.id ? 'Restoring...' : 'Restore'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Table */}
      {(() => {
        const filtered = statusFilter === 'ALL' ? campaigns : campaigns.filter(c => c.status === statusFilter);
        return (
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        {filtered.length === 0 ? (
          <div className="p-20 text-center text-zinc-400 italic text-sm">
            {campaigns.length === 0
              ? 'No campaigns yet. Click "Create New Campaign" to get started.'
              : `No ${statusFilter === 'ALL' ? '' : STATUS_STYLES[statusFilter].label.toLowerCase() + ' '}campaigns found.`
            }
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                {['Name', 'Status', 'Discount', 'Brands', 'Valid Period', 'Created', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map(c => {
                const st = STATUS_STYLES[c.status];
                const brandNames = c.targetAllBrands
                  ? ['All Brands']
                  : c.brandIds.map(id => brands.find(b => b.id === id)?.name ?? id);
                return (
                  <tr key={c.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-zinc-900">{c.name}</p>
                      {c.description && (
                        <p className="text-[11px] text-zinc-400 mt-0.5 truncate max-w-[200px]">{c.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold border', st.pill)}>
                        <span className={cn('w-1.5 h-1.5 rounded-full', st.dot)} />
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-zinc-700">
                      {c.discountType === DiscountType.PERCENTAGE ? `${c.discountValue}%` : `${c.discountValue} flat`}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {brandNames.slice(0, 3).map(name => (
                          <span key={name} className="px-2 py-0.5 bg-zinc-100 text-zinc-600 text-[10px] font-semibold rounded">
                            {name}
                          </span>
                        ))}
                        {brandNames.length > 3 && (
                          <span className="px-2 py-0.5 bg-zinc-100 text-zinc-400 text-[10px] font-semibold rounded">
                            +{brandNames.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500 tabular-nums whitespace-nowrap">
                      {fmtDate(c.validFrom)} · {fmtDate(c.validTo)}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400 tabular-nums">
                      {fmtDate(c.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canEdit && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setEditCampaign(c); setShowForm(true); }}
                            className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
        );
      })()}
      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}

// --- Campaign Form ------------------------------------------------------------

interface CampaignFormProps {
  campaign: Campaign | null;
  brands: Brand[];
  settings: CampaignSettings;
  user: User | null;
  onSave: (c: Campaign) => void;
  onClose: () => void;
}

function CampaignForm({ campaign, brands, settings, user, onSave, onClose }: CampaignFormProps) {
  const today = new Date().toISOString().slice(0, 10);
  const availableAggregators = (settings.aggregators ?? []).length > 0 ? settings.aggregators : [...DEFAULT_AGGREGATORS];

  const [form, setForm] = useState({
    name: campaign?.name ?? '',
    description: campaign?.description ?? '',
    status: campaign?.status ?? CampaignStatus.PLANNED,
    discountType: campaign?.discountType ?? DiscountType.PERCENTAGE,
    discountValue: campaign?.discountValue ?? 10,
    fundingType: campaign?.fundingType ?? FundingType.NONE,
    sterlingSharePct: campaign?.sterlingSharePct ?? 50,
    partnerSharePct: campaign?.partnerSharePct ?? 50,
    targetAllBrands: campaign?.targetAllBrands ?? true,
    brandIds: campaign?.brandIds ?? [] as string[],
    aggregators: campaign?.aggregators ?? [] as string[],
    validFrom: campaign ? new Date(campaign.validFrom).toISOString().slice(0, 10) : today,
    validTo: campaign ? new Date(campaign.validTo).toISOString().slice(0, 10) : today,
    startTime: campaign?.startTime ?? '08:00',
    importantRequirements: campaign?.importantRequirements ?? '',
    requiredItemCount: campaign?.requiredItemCount ?? 1,
  });
  const campaignSnap = React.useRef(campaign ? JSON.parse(JSON.stringify({
    name: campaign.name ?? '',
    description: campaign.description ?? '',
    status: campaign.status ?? CampaignStatus.PLANNED,
    discountType: campaign.discountType ?? DiscountType.PERCENTAGE,
    discountValue: campaign.discountValue ?? 10,
    fundingType: campaign.fundingType ?? FundingType.NONE,
    sterlingSharePct: campaign.sterlingSharePct ?? 50,
    partnerSharePct: campaign.partnerSharePct ?? 50,
    targetAllBrands: campaign.targetAllBrands ?? true,
    brandIds: campaign.brandIds ?? [],
    aggregators: campaign.aggregators ?? [],
    validFrom: new Date(campaign.validFrom).toISOString().slice(0, 10),
    validTo: new Date(campaign.validTo).toISOString().slice(0, 10),
    startTime: campaign.startTime ?? '08:00',
    importantRequirements: campaign.importantRequirements ?? '',
    requiredItemCount: campaign.requiredItemCount ?? 1,
  })) : null);
  const campaignDirty = React.useMemo(() => campaign ? hasDataChanged(campaignSnap.current, form) : !!form.name.trim(), [form, campaign]);

  const isDraft = form.status === CampaignStatus.DRAFT;

  const toggleBrand = (id: string) =>
    setForm(p => ({
      ...p,
      brandIds: p.brandIds.includes(id) ? p.brandIds.filter(b => b !== id) : [...p.brandIds, id],
    }));

  const toggleAgg = (agg: string) =>
    setForm(p => ({
      ...p,
      aggregators: p.aggregators.includes(agg) ? p.aggregators.filter(a => a !== agg) : [...p.aggregators, agg],
    }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const saved: Campaign = {
      id: campaign?.id ?? '',
      name: form.name,
      description: form.description || undefined,
      status: form.status,
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      fundingType: form.fundingType,
      sterlingSharePct: form.fundingType === FundingType.CO_FUNDED ? Number(form.sterlingSharePct) : undefined,
      partnerSharePct: form.fundingType === FundingType.CO_FUNDED ? Number(form.partnerSharePct) : undefined,
      targetAllBrands: form.targetAllBrands,
      brandIds: form.targetAllBrands ? [] : form.brandIds,
      aggregators: form.aggregators,
      validFrom: new Date(form.validFrom).getTime(),
      validTo: new Date(form.validTo).getTime(),
      startTime: form.status === CampaignStatus.PLANNED ? form.startTime : undefined,
      importantRequirements: form.importantRequirements.trim() || undefined,
      requiredItemCount: Math.max(1, Number(form.requiredItemCount) || 1),
      createdAt: campaign?.createdAt ?? Date.now(),
      createdBy: campaign?.createdBy ?? (user?.id ?? ''),
      orgId: campaign?.orgId,
    };
    onSave(saved);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Form Header */}
      <div className="bg-zinc-900 px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
            {campaign ? 'Edit Campaign' : 'New Campaign'}
          </p>
          <p className="text-white font-bold text-sm mt-0.5">
            {campaign ? campaign.name : 'Create Campaign'}
          </p>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors text-zinc-400 hover:text-white">
          <X size={16} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">

        {/* Section 1 · Campaign Information */}
        <FormSection label="Campaign Information">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Campaign Name</label>
              <input
                required
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900"
                placeholder="e.g. Summer Mega Deals"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Status</label>
              <div className="flex items-center gap-2 flex-wrap">
                {([CampaignStatus.DRAFT, CampaignStatus.PLANNED, CampaignStatus.ACTIVE, CampaignStatus.CLOSED] as CampaignStatus[]).map(s => {
                  const st = STATUS_STYLES[s];
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, status: s }))}
                      className={cn(
                        'px-4 py-2 rounded-xl text-sm font-semibold border transition-all',
                        form.status === s
                          ? cn(st.pill, 'shadow-sm')
                          : 'bg-zinc-50 text-zinc-400 border-zinc-200 hover:border-zinc-400'
                      )}
                    >
                      {st.label}
                    </button>
                  );
                })}
              </div>
              {/* Planned tooltip */}
              {form.status === CampaignStatus.PLANNED && (
                <div className="flex items-start gap-2.5 mt-2 p-3 bg-zinc-900 text-white rounded-xl">
                  <Info size={15} className="text-indigo-400 mt-0.5 shrink-0" />
                  <p className="text-xs leading-relaxed">
                    <span className="font-bold text-indigo-300">Scheduled Auto-Post:</span>{' '}
                    This campaign will automatically transition to <span className="font-bold text-emerald-400">ACTIVE</span> on the start date and time you specify.
                  </p>
                </div>
              )}
              {/* Draft info */}
              {form.status === CampaignStatus.DRAFT && (
                <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1.5">
                  <FileEdit size={12} />
                  Draft mode · only the Campaign Name is required. All other fields are optional.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Description <span className="normal-case font-normal text-zinc-300">(optional)</span></label>
              <textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900 resize-none"
                placeholder="Brief description of the campaign goals·"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
                <Hash size={10} /> Target Items per Brand
              </label>
              <input
                type="number"
                min={1}
                value={form.requiredItemCount || 1}
                onChange={e => setForm(p => ({ ...p, requiredItemCount: Math.max(1, parseInt(e.target.value) || 1) }))}
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900"
                placeholder="e.g. 3 · how many menu items each brand should provide"
              />
              <p className="text-[10px] text-zinc-400">Minimum 1. Defines the expected number of menu items per brand. Used for readiness tracking and launch validation.</p>
            </div>
          </div>
        </FormSection>

        {/* Section 2 · Pricing & Funding */}
        <FormSection label="Pricing & Funding">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Discount Type</label>
              <div className="flex gap-2">
                {([DiscountType.PERCENTAGE, DiscountType.FLAT] as DiscountType[]).map(dt => (
                  <button
                    key={dt}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, discountType: dt }))}
                    className={cn(
                      'flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border transition-all',
                      form.discountType === dt
                        ? 'bg-zinc-900 text-white border-zinc-900'
                        : 'bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-zinc-400'
                    )}
                  >
                    {dt === DiscountType.PERCENTAGE ? <Percent size={14} /> : <DollarSign size={14} />}
                    {dt === DiscountType.PERCENTAGE ? 'Percentage' : 'Flat Amount'}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                Discount Value {form.discountType === DiscountType.PERCENTAGE ? '(%)' : '(amount)'}
              </label>
              <input
                required
                type="number"
                min={0}
                step={form.discountType === DiscountType.PERCENTAGE ? 1 : 0.5}
                value={form.discountValue}
                onChange={e => setForm(p => ({ ...p, discountValue: Number(e.target.value) }))}
                className="w-full max-w-[160px] px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Funding Type</label>
              <div className="flex flex-wrap gap-2">
                {([FundingType.NONE, FundingType.CO_FUNDED, FundingType.FULLY_FUNDED] as FundingType[]).map(ft => (
                  <button
                    key={ft}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, fundingType: ft }))}
                    className={cn(
                      'px-4 py-2 rounded-xl text-sm font-semibold border transition-all',
                      form.fundingType === ft
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-zinc-400'
                    )}
                  >
                    {FUNDING_LABELS[ft]}
                  </button>
                ))}
              </div>
            </div>

            {/* Co-Funding Percentage Split · visible only when CO_FUNDED */}
            {form.fundingType === FundingType.CO_FUNDED && (
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">Co-Funding Split (%)</p>
                  {(form.sterlingSharePct + form.partnerSharePct) !== 100 && (
                    <span className="text-[10px] font-bold text-red-600">Total must equal 100%</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Sterling Share (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={form.sterlingSharePct}
                      onChange={e => {
                        const v = Math.min(100, Math.max(0, Number(e.target.value)));
                        setForm(p => ({ ...p, sterlingSharePct: v, partnerSharePct: 100 - v }));
                      }}
                      className="w-full px-3 py-2.5 bg-white border border-teal-200 rounded-xl text-sm focus:outline-none focus:border-teal-600"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Partner / Aggregator Share (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={form.partnerSharePct}
                      onChange={e => {
                        const v = Math.min(100, Math.max(0, Number(e.target.value)));
                        setForm(p => ({ ...p, partnerSharePct: v, sterlingSharePct: 100 - v }));
                      }}
                      className="w-full px-3 py-2.5 bg-white border border-teal-200 rounded-xl text-sm focus:outline-none focus:border-teal-600"
                    />
                  </div>
                </div>
                <div className="h-2 bg-white rounded-full overflow-hidden border border-teal-200">
                  <div className="h-full bg-teal-500 transition-all" style={{ width: `${form.sterlingSharePct}%` }} />
                </div>
                <p className="text-[10px] text-teal-600 text-center">
                  Sterling {form.sterlingSharePct}% · Partner {form.partnerSharePct}%
                  {form.discountType === DiscountType.PERCENTAGE && form.discountValue > 0 && (
                    <> · of {form.discountValue}% discount = Sterling {((form.discountValue * form.sterlingSharePct) / 100).toFixed(1)}% + Partner {((form.discountValue * form.partnerSharePct) / 100).toFixed(1)}%</>
                  )}
                </p>
              </div>
            )}
          </div>
        </FormSection>

        {/* Section 3 · Important Requirements (dynamic, editable by admin) */}
        <FormSection label="Important Requirements">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">
                These requirements will be shown to store managers on the Submit Items page. Leave blank to hide.
              </p>
            </div>
            <textarea
              value={form.importantRequirements}
              onChange={e => setForm(p => ({ ...p, importantRequirements: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm focus:outline-none focus:border-amber-500 resize-none"
              placeholder="e.g. Minimum order value must be QAR 50 for all aggregators·"
            />
          </div>
        </FormSection>

        {/* Section 4 · Brand Targeting */}
        <FormSection label="Brand Targeting">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setForm(p => ({ ...p, targetAllBrands: !p.targetAllBrands }))}
              >
                {form.targetAllBrands
                  ? <ToggleRight size={32} className="text-teal-500" />
                  : <ToggleLeft size={32} className="text-zinc-300" />
                }
              </button>
              <div>
                <p className="text-sm font-semibold text-zinc-900">
                  {form.targetAllBrands ? 'All Brands' : 'Specific Brands'}
                </p>
                <p className="text-xs text-zinc-400">
                  {form.targetAllBrands
                    ? 'Campaign applies to every brand in your portfolio.'
                    : 'Select the brands to include in this campaign.'
                  }
                </p>
              </div>
            </div>

            {!form.targetAllBrands && (
              brands.length === 0 ? (
                <p className="text-xs text-zinc-400 italic">No brands configured. Go to App Settings to add brands.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {brands.map(brand => {
                    const selected = form.brandIds.includes(brand.id);
                    const initials = brand.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
                    return (
                      <button
                        type="button"
                        key={brand.id}
                        onClick={() => toggleBrand(brand.id)}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-xl border text-left transition-all',
                          selected
                            ? 'bg-teal-50 border-teal-400 ring-2 ring-teal-400/30'
                            : 'bg-white border-zinc-200 hover:border-zinc-400'
                        )}
                      >
                        <div className={cn(
                          'w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shrink-0',
                          selected ? 'bg-teal-500 text-white' : 'bg-zinc-100 text-zinc-600'
                        )}>
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className={cn('text-sm font-semibold truncate', selected ? 'text-teal-900' : 'text-zinc-800')}>
                            {brand.name}
                          </p>
                          <p className="text-[10px] text-zinc-400 font-mono truncate">{brand.id}</p>
                        </div>
                        {selected && <Check size={14} className="text-teal-500 ml-auto shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </FormSection>

        {/* Section 5 · Participating Aggregators */}
        <FormSection label="Participating Aggregators">
          <div className="flex flex-wrap gap-2">
            {availableAggregators.map(agg => {
              const active = form.aggregators.includes(agg);
              const logo = (settings.aggregatorLogos ?? {})[agg];
              return (
                <button
                  type="button"
                  key={agg}
                  onClick={() => toggleAgg(agg)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all',
                    active
                      ? 'bg-teal-500 text-white border-teal-500'
                      : 'bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-teal-300 hover:text-teal-600'
                  )}
                >
                  {logo && <img src={logo} alt={agg} className="w-5 h-5 rounded object-contain shrink-0" />}
                  {agg}
                </button>
              );
            })}
          </div>
          {form.aggregators.length === 0 && (
            <p className="text-[11px] text-amber-600 mt-2">Select at least one aggregator.</p>
          )}
        </FormSection>

        {/* Section 6 · Campaign Period */}
        <FormSection label="Campaign Period">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Start Date</label>
              <DatePicker
                required={!isDraft}
                value={form.validFrom}
                onChange={v => setForm(p => ({ ...p, validFrom: v }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">End Date</label>
              <DatePicker
                required={!isDraft}
                value={form.validTo}
                min={form.validFrom}
                onChange={v => setForm(p => ({ ...p, validTo: v }))}
              />
            </div>
          </div>
          {/* Exact Start Time · visible only for PLANNED */}
          {form.status === CampaignStatus.PLANNED && (
            <div className="mt-4 space-y-1.5">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Exact Start Time</label>
              <div className="max-w-[220px]">
                <TimePicker
                  value={form.startTime}
                  onChange={v => setForm(p => ({ ...p, startTime: v }))}
                  required
                />
              </div>
              <p className="text-[10px] text-indigo-500 mt-1">The campaign will auto-post at this exact time on the Start Date.</p>
            </div>
          )}
        </FormSection>

        {/* Actions */}
        <div className="flex gap-3 pt-2 border-t border-zinc-100">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-semibold hover:bg-zinc-50 transition-all"
          >
            Cancel
          </button>
          {!campaignDirty && campaign && <span className="text-[10px] text-zinc-400 self-center">No changes detected</span>}
          <button
            type="submit"
            disabled={(!isDraft && form.aggregators.length === 0) || !campaignDirty}
            className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {isDraft ? 'Save Draft' : (campaign ? 'Save Changes' : 'Create Campaign')}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">{label}</p>
        <div className="flex-1 h-px bg-zinc-100" />
      </div>
      {children}
    </div>
  );
}

// --- Submit Items Tab (Store Manager) · High-Fidelity Workspace ---------------

/** Per-brand submission state tracker */
interface BrandSubmissionState {
  subId: string;
  brandId: string;
  brandName: string;
  items: SubmissionItem[];
  collapsedCards: Set<string>;
}

function SubmitItemsTab({ user }: { user: User | null }) {
  const repo = getRepository();
  const location = useLocation();
  const toast = useToast();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [brandSubs, setBrandSubs] = useState<BrandSubmissionState[]>([]);
  const [activeBrandIdx, setActiveBrandIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // -- Existing submissions (for Summary View) --
  const [mySubmissions, setMySubmissions] = useState<CampaignSubmission[]>([]);
  const [viewMode, setViewMode] = useState<'new' | 'all' | 'in-review' | 'ready' | 'live' | 'rejected'>('new');
  const [selectedSummary, setSelectedSummary] = useState<CampaignSubmission | null>(null);
  const [subSearchQuery, setSubSearchQuery] = useState('');
  const [subCampaignFilter, setSubCampaignFilter] = useState<string>('ALL');

  // -- Dynamic department stages from registry --
  const [departments, setDepartments] = useState<Department[]>([]);
  const [deptReviewers, setDeptReviewers] = useState<User[]>([]);
  const dynamicStages = (() => {
    const campaignDepts = departments.filter(d => (d.linkedModules ?? []).includes('aggregator'));
    if (campaignDepts.length > 0) return campaignDepts.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(d => d.name);
    return [...DEPARTMENT_STAGES];
  })();

  // -- Item detail expand + cost map --
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [itemCostMap, setItemCostMap] = useState<Record<string, { baseCost: number; options: Record<string, number> }>>({});

  // Fetch cost map when a menu-linked item is expanded
  useEffect(() => {
    if (!expandedItemId) return;
    // Find the submission item across all submissions
    let item: any = null;
    for (const s of mySubs) { item = s.items.find(it => it.id === expandedItemId); if (item) break; }
    if (!item?.menuLinked || !item?.menuProfileId || !item?.menuItemId) return;
    if (itemCostMap[item.menuItemId]) return; // already fetched
    apiFetch<{ data: Record<string, { baseCost: number; options: Record<string, number> }> }>(`/api/menu/recipes/cost-map/${item.menuProfileId}`)
      .then(j => setItemCostMap(prev => ({ ...prev, ...j.data })))
      .catch(() => {});
  }, [expandedItemId]);

  // -- Unlocked item editing state --
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; price: number; description: string; imageUrl: string }>({ name: '', price: 0, description: '', imageUrl: '' });
  const [originalEditForm, setOriginalEditForm] = useState<{ name: string; price: number; description: string; imageUrl: string } | null>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  // -- Add new item to existing submission --
  const [addingItemToSubId, setAddingItemToSubId] = useState<string | null>(null);
  const [newItemForm, setNewItemForm] = useState<{ name: string; nameAR: string; price: number; description: string; descriptionAR: string; imageUrl: string }>({ name: '', nameAR: '', price: 0, description: '', descriptionAR: '', imageUrl: '' });
  const [savingNewItem, setSavingNewItem] = useState(false);

  // -- Menu Integration --
  const [menuSelectorOpen, setMenuSelectorOpen] = useState(false);
  const [menuSelectorSubId, setMenuSelectorSubId] = useState<string | null>(null);
  const userHasMenuAccess = hasMenuAccess(user);

  // -- Settings (for manual entry toggle) --
  const [campaignSettings, setCampaignSettings] = useState<CampaignSettings>({ brands: [], aggregators: [] });
  const allowManualEntry = campaignSettings.allowManualEntry !== false;

  const load = async () => {
    const [c, bRaw, subs, depts, users, settings] = await Promise.all([repo.getCampaigns(), repo.getBrands(), repo.getCampaignSubmissions(), repo.getDepartments(), repo.getUsers(), repo.getCampaignSettings()]);
    setDepartments(depts);
    setDeptReviewers(users);
    if (settings) setCampaignSettings(settings);
    const b = bRaw.filter(br => br.enabledModules?.includes('aggregator'));
    const now = Date.now();
    const userBrandIds = user?.assignedBrands;
    // Brand-filtered brands
    const filteredBrands = userBrandIds && userBrandIds.length > 0 ? b.filter(br => userBrandIds.includes(br.id)) : b;
    setAllBrands(filteredBrands);

    // Brand Lock: if manager has NO assigned brands ? see NOTHING
    const hasBrands = userBrandIds && userBrandIds.length > 0;

    // Campaign visibility: ACTIVE + within date range + brand intersection
    const activeCampaigns = !hasBrands
      ? []   // No assigned brands ? empty list (brand lock requirement)
      : c.filter(x => {
          if (x.status !== CampaignStatus.ACTIVE) return false;
          if (x.validFrom > now || x.validTo < now) return false;
          // Brand Lock: campaign must target all brands OR intersect with user's assigned brands
          if (!x.targetAllBrands && !x.brandIds.some(bid => userBrandIds!.includes(bid))) return false;
          return true;
        });
    setCampaigns(activeCampaigns);
    // Only show this user's submissions
    const userSubs = user ? subs.filter(s => s.submittedBy === user.id) : [];
    setMySubmissions(userSubs);

    // Deep-link: auto-select campaign from URL param
    const params = new URLSearchParams(location.search);
    const campaignParam = params.get('campaign');
    if (campaignParam && activeCampaigns.some(x => x.id === campaignParam)) {
      setSelectedCampaignId(campaignParam);
      // If user has existing submissions for this campaign, also set campaign filter and show All tab
      const hasExistingSubs = userSubs.some(s => s.campaignId === campaignParam);
      if (hasExistingSubs) {
        setSubCampaignFilter(campaignParam);
        // Only auto-switch to 'all' if no explicit tab param
        const tabP = params.get('tab');
        if (!tabP) setViewMode('all');
      }
    }
    // Deep-link: auto-select sub-tab from URL param
    const tabParam = params.get('tab');
    if (tabParam && ['all', 'in-review', 'ready', 'live', 'rejected'].includes(tabParam)) {
      setViewMode(tabParam as typeof viewMode);
    }
    // Deep-link: auto-expand a specific submission
    const subParam = params.get('submission');
    if (subParam) {
      const targetSub = userSubs.find(s => s.id === subParam);
      if (targetSub) {
        setSelectedSummary(targetSub);
        setSubCampaignFilter(targetSub.campaignId);
        if (!tabParam) setViewMode('all');
      }
    }
  };

  useEffect(() => { load(); const h = (e: any) => { if (import.meta.env.DEV && e && e.type === 'PULSE_UPDATE') console.log('💓 Heartbeat: Data Refreshed (Aggregator) ' + e.type); load(); }; window.addEventListener('PULSE_UPDATE', h); return () => window.removeEventListener('PULSE_UPDATE', h); }, []);

  const campaign = campaigns.find(c => c.id === selectedCampaignId) ?? null;

  // Resolve brands applicable to the selected campaign
  const applicableBrands = campaign
    ? campaign.targetAllBrands
      ? allBrands
      : allBrands.filter(b => campaign.brandIds.includes(b.id))
    : [];

  // When campaign changes, build per-brand submission stubs · skip brands that already have a submission
  useEffect(() => {
    if (!campaign || applicableBrands.length === 0) {
      setBrandSubs([]);
      setActiveBrandIdx(0);
      return;
    }
    // Filter out brands that already have a PENDING / IN_REVIEW / APPROVED submission for this campaign
    // REJECTED submissions are excluded · managers can restart after rejection
    const availableBrands = applicableBrands.filter(b =>
      !mySubmissions.some(s => s.campaignId === campaign.id && s.brandId === b.id && s.status !== SubmissionStatus.REJECTED)
    );
    if (availableBrands.length === 0) {
      // All brands already submitted · go to summary view
      setBrandSubs([]);
      setActiveBrandIdx(0);
      setSelectedCampaignId('');
      setViewMode('all');
      return;
    }
    setBrandSubs(availableBrands.map(b => ({
      subId: `SUB-${crypto.randomUUID().slice(0, 6).toUpperCase()}-${b.name.slice(0, 3).toUpperCase()}`,
      brandId: b.id,
      brandName: b.name,
      items: [],
      collapsedCards: new Set<string>(),
    })));
    setActiveBrandIdx(0);
    setValidationError('');
  }, [selectedCampaignId, campaign?.id]);

  const activeSub = brandSubs[activeBrandIdx] ?? null;

  // Item helpers scoped to active brand
  const addItem = () => {
    setBrandSubs(prev => prev.map((bs, i) =>
      i === activeBrandIdx
        ? { ...bs, items: [...bs.items, { id: crypto.randomUUID(), name: '', nameAR: '', price: 0, description: '', descriptionAR: '' }] }
        : bs
    ));
  };
  const removeItem = (itemId: string) => {
    setBrandSubs(prev => prev.map((bs, i) =>
      i === activeBrandIdx
        ? { ...bs, items: bs.items.filter(it => it.id !== itemId) }
        : bs
    ));
  };
  const updateItem = (itemId: string, field: keyof SubmissionItem, value: string | number) => {
    setBrandSubs(prev => prev.map((bs, i) =>
      i === activeBrandIdx
        ? { ...bs, items: bs.items.map(it => it.id === itemId ? { ...it, [field]: value } : it) }
        : bs
    ));
  };
  const toggleCollapse = (itemId: string) => {
    setBrandSubs(prev => prev.map((bs, i) => {
      if (i !== activeBrandIdx) return bs;
      const next = new Set(bs.collapsedCards);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return { ...bs, collapsedCards: next };
    }));
  };

  // -- Image upload helper (FileReader ? Base64) --
  const handleImageUpload = (itemId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File Too Large', 'Please select an image under 5MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid File', 'Please select a valid image file (JPG, PNG, etc.).');
      return;
    }
    uploadFile(file, { moduleId: 'aggregator' }).then(url => {
      updateItem(itemId, 'imageUrl', url);
    }).catch(() => {
      toast.error('Upload Failed', 'Could not upload the image file. Please try again.');
    });
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const totalItems = brandSubs.reduce((sum, bs) => sum + bs.items.filter(it => it.name.trim()).length, 0);
  const canSubmit = campaign && brandSubs.length > 0 && brandSubs.every(bs => bs.items.length > 0 && bs.items.every(it => it.name.trim() && it.price > 0));
  const canSubmitBrand = activeSub && activeSub.items.length > 0 && activeSub.items.every(it => it.name.trim() && it.price > 0);
  const editFormDirty = originalEditForm ? (editForm.name !== originalEditForm.name || editForm.price !== originalEditForm.price || editForm.description !== originalEditForm.description || editForm.imageUrl !== originalEditForm.imageUrl) : false;

  const handleSubmitAll = async () => {
    if (!user || !campaign) return;
    // Strict validation · every item must have a name and price > 0
    const incomplete = brandSubs.some(bs => bs.items.some(it => !it.name.trim() || it.price <= 0));
    if (incomplete) {
      setValidationError('Incomplete Items: Please provide a name and price for all items.');
      toast.error('Validation Failed', 'Please provide a name and price for all items.');
      return;
    }
    // Duplicate prevention · double-check against existing submissions (exclude REJECTED · managers can retry)
    const freshSubs = await repo.getCampaignSubmissions();
    const duplicateBrand = brandSubs.find(bs =>
      freshSubs.some(s => s.campaignId === campaign.id && s.brandId === bs.brandId && s.submittedBy === user.id && s.status !== SubmissionStatus.REJECTED)
    );
    if (duplicateBrand) {
      setValidationError(`Error: A submission already exists for ${duplicateBrand.brandName} in this campaign.`);
      toast.error('Duplicate Submission', `A submission already exists for ${duplicateBrand.brandName}.`);
      return;
    }
    setValidationError('');
    setSubmitting(true);

    for (const bs of brandSubs) {
      const submission: CampaignSubmission = {
        id: crypto.randomUUID(),
        campaignId: campaign.id,
        campaignName: campaign.name,
        brandId: bs.brandId,
        brandName: bs.brandName,
        submittedBy: user.id,
        submitterName: user.name,
        currentStage: dynamicStages[0],
        status: SubmissionStatus.PENDING,
        items: bs.items.map(it => ({ ...it, name: it.name.trim(), locked: true })),
        reviewHistory: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      await repo.saveCampaignSubmission(submission);
    }

    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `Submitted ${totalItems} item(s) across ${brandSubs.length} brand(s) for campaign "${campaign.name}"`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });

    setSelectedCampaignId('');
    setSubmitting(false);
    setViewMode('all');
    toast.success('Campaign Submitted!', `${totalItems} item(s) across ${brandSubs.length} brand(s) submitted successfully.`);
    await load();
  };

  // -- Submit a single brand --
  const handleSubmitBrand = async () => {
    if (!user || !campaign || !activeSub) return;
    const incomplete = activeSub.items.some(it => !it.name.trim() || it.price <= 0);
    if (incomplete) {
      setValidationError(`Incomplete Items: Please provide a name and price for all items in ${activeSub.brandName}.`);
      toast.error('Validation Failed', 'Please provide a name and price for all items.');
      return;
    }
    const freshSubs = await repo.getCampaignSubmissions();
    if (freshSubs.some(s => s.campaignId === campaign.id && s.brandId === activeSub.brandId && s.submittedBy === user.id && s.status !== SubmissionStatus.REJECTED)) {
      setValidationError(`Error: A submission already exists for ${activeSub.brandName}.`);
      toast.error('Duplicate Submission', `A submission already exists for ${activeSub.brandName}.`);
      return;
    }
    setValidationError('');
    setSubmitting(true);
    const submission: CampaignSubmission = {
      id: crypto.randomUUID(),
      campaignId: campaign.id,
      campaignName: campaign.name,
      brandId: activeSub.brandId,
      brandName: activeSub.brandName,
      submittedBy: user.id,
      submitterName: user.name,
      currentStage: dynamicStages[0],
      status: SubmissionStatus.PENDING,
      items: activeSub.items.map(it => ({ ...it, name: it.name.trim(), locked: true })),
      reviewHistory: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    await repo.saveCampaignSubmission(submission);
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `Submitted ${activeSub.items.length} item(s) for "${activeSub.brandName}" in campaign "${campaign.name}"`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });
    const remaining = brandSubs.filter((_, i) => i !== activeBrandIdx);
    if (remaining.length === 0) {
      setSelectedCampaignId('');
      setViewMode('all');
    } else {
      setBrandSubs(remaining);
      setActiveBrandIdx(Math.min(activeBrandIdx, remaining.length - 1));
    }
    setSubmitting(false);
    toast.success('Brand Submitted!', `${activeSub.items.length} item(s) for "${activeSub.brandName}" submitted successfully.`);
    await load();
  };

  // -- Add a new item to an existing submission --
  const handleAddNewItem = async (sub: CampaignSubmission) => {
    if (!user || !newItemForm.name.trim() || newItemForm.price <= 0) return;
    setSavingNewItem(true);
    const newItem: SubmissionItem = {
      id: crypto.randomUUID(),
      name: newItemForm.name.trim(),
      nameAR: newItemForm.nameAR.trim() || undefined,
      price: newItemForm.price,
      description: newItemForm.description.trim() || undefined,
      descriptionAR: newItemForm.descriptionAR.trim() || undefined,
      imageUrl: newItemForm.imageUrl || undefined,
      locked: true,
      resubmittedAt: Date.now(),
    };
    const resubmitAction: ReviewAction = {
      departmentId: sub.currentStage,
      departmentName: sub.brandName,
      action: 'RESUBMIT',
      reviewerId: user.id,
      reviewerName: user.name,
      note: `Added New Item: "${newItem.name}" (QAR ${newItem.price.toFixed(2)})`,
      changeDetails: [`Added new item "${newItem.name}" at QAR ${newItem.price.toFixed(2)}`],
      timestamp: Date.now(),
    };
    const updated: CampaignSubmission = {
      ...sub,
      items: [...sub.items, newItem],
      reviewHistory: [...sub.reviewHistory, resubmitAction],
      updatedAt: Date.now(),
    };
    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `Added new item "${newItem.name}" to "${sub.campaignName}" (${sub.brandName})`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });
    // Notify current department reviewers
    const deptUsers = deptReviewers.filter(u => {
      const dept = departments.find(d => d.name === sub.currentStage);
      return dept && u.assignedDepartments?.includes(dept.id);
    });
    for (const du of deptUsers) {
      await repo.saveNotification({
        id: crypto.randomUUID(),
        recipientId: du.id,
        actorId: user.id,
        type: NotificationType.INFO,
        title: 'New Item Added to Submission',
        message: `"${newItem.name}" added to "${sub.campaignName}" (${sub.brandName}). Please review.`,
        targetPath: '/apps/aggregator/review',
        isRead: false,
        createdAt: Date.now(),
      });
    }
    setAddingItemToSubId(null);
    setNewItemForm({ name: '', nameAR: '', price: 0, description: '', descriptionAR: '', imageUrl: '' });
    setSavingNewItem(false);
    toast.success('Item Added', `"${newItem.name}" has been added to the submission.`);
    await load();
  };

  // -- Resubmit a single unlocked item --
  const handleResubmitItem = async (sub: CampaignSubmission, itemId: string) => {
    if (!user) return;

    // -- Strict dirty check · must have originalEditForm to compare --
    if (!originalEditForm) return;
    const changeDetails = detectItemChanges(originalEditForm, editForm);
    if (changeDetails.length === 0) {
      toast.warning('No Changes Detected', 'Please update the data before resubmitting.');
      return;
    }

    setResubmitting(true);
    const updatedItems = sub.items.map(it =>
      it.id === itemId
        ? { ...it, ...editForm, name: editForm.name.trim(), imageUrl: editForm.imageUrl || undefined, locked: true, adminNote: undefined, unlockedAt: undefined, unlockedBy: undefined, resubmittedAt: Date.now() }
        : it
    );

    // -- Build the RESUBMIT review history entry --
    const resubmitAction: ReviewAction = {
      departmentId: sub.currentStage,
      departmentName: sub.brandName,
      action: 'RESUBMIT',
      reviewerId: user.id,
      reviewerName: user.name,
      note: `Resubmitted Fixed Item: "${editForm.name.trim()}"`,
      changeDetails,
      timestamp: Date.now(),
    };

    const updated: CampaignSubmission = {
      ...sub,
      items: updatedItems,
      reviewHistory: [...sub.reviewHistory, resubmitAction],
      updatedAt: Date.now(),
    };
    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `Resubmitted corrected item "${editForm.name}" in "${sub.campaignName}" (${sub.brandName}): ${changeDetails.join('; ')}`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });

    // -- Notify the current department that item is ready for re-review --
    const deptUsers = deptReviewers.filter(u => {
      const dept = departments.find(d => d.name === sub.currentStage);
      return dept && u.assignedDepartments?.includes(dept.id);
    });
    for (const du of deptUsers) {
      await repo.saveNotification({
        id: crypto.randomUUID(),
        recipientId: du.id,
        actorId: user.id,
        type: NotificationType.INFO,
        title: 'Item Updated · Ready for Re-Review',
        message: `"${editForm.name.trim()}" in "${sub.campaignName}" (${sub.brandName}) has been updated and is ready for re-review.`,
        targetPath: '/apps/aggregator/review',
        isRead: false,
        createdAt: Date.now(),
      });
    }

    setEditingItemId(null);
    setResubmitting(false);
    toast.success('Item Resubmitted', `"${editForm.name}" has been corrected and resubmitted.`);
    await load();
  };

  // Count unlocked items across all submissions
  const unlockedTotal = mySubmissions.reduce((sum, s) => sum + s.items.filter(it => it.locked === false).length, 0);

  // Submission counts by category
  const subsInReview = mySubmissions.filter(s => s.status === SubmissionStatus.PENDING || s.status === SubmissionStatus.IN_REVIEW);
  const subsReady = mySubmissions.filter(s => s.status === SubmissionStatus.APPROVED || s.status === SubmissionStatus.DEPLOYMENT_READY);
  const subsLive = mySubmissions.filter(s => s.status === SubmissionStatus.COMPLETED);
  const subsRejected = mySubmissions.filter(s => s.status === SubmissionStatus.REJECTED);

  // Filtered submissions based on active viewMode sub-tab + campaign filter + search
  const filteredSubs = (() => {
    let base: CampaignSubmission[];
    switch (viewMode) {
      case 'in-review': base = subsInReview; break;
      case 'ready': base = subsReady; break;
      case 'live': base = subsLive; break;
      case 'rejected': base = subsRejected; break;
      case 'all': default: base = mySubmissions; break;
    }
    // Campaign filter
    if (subCampaignFilter !== 'ALL') {
      base = base.filter(s => s.campaignId === subCampaignFilter);
    }
    // Search filter (by campaign name, brand name, item names)
    if (subSearchQuery.trim()) {
      const q = subSearchQuery.toLowerCase().trim();
      base = base.filter(s =>
        s.campaignName.toLowerCase().includes(q) ||
        s.brandName.toLowerCase().includes(q) ||
        s.items.some(it => it.name.toLowerCase().includes(q))
      );
    }
    return base;
  })();

  const SUB_TABS: { key: typeof viewMode; label: string; count: number; color: string; activeColor: string }[] = [
    { key: 'new', label: 'New Submission', count: campaigns.length, color: 'text-zinc-500 border-zinc-200 bg-zinc-50 hover:border-zinc-400', activeColor: 'bg-zinc-900 text-white border-zinc-900' },
    { key: 'all', label: 'All Submissions', count: mySubmissions.length, color: 'text-zinc-500 border-zinc-200 bg-zinc-50 hover:border-zinc-400', activeColor: 'bg-zinc-900 text-white border-zinc-900' },
    { key: 'in-review', label: 'In Review', count: subsInReview.length, color: 'text-blue-600 border-blue-200 bg-blue-50 hover:border-blue-400', activeColor: 'bg-blue-600 text-white border-blue-600' },
    { key: 'ready', label: 'Deploy Ready', count: subsReady.length, color: 'text-violet-600 border-violet-200 bg-violet-50 hover:border-violet-400', activeColor: 'bg-violet-600 text-white border-violet-600' },
    { key: 'live', label: 'Live', count: subsLive.length, color: 'text-emerald-600 border-emerald-200 bg-emerald-50 hover:border-emerald-400', activeColor: 'bg-emerald-600 text-white border-emerald-600' },
    { key: 'rejected', label: 'Rejected', count: subsRejected.length, color: 'text-red-600 border-red-200 bg-red-50 hover:border-red-400', activeColor: 'bg-red-600 text-white border-red-600' },
  ];

  // -- Campaign selection / view toggle stage --
  if (!campaign) {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Submit Items</h2>
          <p className="text-sm text-slate-500 mt-1">Select an active campaign or view your submissions.</p>
        </div>

        {/* -- Sub-tab navigation -- */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {SUB_TABS.map(tab => {
            if (tab.count === 0 && tab.key !== 'new') return null;
            const isActive = viewMode === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setViewMode(tab.key)}
                className={cn("px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all relative", isActive ? tab.activeColor : tab.color)}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={cn("ml-1.5 text-[9px]", isActive ? 'opacity-80' : 'opacity-60')}>({tab.count})</span>
                )}
                {tab.key === 'all' && unlockedTotal > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center animate-bounce">{unlockedTotal}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* -- Unlocked items alert banner -- */}
        {unlockedTotal > 0 && viewMode === 'new' && (
          <button
            onClick={() => setViewMode('all')}
            className="w-full flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors text-left"
          >
            <AlertCircle size={18} className="text-amber-600 shrink-0" />
            <p className="text-sm font-semibold text-amber-700">
              Action Required: {unlockedTotal} item(s) unlocked for editing · click to view
            </p>
          </button>
        )}

        {/* -- Summary View: My Submissions (filtered by active sub-tab) -- */}
        {viewMode !== 'new' && (
          <div className="space-y-4">
            {/* Section header with context */}
            {viewMode !== 'all' && (
              <div className={cn("flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold",
                viewMode === 'in-review' ? 'bg-blue-50 text-blue-700' :
                viewMode === 'ready' ? 'bg-violet-50 text-violet-700' :
                viewMode === 'live' ? 'bg-emerald-50 text-emerald-700' :
                'bg-red-50 text-red-700'
              )}>
                {viewMode === 'in-review' && <><Clock size={13} /> Submissions currently being reviewed by departments</>}
                {viewMode === 'ready' && <><Rocket size={13} /> Submissions approved and ready for deployment</>}
                {viewMode === 'live' && <><Radio size={13} /> Submissions that are live on aggregator platforms</>}
                {viewMode === 'rejected' && <><Ban size={13} /> Submissions that were rejected · may need resubmission</>}
              </div>
            )}

            {/* Search bar + Campaign filter */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search by campaign, brand, or item name·"
                  value={subSearchQuery}
                  onChange={e => setSubSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-500 transition-colors"
                />
                {subSearchQuery && (
                  <button onClick={() => setSubSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                    <X size={14} />
                  </button>
                )}
              </div>
              <CustomSelect
                value={subCampaignFilter}
                onChange={v => setSubCampaignFilter(v)}
                options={[
                  { value: 'ALL', label: 'All Campaigns' },
                  ...[...new Map(mySubmissions.map(s => [s.campaignId, s.campaignName])).entries()].map(([id, name]: [string, string]) => ({ value: id, label: name })),
                ]}
                placeholder="All Campaigns"
                className="min-w-[180px]"
              />
              {(subSearchQuery || subCampaignFilter !== 'ALL') && (
                <button
                  onClick={() => { setSubSearchQuery(''); setSubCampaignFilter('ALL'); }}
                  className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-lg hover:bg-zinc-200 transition-colors shrink-0"
                >
                  <X size={12} /> Clear
                </button>
              )}
            </div>

            {filteredSubs.length === 0 ? (
              <div className="bg-white border border-[#E5E7EB] rounded-xl p-12 text-center shadow-[0_2px_8px_rgba(0,0,0,0.06),0_0_1px_rgba(0,0,0,0.04)]">
                <ClipboardList size={32} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-sm font-semibold text-zinc-400">
                  {(subSearchQuery || subCampaignFilter !== 'ALL')
                    ? 'No submissions match your filters'
                    : viewMode === 'all' ? 'No submissions yet' :
                      viewMode === 'in-review' ? 'No submissions in review' :
                      viewMode === 'ready' ? 'No submissions ready for deployment' :
                      viewMode === 'live' ? 'No live submissions' :
                      'No rejected submissions'}
                </p>
              </div>
            ) : (
              filteredSubs.map(sub => {
                const unlockedItems = sub.items.filter(it => it.locked === false);
                const isExpanded = selectedSummary?.id === sub.id;
                const statusMeta = STATUS_COLOR[sub.status];
                return (
                  <div key={sub.id} className={cn(
                    "bg-white border rounded-xl overflow-hidden transition-all",
                    unlockedItems.length > 0 ? "border-amber-300 shadow-md" : "border-zinc-200"
                  )}>
                    {/* Card header */}
                    <div
                      className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-zinc-50 transition-colors"
                      onClick={() => setSelectedSummary(isExpanded ? null : sub)}
                    >
                      {(() => {
                        const isCompleted = sub.status === SubmissionStatus.COMPLETED || sub.status === SubmissionStatus.DEPLOYMENT_READY || sub.currentStage === 'DONE';
                        if (isCompleted) {
                          return (
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-teal-600 text-white shrink-0">
                              <ShieldCheck size={18} />
                            </div>
                          );
                        }
                        const firstImage = sub.items.find(it => it.imageUrl)?.imageUrl;
                        if (firstImage && unlockedItems.length === 0) {
                          return (
                            <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 border border-zinc-200 bg-zinc-50">
                              <img src={getMediaUrl(firstImage)} alt={sub.brandName} className="w-full h-full object-cover" />
                            </div>
                          );
                        }
                        return (
                          <div className={cn(
                            "w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0",
                            unlockedItems.length > 0 ? "bg-amber-500" : "bg-zinc-900"
                          )}>
                            {unlockedItems.length > 0 ? <Unlock size={18} /> : <Package size={18} />}
                          </div>
                        );
                      })()}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-zinc-900 truncate">{sub.campaignName}</p>
                          <span className={cn("px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border", statusMeta.pill)}>
                            <span className={cn("inline-block w-1.5 h-1.5 rounded-full mr-1.5", statusMeta.dot)} />
                            {sub.status}
                          </span>
                          {unlockedItems.length > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200">
                              {unlockedItems.length} Unlocked
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-400">
                          <span className="flex items-center gap-1"><Building2 size={11} /> {sub.brandName}</span>
                          <span>{getApprovedItems(sub).length} item(s)</span>
                          <span className="flex items-center gap-1"><Clock size={11} /> {new Date(sub.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="hidden sm:block shrink-0">
                        <ReviewTimeline compact currentStage={sub.currentStage} status={sub.status} reviewHistory={sub.reviewHistory} stages={dynamicStages} />
                      </div>
                      <ChevronRight size={16} className={cn("text-zinc-400 transition-transform shrink-0", isExpanded && "rotate-90")} />
                    </div>

                    {/* Expanded summary */}
                    {isExpanded && (
                      <div className="border-t border-zinc-200 bg-zinc-50 px-5 py-5 space-y-5">

                        {/* Finalized Record Banner */}
                        {(sub.status === SubmissionStatus.COMPLETED || sub.status === SubmissionStatus.DEPLOYMENT_READY || sub.currentStage === 'DONE') && (
                          <div className="flex items-center gap-2.5 px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl">
                            <ShieldCheck size={16} className="text-teal-600 shrink-0" />
                            <div>
                              <p className="text-xs font-bold text-teal-700">Finalized Record</p>
                              <p className="text-[10px] text-teal-600">This submission is completed and cannot be modified.</p>
                            </div>
                          </div>
                        )}

                        {/* Items list · read-only by default, editable for unlocked items */}
                        <div className="space-y-2">
                          {(() => {
                            const activeItems = sub.items.filter(it => it.itemStatus !== 'DISCARDED');
                            const discardedItems = sub.items.filter(it => it.itemStatus === 'DISCARDED');
                            return (
                              <>
                          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Items ({activeItems.length})</p>
                          <div className="space-y-2">
                            {activeItems.map((item, idx) => {
                              const isUnlocked = item.locked === false;
                              const isEditing = editingItemId === item.id;
                              const isFinalized = sub.status === SubmissionStatus.COMPLETED || sub.status === SubmissionStatus.DEPLOYMENT_READY || sub.currentStage === 'DONE';
                              return (
                                <div key={item.id} className={cn(
                                  "bg-white border rounded-xl transition-all",
                                  isUnlocked ? "border-amber-300" : "border-zinc-200"
                                )}>
                                  {/* Item row */}
                                  <div className="flex items-center gap-3 px-4 py-3">
                                    <span className="text-[9px] font-bold text-zinc-400 w-5">{idx + 1}</span>
                                    {isUnlocked ? (
                                      <Unlock size={14} className="text-amber-500 shrink-0" />
                                    ) : (
                                      <Lock size={14} className="text-zinc-300 shrink-0" />
                                    )}
                                    {item.imageUrl ? (
                                      <div className="w-10 h-10 rounded-lg overflow-hidden border border-zinc-200 shrink-0 bg-zinc-50">
                                        <img src={getMediaUrl(item.imageUrl)} alt={item.name} className="w-full h-full object-cover" />
                                      </div>
                                    ) : (
                                      <div className="w-10 h-10 rounded-lg border border-zinc-100 bg-zinc-50 flex items-center justify-center shrink-0">
                                        <ImageIcon size={16} className="text-zinc-300" />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-semibold text-zinc-900">{item.name}</p>
                                      {item.nameAR && <p className="text-xs text-zinc-400 font-arabic mt-0.5" dir="rtl">{item.nameAR}</p>}
                                      {item.description && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-1">{item.description}</p>}
                                    </div>
                                    <span className="text-sm font-mono text-zinc-600">QAR {item.price.toFixed(2)}</span>
                                    {/* Expand/collapse toggle for menu-linked items */}
                                    {item.menuLinked && (
                                      <button
                                        onClick={e => { e.stopPropagation(); setExpandedItemId(expandedItemId === item.id ? null : item.id); }}
                                        className={cn('p-1.5 rounded-lg transition-all', expandedItemId === item.id ? 'bg-indigo-100 text-indigo-600' : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200')}
                                        title="Show details"
                                      >
                                        <ChevronDown size={13} className={cn('transition-transform', expandedItemId === item.id && 'rotate-180')} />
                                      </button>
                                    )}
                                    {isFinalized ? (
                                      <span className="flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-[10px] font-bold">
                                        <CheckCircle2 size={12} /> Verified
                                      </span>
                                    ) : isUnlocked && !isEditing ? (
                                      <button
                                        onClick={() => {
                                          setEditingItemId(item.id);
                                          const form = { name: item.name, price: item.price, description: item.description ?? '', imageUrl: item.imageUrl ?? '' };
                                          setEditForm(form);
                                          setOriginalEditForm(form);
                                        }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-bold hover:bg-amber-100 transition-all"
                                      >
                                        <Edit2 size={12} /> Edit
                                      </button>
                                    ) : !isEditing ? (
                                      <div className="relative group/lock">
                                        <button
                                          disabled
                                          className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 text-zinc-400 border border-zinc-200 rounded-lg text-xs font-bold cursor-not-allowed"
                                        >
                                          <Lock size={12} /> Edit
                                        </button>
                                        <div className="absolute bottom-full right-0 mb-1.5 px-3 py-1.5 bg-zinc-900 text-white text-[10px] font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover/lock:opacity-100 transition-opacity pointer-events-none z-10">
                                          Editing is locked while the department is reviewing your data.
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>

                                  {/* Admin feedback note */}
                                  {item.adminNote && (
                                    <div className="mx-4 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                      <div className="flex items-start gap-2">
                                        <MessageSquare size={12} className="text-amber-600 mt-0.5 shrink-0" />
                                        <div className="text-xs">
                                          <p className="font-bold text-amber-700">Admin Feedback</p>
                                          <p className="text-amber-600 mt-0.5">{item.adminNote}</p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* ═══ Slide-down: Imported Options + Cost Card ═══ */}
                                  {expandedItemId === item.id && item.menuLinked && (
                                    <div className="border-t border-zinc-100 bg-zinc-50/50 px-4 py-4 space-y-4 animate-in slide-in-from-top-2">
                                      {/* ── Menu Source Tag ── */}
                                      <div className="flex items-center gap-2">
                                        <span className="flex items-center gap-1.5 text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-lg">
                                          <Link2 size={10} /> From: {item.menuProfileName || 'Menu Profile'}
                                        </span>
                                        {item.originalMenuPrice != null && item.originalMenuPrice !== item.price && (
                                          <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
                                            <AlertTriangle size={10} /> Price changed from QAR {item.originalMenuPrice.toFixed(2)}
                                          </span>
                                        )}
                                      </div>

                                      {/* ── Imported Option Groups ── */}
                                      {item.importedOptions && item.importedOptions.length > 0 && (
                                        <div className="space-y-2">
                                          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
                                            <Layers size={12} className="text-indigo-500" />
                                            Option Groups
                                            <span className="text-[9px] font-normal text-zinc-300 ml-1">({item.importedOptions.length})</span>
                                          </p>
                                          <div className="space-y-2">
                                            {item.importedOptions.map((og: any) => (
                                              <div key={og.id} className="px-3 py-2.5 bg-white border border-zinc-200 rounded-xl">
                                                <div className="flex items-center gap-2 mb-1.5">
                                                  <span className="text-xs font-semibold text-zinc-800">{og.nameEN}</span>
                                                  {og.nameAR && <span className="text-[10px] text-zinc-400 font-arabic">{og.nameAR}</span>}
                                                  <span className="text-[8px] px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded font-bold">
                                                    {og.minSelection ?? 0}–{og.maxSelection ?? '∞'}
                                                  </span>
                                                  {og.minSelection != null && og.minSelection > 0 && (
                                                    <span className="text-[8px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-bold border border-red-100">Required</span>
                                                  )}
                                                </div>
                                                <div className="flex flex-wrap gap-1.5">
                                                  {(og.options || []).map((opt: any) => {
                                                    const optCost = item.menuItemId ? itemCostMap[item.menuItemId]?.options?.[opt.id] : undefined;
                                                    return (
                                                      <span key={opt.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-[10px] text-zinc-700">
                                                        {opt.nameEN}
                                                        {opt.extraPrice > 0 && <span className="font-mono text-emerald-600">+{opt.extraPrice.toFixed(2)}</span>}
                                                        {optCost != null && optCost > 0 && (
                                                          <span className="font-mono text-amber-600" title="Ingredient cost">
                                                            (cost {optCost.toFixed(2)})
                                                          </span>
                                                        )}
                                                      </span>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* ── Cost Breakdown Card ── */}
                                      {item.menuItemId && itemCostMap[item.menuItemId] && (() => {
                                        const ce = itemCostMap[item.menuItemId];
                                        const sellPrice = item.price;
                                        const fc = sellPrice > 0 ? (ce.baseCost / sellPrice) * 100 : 0;
                                        const profit = sellPrice - ce.baseCost;
                                        const fcColor = fc <= 30 ? 'text-emerald-600' : fc <= 40 ? 'text-amber-600' : 'text-red-600';
                                        const fcBg = fc <= 30 ? 'bg-emerald-50 border-emerald-200' : fc <= 40 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
                                        return (
                                          <div className="space-y-2">
                                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
                                              <DollarSign size={12} className="text-amber-500" />
                                              Cost Analysis
                                            </p>
                                            <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
                                              <div className="grid grid-cols-3 gap-3">
                                                <div className="text-center">
                                                  <p className="text-[9px] font-bold text-zinc-400 uppercase">Sell Price</p>
                                                  <p className="text-base font-bold text-zinc-800 mt-0.5">QAR {sellPrice.toFixed(2)}</p>
                                                </div>
                                                <div className="text-center">
                                                  <p className="text-[9px] font-bold text-zinc-400 uppercase">Base Cost</p>
                                                  <p className="text-base font-bold text-amber-600 mt-0.5">QAR {ce.baseCost.toFixed(2)}</p>
                                                </div>
                                                <div className="text-center">
                                                  <p className="text-[9px] font-bold text-zinc-400 uppercase">Profit</p>
                                                  <p className={cn('text-base font-bold mt-0.5', profit >= 0 ? 'text-emerald-600' : 'text-red-600')}>QAR {profit.toFixed(2)}</p>
                                                </div>
                                              </div>
                                              <div className={cn('flex items-center justify-center gap-2 px-3 py-2 rounded-lg border', fcBg)}>
                                                <span className="text-[10px] font-bold text-zinc-500">Food Cost %</span>
                                                <span className={cn('text-lg font-black', fcColor)}>{fc.toFixed(1)}%</span>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })()}

                                      {/* No options + no cost = minimal info */}
                                      {(!item.importedOptions || item.importedOptions.length === 0) && !(item.menuItemId && itemCostMap[item.menuItemId]) && (
                                        <p className="text-xs text-zinc-400 italic">No option groups or cost data available for this item.</p>
                                      )}
                                    </div>
                                  )}

                                  {/* Inline edit form for unlocked items */}
                                  {isEditing && (
                                    <div className="border-t border-amber-200 bg-amber-50/50 px-4 py-4 space-y-3">
                                      <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Edit Item</p>
                                      <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Item Name</label>
                                        <input
                                          value={editForm.name}
                                          onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                          className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 transition-colors"
                                        />
                                      </div>
                                      <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Price</label>
                                        <div className="relative">
                                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-zinc-400">QAR</span>
                                          <input
                                            type="number"
                                            min={0}
                                            step={0.01}
                                            value={editForm.price || ''}
                                            onChange={e => setEditForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                            className="w-full pl-12 pr-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 transition-colors"
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Description</label>
                                        <textarea
                                          value={editForm.description}
                                          onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                          rows={2}
                                          className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 resize-none transition-colors"
                                        />
                                      </div>
                                      {/* Image upload area for edit mode */}
                                      <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Photo</label>
                                        {editForm.imageUrl ? (
                                          <div className="relative group rounded-lg overflow-hidden border border-zinc-200">
                                            <img src={getMediaUrl(editForm.imageUrl)} alt={editForm.name || 'Item photo'} className="w-full h-36 object-cover" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                              <label className="flex items-center gap-1.5 px-3 py-2 bg-white text-zinc-700 rounded-lg text-xs font-bold cursor-pointer hover:bg-zinc-100 transition-colors">
                                                <Upload size={12} /> Change
                                                <input type="file" accept="image/*" className="hidden" onChange={e => {
                                                  const file = e.target.files?.[0];
                                                  if (!file) return;
                                                  if (file.size > 5 * 1024 * 1024) { toast.error('File Too Large', 'Max 5MB.'); return; }
                                                  if (!file.type.startsWith('image/')) { toast.error('Invalid File', 'Select an image.'); return; }
                                                  uploadFile(file, { moduleId: 'aggregator' }).then(url => setEditForm(prev => ({ ...prev, imageUrl: url }))).catch(() => toast.error('Upload Failed', 'Could not upload image.'));
                                                  e.target.value = '';
                                                }} />
                                              </label>
                                              <button
                                                type="button"
                                                onClick={() => setEditForm(prev => ({ ...prev, imageUrl: '' }))}
                                                className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-bold hover:bg-red-600 transition-colors"
                                              >
                                                <Trash2 size={12} /> Remove
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <label className="border-2 border-dashed border-zinc-200 rounded-lg p-6 flex flex-col items-center justify-center text-center hover:border-amber-400 hover:bg-amber-50/30 transition-colors cursor-pointer">
                                            <ImageIcon size={24} className="text-zinc-300 mb-1.5" />
                                            <p className="text-xs text-zinc-400">Upload Photo (Optional)</p>
                                            <p className="text-[10px] text-zinc-300 mt-0.5">JPG, PNG up to 5MB</p>
                                            <input type="file" accept="image/*" className="hidden" onChange={e => {
                                              const file = e.target.files?.[0];
                                              if (!file) return;
                                              if (file.size > 5 * 1024 * 1024) { toast.error('File Too Large', 'Max 5MB.'); return; }
                                              if (!file.type.startsWith('image/')) { toast.error('Invalid File', 'Select an image.'); return; }
                                              uploadFile(file, { moduleId: 'aggregator' }).then(url => setEditForm(prev => ({ ...prev, imageUrl: url }))).catch(() => toast.error('Upload Failed', 'Could not upload image.'));
                                              e.target.value = '';
                                            }} />
                                          </label>
                                        )}
                                      </div>
                                      <div className="flex gap-2 pt-1">
                                        <button
                                          onClick={() => setEditingItemId(null)}
                                          className="px-3 py-2 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-600 hover:bg-zinc-50 transition-all"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={() => handleResubmitItem(sub, item.id)}
                                          disabled={!editForm.name.trim() || editForm.price <= 0 || resubmitting || !editFormDirty}
                                          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                        >
                                          <Send size={12} /> {resubmitting ? 'Sending·' : !editFormDirty ? 'No Changes' : 'Resubmit Item'}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* -- Discarded / Not Used Section -- */}
                          {discardedItems.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <p className="text-[10px] font-bold text-red-400 uppercase tracking-widest">Discarded / Not Used ({discardedItems.length})</p>
                              <div className="space-y-2">
                                {discardedItems.map((item) => (
                                  <div key={item.id} className="bg-zinc-50 border border-zinc-200 rounded-xl opacity-60">
                                    <div className="flex items-center gap-3 px-4 py-3">
                                      <Ban size={14} className="text-red-400 shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-zinc-500 line-through">{item.name}</p>
                                        <p className="text-xs text-zinc-400 mt-0.5 font-mono">QAR {item.price.toFixed(2)}</p>
                                      </div>
                                      <span className="px-2 py-0.5 text-[9px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-full uppercase shrink-0">Discarded</span>
                                    </div>
                                    {item.discardReason && (
                                      <div className="mx-4 mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                                        <div className="flex items-start gap-2">
                                          <Info size={11} className="text-red-400 mt-0.5 shrink-0" />
                                          <div className="text-xs">
                                            <p className="font-bold text-red-600">Reason{item.discardedBy ? ` (${item.discardedBy})` : ''}</p>
                                            <p className="text-red-500 mt-0.5">{item.discardReason}</p>
                                            {item.discardedAt && <p className="text-red-300 mt-0.5 text-[10px]">{new Date(item.discardedAt).toLocaleString()}</p>}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                              </>
                            );
                          })()}
                        </div>

                        {/* Add New Item · hidden for completed/done submissions */}
                        {sub.status === SubmissionStatus.COMPLETED || sub.status === SubmissionStatus.DEPLOYMENT_READY || sub.currentStage === 'DONE' ? null : addingItemToSubId === sub.id ? (
                          <div className="bg-white border border-emerald-200 rounded-xl px-4 py-4 space-y-3">
                            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Add New Item</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Item Name (EN) *</label>
                                <input
                                  value={newItemForm.name}
                                  onChange={e => setNewItemForm(prev => ({ ...prev, name: e.target.value }))}
                                  placeholder="e.g. Big Mac Meal"
                                  className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Item Name (AR)</label>
                                <input
                                  value={newItemForm.nameAR}
                                  onChange={e => setNewItemForm(prev => ({ ...prev, nameAR: e.target.value }))}
                                  placeholder="????: ???? ??? ???"
                                  dir="rtl"
                                  className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm text-right focus:outline-none focus:border-emerald-500 transition-colors"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Price (QAR) *</label>
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={newItemForm.price || ''}
                                  onChange={e => setNewItemForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                  placeholder="0.00"
                                  className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Description (EN)</label>
                                <textarea
                                  value={newItemForm.description}
                                  onChange={e => setNewItemForm(prev => ({ ...prev, description: e.target.value }))}
                                  rows={2}
                                  placeholder="Optional description..."
                                  className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500 resize-none transition-colors"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Description (AR)</label>
                                <textarea
                                  value={newItemForm.descriptionAR}
                                  onChange={e => setNewItemForm(prev => ({ ...prev, descriptionAR: e.target.value }))}
                                  rows={2}
                                  dir="rtl"
                                  placeholder="????? ????????..."
                                  className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-right focus:outline-none focus:border-emerald-500 resize-none transition-colors"
                                />
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Photo</label>
                              {newItemForm.imageUrl ? (
                                <div className="relative group rounded-lg overflow-hidden border border-zinc-200">
                                  <img src={getMediaUrl(newItemForm.imageUrl)} alt={newItemForm.name || 'Item photo'} className="w-full h-36 object-cover" />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                    <label className="flex items-center gap-1.5 px-3 py-2 bg-white text-zinc-700 rounded-lg text-xs font-bold cursor-pointer hover:bg-zinc-100 transition-colors">
                                      <Upload size={12} /> Change
                                      <input type="file" accept="image/*" className="hidden" onChange={e => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        if (file.size > 5 * 1024 * 1024) { toast.error('File Too Large', 'Max 5MB.'); return; }
                                        if (!file.type.startsWith('image/')) { toast.error('Invalid File', 'Select an image.'); return; }
                                        uploadFile(file, { moduleId: 'aggregator' }).then(url => setNewItemForm(prev => ({ ...prev, imageUrl: url }))).catch(() => toast.error('Upload Failed', 'Could not upload image.'));
                                        e.target.value = '';
                                      }} />
                                    </label>
                                    <button type="button" onClick={() => setNewItemForm(prev => ({ ...prev, imageUrl: '' }))} className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-bold hover:bg-red-600 transition-colors">
                                      <Trash2 size={12} /> Remove
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <label className="border-2 border-dashed border-zinc-200 rounded-lg p-4 flex flex-col items-center justify-center text-center hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors cursor-pointer">
                                  <ImageIcon size={20} className="text-zinc-300 mb-1" />
                                  <p className="text-xs text-zinc-400">Upload Photo (Optional)</p>
                                  <p className="text-[10px] text-zinc-300 mt-0.5">JPG, PNG up to 5MB</p>
                                  <input type="file" accept="image/*" className="hidden" onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (file.size > 5 * 1024 * 1024) { toast.error('File Too Large', 'Max 5MB.'); return; }
                                    if (!file.type.startsWith('image/')) { toast.error('Invalid File', 'Select an image.'); return; }
                                    uploadFile(file, { moduleId: 'aggregator' }).then(url => setNewItemForm(prev => ({ ...prev, imageUrl: url }))).catch(() => toast.error('Upload Failed', 'Could not upload image.'));
                                    e.target.value = '';
                                  }} />
                                </label>
                              )}
                            </div>
                            <div className="flex gap-2 pt-1">
                              <button onClick={() => { setAddingItemToSubId(null); setNewItemForm({ name: '', nameAR: '', price: 0, description: '', descriptionAR: '', imageUrl: '' }); }} className="px-3 py-2 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-600 hover:bg-zinc-50 transition-all">
                                Cancel
                              </button>
                              <button
                                onClick={() => handleAddNewItem(sub)}
                                disabled={!newItemForm.name.trim() || newItemForm.price <= 0 || savingNewItem}
                                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                              >
                                <Plus size={12} /> {savingNewItem ? 'Saving·' : 'Add Item'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center justify-center gap-3">
                            {allowManualEntry && (
                              <button
                                onClick={() => { setAddingItemToSubId(sub.id); setEditingItemId(null); }}
                                className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-emerald-300 rounded-xl text-xs font-bold text-emerald-600 hover:bg-emerald-50 hover:border-emerald-500 transition-all"
                              >
                                <Plus size={14} /> Add Manual Item
                              </button>
                            )}
                            {userHasMenuAccess ? (
                              <button
                                onClick={() => setMenuSelectorSubId(sub.id)}
                                className="flex items-center gap-2 px-4 py-2.5 border-2 border-teal-300 bg-teal-50 rounded-xl text-xs font-bold text-teal-700 hover:bg-teal-100 hover:border-teal-500 transition-all"
                              >
                                <BookOpen size={14} /> Add from Menu
                              </button>
                            ) : (
                              <div className="relative group/menu-tip">
                                <button
                                  type="button"
                                  disabled
                                  className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-zinc-200 rounded-xl text-xs font-bold text-zinc-400 cursor-not-allowed opacity-60"
                                >
                                  <BookOpen size={14} /> Add from Menu
                                </button>
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-zinc-900 text-white text-[10px] font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover/menu-tip:opacity-100 transition-opacity pointer-events-none z-20">
                                  Menu Access Required
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Menu Selector Modal for existing submission */}
                        <MenuSelectorModal
                          open={menuSelectorSubId === sub.id}
                          onClose={() => setMenuSelectorSubId(null)}
                          user={user}
                          brandId={sub.brandId}
                          onImport={async (items, profileName) => {
                            if (!user) return;
                            const resubmitAction: ReviewAction = {
                              departmentId: sub.currentStage,
                              departmentName: sub.brandName,
                              action: 'RESUBMIT',
                              reviewerId: user.id,
                              reviewerName: user.name,
                              note: `Imported ${items.length} item(s) from menu profile "${profileName}"`,
                              changeDetails: items.map(it => `Imported "${it.name}" at QAR ${it.price.toFixed(2)}`),
                              timestamp: Date.now(),
                            };
                            const updated: CampaignSubmission = {
                              ...sub,
                              items: [...sub.items, ...items.map(it => ({ ...it, locked: true, resubmittedAt: Date.now() }))],
                              reviewHistory: [...sub.reviewHistory, resubmitAction],
                              updatedAt: Date.now(),
                            };
                            await repo.saveCampaignSubmission(updated);
                            setMenuSelectorSubId(null);
                            toast.success('Items Imported!', `${items.length} item(s) imported from "${profileName}".`);
                            await load();
                          }}
                        />

                        {/* Review history */}
                        {sub.reviewHistory.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Review History</p>
                            <div className="space-y-2">
                              {sub.reviewHistory.map((action, i) => (
                                <div key={i} className={cn(
                                  "flex items-start gap-3 px-3 py-2.5 rounded-lg border text-xs",
                                  action.action === 'APPROVE' ? "bg-emerald-50 border-emerald-200"
                                    : action.action === 'UNLOCK' ? "bg-amber-50 border-amber-200"
                                    : action.action === 'RESUBMIT' ? "bg-indigo-50 border-indigo-200"
                                    : "bg-red-50 border-red-200"
                                )}>
                                  {action.action === 'APPROVE'
                                    ? <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                                    : action.action === 'UNLOCK'
                                    ? <Unlock size={14} className="text-amber-600 mt-0.5 shrink-0" />
                                    : action.action === 'RESUBMIT'
                                    ? <RefreshCw size={14} className="text-indigo-600 mt-0.5 shrink-0" />
                                    : <XCircle size={14} className="text-red-600 mt-0.5 shrink-0" />
                                  }
                                  <div className="flex-1 min-w-0">
                                    <p className={cn("font-bold",
                                      action.action === 'APPROVE' ? "text-emerald-700"
                                        : action.action === 'UNLOCK' ? "text-amber-700"
                                        : action.action === 'RESUBMIT' ? "text-indigo-700"
                                        : "text-red-700"
                                    )}>
                                      [{action.departmentName}] {action.action === 'APPROVE' ? 'Approved' : action.action === 'UNLOCK' ? 'Unlocked Item' : action.action === 'RESUBMIT' ? 'Resubmitted' : 'Rejected'} by {action.reviewerName}
                                    </p>
                                    {action.note && <p className="text-zinc-600 mt-0.5">{action.note}</p>}
                                    {action.changeDetails && action.changeDetails.length > 0 && (
                                      <ul className="mt-1 space-y-0.5 text-indigo-600">
                                        {action.changeDetails.map((cd, ci) => (
                                          <li key={ci} className="flex items-center gap-1"><ChevronRight size={10} className="shrink-0" /> {cd}</li>
                                        ))}
                                      </ul>
                                    )}
                                    <p className="text-zinc-400 mt-0.5">{new Date(action.timestamp).toLocaleString()}</p>
                                  </div>

                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* -- New Submission: Campaign selector -- */}
        {viewMode === 'new' && (
          <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-6 space-y-4">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Campaign</label>
            {campaigns.length === 0 ? (
              <div className="py-12 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center">
                    <Megaphone size={28} className="text-zinc-300" />
                  </div>
                  <p className="text-sm font-bold text-zinc-500">No Active Campaigns</p>
                  <p className="text-xs text-zinc-400 max-w-xs mx-auto">No active campaigns found for your assigned brands. Check back later for new promotions.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {campaigns.map(c => {
                  const existingSubs = mySubmissions.filter(s => s.campaignId === c.id && s.status !== SubmissionStatus.REJECTED);
                  const rejectedSubs = mySubmissions.filter(s => s.campaignId === c.id && s.status === SubmissionStatus.REJECTED);
                  const hasExisting = existingSubs.length > 0;
                  const hasRejected = rejectedSubs.length > 0;
                  const rejectionAttempt = rejectedSubs.length; // how many times rejected
                  // Check if ALL applicable brands are already submitted (exclude rejected)
                  const cBrands = c.targetAllBrands ? allBrands : allBrands.filter(b => c.brandIds.includes(b.id));
                  const allBrandsSubmitted = cBrands.length > 0 && cBrands.every(b => existingSubs.some(s => s.brandId === b.id));
                  // Time calculations
                  const now = Date.now();
                  const msLeft = c.validTo - now;
                  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
                  const hoursLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60)));
                  const isExpiringSoon = daysLeft <= 2;
                  const isUrgent = hoursLeft <= 24;
                  const submittedBrandCount = existingSubs.length;
                  const remainingBrandCount = cBrands.length - submittedBrandCount;
                  const progressPct = cBrands.length > 0 ? Math.round((submittedBrandCount / cBrands.length) * 100) : 0;
                  // Due date formatting
                  const dueDate = new Date(c.validTo);
                  const dueDateStr = dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                  const startDate = new Date(c.validFrom);
                  const startDateStr = startDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                  return (
                    <button
                      key={c.id}
                      onClick={() => {
                        if (allBrandsSubmitted) {
                          // All brands done · go to summary view
                          setViewMode('all');
                        } else {
                          setSelectedCampaignId(c.id);
                        }
                      }}
                      className={cn(
                        "w-full flex flex-col gap-3 p-4 border rounded-xl transition-all text-left group",
                        allBrandsSubmitted
                          ? "bg-emerald-50 border-emerald-200 hover:border-emerald-400"
                          : isUrgent && !hasExisting
                            ? "bg-red-50 border-red-200 hover:border-red-400 ring-1 ring-red-100"
                            : hasExisting
                              ? "bg-blue-50 border-blue-200 hover:border-blue-400"
                              : "bg-zinc-50 border-zinc-200 hover:border-emerald-400 hover:bg-emerald-50"
                      )}
                    >
                      {/* Row 1: Icon + Name + Deadline + CTA */}
                      <div className="flex items-center gap-4 w-full">
                        <div className={cn(
                          "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors",
                          allBrandsSubmitted
                            ? "bg-emerald-500 text-white"
                            : isUrgent && !hasExisting
                              ? "bg-red-500 text-white"
                              : hasExisting
                                ? "bg-blue-100 text-blue-700 group-hover:bg-blue-500 group-hover:text-white"
                                : "bg-emerald-100 text-emerald-700 group-hover:bg-emerald-500 group-hover:text-white"
                        )}>
                          {allBrandsSubmitted ? <CheckCircle2 size={18} /> : isUrgent && !hasExisting ? <AlertCircle size={18} /> : <Megaphone size={18} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-zinc-900">{c.name}</p>
                            <span className={cn(
                              "px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border",
                              allBrandsSubmitted
                                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                : hasExisting
                                  ? "bg-blue-100 text-blue-700 border-blue-200"
                                  : "bg-zinc-100 text-zinc-500 border-zinc-200"
                            )}>
                              {allBrandsSubmitted ? '? Completed' : hasExisting ? 'In Progress' : hasRejected ? `? Rejected (${rejectionAttempt}·)` : 'Not Started'}
                            </span>
                            {hasRejected && !allBrandsSubmitted && !hasExisting && (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border bg-red-50 text-red-600 border-red-200">
                                Attempt {rejectionAttempt + 1}
                              </span>
                            )}
                          </div>
                          {c.description && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-1">{c.description}</p>}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <div className="flex items-center gap-1 mb-0.5">
                            {c.aggregators.slice(0, 4).map(agg => {
                              const logo = (campaignSettings.aggregatorLogos ?? {})[agg];
                              return logo
                                ? <img key={agg} src={logo} alt={agg} title={agg} className="w-5 h-5 rounded object-contain border border-zinc-200" />
                                : <span key={agg} title={agg} className="w-5 h-5 rounded bg-teal-50 border border-teal-200 flex items-center justify-center text-[7px] font-bold text-teal-600">{agg.charAt(0)}</span>;
                            })}
                            {c.aggregators.length > 4 && <span className="text-[8px] font-bold text-zinc-400">+{c.aggregators.length - 4}</span>}
                          </div>
                          {allBrandsSubmitted ? (
                            <span className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest flex items-center gap-1"><Eye size={12} /> View</span>
                          ) : hasExisting ? (
                            <span className="text-[9px] font-bold text-blue-600 uppercase tracking-widest flex items-center gap-1"><ArrowRight size={12} /> Continue</span>
                          ) : (
                            <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1"><Plus size={12} /> Start</span>
                          )}
                          <ChevronRight size={16} className="text-zinc-400" />
                        </div>
                      </div>

                      {/* Row 2: Progress bar + Stats row */}
                      <div className="w-full space-y-2.5 pl-14">
                        {/* Progress bar */}
                        {cBrands.length > 1 && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Brand Progress</span>
                              <span className={cn(
                                "text-[10px] font-bold",
                                allBrandsSubmitted ? "text-emerald-600" : hasExisting ? "text-blue-600" : "text-zinc-400"
                              )}>{submittedBrandCount}/{cBrands.length}</span>
                            </div>
                            <div className="w-full h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                              <div
                                className={cn(
                                  "h-full rounded-full transition-all",
                                  allBrandsSubmitted ? "bg-emerald-500" : hasExisting ? "bg-blue-500" : "bg-zinc-300"
                                )}
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Stats chips */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Deadline */}
                          <span className={cn(
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border",
                            isUrgent
                              ? "bg-red-50 text-red-700 border-red-200"
                              : isExpiringSoon
                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                : "bg-zinc-50 text-zinc-500 border-zinc-200"
                          )}>
                            <Clock size={10} />
                            {isUrgent
                              ? `? ${hoursLeft}h left`
                              : daysLeft === 0
                                ? 'Expires today'
                                : `${daysLeft}d left`}
                          </span>
                          {/* Due date */}
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] text-zinc-400 bg-zinc-50 border border-zinc-100">
                            <Calendar size={10} />
                            {startDateStr} ? {dueDateStr}
                          </span>
                          {/* Discount */}
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-violet-50 text-violet-600 border border-violet-200">
                            <Tag size={10} />
                            {c.discountValue}{c.discountType === DiscountType.PERCENTAGE ? '%' : ' QAR'} OFF
                          </span>
                          {/* Target items */}
                          {!!(c.requiredItemCount && c.requiredItemCount > 0) && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                              <Target size={10} /> Target: {c.requiredItemCount} items / brand
                            </span>
                          )}
                          {/* Remaining brands warning */}
                          {hasExisting && !allBrandsSubmitted && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                              <AlertTriangle size={10} /> {remainingBrandCount} brand{remainingBrandCount > 1 ? 's' : ''} pending
                            </span>
                          )}
                        </div>

                        {/* Requirements */}
                        {c.importantRequirements && (
                          <div className="flex items-start gap-1.5 mt-0.5">
                            <AlertTriangle size={11} className="text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-[10px] text-amber-700 leading-snug line-clamp-2">{c.importantRequirements}</p>
                          </div>
                        )}

                        {/* Urgent CTA banner for not-started campaigns expiring soon */}
                        {!hasExisting && isUrgent && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-red-100 border border-red-200 rounded-lg mt-1">
                            <Zap size={12} className="text-red-600 shrink-0" />
                            <p className="text-[10px] font-bold text-red-700">Action Required · This campaign expires in {hoursLeft < 24 ? `${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''}` : `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}. Submit your items now!</p>
                          </div>
                        )}
                        {/* Incomplete CTA for partially submitted */}
                        {hasExisting && !allBrandsSubmitted && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-blue-100 border border-blue-200 rounded-lg mt-1">
                            <ArrowRight size={12} className="text-blue-600 shrink-0" />
                            <p className="text-[10px] font-bold text-blue-700">Incomplete · {remainingBrandCount} brand{remainingBrandCount > 1 ? 's' : ''} still need items. Continue to finish your submission.</p>
                          </div>
                        )}
                        {/* Rejected · prompt to resubmit */}
                        {hasRejected && !hasExisting && !allBrandsSubmitted && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg mt-1">
                            <RotateCcw size={12} className="text-red-600 shrink-0" />
                            <p className="text-[10px] font-bold text-red-700">
                              Your previous submission was rejected ({rejectionAttempt} time{rejectionAttempt > 1 ? 's' : ''}).
                              You can start a fresh submission now.
                            </p>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // -- Full workspace --
  const aggName = campaign.aggregators.length === 1 ? campaign.aggregators[0] : campaign.aggregators.join(', ');
  const campaignTitle = `${campaign.name}${campaign.aggregators.length === 1 ? ' · ' + aggName : ''}`;

  return (
    <div className="space-y-5">

      {/* -- Top Header Card -- */}
      <div className="bg-gradient-to-br from-white to-emerald-50 border border-emerald-200 rounded-2xl p-6 space-y-3">
        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Campaign</p>
        <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">{campaignTitle}</h2>
        {campaign.description && (
          <p className="text-sm text-zinc-500 leading-relaxed">{campaign.description}</p>
        )}
        {campaign.importantRequirements && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle size={13} className="text-amber-500 shrink-0" />
            <p className="text-xs text-amber-700 leading-snug whitespace-pre-line">{campaign.importantRequirements}</p>
          </div>
        )}
        <p className="text-xs text-zinc-400">
          Applies to: <span className="font-semibold text-zinc-600">{campaign.targetAllBrands ? 'All Brands' : applicableBrands.map(b => b.name).join(', ')}</span>
        </p>
        {campaign.aggregators.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-400">Aggregators:</span>
            {campaign.aggregators.map(agg => {
              const logo = (campaignSettings.aggregatorLogos ?? {})[agg];
              return (
                <span key={agg} className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
                  {logo && <img src={logo} alt={agg} className="w-4 h-4 rounded object-contain" />}
                  {agg}
                </span>
              );
            })}
          </div>
        )}
        <button
          onClick={() => setSelectedCampaignId('')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-500 border border-zinc-200 bg-white rounded-lg hover:bg-zinc-50 transition-all mt-1"
        >
          <ArrowLeft size={12} /> Back
        </button>
      </div>

      {/* -- Brand Tabs (pills) -- */}
      {brandSubs.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {brandSubs.map((bs, idx) => (
            <button
              key={bs.brandId}
              onClick={() => setActiveBrandIdx(idx)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all',
                idx === activeBrandIdx
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-zinc-200 bg-white text-zinc-500 hover:border-zinc-400'
              )}
            >
              <span className="font-bold">{bs.brandName}</span>
              <span className="text-[10px] font-mono text-zinc-400">{bs.subId}</span>
            </button>
          ))}
        </div>
      )}

      {/* -- Active brand workspace -- */}
      {activeSub && (
        <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Submission meta-data header */}
          <div className="px-6 py-5 border-b border-zinc-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Submitting For</p>
                <p className="text-xl font-bold text-zinc-900 mt-1">{activeSub.brandName}</p>
                <p className="text-xs text-zinc-400 mt-0.5 font-mono">{activeSub.subId} · {activeSub.brandName}</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Manager</p>
                <p className="text-sm font-bold text-zinc-900 mt-1">{user?.name ?? 'Unknown'}</p>
                <p className="text-[10px] text-zinc-400 font-mono mt-0.5">ID: {user?.id ?? ''}</p>
              </div>
            </div>
          </div>

          {/* Add Items section */}
          <div className="px-6 py-5 space-y-5">
            {/* Sticky Item Counter */}
            <div className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-white/95 backdrop-blur border-b border-zinc-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-zinc-900">Add Items</h3>
                  <p className="text-sm text-zinc-400 mt-0.5">
                    For <span className="text-emerald-600 font-semibold">{activeSub.brandName}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {campaign.importantRequirements && (
                    <div className="relative group/req">
                      <button type="button" className="p-2 text-amber-500 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all">
                        <Info size={18} />
                      </button>
                      <div className="absolute right-0 top-full mt-1 w-72 px-4 py-3 bg-amber-50 border border-amber-300 rounded-xl shadow-lg opacity-0 group-hover/req:opacity-100 pointer-events-none group-hover/req:pointer-events-auto transition-opacity z-20">
                        <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Important Requirements</p>
                        <p className="text-xs text-amber-800 leading-relaxed whitespace-pre-line mt-1">{campaign.importantRequirements}</p>
                      </div>
                    </div>
                  )}
                  <div className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-bold",
                    campaign.requiredItemCount && campaign.requiredItemCount > 0
                      ? activeSub.items.filter(it => it.name.trim()).length >= campaign.requiredItemCount
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : "bg-amber-50 border-amber-200 text-amber-700"
                      : "bg-zinc-50 border-zinc-200 text-zinc-600"
                  )}>
                    <Hash size={14} />
                    {activeSub.items.filter(it => it.name.trim()).length}
                    {campaign.requiredItemCount && campaign.requiredItemCount > 0
                      ? ` / ${campaign.requiredItemCount}`
                      : ''}
                    <span className="text-xs font-normal ml-1">items</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Item detail cards */}
            {activeSub.items.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-200 rounded-2xl p-10 text-center">
                <Package size={36} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-base font-bold text-zinc-600">No Items Added Yet</p>
                <p className="text-sm text-zinc-400 mt-1">Add items manually or import from your master menu below.</p>
              </div>
            ) : (
            <div className="space-y-4">
              {activeSub.items.map((item, idx) => {
                const collapsed = activeSub.collapsedCards.has(item.id);
                return (
                  <div key={item.id} className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
                    {/* Card header · always visible */}
                    {collapsed ? (
                      <div
                        className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-zinc-50 transition-colors"
                        onClick={() => toggleCollapse(item.id)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Item #{idx + 1}</span>
                          {item.menuLinked && (
                            <span className="text-teal-600" title="Menu Linked">
                              <Link2 size={12} />
                            </span>
                          )}
                          <span className="text-sm font-semibold text-zinc-900">{item.name || '(Untitled)'}</span>
                          {item.price > 0 && <span className="text-xs text-zinc-400 font-mono">QAR {item.price.toFixed(2)}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {activeSub.items.length > 1 && (
                            <button
                              onClick={e => { e.stopPropagation(); removeItem(item.id); }}
                              className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                          <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1">
                            Open <ChevronDown size={12} />
                          </span>
                        </div>
                      </div>
                    ) : (
                      /* Expanded card */
                      <div className="p-5 space-y-5">
                        {/* Card title bar */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Item Details #{idx + 1}</p>
                            {item.menuLinked && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 text-[9px] font-bold rounded-lg border border-teal-200">
                                <Link2 size={10} /> Menu Link
                              </span>
                            )}
                          </div>
                          {activeSub.items.length > 1 && (
                            <button
                              onClick={() => removeItem(item.id)}
                              className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>

                        {/* Item Name · 50/50 EN/AR Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Item Name (EN)</label>
                            <input
                              value={item.name}
                              onChange={e => updateItem(item.id, 'name', e.target.value)}
                              placeholder="Item Name"
                              className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Item Name (AR)</label>
                            <input
                              value={item.nameAR || ''}
                              onChange={e => updateItem(item.id, 'nameAR', e.target.value)}
                              placeholder="\u0627\u0633\u0645 \u0627\u0644\u0635\u0646\u0641"
                              dir="rtl"
                              className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-right focus:outline-none focus:border-emerald-500 transition-colors"
                            />
                          </div>
                        </div>

                        {/* Original Price */}
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Original Price</label>
                            {item.menuLinked && item.originalMenuPrice != null && item.price !== item.originalMenuPrice && (
                              <span className="text-[9px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 font-bold">
                                Menu: {item.originalMenuPrice.toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-bold text-zinc-400">QAR</span>
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={item.price || ''}
                              onChange={e => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                              placeholder="Original Price"
                              className={cn(
                                "w-full pl-12 pr-4 py-3.5 bg-zinc-50 border rounded-xl text-sm focus:outline-none focus:border-emerald-500 transition-colors",
                                item.menuLinked && item.originalMenuPrice != null && item.price !== item.originalMenuPrice
                                  ? "border-amber-300 bg-amber-50/30"
                                  : "border-zinc-200"
                              )}
                            />
                          </div>
                        </div>

                        {/* Description · 50/50 EN/AR Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Description (EN) <span className="normal-case font-normal text-zinc-300">(optional)</span></label>
                            <textarea
                              value={item.description ?? ''}
                              onChange={e => updateItem(item.id, 'description', e.target.value)}
                              placeholder="Description (Optional)"
                              rows={2}
                              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-emerald-500 resize-none transition-colors"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Description (AR) <span className="normal-case font-normal text-zinc-300">(optional)</span></label>
                            <textarea
                              value={item.descriptionAR ?? ''}
                              onChange={e => updateItem(item.id, 'descriptionAR', e.target.value)}
                              placeholder="\u0627\u0644\u0648\u0635\u0641 \u0628\u0627\u0644\u0639\u0631\u0628\u064a\u0629"
                              dir="rtl"
                              rows={2}
                              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-right focus:outline-none focus:border-emerald-500 resize-none transition-colors"
                            />
                          </div>
                        </div>

                        {/* Image upload */}
                        {item.imageUrl ? (
                          <div className="relative group rounded-xl overflow-hidden border border-zinc-200">
                            <img src={getMediaUrl(item.imageUrl)} alt={item.name || 'Item photo'} className="w-full h-48 object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                              <label className="flex items-center gap-1.5 px-3 py-2 bg-white text-zinc-700 rounded-lg text-xs font-bold cursor-pointer hover:bg-zinc-100 transition-colors">
                                <Upload size={12} /> Change
                                <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(item.id, e)} />
                              </label>
                              <button
                                type="button"
                                onClick={() => updateItem(item.id, 'imageUrl', '')}
                                className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-bold hover:bg-red-600 transition-colors"
                              >
                                <Trash2 size={12} /> Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="border-2 border-dashed border-zinc-200 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors cursor-pointer">
                            <ImageIcon size={28} className="text-zinc-300 mb-2" />
                            <p className="text-sm text-zinc-400">Upload Photo (Optional)</p>
                            <p className="text-[10px] text-zinc-300 mt-1">Click to browse · JPG, PNG up to 5MB</p>
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(item.id, e)} />
                          </label>
                        )}

                        {/* Imported Modifiers (Menu-Linked items only) */}
                        {item.menuLinked && item.importedOptions && item.importedOptions.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
                              <Layers size={12} className="text-indigo-500" />
                              Imported Modifiers
                              <span className="text-[9px] font-normal text-zinc-300 ml-1">({item.importedOptions.length} group{item.importedOptions.length > 1 ? 's' : ''})</span>
                            </p>
                            <div className="space-y-2">
                              {item.importedOptions.map(og => (
                                <div key={og.id} className="px-3 py-2 bg-indigo-50/50 border border-indigo-100 rounded-lg">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-semibold text-indigo-700">{og.nameEN}</span>
                                    {og.nameAR && <span className="text-[10px] text-indigo-400 font-arabic">{og.nameAR}</span>}
                                    {og.minSelection != null && og.minSelection > 0 && (
                                      <span className="text-[8px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-bold border border-red-100">Required</span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {og.options.map(opt => (
                                      <span key={opt.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-indigo-100 rounded text-[10px] text-zinc-600">
                                        {opt.nameEN}
                                        {opt.extraPrice > 0 && <span className="text-[9px] font-mono text-emerald-600">+{opt.extraPrice.toFixed(2)}</span>}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Collapse toggle */}
                        <button
                          type="button"
                          onClick={() => toggleCollapse(item.id)}
                          className="w-full flex items-center justify-center gap-1.5 pt-3 border-t border-zinc-100 text-[9px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-600 transition-colors"
                        >
                          Close <ChevronUp size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}

            {/* Add Item Actions (Manual + Menu Import) */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {allowManualEntry && (
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center gap-2 px-6 py-2.5 border-2 border-dashed border-emerald-300 rounded-xl text-sm font-bold text-emerald-600 hover:bg-emerald-50 hover:border-emerald-500 transition-all"
                >
                  <Plus size={16} /> Add Manual Item
                </button>
              )}

              {/* Add from Menu · Access Guard */}
              {userHasMenuAccess ? (
                <button
                  type="button"
                  onClick={() => setMenuSelectorOpen(true)}
                  className="flex items-center gap-2 px-6 py-2.5 border-2 border-teal-300 bg-teal-50 rounded-xl text-sm font-bold text-teal-700 hover:bg-teal-100 hover:border-teal-500 transition-all"
                >
                  <BookOpen size={16} /> Add from Menu
                </button>
              ) : (
                <div className="relative group/menu-tip">
                  <button
                    type="button"
                    disabled
                    className="flex items-center gap-2 px-6 py-2.5 border-2 border-dashed border-zinc-200 rounded-xl text-sm font-bold text-zinc-400 cursor-not-allowed opacity-60"
                  >
                    <BookOpen size={16} /> Add from Menu
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-zinc-900 text-white text-[10px] font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover/menu-tip:opacity-100 transition-opacity pointer-events-none z-20">
                    Menu Access Required: Please contact Admin to enable Menu Management for your account.
                  </div>
                </div>
              )}
            </div>

            {/* Menu Selector Modal */}
            <MenuSelectorModal
              open={menuSelectorOpen}
              onClose={() => setMenuSelectorOpen(false)}
              user={user}
              brandId={activeSub?.brandId}
              onImport={(items, profileName) => {
                setBrandSubs(prev => prev.map((bs, i) =>
                  i === activeBrandIdx
                    ? {
                        ...bs,
                        items: [
                          ...bs.items.filter(it => it.name.trim()),
                          ...items,
                        ],
                      }
                    : bs
                ));
                toast.success(
                  'Items Imported!',
                  `${items.length} item${items.length > 1 ? 's' : ''} successfully imported from ${profileName}.`
                );
              }}
            />
          </div>

          {/* -- Sticky Footer Bar -- */}
          {validationError && (
            <div className="mx-6 mt-4 flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle size={16} className="text-red-600 shrink-0" />
              <p className="text-sm font-semibold text-red-700">{validationError}</p>
              <button onClick={() => setValidationError('')} className="ml-auto p-1 text-red-400 hover:text-red-600"><X size={14} /></button>
            </div>
          )}
          <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <p className="text-xs text-zinc-500">
              Campaign: <span className="font-bold text-zinc-700">{campaignTitle}</span>
              {brandSubs.length > 1 && <span className="ml-2 text-zinc-400">({brandSubs.length} brands remaining)</span>}
            </p>
            <div className="flex items-center gap-3">
              {/* Submit Brand button */}
              <div className="relative group/tip">
                <button
                  onClick={handleSubmitBrand}
                  disabled={!canSubmitBrand || submitting}
                  className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <Send size={14} />
                  {submitting ? 'Submitting·' : `Submit ${activeSub?.brandName ?? 'Brand'}`}
                </button>
                {!canSubmitBrand && (
                  <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-zinc-900 text-white text-[10px] font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none z-10">
                    Complete all items (Name & Price required) to submit this brand.
                  </div>
                )}
                {canSubmitBrand && campaign.requiredItemCount && campaign.requiredItemCount > 0 && activeSub && activeSub.items.filter(it => it.name.trim()).length < campaign.requiredItemCount && (
                  <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-amber-600 text-white text-[10px] font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none z-10">
                    Requirement Not Met: Please add {campaign.requiredItemCount - activeSub.items.filter(it => it.name.trim()).length} more item(s).
                  </div>
                )}
              </div>
              {/* Submit All button */}
              {brandSubs.length > 1 && (
                <div className="relative group/tip2">
                  <button
                    onClick={handleSubmitAll}
                    disabled={!canSubmit || submitting}
                    className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    {submitting ? 'Submitting·' : `Submit All (${brandSubs.length} Brands)`}
                  </button>
                  {!canSubmit && (
                    <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-zinc-900 text-white text-[10px] font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover/tip2:opacity-100 transition-opacity pointer-events-none z-10">
                      Complete all items across all brands (Name & Price required) to submit everything.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Review Inbox Tab (Admin / Department Reviewer) ---------------------------

// -- Dynamic stage color palette (cycles for any number of departments) --
const STAGE_COLOR_PALETTE: { color: string; bg: string; border: string }[] = [
  { color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  { color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  { color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200' },
  { color: 'text-teal-700',   bg: 'bg-teal-50',   border: 'border-teal-200' },
  { color: 'text-rose-700',   bg: 'bg-rose-50',   border: 'border-rose-200' },
  { color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  { color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
  { color: 'text-cyan-700',   bg: 'bg-cyan-50',   border: 'border-cyan-200' },
];
const DONE_STAGE_META = { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' };

function buildStageMeta(stages: string[]): Record<string, { color: string; bg: string; border: string }> {
  const meta: Record<string, { color: string; bg: string; border: string }> = {};
  stages.forEach((stage, i) => {
    meta[stage] = STAGE_COLOR_PALETTE[i % STAGE_COLOR_PALETTE.length];
  });
  meta['DONE'] = DONE_STAGE_META;
  return meta;
}

const STATUS_COLOR: Record<SubmissionStatus, { pill: string; dot: string }> = {
  [SubmissionStatus.PENDING]:          { pill: 'bg-amber-50 text-amber-700 border-amber-200',       dot: 'bg-amber-500' },
  [SubmissionStatus.IN_REVIEW]:        { pill: 'bg-blue-50 text-blue-700 border-blue-200',          dot: 'bg-blue-500' },
  [SubmissionStatus.APPROVED]:         { pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  [SubmissionStatus.DEPLOYMENT_READY]: { pill: 'bg-violet-50 text-violet-700 border-violet-200',    dot: 'bg-violet-500' },
  [SubmissionStatus.POSTPONED]:        { pill: 'bg-orange-50 text-orange-700 border-orange-200',    dot: 'bg-orange-500' },
  [SubmissionStatus.COMPLETED]:        { pill: 'bg-teal-50 text-teal-700 border-teal-200',          dot: 'bg-teal-600' },
  [SubmissionStatus.REJECTED]:         { pill: 'bg-red-50 text-red-700 border-red-200',            dot: 'bg-red-500' },
};

function ReviewInboxTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'aggregator', 'review', 'admin');
  const isSuperAdmin = isAdmin; // Platform roles and ADMIN both bypass via hasTabVariant
  const repo = getRepository();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<CampaignSubmission[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<CampaignSubmission | null>(null);
  const [reviewNote, setReviewNote] = useState('');

  // -- Advanced filtering via dedicated hook --
  const {
    filters: reviewFilters,
    setSubTab,
    setSearchQuery,
    setDepartmentFilter,
    setDateFrom,
    setDateTo,
    filtered: filteredSubmissions,
    counts: subTabCounts,
  } = useFilteredSubmissions(submissions, campaigns, departments);

  // -- Per-item feedback state --
  const [itemNotes, setItemNotes] = useState<Record<string, string>>({});
  const [unlockingIds, setUnlockingIds] = useState<Set<string>>(new Set());

  // -- Discussion chat state (WhatsApp-style: staged attachment + @mention + item ref) --
  const [chatInput, setChatInput] = useState('');
  const [stagedFile, setStagedFile] = useState<{ name: string; type: 'image' | 'document'; data: string; size: number } | null>(null);
  const [mentionDropdown, setMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [chatItemRef, setChatItemRef] = useState<{ itemId: string; itemName: string } | null>(null);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);

  // -- File attachment ref for discussion --
  const fileInputRef = useRef<HTMLInputElement>(null);

  // -- Review Decision Popup state --
  const [decisionPopup, setDecisionPopup] = useState<{
    sub: CampaignSubmission;
    path: 'approve' | 'reject' | null;
    note: string;
    rejectReason: string;
  } | null>(null);

  // -- History Audit Popup state --
  const [showAuditPopup, setShowAuditPopup] = useState(false);

  // -- Item Discard Modal state --
  const [discardModal, setDiscardModal] = useState<{ sub: CampaignSubmission; itemId: string; itemName: string } | null>(null);
  const [discardReason, setDiscardReason] = useState('');
  const [discarding, setDiscarding] = useState(false);

  // -- Undo discard state (5-second window) --
  const [undoDiscard, setUndoDiscard] = useState<{ sub: CampaignSubmission; itemId: string; itemName: string; timer: ReturnType<typeof setTimeout> } | null>(null);

  // -- Item detail expand + cost map (for option groups & cost card) --
  const [reviewExpandedItemId, setReviewExpandedItemId] = useState<string | null>(null);
  const [reviewItemCostMap, setReviewItemCostMap] = useState<Record<string, { baseCost: number; options: Record<string, number> }>>({});

  // Fetch cost map when a menu-linked item is expanded in review
  useEffect(() => {
    if (!reviewExpandedItemId || !selectedSubmission) return;
    const item = selectedSubmission.items.find(it => it.id === reviewExpandedItemId);
    if (!item?.menuLinked || !item?.menuProfileId || !item?.menuItemId) return;
    if (reviewItemCostMap[item.menuItemId]) return;
    apiFetch<{ data: Record<string, { baseCost: number; options: Record<string, number> }> }>(`/api/menu/recipes/cost-map/${item.menuProfileId}`)
      .then(j => setReviewItemCostMap(prev => ({ ...prev, ...j.data })))
      .catch(() => {});
  }, [reviewExpandedItemId, selectedSubmission?.id]);

  const load = async () => {
    const [s, d, c, b] = await Promise.all([
      repo.getCampaignSubmissions(),
      repo.getDepartments(),
      repo.getCampaigns(),
      repo.getBrands(),
    ]);
    // -- Data Isolation: Managers ONLY see their own submissions --
    // Admin / Super Admin see all org-scoped submissions (orgId is already filtered by repository).
    // Store Manager sees ONLY submissions they submitted.
    const isolated = isAdmin ? s : s.filter(sub => sub.submittedBy === user?.id);
    setSubmissions(isolated);
    setDepartments(d);
    setCampaigns(c);
    setBrands(b.filter(br => br.enabledModules?.includes('aggregator')));
    // -- Sync selectedSubmission with fresh data to prevent stale drawer --
    setSelectedSubmission(prev => {
      if (!prev) return null;
      return isolated.find(sub => sub.id === prev.id) ?? null;
    });
  };
  useEffect(() => { load(); const h = (e: any) => { if (import.meta.env.DEV && e && e.type === 'PULSE_UPDATE') console.log('💓 Heartbeat: Data Refreshed (Aggregator) ' + e.type); load(); }; window.addEventListener('PULSE_UPDATE', h); return () => window.removeEventListener('PULSE_UPDATE', h); }, []);

  // -- Deep link: auto-expand submission from URL ?sub=XXX --
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subId = params.get('sub');
    if (subId && submissions.length > 0) {
      const target = submissions.find(s => s.id === subId);
      if (target) setSelectedSubmission(target);
    }
  }, [location.search, submissions.length]);

  // Dynamic department stages from registry
  const dynamicStages = (() => {
    const campaignDepts = departments.filter(d => (d.linkedModules ?? []).includes('aggregator'));
    if (campaignDepts.length > 0) return campaignDepts.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(d => d.name);
    return [...DEPARTMENT_STAGES];
  })();
  const brandLookup = React.useMemo(
    () => new Map(brands.map(brand => [brand.id, brand])),
    [brands]
  );
  const hasActiveReviewFilters = Boolean(
    reviewFilters.searchQuery.trim() ||
    reviewFilters.departmentFilter ||
    reviewFilters.dateFrom ||
    reviewFilters.dateTo
  );
  const STAGE_META = buildStageMeta(dynamicStages);

  // Department name lookup
  const deptName = (deptId: string) => departments.find(d => d.id === deptId)?.name ?? deptId;

  // Which department IDs is this user assigned to?
  const userDeptIds = user?.assignedDepartments ?? [];
  const userDeptNames = userDeptIds.map(id => deptName(id));

  // Resolve acting department ID for the current stage.
  // Super Admin has no assignedDepartments but acts AS the stage's department.
  // Regular Admin resolves from their assignments. Both paths use this helper.
  const resolveActingDeptId = (stage: string | null): string =>
    stage ? (departments.find(d => d.name.toUpperCase() === stage.toUpperCase())?.id ?? '') : '';

  // Can this user review the current stage of a submission?
  // Admin & Super Admin can view and interact (unlock items, discuss).
  const canReview = (sub: CampaignSubmission): boolean => {
    if (sub.status === SubmissionStatus.APPROVED || sub.status === SubmissionStatus.COMPLETED || sub.status === SubmissionStatus.DEPLOYMENT_READY || sub.status === SubmissionStatus.REJECTED || sub.currentStage === 'DONE') return false;
    return isSuperAdmin || isAdmin;
  };

  // SECURITY GATE: Can this user MAKE DECISIONS (approve/reject) on this submission's current stage?
  // Super Admin always can. Regular Admin must have the stage's department in their assignedDepartments.
  const canMakeDecision = (sub: CampaignSubmission): boolean => {
    if (sub.status === SubmissionStatus.APPROVED || sub.status === SubmissionStatus.COMPLETED || sub.status === SubmissionStatus.DEPLOYMENT_READY || sub.status === SubmissionStatus.REJECTED || sub.currentStage === 'DONE') return false;
    if (isSuperAdmin) return true;
    if (!isAdmin) return false;
    const stageDeptId = resolveActingDeptId(sub.currentStage);
    return stageDeptId ? userDeptIds.includes(stageDeptId) : false;
  };

  // Submission Reviews · filtering is now handled by useFilteredSubmissions hook

  const handleApprove = async (sub: CampaignSubmission, noteOverride?: string) => {
    if (!user) return;
    const approveNote = (noteOverride ?? reviewNote).trim();
    const effectiveStage = sub.currentStage || dynamicStages[0] || 'DONE';
    const currentIdx = dynamicStages.findIndex(s => s.toUpperCase() === effectiveStage.toUpperCase());

    // Guard: if current stage not found in dynamic stages, treat as last stage ? DONE
    if (currentIdx === -1) {
      console.warn(`[handleApprove] Stage "${sub.currentStage}" not found in dynamicStages · advancing to DONE`);
    }

    const isLastStage = currentIdx === -1 || currentIdx === dynamicStages.length - 1;
    const nextStage: DepartmentStage = isLastStage ? 'DONE' : dynamicStages[currentIdx + 1];

    const action: ReviewAction = {
      departmentId: resolveActingDeptId(sub.currentStage),
      departmentName: sub.currentStage,
      action: 'APPROVE',
      reviewerId: user.id,
      reviewerName: user.name,
      note: approveNote || undefined,
      timestamp: Date.now(),
    };

    const updated: CampaignSubmission = {
      ...sub,
      currentStage: nextStage,
      status: nextStage === 'DONE' ? SubmissionStatus.DEPLOYMENT_READY : SubmissionStatus.IN_REVIEW,
      reviewHistory: [...sub.reviewHistory, action],
      updatedAt: Date.now(),
    };

    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `[${sub.currentStage}] Approved submission "${sub.campaignName}" (${sub.brandName})${nextStage === 'DONE' ? ' · DEPLOYMENT READY' : ` ? forwarded to ${nextStage}`}`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });

    setReviewNote('');
    setSelectedSubmission(updated);
    toast.success(
      nextStage === 'DONE' ? 'All Reviews Complete!' : 'Approved & Forwarded',
      nextStage === 'DONE'
        ? `"${sub.campaignName}" (${sub.brandName}) is now deployment-ready. Head to Launch Readiness to launch.`
        : `"${sub.campaignName}" (${sub.brandName}) forwarded to ${nextStage}.`
    );
    await load();
  };

  // -- Unlock a specific item for manager correction --
  const handleUnlockItem = async (sub: CampaignSubmission, itemId: string) => {
    if (!user) return;
    const note = itemNotes[itemId]?.trim() ?? '';
    const updatedItems = sub.items.map(it =>
      it.id === itemId
        ? { ...it, locked: false, adminNote: note || it.adminNote, unlockedAt: Date.now(), unlockedBy: user.name }
        : it
    );
    const updated: CampaignSubmission = {
      ...sub,
      items: updatedItems,
      // Status stays unchanged · unlock is surgical, doesn't affect overall flow
      reviewHistory: [...sub.reviewHistory, {
        departmentId: resolveActingDeptId(sub.currentStage),
        departmentName: sub.currentStage,
        action: 'UNLOCK' as const,
        reviewerId: user.id,
        reviewerName: user.name,
        note: `Unlocked item "${sub.items.find(it => it.id === itemId)?.name}" for correction${note ? ': ' + note : ''}`,
        unlockedItemIds: [itemId],
        timestamp: Date.now(),
      }],
      updatedAt: Date.now(),
    };
    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `Unlocked item "${sub.items.find(it => it.id === itemId)?.name}" in "${sub.campaignName}" (${sub.brandName}) for store manager correction`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });

    // -- Notify the Store Manager who submitted (Item Unlock ? manager alert) --
    await repo.saveNotification({
      id: crypto.randomUUID(),
      recipientId: sub.submittedBy,
      actorId: user.id,
      type: NotificationType.WARNING,
      title: 'Action Required: Item Unlocked',
      message: `"${sub.items.find(it => it.id === itemId)?.name}" in "${sub.campaignName}" needs correction.${note ? ' Feedback: ' + note : ''}`,
      targetPath: '/apps/aggregator/submit',
      isRead: false,
      createdAt: Date.now(),
    });

    setItemNotes(prev => { const next = { ...prev }; delete next[itemId]; return next; });
    setUnlockingIds(prev => { const next = new Set(prev); next.delete(itemId); return next; });
    setSelectedSubmission(updated);
    toast.warning('Item Unlocked', `"${sub.items.find(it => it.id === itemId)?.name}" sent back to manager for correction.`);
    await load();
  };

  // -- Discard a specific item · Admin only --
  const handleDiscardItem = async (sub: CampaignSubmission, itemId: string, reason: string) => {
    if (!user || !reason.trim()) return;
    setDiscarding(true);
    const item = sub.items.find(it => it.id === itemId);
    if (!item) { setDiscarding(false); return; }

    const updatedItems = sub.items.map(it =>
      it.id === itemId
        ? { ...it, itemStatus: 'DISCARDED' as const, discardReason: reason.trim(), discardedBy: user.name, discardedAt: Date.now() }
        : it
    );
    const updated: CampaignSubmission = { ...sub, items: updatedItems, updatedAt: Date.now() };
    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(), userId: user.id, userName: user.name,
      action: `Discarded item "${item.name}" from "${sub.campaignName}" (${sub.brandName}): ${reason.trim()}`,
      timestamp: Date.now(), moduleId: 'aggregator',
    });
    await repo.saveNotification({
      id: crypto.randomUUID(), recipientId: sub.submittedBy, actorId: user.id,
      type: NotificationType.WARNING,
      title: 'Item Discarded',
      message: `Admin has discarded "${item.name}" from your ${sub.brandName} submission for "${sub.campaignName}". Reason: ${reason.trim()}`,
      targetPath: '/apps/aggregator/submit', isRead: false, createdAt: Date.now(),
    });

    setSelectedSubmission(updated);
    setDiscardModal(null);
    setDiscardReason('');
    setDiscarding(false);
    await load();

    // Set up 5-second undo window
    const timer = setTimeout(() => setUndoDiscard(null), 5000);
    setUndoDiscard({ sub: updated, itemId, itemName: item.name, timer });
    toast.warning('Item Discarded', `"${item.name}" has been discarded. You have 5 seconds to undo.`);
  };

  // -- Undo / Restore a discarded item --
  const handleRestoreItem = async (sub: CampaignSubmission, itemId: string) => {
    if (!user) return;
    const item = sub.items.find(it => it.id === itemId);
    if (!item) return;
    const updatedItems = sub.items.map(it =>
      it.id === itemId
        ? { ...it, itemStatus: undefined, discardReason: undefined, discardedBy: undefined, discardedAt: undefined }
        : it
    );
    const updated: CampaignSubmission = { ...sub, items: updatedItems, updatedAt: Date.now() };
    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(), userId: user.id, userName: user.name,
      action: `Restored discarded item "${item.name}" in "${sub.campaignName}" (${sub.brandName})`,
      timestamp: Date.now(), moduleId: 'aggregator',
    });
    setSelectedSubmission(updated);
    if (undoDiscard?.timer) clearTimeout(undoDiscard.timer);
    setUndoDiscard(null);
    toast.success('Item Restored', `"${item.name}" has been restored.`);
    await load();
  };

  // -- Reject submission · permanently kills it --
  const handleReject = async (sub: CampaignSubmission, reason: string) => {
    if (!user || !reason.trim()) return;
    const action: ReviewAction = {
      departmentId: resolveActingDeptId(sub.currentStage),
      departmentName: sub.currentStage,
      action: 'REJECT',
      reviewerId: user.id,
      reviewerName: user.name,
      note: reason.trim(),
      timestamp: Date.now(),
    };

    const updated: CampaignSubmission = {
      ...sub,
      status: SubmissionStatus.REJECTED,
      reviewHistory: [...sub.reviewHistory, action],
      rejectionReason: reason.trim(),
      rejectedBy: user.name,
      rejectedAt: Date.now(),
      updatedAt: Date.now(),
    };

    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `[${sub.currentStage}] REJECTED submission "${sub.campaignName}" (${sub.brandName}): ${reason.trim()}`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });

    setReviewNote('');
    setSelectedSubmission(updated);
    setDecisionPopup(null);
    toast.error('Submission Rejected', `"${sub.campaignName}" (${sub.brandName}) has been rejected.`);
    await load();
  };

  // -- Revert the MOST RECENT review decision (Admin/SuperAdmin only) --
  const handleRevertDecision = async (sub: CampaignSubmission) => {
    if (!user || (!isAdmin && !isSuperAdmin)) return;
    if (sub.reviewHistory.length === 0) return;

    const lastAction = sub.reviewHistory[sub.reviewHistory.length - 1];
    const newHistory = sub.reviewHistory.slice(0, -1);

    // Determine what stage & status to revert to
    let revertStage: DepartmentStage = lastAction.departmentName as DepartmentStage;
    let revertStatus = SubmissionStatus.IN_REVIEW;

    // If the reverted action was a REJECT, restore to IN_REVIEW at that stage
    if (lastAction.action === 'REJECT') {
      revertStage = lastAction.departmentName as DepartmentStage;
      revertStatus = SubmissionStatus.IN_REVIEW;
    }

    const updated: CampaignSubmission = {
      ...sub,
      currentStage: revertStage,
      status: revertStatus,
      reviewHistory: newHistory,
      // Clear rejection fields if reverting a rejection
      ...(lastAction.action === 'REJECT' ? { rejectionReason: undefined, rejectedBy: undefined, rejectedAt: undefined } : {}),
      updatedAt: Date.now(),
    };

    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `[REVERT] Reverted ${lastAction.action} by ${lastAction.reviewerName} on "${sub.campaignName}" (${sub.brandName}) · returned to ${revertStage}`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });

    setSelectedSubmission(updated);
    toast.warning('Decision Reverted', `Reverted ${lastAction.action} · "${sub.campaignName}" (${sub.brandName}) returned to ${revertStage}.`);
    await load();
  };

  // -- Send discussion message (WhatsApp-style: combined text + file + @mentions + item ref) --
  const handleSendMessage = async (
    sub: CampaignSubmission,
    message: string,
    isPublic: boolean,
    attachment?: { name: string; type: 'image' | 'document'; data: string; size: number },
    itemRef?: { itemId: string; itemName: string } | null
  ) => {
    if (!user || (!message.trim() && !attachment)) return;

    // Clear immediately BEFORE async work to prevent double-send on rapid clicks
    setChatInput('');
    setStagedFile(null);
    setChatItemRef(null);

    const userDeptName = userDeptIds.length > 0 ? deptName(userDeptIds[0]) : undefined;

    // Extract @mentions from message text
    const mentionPattern = /@([A-Za-z0-9&\s]+?)(?=\s|$|@)/g;
    const mentions: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = mentionPattern.exec(message)) !== null) {
      const mentioned = match[1].trim();
      // Validate against dynamic departments
      if (dynamicStages.some(s => s.toUpperCase() === mentioned.toUpperCase())) {
        mentions.push(mentioned.toUpperCase());
      }
    }

    const msg: DiscussionMessage = {
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      departmentName: userDeptName,
      message: message.trim(),
      attachmentName: attachment?.name,
      attachmentType: attachment?.type,
      attachmentData: attachment?.data,
      attachmentSize: attachment?.size,
      mentions: mentions.length > 0 ? mentions : undefined,
      itemRef: itemRef ?? undefined,
      public: isPublic,
      timestamp: Date.now(),
    };

    const updated: CampaignSubmission = {
      ...sub,
      discussion: [...(sub.discussion ?? []), msg],
      updatedAt: Date.now(),
    };

    await repo.saveCampaignSubmission(updated);

    // -- Internal chat alerts ? broadcast to all admins (not the sender) --
    if (!isPublic) {
      await repo.saveNotification({
        id: crypto.randomUUID(),
        recipientId: '*',            // broadcast · all admins
        actorId: user.id,            // anti-self: sender won't see their own alert
        type: NotificationType.INFO,
        title: attachment ? 'File Shared in Discussion' : 'Internal Discussion',
        message: `${user.name}${userDeptName ? ` (${userDeptName})` : ''}: "${message.trim().substring(0, 80)}" · ${sub.campaignName}`,
        targetPath: `/apps/aggregator/review?sub=${sub.id}`,
        isRead: false,
        createdAt: Date.now(),
      });
    }

    // -- @Mention notifications ? targeted to mentioned department members --
    for (const mentionedDept of mentions) {
      await repo.saveNotification({
        id: crypto.randomUUID(),
        recipientId: `dept:${mentionedDept}`,   // department-targeted broadcast
        actorId: user.id,
        type: NotificationType.INFO,
        title: 'Department Mentioned',
        message: `${user.name} mentioned @${mentionedDept} in "${sub.campaignName}" (${sub.brandName}) discussion.`,
        targetPath: `/apps/aggregator/review?sub=${sub.id}`,
        isRead: false,
        createdAt: Date.now(),
      });
    }

    setSelectedSubmission(updated);
    await load();
  };

  return (
    <div className="space-y-0">
      {/* -- Header -- */}
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Submission Reviews</h2>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin ? 'Review and approve campaign submissions through the department pipeline.' : 'Track your submissions moving through review stages.'}
          </p>
        </div>
      </div>

      {/* -- Sub-Tab Navigation with Badges -- */}
      <div className="flex items-center gap-1 border-b border-zinc-200 mb-0">
        {([
          { key: 'IN_REVIEW' as ReviewSubTab,  label: 'In Review',  count: subTabCounts.inReview,  activeStyle: 'border-blue-600 text-blue-700',   dotColor: 'bg-blue-500',    badgeBg: 'bg-blue-100 text-blue-700' },
          { key: 'ALL' as ReviewSubTab,         label: 'All',        count: subTabCounts.all,       activeStyle: 'border-zinc-900 text-zinc-900',   dotColor: 'bg-zinc-500',    badgeBg: 'bg-zinc-100 text-zinc-600' },
          { key: 'COMPLETED' as ReviewSubTab,   label: 'Completed',  count: subTabCounts.completed, activeStyle: 'border-emerald-600 text-emerald-700', dotColor: 'bg-emerald-500', badgeBg: 'bg-emerald-100 text-emerald-700' },
          { key: 'REJECTED' as ReviewSubTab,    label: 'Rejected',   count: subTabCounts.rejected,  activeStyle: 'border-red-600 text-red-700',     dotColor: 'bg-red-500',     badgeBg: 'bg-red-100 text-red-700' },
        ]).map(tab => {
          const isActive = reviewFilters.subTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setSubTab(tab.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px",
                isActive
                  ? tab.activeStyle
                  : "border-transparent text-zinc-400 hover:text-zinc-600 hover:border-zinc-300"
              )}
            >
              {tab.label}
              <span className={cn(
                "text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center tabular-nums",
                isActive ? tab.badgeBg : "bg-zinc-100 text-zinc-400"
              )}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* -- Search & Filter Utility Bar -- */}
      <div className="bg-white border border-t-0 border-zinc-200 rounded-b-xl px-4 py-3 flex flex-wrap items-center gap-3 mb-4">
        {/* Global Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={reviewFilters.searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search campaign, brand, or manager·"
            className="w-full pl-9 pr-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-400 focus:bg-white transition-all"
          />
        </div>

        {/* Department Filter (only for IN_REVIEW) */}
        {isAdmin && reviewFilters.subTab === 'IN_REVIEW' && (
          <div className="flex items-center gap-1.5">
            <Filter size={13} className="text-zinc-400" />
            <CustomSelect
              value={reviewFilters.departmentFilter}
              onChange={v => setDepartmentFilter(v)}
              placeholder="All Departments"
              options={[{ value: '', label: 'All Departments' }, ...dynamicStages.map(stage => ({ value: stage, label: stage }))]}
            />
          </div>
        )}

        {/* Date Range Filters */}
        <div className="flex items-center gap-2">
          <Calendar size={13} className="text-zinc-400 shrink-0" />
          <DatePicker
            value={reviewFilters.dateFrom}
            onChange={v => setDateFrom(v)}
            placeholder="From date"
          />
          <span className="text-xs text-zinc-400">to</span>
          <DatePicker
            value={reviewFilters.dateTo}
            onChange={v => setDateTo(v)}
            placeholder="To date"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-400 tabular-nums">{filteredSubmissions.length} shown</span>
          {hasActiveReviewFilters && (
            <button
              onClick={() => {
                setSearchQuery('');
                setDepartmentFilter('');
                setDateFrom('');
                setDateTo('');
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-zinc-200 rounded-lg text-[11px] font-semibold text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 transition-all"
            >
              <RotateCcw size={11} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* -- Submission List · Data-Dense Cards -- */}
      {filteredSubmissions.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
          <ClipboardList size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm font-semibold text-zinc-400">
            {reviewFilters.searchQuery || reviewFilters.departmentFilter || reviewFilters.dateFrom || reviewFilters.dateTo
              ? 'No submissions match your filters'
              : reviewFilters.subTab === 'IN_REVIEW' ? 'No submissions in review'
              : reviewFilters.subTab === 'COMPLETED' ? 'No completed submissions yet'
              : reviewFilters.subTab === 'REJECTED' ? 'No rejected submissions'
              : 'No submissions yet'
            }
          </p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Campaign / Brand</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Manager</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Workflow</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Item Balance</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Status</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filteredSubmissions.map(sub => {
                const brand = brandLookup.get(sub.brandId);
                const effectiveStage = sub.currentStage || dynamicStages[0] || 'DONE';
                const stageMeta = STAGE_META[effectiveStage] ?? STAGE_META['DONE'];
                const statusMeta = STATUS_COLOR[sub.status];
                const reviewable = canReview(sub);
                const camp = campaigns.find(ca => ca.id === sub.campaignId);
                const target = camp?.requiredItemCount ?? 0;
                const approvedCount = getApprovedItems(sub).length;
                const timeInStage = computeTimeInStage(sub);
                const timeLabel = formatDuration(timeInStage);
                const isLongWait = timeInStage > 2 * 24 * 60 * 60 * 1000; // > 2 days
                return (
                  <tr
                    key={sub.id}
                    className={cn(
                      "hover:bg-zinc-50 transition-colors cursor-pointer",
                      reviewable && "bg-indigo-50/30"
                    )}
                    onClick={() => setSelectedSubmission(selectedSubmission?.id === sub.id ? null : sub)}
                  >
                    {/* Campaign / Brand */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl border border-zinc-200 bg-zinc-50 overflow-hidden shrink-0 flex items-center justify-center">
                          {brand?.logoUrl ? (
                            <img src={getMediaUrl(brand.logoUrl)} alt={sub.brandName} className="w-full h-full object-contain" />
                          ) : (
                            <Building2 size={16} className="text-zinc-400" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-zinc-900 truncate max-w-[240px]">{sub.campaignName}</p>
                          <span className="flex items-center gap-1 text-xs text-zinc-400 mt-0.5 truncate">
                            <Building2 size={11} /> {sub.brandName}
                          </span>
                        </div>
                      </div>
                    </td>
                    {/* Manager */}
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5 text-sm text-zinc-600">
                        <Users size={12} className="text-zinc-400" /> {sub.submitterName}
                      </span>
                      <span className="text-[10px] text-zinc-400 mt-0.5 block tabular-nums">{new Date(sub.createdAt).toLocaleDateString()}</span>
                    </td>
                    {/* Workflow */}
                    <td className="px-4 py-3">
                      <div className="space-y-1.5 min-w-[150px]">
                        <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border", stageMeta.bg, stageMeta.color, stageMeta.border)}>
                          Stage: {sub.currentStage || dynamicStages[0] || '—'}
                        </span>
                        <div className={cn(
                          "inline-flex items-center gap-1 text-xs font-semibold tabular-nums",
                          isLongWait ? "text-red-600" : "text-zinc-500"
                        )}>
                          <Clock size={11} className={isLongWait ? "text-red-500" : "text-zinc-400"} />
                          Sitting for {timeLabel}
                        </div>
                        {isLongWait && (
                          <span className="block text-[9px] text-red-500 font-bold">OVERDUE</span>
                        )}
                      </div>
                    </td>
                    {/* Item Balance */}
                    <td className="px-4 py-3">
                      {target > 0 ? (() => {
                        const tc = compareTarget(approvedCount, target);
                        const colors = TARGET_STATUS_COLORS[tc.status];
                        return (
                          <div className="space-y-1">
                            <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border", colors.bg, colors.text, colors.border)}>
                              {tc.shortLabel}
                            </span>
                            <span className="block text-[10px] text-zinc-400">approved / target</span>
                          </div>
                        );
                      })() : (
                        <div>
                          <span className="text-sm text-zinc-600 tabular-nums">{approvedCount}</span>
                          <span className="block text-[10px] text-zinc-400">approved items</span>
                        </div>
                      )}
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border", statusMeta.pill)}>
                        <span className={cn("w-1.5 h-1.5 rounded-full", statusMeta.dot)} />
                        {sub.status}
                      </span>
                    </td>
                    {/* Quick Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedSubmission(sub); setShowAuditPopup(true); }}
                          className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-lg text-[10px] font-semibold text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 transition-all"
                          title="Audit History"
                        >
                          <History size={11} /> Audit History
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedSubmission(selectedSubmission?.id === sub.id ? null : sub); }}
                          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-all"
                          title="Open review"
                        >
                          <ChevronRight size={14} className={cn("transition-transform", selectedSubmission?.id === sub.id && "rotate-90")} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ----------------------------------------------------------------------
       *  FOCUS MODE · Full-Window Review Workspace
       * ---------------------------------------------------------------------- */}
      {selectedSubmission && (() => {
        const sub = selectedSubmission;
        const stageMeta = STAGE_META[sub.currentStage] ?? STAGE_META['DONE'];
        const statusMeta = STATUS_COLOR[sub.status];
        const reviewable = canReview(sub);
        const sortedHistory = [...sub.reviewHistory].sort((a, b) => b.timestamp - a.timestamp);
        const internalMsgs = (sub.discussion ?? []).filter(m => !m.public);

        /** Parse a change detail string into a diff badge */
        const parseChangeBadge = (detail: string): { label: string; from?: string; to?: string } | null => {
          const priceMatch = detail.match(/Updated Price from ([\d.]+) to ([\d.]+)/);
          if (priceMatch) return { label: 'Price Change', from: priceMatch[1], to: priceMatch[2] };
          const renameMatch = detail.match(/Renamed from "(.+?)" to "(.+?)"/);
          if (renameMatch) return { label: 'Name Change', from: renameMatch[1], to: renameMatch[2] };
          if (detail.includes('photo') || detail.includes('Photo')) return { label: 'Photo Change' };
          if (detail.includes('description') || detail.includes('Description')) return { label: 'Description Change' };
          return null;
        };

        /** Render @mentions highlighted in message text */
        const renderMessageText = (text: string, isCurrentUser: boolean) => {
          const parts = text.split(/(@[A-Za-z0-9&\s]+?)(?=\s|$|@)/g);
          return parts.map((part, pi) => {
            if (part.startsWith('@') && dynamicStages.some(s => s.toUpperCase() === part.slice(1).trim().toUpperCase())) {
              return <span key={pi} className={cn("font-bold px-0.5 rounded", isCurrentUser ? "text-blue-300 bg-blue-900/40" : "text-blue-600 bg-blue-100")}>{part}</span>;
            }
            return <span key={pi}>{part}</span>;
          });
        };

        return (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto" onClick={() => setSelectedSubmission(null)}>
            <div className="bg-zinc-50 w-full max-w-7xl mx-4 my-6 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-48px)]" onClick={e => e.stopPropagation()}>

              {/* -- Focus Mode Header -- */}
              <div className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center gap-4 shrink-0">
                <div className="w-10 h-10 bg-zinc-900 rounded-xl flex items-center justify-center text-white shrink-0">
                  <Package size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-zinc-900 truncate">{sub.campaignName}</h2>
                    <span className={cn("px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border", statusMeta.pill)}>
                      <span className={cn("inline-block w-1.5 h-1.5 rounded-full mr-1.5", statusMeta.dot)} />
                      {sub.status}
                    </span>
                    {reviewable && (
                      <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-full text-[9px] font-bold uppercase tracking-widest">Action Required</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-zinc-400">
                    <span className="flex items-center gap-1"><Building2 size={11} /> {sub.brandName}</span>
                    <span className="flex items-center gap-1"><Users size={11} /> {sub.submitterName}</span>
                    <span className="flex items-center gap-1"><Clock size={11} /> {new Date(sub.createdAt).toLocaleDateString()}</span>
                    {(() => {
                      const camp = campaigns.find(ca => ca.id === sub.campaignId);
                      const target = camp?.requiredItemCount ?? 0;
                      const approvedCount = getApprovedItems(sub).length;
                      if (target > 0) {
                        const tc = compareTarget(approvedCount, target);
                        const colors = TARGET_STATUS_COLORS[tc.status];
                        return (
                          <span className={cn("flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border", colors.bg, colors.text, colors.border)}>
                            <Hash size={9} /> Items: {tc.label}
                          </span>
                        );
                      }
                      return <span>{approvedCount} item(s)</span>;
                    })()}
                  </div>
                </div>
                <div className="hidden lg:block">
                  <ReviewTimeline compact currentStage={sub.currentStage} status={sub.status} reviewHistory={sub.reviewHistory} stages={dynamicStages} />
                </div>
                <button
                  onClick={() => navigate(`/apps/support-desk/ticket-center?prefill_type=aggregator_submission&prefill_id=${sub.id}&prefill_label=${encodeURIComponent(`${sub.campaignName} · ${sub.brandName}`)}&prefill_cat=OPERATIONAL`)}
                  className="p-2 hover:bg-amber-100 hover:text-amber-600 rounded-xl transition-all text-zinc-400"
                  title="Report Issue"
                >
                  <AlertCircle size={20} />
                </button>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="p-2 hover:bg-zinc-100 rounded-xl transition-all ml-2"
                >
                  <X size={20} className="text-zinc-400" />
                </button>
              </div>

              {/* -- 60/40 Side-by-Side Body -- */}
              <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[3fr_2fr]">

                {/* ---- LEFT PANEL (60%): Submitted Items ---- */}
                <div className="overflow-y-auto border-r border-zinc-200 p-5 space-y-3">
                  {/* Finalized Record Banner */}
                  {(sub.status === SubmissionStatus.COMPLETED || sub.status === SubmissionStatus.DEPLOYMENT_READY || sub.currentStage === 'DONE') && (
                    <div className="flex items-center gap-2.5 px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl">
                      <ShieldCheck size={16} className="text-teal-600 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-teal-700">Finalized Record</p>
                        <p className="text-[10px] text-teal-600">This submission is completed and cannot be modified.</p>
                      </div>
                    </div>
                  )}

                  {/* Campaign Details Card */}
                  {(() => {
                    const camp = campaigns.find(ca => ca.id === sub.campaignId);
                    if (!camp) return null;
                    return (
                      <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div><p className="text-[9px] font-bold text-zinc-400 uppercase">Campaign</p><p className="text-xs font-bold text-zinc-900 mt-0.5">{camp.name}</p></div>
                          <div><p className="text-[9px] font-bold text-zinc-400 uppercase">Discount</p><p className="text-xs font-bold text-zinc-900 mt-0.5">{camp.discountValue}{camp.discountType === 'PERCENTAGE' ? '%' : ' QAR'}</p></div>
                          <div><p className="text-[9px] font-bold text-zinc-400 uppercase">Period</p><p className="text-xs font-bold text-zinc-900 mt-0.5">{fmtDate(camp.validFrom)} · {fmtDate(camp.validTo)}</p></div>
                          <div><p className="text-[9px] font-bold text-zinc-400 uppercase">Funding</p>
                            <p className="text-xs font-bold text-zinc-900 mt-0.5">{camp.fundingType === 'CO_FUNDED' ? `Co-Funded (${camp.sterlingSharePct ?? 0}% / ${camp.partnerSharePct ?? 0}%)` : camp.fundingType === 'FULLY_FUNDED' ? 'Fully Funded' : 'No Funding'}</p>
                          </div>
                        </div>
                        {camp.aggregators.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[9px] font-bold text-zinc-400 uppercase">Aggregators</span>
                            {camp.aggregators.map(a => <span key={a} className="px-2 py-0.5 bg-white/60 rounded-full text-[10px] font-bold text-violet-700 border border-violet-200">{a}</span>)}
                          </div>
                        )}
                        {camp.importantRequirements && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertTriangle size={11} className="text-amber-500 shrink-0" />
                            <p className="text-[10px] text-amber-700 leading-snug">{camp.importantRequirements}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Submitted Items ({sub.items.filter(it => it.itemStatus !== 'DISCARDED').length}{sub.items.some(it => it.itemStatus === 'DISCARDED') ? ` · ${sub.items.filter(it => it.itemStatus === 'DISCARDED').length} discarded` : ''})</p>
                  <div className="space-y-3">
                    {sub.items.map((item, i) => {
                      const isDiscarded = item.itemStatus === 'DISCARDED';
                      const isUnlocked = item.locked === false;
                      const isUnlocking = unlockingIds.has(item.id);
                      return (
                        <div key={item.id} data-item-id={item.id} className={cn(
                          "bg-white rounded-xl overflow-hidden transition-all duration-500",
                          isDiscarded ? "opacity-50 border border-zinc-200 bg-zinc-50" : "",
                          !isDiscarded && highlightedItemId === item.id ? "ring-2 ring-amber-400 ring-offset-2" : "",
                          !isDiscarded && isUnlocked ? "border-2 border-amber-400" : !isDiscarded && item.resubmittedAt ? "border-2 border-indigo-400" : !isDiscarded ? "border border-zinc-200" : ""
                        )}>
                          <div className="px-4 py-3 space-y-3">
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] font-bold text-zinc-400 w-5">{i + 1}</span>
                              {isDiscarded
                                ? <Ban size={14} className="text-red-400 shrink-0" />
                                : isUnlocked
                                ? <Unlock size={14} className="text-amber-500 shrink-0" />
                                : item.resubmittedAt
                                ? <RefreshCw size={14} className="text-indigo-500 shrink-0" />
                                : <Lock size={14} className="text-zinc-300 shrink-0" />
                              }
                              <p className={cn("text-sm font-bold flex-1 min-w-0 truncate", isDiscarded ? "text-zinc-400 line-through" : "text-zinc-900")}>{item.name}{item.nameAR ? <span className="text-xs text-zinc-400 font-arabic ml-2" dir="rtl">{item.nameAR}</span> : null}</p>
                              {isAdmin && !isDiscarded && (
                                <button
                                  onClick={() => {
                                    setChatItemRef({ itemId: item.id, itemName: item.name });
                                    setShowItemPicker(false);
                                    chatInputRef.current?.focus();
                                  }}
                                  className="p-1 rounded text-zinc-300 hover:text-teal-600 hover:bg-teal-50 transition-all shrink-0"
                                  title={`Reference "${item.name}" in chat`}
                                >
                                  <Link2 size={12} />
                                </button>
                              )}
                              <span className="text-sm font-mono text-zinc-600 shrink-0">QAR {item.price.toFixed(2)}</span>
                              {/* Expand/collapse toggle for menu-linked items */}
                              {item.menuLinked && (
                                <button
                                  onClick={e => { e.stopPropagation(); setReviewExpandedItemId(reviewExpandedItemId === item.id ? null : item.id); }}
                                  className={cn('p-1.5 rounded-lg transition-all shrink-0', reviewExpandedItemId === item.id ? 'bg-indigo-100 text-indigo-600' : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200')}
                                  title="Show options & cost details"
                                >
                                  <ChevronDown size={13} className={cn('transition-transform', reviewExpandedItemId === item.id && 'rotate-180')} />
                                </button>
                              )}
                              {item.menuLinked
                                ? <span className="px-2 py-0.5 text-[9px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full shrink-0 flex items-center gap-1"><BookOpen size={9} /> Menu{item.menuProfileName ? `: ${item.menuProfileName}` : ''}</span>
                                : <span className="px-2 py-0.5 text-[9px] font-bold text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full shrink-0">Manual</span>
                              }
                              {isDiscarded && (
                                <span className="px-2 py-0.5 text-[9px] font-bold text-red-700 bg-red-100 border border-red-200 rounded-full uppercase shrink-0">Discarded</span>
                              )}
                              {!isDiscarded && isUnlocked && (
                                <span className="px-2 py-0.5 text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-full uppercase shrink-0">Unlocked</span>
                              )}
                              {!isDiscarded && !isUnlocked && item.resubmittedAt && (
                                <span className="px-2 py-0.5 text-[9px] font-bold text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-full uppercase shrink-0 flex items-center gap-1">
                                  <RefreshCw size={9} /> New Version
                                </span>
                              )}
                            </div>
                            <div>
                              {item.description
                                ? <p className="text-xs text-zinc-600 leading-relaxed">{item.description}</p>
                                : <p className="text-xs text-zinc-400 italic">No description provided</p>
                              }
                              {item.descriptionAR && <p className="text-xs text-zinc-400 leading-relaxed mt-0.5" dir="rtl">{item.descriptionAR}</p>}
                            </div>
                            <div className="w-full h-40 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center overflow-hidden">
                              {item.imageUrl ? (
                                <img src={getMediaUrl(item.imageUrl)} alt={item.name} className="w-full h-full object-contain" />
                              ) : (
                                <div className="text-center">
                                  <ImageIcon size={24} className="mx-auto text-zinc-300 mb-1" />
                                  <p className="text-[10px] text-zinc-400 italic">No image provided</p>
                                </div>
                              )}
                            </div>
                            {item.adminNote && !isDiscarded && (
                              <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <MessageSquare size={12} className="text-amber-600 mt-0.5 shrink-0" />
                                  <div className="text-xs">
                                    <p className="font-bold text-amber-700">Feedback{item.unlockedBy ? ` by ${item.unlockedBy}` : ''}</p>
                                    <p className="text-amber-600 mt-0.5">{item.adminNote}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                            {isDiscarded && (
                              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <Ban size={12} className="text-red-500 mt-0.5 shrink-0" />
                                  <div className="text-xs flex-1">
                                    <p className="font-bold text-red-700">Discarded{item.discardedBy ? ` by ${item.discardedBy}` : ''}</p>
                                    {item.discardReason && <p className="text-red-600 mt-0.5">{item.discardReason}</p>}
                                    {item.discardedAt && <p className="text-red-400 mt-0.5 text-[10px]">{new Date(item.discardedAt).toLocaleString()}</p>}
                                  </div>
                                  {reviewable && (
                                    <button
                                      onClick={() => handleRestoreItem(sub, item.id)}
                                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-all shrink-0"
                                    >
                                      <RotateCcw size={10} /> Restore
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                            {reviewable && !isDiscarded && !isUnlocked && !isUnlocking && sub.status !== SubmissionStatus.REJECTED && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setUnlockingIds(prev => new Set(prev).add(item.id))}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-all"
                                >
                                  <MessageSquare size={11} /> Feedback / Unlock for Fix
                                </button>
                                <button
                                  onClick={() => setDiscardModal({ sub, itemId: item.id, itemName: item.name })}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
                                  title="Discard this item"
                                >
                                  <Trash2 size={11} /> Discard
                                </button>
                              </div>
                            )}

                            {/* ═══ Slide-down: Imported Options + Cost Card (Review) ═══ */}
                            {reviewExpandedItemId === item.id && item.menuLinked && (
                              <div className="border-t border-zinc-100 bg-zinc-50/50 px-4 py-4 space-y-4 animate-in slide-in-from-top-2 rounded-b-xl">
                                {/* ── Menu Source Tag ── */}
                                <div className="flex items-center gap-2">
                                  <span className="flex items-center gap-1.5 text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-lg">
                                    <Link2 size={10} /> From: {item.menuProfileName || 'Menu Profile'}
                                  </span>
                                  {item.originalMenuPrice != null && item.originalMenuPrice !== item.price && (
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
                                      <AlertTriangle size={10} /> Price changed from QAR {item.originalMenuPrice.toFixed(2)}
                                    </span>
                                  )}
                                </div>

                                {/* ── Imported Option Groups ── */}
                                {item.importedOptions && item.importedOptions.length > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
                                      <Layers size={12} className="text-indigo-500" />
                                      Option Groups
                                      <span className="text-[9px] font-normal text-zinc-300 ml-1">({item.importedOptions.length})</span>
                                    </p>
                                    <div className="space-y-2">
                                      {item.importedOptions.map((og: any) => (
                                        <div key={og.id} className="px-3 py-2.5 bg-white border border-zinc-200 rounded-xl">
                                          <div className="flex items-center gap-2 mb-1.5">
                                            <span className="text-xs font-semibold text-zinc-800">{og.nameEN}</span>
                                            {og.nameAR && <span className="text-[10px] text-zinc-400 font-arabic">{og.nameAR}</span>}
                                            <span className="text-[8px] px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded font-bold">
                                              {og.minSelection ?? 0}–{og.maxSelection ?? '∞'}
                                            </span>
                                            {og.minSelection != null && og.minSelection > 0 && (
                                              <span className="text-[8px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-bold border border-red-100">Required</span>
                                            )}
                                          </div>
                                          <div className="flex flex-wrap gap-1.5">
                                            {(og.options || []).map((opt: any) => {
                                              const optCost = item.menuItemId ? reviewItemCostMap[item.menuItemId]?.options?.[opt.id] : undefined;
                                              return (
                                                <span key={opt.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-[10px] text-zinc-700">
                                                  {opt.nameEN}
                                                  {opt.extraPrice > 0 && <span className="font-mono text-emerald-600">+{opt.extraPrice.toFixed(2)}</span>}
                                                  {optCost != null && optCost > 0 && (
                                                    <span className="font-mono text-amber-600" title="Ingredient cost">
                                                      (cost {optCost.toFixed(2)})
                                                    </span>
                                                  )}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* ── Cost Breakdown Card ── */}
                                {item.menuItemId && reviewItemCostMap[item.menuItemId] && (() => {
                                  const ce = reviewItemCostMap[item.menuItemId];
                                  const sellPrice = item.price;
                                  const fc = sellPrice > 0 ? (ce.baseCost / sellPrice) * 100 : 0;
                                  const profit = sellPrice - ce.baseCost;
                                  const fcColor = fc <= 30 ? 'text-emerald-600' : fc <= 40 ? 'text-amber-600' : 'text-red-600';
                                  const fcBg = fc <= 30 ? 'bg-emerald-50 border-emerald-200' : fc <= 40 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
                                  return (
                                    <div className="space-y-2">
                                      <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
                                        <DollarSign size={12} className="text-amber-500" />
                                        Cost Analysis
                                      </p>
                                      <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
                                        <div className="grid grid-cols-3 gap-3">
                                          <div className="text-center">
                                            <p className="text-[9px] font-bold text-zinc-400 uppercase">Sell Price</p>
                                            <p className="text-base font-bold text-zinc-800 mt-0.5">QAR {sellPrice.toFixed(2)}</p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-[9px] font-bold text-zinc-400 uppercase">Base Cost</p>
                                            <p className="text-base font-bold text-amber-600 mt-0.5">QAR {ce.baseCost.toFixed(2)}</p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-[9px] font-bold text-zinc-400 uppercase">Profit</p>
                                            <p className={cn('text-base font-bold mt-0.5', profit >= 0 ? 'text-emerald-600' : 'text-red-600')}>QAR {profit.toFixed(2)}</p>
                                          </div>
                                        </div>
                                        <div className={cn('flex items-center justify-center gap-2 px-3 py-2 rounded-lg border', fcBg)}>
                                          <span className="text-[10px] font-bold text-zinc-500">Food Cost %</span>
                                          <span className={cn('text-lg font-black', fcColor)}>{fc.toFixed(1)}%</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* No options + no cost = minimal info */}
                                {(!item.importedOptions || item.importedOptions.length === 0) && !(item.menuItemId && reviewItemCostMap[item.menuItemId]) && (
                                  <p className="text-xs text-zinc-400 italic">No option groups or cost data available for this item.</p>
                                )}
                              </div>
                            )}
                          </div>
                          {isUnlocking && (
                            <div className="border-t border-amber-200 bg-amber-50/50 px-4 py-3 space-y-2">
                              <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Item Feedback · Unlock for Fix <span className="text-zinc-400">(visible to manager)</span></p>
                              <textarea
                                value={itemNotes[item.id] ?? ''}
                                onChange={e => setItemNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                                placeholder="Describe what needs to be fixed (e.g., wrong price, incorrect name)·"
                                rows={2}
                                className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 resize-none"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setUnlockingIds(prev => { const next = new Set(prev); next.delete(item.id); return next; })}
                                  className="px-3 py-1.5 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-600 hover:bg-zinc-50 transition-all"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleUnlockItem(sub, item.id)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 transition-all"
                                >
                                  <Unlock size={12} /> Unlock for Fix
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Rejection banner */}
                  {sub.status === SubmissionStatus.REJECTED && (
                    <div className="bg-zinc-900 text-white rounded-xl px-4 py-3 flex items-start gap-3">
                      <Ban size={18} className="shrink-0 mt-0.5" />
                      <div className="text-xs space-y-1">
                        <p className="font-bold text-sm">Submission Rejected</p>
                        {sub.rejectionReason && <p className="text-zinc-300">{sub.rejectionReason}</p>}
                        {sub.rejectedBy && sub.rejectedAt && (
                          <p className="text-zinc-400">by {sub.rejectedBy} · {new Date(sub.rejectedAt).toLocaleString()}</p>
                        )}
                        <p className="text-zinc-500 mt-2 italic">This submission is permanently closed. The store manager must create a new submission.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ---- RIGHT PANEL (40%): History + Discussion + Actions ---- */}
                <div className="overflow-y-auto p-5 flex flex-col gap-4">

                  {/* -- Public Review History · Scrollable Container -- */}
                  <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex flex-col shrink-0">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100">
                      <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1.5">
                        <Eye size={10} /> Review History <span className="text-zinc-300">(Public)</span>
                      </p>
                      {sub.reviewHistory.length > 0 && (
                        <button
                          onClick={() => setShowAuditPopup(true)}
                          className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 hover:text-indigo-700 transition-colors"
                        >
                          <Layers size={10} /> View All
                        </button>
                      )}
                    </div>
                    <div className="max-h-[80px] overflow-y-auto px-4 py-3">
                      {sortedHistory.length === 0 ? (
                        <div className="text-center py-6">
                          <ClipboardList size={20} className="mx-auto text-zinc-200 mb-2" />
                          <p className="text-xs text-zinc-400 italic">No review actions yet.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {sortedHistory.map((action, i) => (
                            <div key={i} className={cn(
                              "flex items-start gap-2.5 px-3 py-2 rounded-lg border text-xs",
                              action.action === 'APPROVE' ? "bg-emerald-50 border-emerald-200"
                                : action.action === 'REJECT' ? "bg-zinc-100 border-zinc-300"
                                : action.action === 'RESUBMIT' ? "bg-indigo-50 border-indigo-200"
                                : "bg-amber-50 border-amber-200"
                            )}>
                              {action.action === 'APPROVE'
                                ? <CheckCircle2 size={13} className="text-emerald-600 mt-0.5 shrink-0" />
                                : action.action === 'REJECT'
                                ? <Ban size={13} className="text-zinc-700 mt-0.5 shrink-0" />
                                : action.action === 'RESUBMIT'
                                ? <RefreshCw size={13} className="text-indigo-600 mt-0.5 shrink-0" />
                                : <Unlock size={13} className="text-amber-600 mt-0.5 shrink-0" />
                              }
                              <div className="flex-1 min-w-0">
                                <p className={cn("font-bold text-[11px]",
                                  action.action === 'APPROVE' ? "text-emerald-700"
                                    : action.action === 'REJECT' ? "text-zinc-800"
                                    : action.action === 'RESUBMIT' ? "text-indigo-700"
                                    : "text-amber-700"
                                )}>
                                  [{action.departmentName}] {action.action === 'APPROVE' ? 'Approved' : action.action === 'REJECT' ? 'Rejected' : action.action === 'RESUBMIT' ? 'Resubmitted' : 'Unlocked'} · {action.reviewerName}
                                </p>
                                {action.note && <p className="text-zinc-500 mt-0.5 text-[10px]">{action.note}</p>}
                                {/* Change detection diff badges */}
                                {action.changeDetails && action.changeDetails.length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {action.changeDetails.map((cd, ci) => {
                                      const badge = parseChangeBadge(cd);
                                      return badge ? (
                                        <span key={ci} className="group relative inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[9px] font-bold border border-indigo-200 cursor-default">
                                          {badge.label}
                                          {badge.from && badge.to && (
                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:flex items-center gap-1 px-2 py-1 bg-zinc-900 text-white rounded-lg text-[9px] font-mono whitespace-nowrap z-10 shadow-lg">
                                              <span className="line-through opacity-60">{badge.from}</span>
                                              <ArrowRight size={8} />
                                              <span className="font-bold">{badge.to}</span>
                                            </span>
                                          )}
                                        </span>
                                      ) : (
                                        <span key={ci} className="flex items-center gap-1 text-[10px] text-indigo-600">
                                          <ChevronRight size={9} className="shrink-0" /> {cd}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                {action.unlockedItemIds && action.unlockedItemIds.length > 0 && (
                                  <p className="text-amber-600 mt-0.5 flex items-center gap-1 text-[10px]">
                                    <Unlock size={9} /> {action.unlockedItemIds.length} item(s) unlocked
                                  </p>
                                )}
                                <p className="text-zinc-400 mt-0.5 text-[9px]">{new Date(action.timestamp).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* -- Internal Discussion (Admin-Only) · Polished -- */}
                  {isAdmin && (
                    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex-1 flex flex-col min-h-[400px]">
                      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-100">
                        <Lock size={10} className="text-zinc-400" />
                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Internal Discussion</p>
                        <span className="text-[9px] font-bold text-red-400 uppercase">(Private)</span>
                        {internalMsgs.length > 0 && (
                          <span className="ml-auto px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded text-[9px] font-bold">{internalMsgs.length}</span>
                        )}
                      </div>

                      {/* Messages area · flex-grow to fill available space */}
                      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                        {internalMsgs.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full py-10">
                            <div className="w-14 h-14 rounded-2xl bg-zinc-100 flex items-center justify-center mb-3">
                              <MessageSquare size={24} className="text-zinc-300" />
                            </div>
                            <p className="text-sm font-semibold text-zinc-400">No messages yet</p>
                            <p className="text-xs text-zinc-300 mt-1 text-center max-w-[220px]">Start a private discussion with your team. Messages here are only visible to admins.</p>
                          </div>
                        ) : (
                          internalMsgs.map(msg => {
                            const isCurrentUser = msg.userId === user?.id;
                            return (
                              <div key={msg.id} className={cn("flex", isCurrentUser ? "justify-end" : "justify-start")}>
                                <div className={cn(
                                  "max-w-[80%] rounded-xl px-2.5 py-1.5 text-[11px]",
                                  isCurrentUser ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-800"
                                )}>
                                  <div className="flex items-center gap-1.5 mb-0.5">
                                    <span className="font-bold text-[10px]">{msg.userName}</span>
                                    {msg.departmentName && (
                                      <span className={cn(
                                        "px-1 py-0.5 rounded text-[7px] font-bold uppercase",
                                        isCurrentUser ? "bg-zinc-700 text-zinc-300" : "bg-zinc-200 text-zinc-500"
                                      )}>
                                        {msg.departmentName}
                                      </span>
                                    )}
                                  </div>
                                  {msg.itemRef && (
                                    <div
                                      onClick={() => {
                                        const el = document.querySelector(`[data-item-id="${msg.itemRef!.itemId}"]`);
                                        if (el) {
                                          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                          setHighlightedItemId(msg.itemRef!.itemId);
                                          setTimeout(() => setHighlightedItemId(null), 2000);
                                        }
                                      }}
                                      className={cn(
                                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold mb-0.5 cursor-pointer hover:opacity-80 transition-opacity",
                                        isCurrentUser ? "bg-amber-900/40 text-amber-300" : "bg-amber-100 text-amber-700 border border-amber-200"
                                      )}
                                    >
                                      <Package size={8} /> RE: {msg.itemRef.itemName}
                                    </div>
                                  )}
                                  <p className={cn("leading-relaxed", isCurrentUser ? "text-zinc-200" : "text-zinc-600")}>
                                    {renderMessageText(msg.message, isCurrentUser)}
                                  </p>
                                  {msg.attachmentName && (
                                    <div className={cn("mt-1 rounded-lg overflow-hidden", isCurrentUser ? "bg-zinc-700" : "bg-zinc-200")}>
                                      {msg.attachmentType === 'image' && msg.attachmentData ? (
                                        <img src={getMediaUrl(msg.attachmentData)} alt={msg.attachmentName} className="w-full max-h-24 object-cover rounded-t-lg" />
                                      ) : null}
                                      <a
                                        href={msg.attachmentData || '#'}
                                        download={msg.attachmentName}
                                        onClick={e => { if (!msg.attachmentData) e.preventDefault(); }}
                                        className="flex items-center gap-1.5 px-2 py-1 hover:opacity-80 transition-opacity"
                                      >
                                        {msg.attachmentType === 'image' ? <ImageIcon size={10} /> : <FileText size={10} />}
                                        <span className="text-[9px] font-medium truncate">{msg.attachmentName}</span>
                                        {msg.attachmentSize && (
                                          <span className={cn("text-[8px] shrink-0", isCurrentUser ? "text-zinc-400" : "text-zinc-500")}>
                                            ({(msg.attachmentSize / 1024).toFixed(0)}KB)
                                          </span>
                                        )}
                                      </a>
                                    </div>
                                  )}
                                  <p className="text-[8px] text-zinc-400 mt-0.5">{new Date(msg.timestamp).toLocaleString()}</p>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Chat input */}
                      {sub.status !== SubmissionStatus.REJECTED && (
                        <div className="border-t border-zinc-200 shrink-0">
                          {chatItemRef && (
                            <div className="px-3 pt-2 pb-0.5">
                              <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-[10px]">
                                <Package size={10} className="text-amber-600" />
                                <span className="font-semibold text-amber-700">RE: {chatItemRef.itemName}</span>
                                <button onClick={() => setChatItemRef(null)} className="p-0.5 text-amber-400 hover:text-red-500"><X size={10} /></button>
                              </div>
                            </div>
                          )}
                          {stagedFile && (
                            <div className="px-3 pt-2 pb-0.5">
                              <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded text-[10px]">
                                {stagedFile.type === 'image' ? <ImageIcon size={10} className="text-indigo-500" /> : <FileText size={10} className="text-indigo-500" />}
                                <span className="font-medium text-indigo-700 truncate max-w-[120px]">{stagedFile.name}</span>
                                <button onClick={() => setStagedFile(null)} className="p-0.5 text-indigo-400 hover:text-red-500"><X size={10} /></button>
                              </div>
                            </div>
                          )}
                          {mentionDropdown && (
                            <div className="px-3 pt-1 pb-0.5">
                              <div className="bg-white border border-zinc-300 rounded-lg shadow-lg max-h-28 overflow-y-auto">
                                {dynamicStages
                                  .filter(s => s.toUpperCase().includes(mentionFilter.toUpperCase()))
                                  .map(stage => (
                                    <button
                                      key={stage}
                                      onClick={() => {
                                        const atIdx = chatInput.lastIndexOf('@');
                                        const before = atIdx >= 0 ? chatInput.slice(0, atIdx) : chatInput;
                                        setChatInput(`${before}@${stage} `);
                                        setMentionDropdown(false);
                                        setMentionFilter('');
                                        chatInputRef.current?.focus();
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center gap-2"
                                    >
                                      <span className="w-2 h-2 rounded-full bg-indigo-400" />
                                      {stage}
                                    </button>
                                  ))}
                                {dynamicStages.filter(s => s.toUpperCase().includes(mentionFilter.toUpperCase())).length === 0 && (
                                  <div className="px-3 py-1.5 text-[10px] text-zinc-400 italic">No matching departments</div>
                                )}
                              </div>
                            </div>
                          )}
                          <div className="px-3 py-2 pb-4 flex items-center gap-1.5">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                              className="hidden"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (file.size > 5 * 1024 * 1024) {
                                  toast.error('File Too Large', 'Max file size is 5MB.');
                                  e.target.value = '';
                                  return;
                                }
                                const attType: 'image' | 'document' = file.type.startsWith('image/') ? 'image' : 'document';
                                uploadFile(file, { moduleId: 'aggregator' }).then(url => {
                                  setStagedFile({ name: file.name, type: attType, data: url, size: file.size });
                                }).catch(() => toast.error('Upload Failed', 'Could not upload file.'));
                                e.target.value = '';
                              }}
                            />
                            <div className="relative">
                              <button
                                onClick={() => setShowItemPicker(p => !p)}
                                className={cn("p-1 rounded transition-all", chatItemRef ? "text-amber-600 bg-amber-50" : "text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100")}
                                title="Reference a specific item"
                              >
                                <Package size={13} />
                              </button>
                              {showItemPicker && sub.items.length > 0 && (
                                <div className="absolute bottom-full left-0 mb-1 bg-white border border-zinc-200 rounded-lg shadow-xl min-w-[180px] max-h-36 overflow-y-auto z-20">
                                  {sub.items.map(item => (
                                    <button
                                      key={item.id}
                                      onClick={() => {
                                        setChatItemRef({ itemId: item.id, itemName: item.name });
                                        setShowItemPicker(false);
                                        chatInputRef.current?.focus();
                                      }}
                                      className={cn(
                                        "w-full text-left px-2.5 py-1.5 text-[10px] font-medium hover:bg-amber-50 transition-colors flex items-center gap-1.5",
                                        chatItemRef?.itemId === item.id ? "bg-amber-50 text-amber-700" : "text-zinc-700"
                                      )}
                                    >
                                      {item.imageUrl ? (
                                        <img src={getMediaUrl(item.imageUrl)} alt="" className="w-4 h-4 rounded object-cover shrink-0" />
                                      ) : (
                                        <Package size={10} className="text-zinc-300 shrink-0" />
                                      )}
                                      <span className="truncate">{item.name}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className={cn("p-1 rounded transition-all", stagedFile ? "text-indigo-600 bg-indigo-50" : "text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100")}
                              title="Attach a file"
                            >
                              <Paperclip size={13} />
                            </button>
                            <input
                              ref={chatInputRef}
                              type="text"
                              value={chatInput}
                              onChange={e => {
                                const val = e.target.value;
                                setChatInput(val);
                                const atIdx = val.lastIndexOf('@');
                                if (atIdx >= 0 && (atIdx === 0 || val[atIdx - 1] === ' ')) {
                                  const afterAt = val.slice(atIdx + 1);
                                  if (!afterAt.includes(' ') || afterAt.length === 0) {
                                    setMentionDropdown(true);
                                    setMentionFilter(afterAt);
                                  } else {
                                    setMentionDropdown(false);
                                  }
                                } else {
                                  setMentionDropdown(false);
                                }
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Escape') { setMentionDropdown(false); setShowItemPicker(false); return; }
                                if (e.key === 'Enter' && (chatInput.trim() || stagedFile)) {
                                  handleSendMessage(sub, chatInput.trim() || (stagedFile ? `📎 ${stagedFile.name}` : ''), false, stagedFile ?? undefined, chatItemRef);
                                }
                              }}
                              placeholder={stagedFile ? "Add a message·" : "Message· (@ to mention)"}
                              className="flex-1 px-2.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-[11px] focus:outline-none focus:border-zinc-400"
                            />
                            <button
                              onClick={() => {
                                if (chatInput.trim() || stagedFile) {
                                  handleSendMessage(sub, chatInput.trim() || (stagedFile ? `📎 ${stagedFile.name}` : ''), false, stagedFile ?? undefined, chatItemRef);
                                }
                              }}
                              disabled={!chatInput.trim() && !stagedFile}
                              className="p-1.5 text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                              <Send size={13} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* -- Review Action -- */}
                  {(sub.status === SubmissionStatus.COMPLETED || sub.status === SubmissionStatus.DEPLOYMENT_READY || sub.currentStage === 'DONE') ? (
                    <div className="bg-white border-t-2 border-teal-200 rounded-xl px-4 py-4 mt-2 shrink-0">
                      <div className="flex items-center gap-2.5 mb-3">
                        <ShieldCheck size={14} className="text-teal-600" />
                        <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest">Review Complete</p>
                      </div>
                      <Link
                        to={`/apps/aggregator/${sub.status === SubmissionStatus.COMPLETED ? 'live' : 'deployment'}`}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 transition-all"
                      >
                        <Rocket size={14} /> {sub.status === SubmissionStatus.COMPLETED ? 'View in Live Campaigns' : 'View in Launch Readiness'}
                      </Link>
                    </div>
                  ) : reviewable && sub.status !== SubmissionStatus.REJECTED && (
                    <div className="bg-white border-t-2 border-zinc-200 rounded-xl px-4 py-4 mt-2 shrink-0">
                      <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Your Review · {sub.currentStage} Department</p>
                      {canMakeDecision(sub) ? (
                        <button
                          onClick={() => setDecisionPopup({ sub, path: null, note: '', rejectReason: '' })}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 transition-all"
                        >
                          <ClipboardList size={14} /> Make Decision
                        </button>
                      ) : (
                        <div className="flex items-start gap-2.5 px-3 py-3 bg-zinc-50 border border-zinc-200 rounded-xl">
                          <Lock size={14} className="text-zinc-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-bold text-zinc-500">Restricted</p>
                            <p className="text-[10px] text-zinc-400 mt-0.5">You do not have permission to approve the <span className="font-bold text-zinc-600">{sub.currentStage}</span> stage.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* -- History Audit Popup -- */}
      {showAuditPopup && selectedSubmission && (
        <HistoryAuditPopup submission={selectedSubmission} onClose={() => setShowAuditPopup(false)} onRevert={(isAdmin || isSuperAdmin) ? handleRevertDecision : undefined} />
      )}

      {/* -- Review Decision Popup (3-path modal) -- */}
      {decisionPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setDecisionPopup(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-zinc-900">Review Decision</h3>
                <p className="text-xs text-zinc-400 mt-0.5">{decisionPopup.sub.campaignName} · {decisionPopup.sub.brandName}</p>
              </div>
              <button onClick={() => setDecisionPopup(null)} className="p-1.5 hover:bg-zinc-100 rounded-lg transition-all">
                <X size={18} className="text-zinc-400" />
              </button>
            </div>

            {/* Path selection */}
            {!decisionPopup.path ? (
              <div className="p-6 space-y-3">
                <p className="text-sm font-semibold text-zinc-700 mb-4">Choose an action for this submission:</p>
                {/* Approve */}
                <button
                  onClick={() => setDecisionPopup(prev => prev ? { ...prev, path: 'approve' } : null)}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 transition-all group"
                >
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                    <Check size={20} strokeWidth={3} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-emerald-800">Approve & Forward</p>
                    <p className="text-xs text-emerald-600">Advance to the next department stage</p>
                  </div>
                  <ChevronRight size={16} className="ml-auto text-emerald-400" />
                </button>
                {/* Reject */}
                <button
                  onClick={() => setDecisionPopup(prev => prev ? { ...prev, path: 'reject' } : null)}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300 transition-all group"
                >
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                    <Ban size={20} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-red-800">Reject Submission</p>
                    <p className="text-xs text-red-600">Permanently close · manager must create a new submission</p>
                  </div>
                  <ChevronRight size={16} className="ml-auto text-red-400" />
                </button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {/* Back button */}
                <button
                  onClick={() => setDecisionPopup(prev => prev ? { ...prev, path: null } : null)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-zinc-700 transition-all"
                >
                  <ArrowLeft size={12} /> Back to choices
                </button>

                {/* Approve confirm */}
                {decisionPopup.path === 'approve' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                        <Check size={20} strokeWidth={3} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-emerald-800">Approve & Forward</p>
                        <p className="text-xs text-emerald-600">Submission will advance to the next stage</p>
                      </div>
                    </div>
                    <textarea
                      value={decisionPopup.note}
                      onChange={e => setDecisionPopup(prev => prev ? { ...prev, note: e.target.value } : null)}
                      placeholder="Add an optional note·"
                      rows={2}
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500 resize-none"
                    />
                    <button
                      onClick={() => { handleApprove(decisionPopup.sub, decisionPopup.note).then(() => setDecisionPopup(null)); }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 transition-all"
                    >
                      <Check size={16} /> Confirm Approval
                    </button>
                  </div>
                )}

                {/* Reject confirm */}
                {decisionPopup.path === 'reject' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-white">
                        <Ban size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-red-800">Reject Submission</p>
                        <p className="text-xs text-red-600">This action is permanent and cannot be undone</p>
                      </div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <div className="flex items-start gap-2">
                        <AlertTriangle size={14} className="text-red-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-red-700">
                          <strong>Warning:</strong> Rejecting this submission will permanently close it.
                          The store manager will need to create an entirely new submission.
                        </p>
                      </div>
                    </div>
                    <textarea
                      value={decisionPopup.rejectReason}
                      onChange={e => setDecisionPopup(prev => prev ? { ...prev, rejectReason: e.target.value } : null)}
                      placeholder="Rejection reason (required)·"
                      rows={3}
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-red-500 resize-none"
                    />
                    <button
                      onClick={() => handleReject(decisionPopup.sub, decisionPopup.rejectReason)}
                      disabled={!decisionPopup.rejectReason.trim()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-800 text-white rounded-xl text-sm font-bold hover:bg-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      <Ban size={16} /> Reject Permanently
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* -- Discard Item Confirmation Modal -- */}
      {discardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setDiscardModal(null); setDiscardReason(''); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-zinc-900">Discard Item</h3>
                <p className="text-xs text-zinc-400 mt-0.5">"{discardModal.itemName}"</p>
              </div>
              <button onClick={() => { setDiscardModal(null); setDiscardReason(''); }} className="p-1.5 hover:bg-zinc-100 rounded-lg transition-all">
                <X size={18} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-zinc-600">This item will be permanently excluded from this submission. The manager will be notified.</p>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Reason *</label>
                <textarea
                  value={discardReason}
                  onChange={e => setDiscardReason(e.target.value)}
                  placeholder="e.g. Item limit reached, duplicate item, not eligible for this campaign·"
                  rows={3}
                  className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-red-400 resize-none"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => { setDiscardModal(null); setDiscardReason(''); }}
                  className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm font-semibold text-zinc-600 hover:bg-zinc-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDiscardItem(discardModal.sub, discardModal.itemId, discardReason)}
                  disabled={!discardReason.trim() || discarding}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <Trash2 size={14} /> {discarding ? 'Discarding·' : 'Permanently Discard Item'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* -- Undo Discard Toast (5-second window) -- */}
      {undoDiscard && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 bg-zinc-900 text-white rounded-xl shadow-2xl animate-in slide-in-from-bottom-4">
          <Ban size={16} className="text-red-400 shrink-0" />
          <p className="text-sm font-semibold">"{undoDiscard.itemName}" discarded</p>
          <button
            onClick={() => handleRestoreItem(undoDiscard.sub, undoDiscard.itemId)}
            className="px-3 py-1.5 bg-white text-zinc-900 rounded-lg text-xs font-bold hover:bg-zinc-100 transition-all"
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
}

// --- Launch Readiness Tab · Pre-Launch Command Center -----------------------

function DeploymentCenterTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'aggregator', 'deployment', 'admin');
  const isSuperAdmin = isAdmin;
  const repo = getRepository();
  const toast = useToast();
  const navigate = useNavigate();
  const [allSubmissions, setAllSubmissions] = useState<CampaignSubmission[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [menuProfiles, setMenuProfiles] = useState<{ id: string; name: string; brandId: string; branchIds: string[] }[]>([]);

  // UI state
  const [launching, setLaunching] = useState<string | null>(null);
  const [launchConfetti, setLaunchConfetti] = useState(false);
  const [fullDataDrawer, setFullDataDrawer] = useState<CampaignReadiness | null>(null);
  const [launchConfirmTarget, setLaunchConfirmTarget] = useState<CampaignReadiness | null>(null);

  // Dispatch config modal state
  const [dispatchTarget, setDispatchTarget] = useState<CampaignReadiness | null>(null);
  const [dispatchConfig, setDispatchConfig] = useState<DispatchConfig>({ ...DEFAULT_DISPATCH_CONFIG });
  const [generating, setGenerating] = useState(false);
  const [dispatchTab, setDispatchTab] = useState<'readiness' | 'menu' | 'audit'>('menu');
  const [dispatchTemplate, setDispatchTemplate] = useState<'full' | 'summary' | 'custom'>('full');

  // Discussion chat state
  const [chatInput, setChatInput] = useState('');
  const [stagedFile, setStagedFile] = useState<{ name: string; type: 'image' | 'document'; data: string; size: number } | null>(null);
  const [chatTarget, setChatTarget] = useState<CampaignSubmission | null>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Aggregator process tracker: { [campaignId]: { [aggregatorName]: { completed: boolean; completedAt?: number; completedBy?: string } } }
  // Persisted on submissions via aggregatorProcess field
  const [aggTracker, setAggTracker] = useState<Record<string, Record<string, { completed: boolean; completedAt?: number; completedBy?: string }>>>({});

  // Load persisted aggregator tracker from submissions
  const loadAggTracker = (subs: CampaignSubmission[]) => {
    const tracker: Record<string, Record<string, { completed: boolean; completedAt?: number; completedBy?: string }>> = {};
    for (const sub of subs) {
      if (sub.aggregatorProcess && !tracker[sub.campaignId]) {
        tracker[sub.campaignId] = { ...sub.aggregatorProcess };
      }
    }
    setAggTracker(tracker);
  };

  const markAggCompleted = async (campId: string, aggName: string) => {
    // Immutable: once completed, cannot be unchecked
    if (aggTracker[campId]?.[aggName]?.completed) return;
    const entry = { completed: true, completedAt: Date.now(), completedBy: user?.name ?? 'Unknown' };
    const updatedTracker = {
      ...aggTracker,
      [campId]: { ...aggTracker[campId], [aggName]: entry },
    };
    setAggTracker(updatedTracker);

    // Persist to all submissions for this campaign
    const campSubs = allSubmissions.filter(s => s.campaignId === campId && s.status !== SubmissionStatus.REJECTED);
    for (const sub of campSubs) {
      const updated: CampaignSubmission = {
        ...sub,
        aggregatorProcess: updatedTracker[campId],
        updatedAt: Date.now(),
      };
      await repo.saveCampaignSubmission(updated);
    }
    await load();
  };

  // Collapse state for campaign cards (accordion)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const toggleCard = (campId: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(campId)) next.delete(campId); else next.add(campId);
      return next;
    });
  };

  // Brand submission details drawer (Golden Record view)
  const [brandDetailDrawer, setBrandDetailDrawer] = useState<{ campaign: Campaign; submission: CampaignSubmission } | null>(null);

  // Postpone dialog state · supports both single brand and full campaign
  const [postponeTarget, setPostponeTarget] = useState<{ sub?: CampaignSubmission; subs?: CampaignSubmission[]; campaign: Campaign; mode: 'brand' | 'campaign' } | null>(null);
  const [postponeReason, setPostponeReason] = useState('');
  const [postponeUntil, setPostponeUntil] = useState('');

  const load = async () => {
    const [s, c, b, d, mp] = await Promise.all([
      repo.getCampaignSubmissions(),
      repo.getCampaigns(),
      repo.getBrands(),
      repo.getDepartments(),
      repo.getAll<{ id: string; name: string; brandId: string; branchIds: string[]; connectedModules?: string[] }>('menu-profiles'),
    ]);
    const isolated = isAdmin ? s : s.filter(sub => sub.submittedBy === user?.id);
    setAllSubmissions(isolated);
    setCampaigns(c);
    setBrands(b.filter(br => br.enabledModules?.includes('aggregator')));
    setDepartments(d);
    setMenuProfiles(mp.map(p => ({ id: p.id, name: p.name, brandId: p.brandId, branchIds: p.branchIds ?? [] })));
    loadAggTracker(isolated);
  };
  useEffect(() => { load(); const h = (e: any) => { if (import.meta.env.DEV && e && e.type === 'PULSE_UPDATE') console.log('💓 Heartbeat: Data Refreshed (Aggregator) ' + e.type); load(); }; window.addEventListener('PULSE_UPDATE', h); return () => window.removeEventListener('PULSE_UPDATE', h); }, []);

  // Dynamic department stages
  const dynamicStages = (() => {
    const campaignDepts = departments.filter(d => (d.linkedModules ?? []).includes('aggregator'));
    if (campaignDepts.length > 0) return campaignDepts.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(d => d.name);
    return [...DEPARTMENT_STAGES];
  })();

  const deptName = (deptId: string) => departments.find(d => d.id === deptId)?.name ?? deptId;
  const userDeptIds = user?.assignedDepartments ?? [];

  // -- Compute readiness for each ACTIVE/PLANNED campaign that has submissions --
  const campaignReadiness: CampaignReadiness[] = (() => {
    // Find campaigns that have at least one submission (any status in the pipeline)
    const campIdsWithSubs = new Set(
      allSubmissions
        .filter(s => s.status !== SubmissionStatus.REJECTED)
        .map(s => s.campaignId)
    );
    return Array.from(campIdsWithSubs)
      .map(cid => {
        const camp = campaigns.find(c => c.id === cid);
        if (!camp || camp.status === CampaignStatus.DRAFT || camp.status === CampaignStatus.CLOSED) return null;
        const cr = computeCampaignReadiness(camp, allSubmissions, brands);

        // Enrich brand entries with branch breakdown
        for (const brandEntry of cr.brands) {
          const brand = brands.find(b => b.id === brandEntry.brandId);
          const brandBranches = brand?.branches ?? [];
          const brandProfiles = menuProfiles.filter(p => p.brandId === brandEntry.brandId);
          brandEntry.branches = brandBranches.map(br => {
            const profile = brandProfiles.find(p => p.branchIds.includes(br.id));
            return { branchId: br.id, branchName: br.name, menuProfileId: profile?.id, menuProfileName: profile?.name, hasMenu: !!profile };
          });
        }

        // If ALL brands for this campaign are COMPLETED (launched), hide from Launch Readiness
        const hasPipelineSubs = allSubmissions.some(s =>
          s.campaignId === cid &&
          s.status !== SubmissionStatus.COMPLETED &&
          s.status !== SubmissionStatus.REJECTED
        );
        if (!hasPipelineSubs && cr.readyCount === 0) return null;

        return cr;
      })
      .filter(Boolean) as CampaignReadiness[];
  })();

  // Helper: get completed aggregators for a campaign from tracker
  const getCompletedAggs = (campId: string): string[] => {
    const tracker = aggTracker[campId] ?? {};
    return Object.entries(tracker).filter(([, v]) => (v as { completed: boolean }).completed).map(([k]) => k);
  };

  // -- Launch All Ready Brands for a campaign -----------------------------
  const handleLaunchAll = async (cr: CampaignReadiness) => {
    if (!user) return;
    const completedAggs = getCompletedAggs(cr.campaign.id);
    if (completedAggs.length === 0) {
      toast.error('No Aggregators Ready', 'Mark at least one aggregator as completed in the Aggregator Process before launching.');
      return;
    }
    setLaunching(cr.campaign.id);

    // Get submissions that are DEPLOYMENT_READY (not already launched)
    const tolaunchSubs = cr.readySubmissions.filter(s => s.status === SubmissionStatus.DEPLOYMENT_READY);
    // Get COMPLETED submissions that need liveAggregators update
    const completedSubs = allSubmissions.filter(s => s.campaignId === cr.campaign.id && s.status === SubmissionStatus.COMPLETED);

    // Launch new submissions
    for (const sub of tolaunchSubs) {
      const updated: CampaignSubmission = {
        ...sub,
        status: SubmissionStatus.COMPLETED,
        liveAggregators: completedAggs,
        aggregatorProcess: aggTracker[cr.campaign.id],
        reviewHistory: [...sub.reviewHistory, {
          departmentId: '',
          departmentName: 'DEPLOYMENT',
          action: 'APPROVE' as const,
          reviewerId: user.id,
          reviewerName: user.name,
          note: `Campaign launched · live on: ${completedAggs.join(', ')}`,
          timestamp: Date.now(),
        }],
        updatedAt: Date.now(),
      };
      await repo.saveCampaignSubmission(updated);
    }

    // Update already-live submissions with new aggregator status
    for (const sub of completedSubs) {
      const existingLive = sub.liveAggregators ?? [];
      const newAggs = completedAggs.filter(a => !existingLive.includes(a));
      if (newAggs.length > 0) {
        const updated: CampaignSubmission = {
          ...sub,
          liveAggregators: [...new Set([...existingLive, ...completedAggs])],
          aggregatorProcess: aggTracker[cr.campaign.id],
          reviewHistory: [...sub.reviewHistory, {
            departmentId: '',
            departmentName: 'DEPLOYMENT',
            action: 'APPROVE' as const,
            reviewerId: user.id,
            reviewerName: user.name,
            note: `Aggregator update · now live on: ${newAggs.join(', ')}`,
            timestamp: Date.now(),
          }],
          updatedAt: Date.now(),
        };
        await repo.saveCampaignSubmission(updated);
      }
    }

    const totalLaunched = tolaunchSubs.length;
    const totalUpdated = completedSubs.filter(s => {
      const existingLive = s.liveAggregators ?? [];
      return completedAggs.some(a => !existingLive.includes(a));
    }).length;

    // -- Inject approved items into connected Menu Profiles (OFFERS category) --
    const injectionResults: { brandName: string; profileName: string; count: number }[] = [];
    const camp = cr.campaign;
    for (const sub of tolaunchSubs) {
      const liveItems = sub.items.filter(it => it.itemStatus !== 'DISCARDED');
      if (liveItems.length === 0) continue;
      const injectedItems: InjectedItem[] = liveItems.map(it => ({
        sourceEntityId: it.id,
        nameEN: it.name,
        nameAR: it.nameAR,
        descriptionEN: it.description,
        descriptionAR: it.descriptionAR,
        basePrice: it.price,
        image: it.imageUrl,
        // Deep-copy imported option groups for full-fidelity menu injection
        optionGroups: it.importedOptions
          ? it.importedOptions.map(g => ({
              id: g.id,
              nameEN: g.nameEN,
              nameAR: g.nameAR,
              minSelection: g.minSelection,
              maxSelection: g.maxSelection,
              options: g.options.map(o => ({ id: o.id, nameEN: o.nameEN, nameAR: o.nameAR, extraPrice: o.extraPrice })),
            }))
          : undefined,
        // Campaign metadata for strikethrough pricing & lifecycle
        campaignMeta: {
          campaignId: camp.id,
          campaignName: camp.name,
          discountValue: camp.discountValue,
          discountType: camp.discountType,
          fundingType: camp.fundingType,
          sterlingSharePct: camp.sterlingSharePct,
          partnerSharePct: camp.partnerSharePct,
          startDate: camp.validFrom,
          endDate: camp.validTo,
        },
      }));
      try {
        const results = await pushItemsToConnectedMenus(
          'aggregator',
          sub.brandId,
          injectedItems,
          liveItems.map(it => it.id),
        );
        for (const r of results) {
          if (r.itemsInjected > 0) {
            injectionResults.push({ brandName: sub.brandName, profileName: r.profileName, count: r.itemsInjected });
          }
        }
      } catch (err) {
        console.error(`Menu injection failed for ${sub.brandName}:`, err);
      }
    }

    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `🚀 Launched "${cr.campaign.name}" · ${completedAggs.length} aggregator(s) [${completedAggs.join(', ')}]${totalLaunched > 0 ? ` · ${totalLaunched} brand(s) went live` : ''}${totalUpdated > 0 ? ` · ${totalUpdated} brand(s) updated` : ''}`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });

    // Notify only users whose brands match the campaign's target brands
    const launchRecipientId = cr.campaign.targetAllBrands
      ? '*'
      : `brands:${cr.campaign.brandIds.join(',')}`;
    await repo.saveNotification({
      id: crypto.randomUUID(),
      recipientId: launchRecipientId,
      actorId: user.id,
      type: NotificationType.APPROVED,
      title: 'Campaign Launched',
      message: `"${cr.campaign.name}" is now live on ${completedAggs.join(', ')}!${totalLaunched > 0 ? ` ${totalLaunched} brand(s) deployed.` : ''}`,
      targetPath: '/apps/aggregator/submit',
      isRead: false,
      createdAt: Date.now(),
    });

    setLaunchConfetti(true);
    setTimeout(() => setLaunchConfetti(false), 3000);

    const allAggs = cr.campaign.aggregators;
    const pendingAggs = allAggs.filter(a => !completedAggs.includes(a));
    const msg = totalLaunched > 0
      ? `${totalLaunched} brand(s) now live on ${completedAggs.join(', ')}!`
      : `Updated ${totalUpdated} brand(s) with new aggregator(s): ${completedAggs.filter(a => !(completedSubs[0]?.liveAggregators ?? []).includes(a)).join(', ')}`;
    const injectionMsg = injectionResults.length > 0
      ? ` · Menu sync: ${injectionResults.map(r => `${r.count} items → ${r.brandName} (${r.profileName})`).join(', ')}`
      : '';
    toast.success('🚀 Campaign Launched!', msg + injectionMsg + (pendingAggs.length > 0 ? ` · ${pendingAggs.length} aggregator(s) still pending.` : ''));
    setLaunching(null);
    await load();
  };

  // -- Postpone submission(s) · single brand or full campaign -------------
  const handlePostpone = async () => {
    if (!user || !postponeTarget || !postponeReason.trim()) return;
    const targets = postponeTarget.mode === 'campaign'
      ? (postponeTarget.subs ?? []).filter(s => s.status === SubmissionStatus.DEPLOYMENT_READY)
      : postponeTarget.sub ? [postponeTarget.sub] : [];
    if (targets.length === 0) return;

    const reason = postponeReason.trim();
    const untilTs = postponeUntil ? new Date(postponeUntil).getTime() : undefined;
    const noteText = `? Postponed · ${reason}${untilTs ? ` (until ${fmtDate(untilTs)})` : ''}`;

    for (const sub of targets) {
      const updated: CampaignSubmission = {
        ...sub,
        status: SubmissionStatus.POSTPONED,
        postponeReason: reason,
        postponeUntil: untilTs,
        postponedBy: user.id,
        postponedAt: Date.now(),
        reviewHistory: [...sub.reviewHistory, {
          departmentId: '',
          departmentName: 'DEPLOYMENT',
          action: 'APPROVE' as const,
          reviewerId: user.id,
          reviewerName: user.name,
          note: noteText,
          timestamp: Date.now(),
        }],
        updatedAt: Date.now(),
      };
      await repo.saveCampaignSubmission(updated);
    }

    const campName = postponeTarget.campaign.name;
    const logAction = postponeTarget.mode === 'campaign'
      ? `? Postponed entire campaign "${campName}" · ${targets.length} brand(s) · ${reason}`
      : `? Postponed "${targets[0].campaignName}" (${targets[0].brandName}) · ${reason}`;
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: logAction,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });

    const toastMsg = postponeTarget.mode === 'campaign'
      ? `${targets.length} brand(s) for "${campName}" postponed.`
      : `"${targets[0].campaignName}" (${targets[0].brandName}) has been postponed.`;
    toast.success('Campaign Postponed', toastMsg);
    setPostponeTarget(null);
    setPostponeReason('');
    setPostponeUntil('');
    await load();
  };

  // -- Unpostpone · bring back to DEPLOYMENT_READY -----------------------
  const handleUnpostpone = async (sub: CampaignSubmission) => {
    if (!user) return;
    const updated: CampaignSubmission = {
      ...sub,
      status: SubmissionStatus.DEPLOYMENT_READY,
      postponeReason: undefined,
      postponeUntil: undefined,
      postponedBy: undefined,
      postponedAt: undefined,
      reviewHistory: [...sub.reviewHistory, {
        departmentId: '',
        departmentName: 'DEPLOYMENT',
        action: 'APPROVE' as const,
        reviewerId: user.id,
        reviewerName: user.name,
        note: '? Unpostponed · returned to Launch Readiness.',
        timestamp: Date.now(),
      }],
      updatedAt: Date.now(),
    };
    await repo.saveCampaignSubmission(updated);
    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `? Unpostponed "${sub.campaignName}" (${sub.brandName}) · returned to deployment ready`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });
    toast.success('Campaign Restored', `"${sub.campaignName}" (${sub.brandName}) is back in Launch Readiness.`);
    await load();
  };

  // -- Open Dispatch Configuration Modal ----------------------------------
  const openDispatchModal = (cr: CampaignReadiness) => {
    if (cr.readySubmissions.length === 0) {
      toast.error('No Data', 'No ready brands to export.');
      return;
    }
    setDispatchConfig({
      ...DEFAULT_DISPATCH_CONFIG,
      columns: { ...ALL_COLUMNS_ON },
      readinessColumns: { ...ALL_READINESS_COLUMNS_ON },
      auditColumns: { ...ALL_AUDIT_COLUMNS_ON },
      selectedBrandIds: cr.readySubmissions.map(s => s.brandId),
    });
    setDispatchTemplate('full');
    setDispatchTarget(cr);
  };

  /** Apply a dispatch template preset */
  const applyDispatchTemplate = (template: 'full' | 'summary' | 'custom') => {
    setDispatchTemplate(template);
    if (template === 'full') {
      setDispatchConfig(prev => ({
        ...prev,
        includeOverview: true,
        includeBrandReadiness: true,
        includeApprovedMenu: true,
        includeBrandSheets: true,
        includeAuditHistory: true,
        includeImages: true,
        columns: { ...ALL_COLUMNS_ON },
        readinessColumns: { ...ALL_READINESS_COLUMNS_ON },
        auditColumns: { ...ALL_AUDIT_COLUMNS_ON },
      }));
    } else if (template === 'summary') {
      setDispatchConfig(prev => ({
        ...prev,
        includeOverview: true,
        includeBrandReadiness: true,
        includeApprovedMenu: true,
        includeBrandSheets: false,
        includeAuditHistory: false,
        includeImages: false,
        columns: {
          itemName: true,
          itemNameAR: false,
          description: false,
          descriptionAR: false,
          optionGroupsEN: false,
          optionGroupsAR: false,
          originalPrice: true,
          discountedPrice: true,
          internalBrandId: false,
          imageUrl: false,
          itemId: false,
        },
        readinessColumns: {
          brandName: true,
          branches: true,
          itemCount: true,
          target: true,
          balance: true,
          status: true,
          currentStage: false,
          submittedBy: false,
        },
        auditColumns: { ...ALL_AUDIT_COLUMNS_ON },
      }));
    }
    // 'custom' · keep current config as-is
  };

  // -- Generate Dispatch Package ------------------------------------------
  const handleGenerateDispatch = async () => {
    if (!dispatchTarget) return;
    setGenerating(true);
    try {
      const result = await DispatchService.generate(
        dispatchTarget.campaign,
        dispatchTarget.readySubmissions,
        dispatchTarget.brands,
        dispatchConfig,
      );
      setDispatchTarget(null);
      toast.success(
        '📦 Dispatch Package Ready!',
        `${result.brandCount} Brand${result.brandCount !== 1 ? 's' : ''} · ${result.itemCount} Items${result.imageCount > 0 ? ` · ${result.imageCount} Images` : ''} included.`,
      );
    } catch (err) {
      console.error('Dispatch generation error:', err);
      toast.error('Export Failed', err instanceof Error ? err.message : 'Error generating dispatch package.');
    } finally {
      setGenerating(false);
    }
  };

  // -- Discussion chat ----------------------------------------------------
  const handleSendMessage = async (
    sub: CampaignSubmission,
    message: string,
    attachment?: { name: string; type: 'image' | 'document'; data: string; size: number }
  ) => {
    if (!user || (!message.trim() && !attachment)) return;
    setChatInput('');
    setStagedFile(null);
    const userDeptName = userDeptIds.length > 0 ? deptName(userDeptIds[0]) : undefined;
    const msg: DiscussionMessage = {
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      departmentName: userDeptName,
      message: message.trim(),
      attachmentName: attachment?.name,
      attachmentType: attachment?.type,
      attachmentData: attachment?.data,
      attachmentSize: attachment?.size,
      public: false,
      timestamp: Date.now(),
    };
    const updated: CampaignSubmission = {
      ...sub,
      discussion: [...(sub.discussion ?? []), msg],
      updatedAt: Date.now(),
    };
    await repo.saveCampaignSubmission(updated);
    setChatTarget(updated);
    await load();
  };

  return (
    <div className="space-y-6">
      {/* Launch confetti */}
      {launchConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {Array.from({ length: 60 }).map((_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-${Math.random() * 20}%`,
                width: `${6 + Math.random() * 8}px`,
                height: `${6 + Math.random() * 8}px`,
                backgroundColor: ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899'][i % 6],
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                animation: `confetti-fall ${2 + Math.random() * 2}s ease-in forwards`,
                animationDelay: `${Math.random() * 0.5}s`,
              }}
            />
          ))}
          <style>{`@keyframes confetti-fall { 0% { transform: translateY(0) rotate(0); opacity: 1; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
        </div>
      )}

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Launch Readiness</h2>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? 'Pre-launch command center · verify brand readiness, export dispatch packages, then launch.'
              : 'Track your approved submissions awaiting admin deployment.'}
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-xl text-xs font-bold text-violet-700">
          <Rocket size={14} /> {campaignReadiness.length} Campaign{campaignReadiness.length !== 1 ? 's' : ''} in Pipeline
        </div>
      </div>

      {campaignReadiness.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
          <Rocket size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm font-semibold text-zinc-400">No campaigns awaiting deployment</p>
          <p className="text-xs text-zinc-400 mt-1">Campaigns will appear here once at least one brand passes all review stages.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaignReadiness.map(cr => {
            const camp = cr.campaign;
            const daysLeft = Math.max(0, Math.ceil((camp.validTo - Date.now()) / (1000 * 60 * 60 * 24)));
            const targetCount = camp.requiredItemCount ?? 0;
            const isExpanded = expandedCards.has(camp.id);
            const allReady = cr.readyCount === cr.brands.length && cr.brands.length > 0;
            const completedAggNames = getCompletedAggs(camp.id);
            const liveAggs = completedAggNames.length;
            const totalAggs = camp.aggregators.length;
            const pendingAggNames = camp.aggregators.filter(a => !completedAggNames.includes(a));
            // Check if all brands are already launched but aggregators remain
            const completedSubs = allSubmissions.filter(s => s.campaignId === camp.id && s.status === SubmissionStatus.COMPLETED);
            const alreadyLiveAggs = completedSubs[0]?.liveAggregators ?? [];
            const newAggsReady = completedAggNames.filter(a => !alreadyLiveAggs.includes(a));
            const isUpdateMode = cr.readyCount === 0 && completedSubs.length > 0 && newAggsReady.length > 0;
            const hasNoNewWork = cr.readyCount === 0 && completedSubs.length > 0 && newAggsReady.length === 0;

            return (
              <div key={camp.id} className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
                {/* -- Collapsed Summary Bar (always visible) --------- */}
                <div
                  className="px-4 py-3 bg-gradient-to-r from-violet-50 to-indigo-50 border-b border-zinc-200 cursor-pointer select-none hover:from-violet-100/60 hover:to-indigo-100/60 transition-all"
                  onClick={() => toggleCard(camp.id)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 bg-violet-600 rounded-xl flex items-center justify-center text-white shrink-0">
                        <Rocket size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-bold text-zinc-900 truncate">{camp.name}</h3>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700 border border-violet-200">
                            {camp.discountValue}{camp.discountType === 'PERCENTAGE' ? '%' : ' QAR'}
                          </span>
                          <span className={cn(
                            "px-2 py-0.5 rounded-full text-[10px] font-bold border",
                            allReady || isUpdateMode
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : cr.readyCount > 0
                              ? "bg-amber-100 text-amber-700 border-amber-200"
                              : "bg-red-100 text-red-700 border-red-200"
                          )}>
                            {isUpdateMode ? `?? ${completedSubs.length} Launched` : allReady ? '??' : cr.readyCount > 0 ? '??' : '??'} {!isUpdateMode && `${cr.readyCount}/${cr.brands.length} Ready`}
                          </span>
                          <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold border",
                            liveAggs === totalAggs ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : liveAggs > 0 ? "bg-amber-100 text-amber-700 border-amber-200"
                            : "bg-zinc-100 text-zinc-500 border-zinc-200"
                          )}>
                            <Globe size={9} className="inline mr-0.5" /> {liveAggs}/{totalAggs} Agg
                          </span>
                          <span className={cn("text-[10px] font-bold", daysLeft > 7 ? "text-emerald-600" : daysLeft > 0 ? "text-amber-600" : "text-red-500")}>
                            {daysLeft > 0 ? `${daysLeft}d left` : 'Expired'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Action Buttons (visible in collapsed state) */}
                    <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                      {isAdmin && (
                        <button
                          onClick={() => setPostponeTarget({ subs: cr.readySubmissions, campaign: camp, mode: 'campaign' })}
                          disabled={cr.readySubmissions.filter(s => s.status === SubmissionStatus.DEPLOYMENT_READY).length === 0}
                          className={cn(
                            "flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all",
                            cr.readySubmissions.filter(s => s.status === SubmissionStatus.DEPLOYMENT_READY).length === 0
                              ? "bg-zinc-200 text-zinc-400 cursor-not-allowed"
                              : "bg-orange-500 text-white hover:bg-orange-600 shadow-sm"
                          )}
                        >
                          <Clock size={11} /> Postpone All
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => openDispatchModal(cr)}
                          disabled={cr.readySubmissions.length === 0}
                          className={cn(
                            "flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all",
                            cr.readySubmissions.length === 0
                              ? "bg-zinc-200 text-zinc-400 cursor-not-allowed"
                              : "bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 shadow-sm"
                          )}
                        >
                          <Package size={11} /> Dispatch
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => {
                            if (liveAggs === 0) {
                              toast.error('No Aggregators Ready', 'Mark at least one aggregator as completed before launching.');
                              return;
                            }
                            if (!isUpdateMode) {
                              const target = camp.requiredItemCount ?? 0;
                              const hasPartialItems = target > 0 && cr.brands.some(b => b.status === 'READY' && b.itemCount < target);
                              if (cr.missingCount > 0 || cr.inReviewCount > 0 || hasPartialItems) {
                                setLaunchConfirmTarget(cr);
                              } else {
                                handleLaunchAll(cr);
                              }
                            } else {
                              handleLaunchAll(cr);
                            }
                          }}
                          disabled={launching === camp.id || (cr.readyCount === 0 && !isUpdateMode) || liveAggs === 0 || hasNoNewWork}
                          className={cn(
                            "flex items-center gap-1.5 rounded-lg font-bold transition-all",
                            isUpdateMode && !hasNoNewWork
                              ? "px-4 py-2 text-xs bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-700 hover:to-emerald-700 animate-pulse shadow-lg shadow-teal-300/50"
                              : allReady && liveAggs > 0
                              ? "px-4 py-2 text-xs bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 animate-pulse shadow-lg shadow-violet-300/50"
                              : (cr.readyCount === 0 && !isUpdateMode) || liveAggs === 0
                              ? "px-3 py-1.5 text-[10px] bg-zinc-200 text-zinc-400 cursor-not-allowed"
                              : "px-3 py-1.5 text-[10px] bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                          )}
                        >
                          {launching === camp.id ? <RotateCcw size={13} className="animate-spin" /> : <Rocket size={isUpdateMode || allReady ? 13 : 11} />}
                          {launching === camp.id ? 'Launching·'
                            : isUpdateMode && !hasNoNewWork ? `🔄 Update Live (${newAggsReady.length} new)`
                            : allReady && liveAggs > 0 ? `🚀 Launch All (${cr.readyCount})`
                            : liveAggs === 0 ? 'Awaiting Aggregators'
                            : `Launch (${cr.readyCount})`}
                        </button>
                      )}
                      <button onClick={() => toggleCard(camp.id)} className="p-1.5 hover:bg-white/60 rounded-lg transition-all">
                        {isExpanded ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* -- Expanded Detail (accordion content) ------------ */}
                {isExpanded && (
                  <>
                    {/* Secondary info badges */}
                    <div className="px-4 py-2 bg-zinc-50/50 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
                      {targetCount > 0 && (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-200">
                          <Target size={9} /> Target: {targetCount} items / brand
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-[10px] text-zinc-500">
                        <Calendar size={9} /> {fmtDate(camp.validFrom)} · {fmtDate(camp.validTo)}
                      </span>
                      <span className="flex items-center gap-1 text-[10px] font-bold bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full border border-zinc-200">
                        <Package size={9} /> {cr.totalItems} Items
                      </span>
                      <button
                        onClick={() => setFullDataDrawer(cr)}
                        className="flex items-center gap-1 text-[10px] font-bold text-violet-600 hover:text-violet-700 hover:bg-violet-50 px-2 py-0.5 rounded-full transition-all"
                      >
                        <Eye size={9} /> Full Data
                      </button>
                    </div>

                    {/* -- Brand Readiness Grid ----------------------- */}
                    <div className="px-4 py-3">
                      <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-2">Brand Readiness</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1.5">
                        {cr.brands.map(brandEntry => {
                          const tc = targetCount > 0 && brandEntry.status === 'READY'
                            ? compareTarget(brandEntry.itemCount, targetCount) : null;
                          const tcColors = tc ? TARGET_STATUS_COLORS[tc.status] : null;

                          return (
                            <div
                              key={brandEntry.brandId}
                              className={cn(
                                "flex flex-col gap-1 px-2.5 py-2 rounded-xl border transition-all",
                                brandEntry.status === 'READY' && tc
                                  ? cn(tcColors!.bg.replace('100', '50'), tcColors!.border)
                                  : brandEntry.status === 'READY'     ? "bg-emerald-50 border-emerald-200"
                                  : brandEntry.status === 'IN_REVIEW' ? "bg-amber-50 border-amber-200"
                                  : "bg-red-50 border-red-200",
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <div className={cn(
                                  "w-6 h-6 rounded-lg flex items-center justify-center text-white shrink-0 text-[9px] font-bold",
                                  brandEntry.status === 'READY' && tc
                                    ? tc.status === 'PARTIAL' ? "bg-amber-500" : tc.status === 'OVER_SUBMITTED' ? "bg-indigo-600" : "bg-emerald-600"
                                    : brandEntry.status === 'READY'     ? "bg-emerald-600"
                                    : brandEntry.status === 'IN_REVIEW' ? "bg-amber-500"
                                    : "bg-red-500",
                                )}>
                                  {brandEntry.status === 'READY' ? <CheckCircle2 size={11} /> : brandEntry.status === 'IN_REVIEW' ? <Clock size={11} /> : <AlertCircle size={11} />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-bold text-zinc-900 truncate">{brandEntry.brandName}</p>
                                  {(() => {
                                    if (brandEntry.status === 'READY' && tc) {
                                      const colors = TARGET_STATUS_COLORS[tc.status];
                                      return <p className={cn("text-[8px] font-bold", colors.text)}>{tc.shortLabel}</p>;
                                    }
                                    if (brandEntry.status === 'READY') return <p className="text-[8px] font-bold text-emerald-600">{brandEntry.itemCount} items</p>;
                                    if (brandEntry.status === 'IN_REVIEW') return <p className="text-[8px] font-bold text-amber-600">@ {brandEntry.currentStage ?? 'Review'}</p>;
                                    return <p className="text-[8px] font-bold text-red-500">Missing</p>;
                                  })()}
                                </div>
                              </div>
                              {/* Branch breakdown */}
                              {brandEntry.branches && brandEntry.branches.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {brandEntry.branches.map(br => (
                                    <span key={br.branchId} className={cn(
                                      "inline-flex items-center gap-0.5 text-[7px] font-bold px-1.5 py-0.5 rounded-full border",
                                      br.hasMenu ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-400 border-zinc-200",
                                    )}>
                                      <MapPin size={7} /> {br.branchName}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {brandEntry.status === 'READY' && brandEntry.submission && (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => setBrandDetailDrawer({ campaign: camp, submission: brandEntry.submission! })}
                                    className="flex items-center justify-center gap-1 text-[8px] font-bold text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-lg py-0.5 flex-1 transition-all"
                                  >
                                    <Eye size={8} /> Details
                                  </button>
                                  {isAdmin && (
                                    <button
                                      onClick={() => setPostponeTarget({ sub: brandEntry.submission!, campaign: camp, mode: 'brand' })}
                                      className="flex items-center justify-center gap-1 text-[8px] font-bold text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg py-0.5 flex-1 transition-all"
                                    >
                                      <Clock size={8} /> Postpone
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* -- Partial Data Warning -------------------------- */}
                    {isAdmin && (cr.missingCount > 0 || cr.inReviewCount > 0) && (
                      <div className="px-4 py-2 border-t border-zinc-100">
                        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                          <AlertTriangle size={12} className="text-amber-600 mt-0.5 shrink-0" />
                          <p className="text-[10px] text-amber-700">
                            {cr.missingCount > 0 && `${cr.missingCount} missing. `}
                            {cr.inReviewCount > 0 && `${cr.inReviewCount} in review. `}
                            Export includes only {cr.readyCount} ready brand(s).
                          </p>
                        </div>
                      </div>
                    )}

                    {/* -- Postponed Submissions ------------------------- */}
                    {(() => {
                      const postponed = allSubmissions.filter(s => s.campaignId === camp.id && s.status === SubmissionStatus.POSTPONED);
                      if (postponed.length === 0) return null;
                      return (
                        <div className="px-4 py-2 border-t border-zinc-100">
                          <p className="text-[9px] font-bold text-orange-500 uppercase tracking-widest mb-2">? Postponed ({postponed.length})</p>
                          <div className="space-y-1.5">
                            {postponed.map(ps => (
                              <div key={ps.id} className="flex items-center gap-3 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-xl">
                                <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center text-white shrink-0"><Clock size={12} /></div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] font-bold text-zinc-900 truncate">{ps.brandName}</p>
                                  <p className="text-[10px] text-orange-700 truncate">{ps.postponeReason}</p>
                                  {ps.postponeUntil && <p className="text-[9px] text-zinc-400">Until: {fmtDate(ps.postponeUntil)}</p>}
                                </div>
                                <div className="flex items-center gap-1 text-[9px] text-zinc-400 shrink-0">
                                  {ps.postponedAt && <span>{fmtDate(ps.postponedAt)}</span>}
                                </div>
                                {isAdmin && (
                                  <button
                                    onClick={() => handleUnpostpone(ps)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg text-[10px] font-bold hover:bg-emerald-700 transition-all shrink-0"
                                  >
                                    <Rocket size={10} /> Unpostpone
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* -- Update Live Aggregators Reminder ----------- */}
                    {isUpdateMode && newAggsReady.length > 0 && (
                      <div className="px-4 py-2 border-t border-zinc-100">
                        <div className="flex items-start gap-3 px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl">
                          <Globe size={16} className="text-teal-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-bold text-teal-700">New Aggregator(s) Ready to Go Live!</p>
                            <p className="text-[10px] text-teal-600 mt-0.5">
                              {newAggsReady.join(', ')} {newAggsReady.length === 1 ? 'has' : 'have'} been completed.
                              Click <strong>Update Live</strong> to push to Live Campaigns.
                            </p>
                            <div className="flex items-center gap-1.5 mt-2">
                              {newAggsReady.map(a => (
                                <span key={a} className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-teal-100 text-teal-700 border border-teal-200">{a}</span>
                              ))}
                            </div>
                          </div>
                          <button
                            onClick={() => handleLaunchAll(cr)}
                            disabled={launching === camp.id}
                            className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 text-white rounded-lg text-xs font-bold hover:bg-teal-700 transition-all shrink-0"
                          >
                            <Rocket size={12} /> Update Live
                          </button>
                        </div>
                      </div>
                    )}

                    {/* -- Pending Aggregators Warning --------------- */}
                    {pendingAggNames.length > 0 && liveAggs > 0 && !isUpdateMode && (
                      <div className="px-4 py-2 border-t border-zinc-100">
                        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                          <AlertTriangle size={12} className="text-amber-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-[10px] font-bold text-amber-700">
                              {pendingAggNames.length} aggregator{pendingAggNames.length > 1 ? 's' : ''} still pending: {pendingAggNames.join(', ')}
                            </p>
                            <p className="text-[9px] text-amber-600">Complete them in the Aggregator Process below, then launch again to update Live Campaigns.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* -- Aggregator Process & Discussion Section ---- */}
                    {isAdmin && (
                      <div className="px-4 py-3 border-t border-zinc-100">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

                          {/* -- LEFT: Aggregator Process Checklist (Immutable) -- */}
                          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-zinc-100">
                              <Globe size={10} className="text-zinc-400" />
                              <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Aggregator Process</p>
                              <span className="ml-auto px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded text-[8px] font-bold">
                                {liveAggs}/{camp.aggregators.length}
                              </span>
                            </div>
                            <div className="divide-y divide-zinc-100">
                              {camp.aggregators.map(agg => {
                                const tracker = aggTracker[camp.id]?.[agg];
                                const isCompleted = tracker?.completed ?? false;
                                return (
                                  <div key={agg} className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-zinc-50/60 transition-colors">
                                    <button
                                      onClick={() => markAggCompleted(camp.id, agg)}
                                      disabled={isCompleted}
                                      className={cn(
                                        "w-5 h-5 rounded-md flex items-center justify-center shrink-0 border transition-all",
                                        isCompleted
                                          ? "bg-emerald-600 border-emerald-600 text-white cursor-not-allowed"
                                          : "bg-white border-zinc-300 text-zinc-300 hover:border-emerald-400 hover:text-emerald-400 cursor-pointer"
                                      )}
                                    >
                                      {isCompleted && <Check size={10} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <p className={cn("text-[10px] font-bold", isCompleted ? "text-emerald-700" : "text-zinc-700")}>{agg}</p>
                                      <p className="text-[8px] text-zinc-400">
                                        {isCompleted
                                          ? `Completed by ${tracker!.completedBy} · ${new Date(tracker!.completedAt!).toLocaleString()}`
                                          : 'Pending · Not yet dispatched'}
                                      </p>
                                    </div>
                                    <span className={cn(
                                      "px-1.5 py-0.5 rounded-full text-[8px] font-bold border",
                                      isCompleted
                                        ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                        : "bg-zinc-100 text-zinc-500 border-zinc-200"
                                    )}>
                                      {isCompleted ? 'COMPLETED' : 'PENDING'}
                                    </span>
                                  </div>
                                );
                              })}
                              {camp.aggregators.length === 0 && (
                                <div className="px-3 py-4 text-center">
                                  <Globe size={14} className="mx-auto text-zinc-300 mb-1" />
                                  <p className="text-[10px] text-zinc-400">No aggregators assigned</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* -- RIGHT: Internal Discussion --------------- */}
                          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex flex-col">
                            <div className="border-b border-zinc-100">
                              <div className="flex items-center gap-1.5 px-3 py-2">
                                <Lock size={10} className="text-zinc-400" />
                                <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">Internal Discussion</p>
                                <span className="text-[8px] font-bold text-red-400 uppercase">(Private)</span>
                              </div>
                              {cr.readySubmissions.length > 1 && (
                                <div className="flex gap-1 px-3 pb-1.5 overflow-x-auto">
                                  {cr.readySubmissions.map(sub => {
                                    const isActive = chatTarget?.id === sub.id || (!chatTarget && sub.id === cr.readySubmissions[0].id);
                                    return (
                                      <button
                                        key={sub.id}
                                        onClick={() => setChatTarget(sub)}
                                        className={cn(
                                          "px-2 py-0.5 rounded-md text-[9px] font-bold whitespace-nowrap transition-all",
                                          isActive
                                            ? "bg-violet-100 text-violet-700 border border-violet-300"
                                            : "bg-zinc-50 text-zinc-500 border border-zinc-200 hover:bg-zinc-100"
                                        )}
                                      >
                                        {sub.brandName}
                                        {(sub.discussion ?? []).filter(m => !m.public).length > 0 && (
                                          <span className="ml-1 px-1 py-0.5 bg-zinc-200 text-zinc-500 rounded text-[7px]">
                                            {(sub.discussion ?? []).filter(m => !m.public).length}
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Messages area */}
                            {(() => {
                              const activeSub = (chatTarget && cr.readySubmissions.find(s => s.id === chatTarget.id)) ? chatTarget : cr.readySubmissions[0];
                              if (!activeSub) return null;
                              const internalMsgs = (activeSub.discussion ?? []).filter(m => !m.public);

                              return (
                                <>
                                  <div className="flex-1 h-40 overflow-y-auto px-3 py-2 space-y-1.5">
                                    {internalMsgs.length === 0 ? (
                                      <div className="flex flex-col items-center justify-center h-full">
                                        <MessageSquare size={16} className="text-zinc-300 mb-1" />
                                        <p className="text-[10px] text-zinc-400">No messages yet</p>
                                      </div>
                                    ) : (
                                      internalMsgs.map(msg => {
                                        const isCurrentUser = msg.userId === user?.id;
                                        return (
                                          <div key={msg.id} className={cn("flex", isCurrentUser ? "justify-end" : "justify-start")}>
                                            <div className={cn(
                                              "max-w-[80%] rounded-xl px-2.5 py-1.5 text-[10px]",
                                              isCurrentUser ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-800"
                                            )}>
                                              <div className="flex items-center gap-1.5 mb-0.5">
                                                <span className="font-bold text-[9px]">{msg.userName}</span>
                                                {msg.departmentName && (
                                                  <span className={cn(
                                                    "px-1 py-0.5 rounded text-[7px] font-bold uppercase",
                                                    isCurrentUser ? "bg-zinc-700 text-zinc-300" : "bg-zinc-200 text-zinc-500"
                                                  )}>
                                                    {msg.departmentName}
                                                  </span>
                                                )}
                                              </div>
                                              <p className={cn("leading-relaxed", isCurrentUser ? "text-zinc-200" : "text-zinc-600")}>
                                                {msg.message}
                                              </p>
                                              {msg.attachmentName && (
                                                <div className={cn("mt-1 rounded-lg overflow-hidden", isCurrentUser ? "bg-zinc-700" : "bg-zinc-200")}>
                                                  {msg.attachmentType === 'image' && msg.attachmentData ? (
                                                    <img src={getMediaUrl(msg.attachmentData)} alt={msg.attachmentName} className="w-full max-h-20 object-cover rounded-t-lg" />
                                                  ) : null}
                                                  <a
                                                    href={msg.attachmentData || '#'}
                                                    download={msg.attachmentName}
                                                    onClick={e => { if (!msg.attachmentData) e.preventDefault(); }}
                                                    className="flex items-center gap-1.5 px-2 py-1 hover:opacity-80 transition-opacity"
                                                  >
                                                    {msg.attachmentType === 'image' ? <ImageIcon size={9} /> : <FileText size={9} />}
                                                    <span className="text-[8px] font-medium truncate">{msg.attachmentName}</span>
                                                  </a>
                                                </div>
                                              )}
                                              <p className="text-[7px] text-zinc-400 mt-0.5">{new Date(msg.timestamp).toLocaleString()}</p>
                                            </div>
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>

                                  {/* Chat input */}
                                  <div className="border-t border-zinc-200 shrink-0">
                                    {stagedFile && (
                                      <div className="px-2.5 pt-1.5 pb-0.5">
                                        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded text-[9px]">
                                          {stagedFile.type === 'image' ? <ImageIcon size={9} className="text-indigo-500" /> : <FileText size={9} className="text-indigo-500" />}
                                          <span className="font-medium text-indigo-700 truncate max-w-[100px]">{stagedFile.name}</span>
                                          <button onClick={() => setStagedFile(null)} className="p-0.5 text-indigo-400 hover:text-red-500"><X size={9} /></button>
                                        </div>
                                      </div>
                                    )}
                                    <div className="px-2.5 py-1.5 flex items-center gap-1.5">
                                      <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                        className="hidden"
                                        onChange={e => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          if (file.size > 5 * 1024 * 1024) {
                                            toast.error('File Too Large', 'Max file size is 5MB.');
                                            e.target.value = '';
                                            return;
                                          }
                                          const attType: 'image' | 'document' = file.type.startsWith('image/') ? 'image' : 'document';
                                          uploadFile(file, { moduleId: 'aggregator' }).then(url => {
                                            setStagedFile({ name: file.name, type: attType, data: url, size: file.size });
                                          }).catch(() => toast.error('Upload Failed', 'Could not upload file.'));
                                          e.target.value = '';
                                        }}
                                      />
                                      <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className={cn("p-1 rounded transition-all", stagedFile ? "text-indigo-600 bg-indigo-50" : "text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100")}
                                        title="Attach a file"
                                      >
                                        <Paperclip size={11} />
                                      </button>
                                      <input
                                        ref={chatInputRef}
                                        type="text"
                                        value={chatInput}
                                        onChange={e => setChatInput(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter' && (chatInput.trim() || stagedFile)) {
                                            handleSendMessage(activeSub, chatInput.trim() || (stagedFile ? `📎 ${stagedFile.name}` : ''), stagedFile ?? undefined);
                                          }
                                        }}
                                        placeholder="Message·"
                                        className="flex-1 px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-md text-[10px] focus:outline-none focus:border-zinc-400"
                                      />
                                      <button
                                        onClick={() => {
                                          if (chatInput.trim() || stagedFile) {
                                            handleSendMessage(activeSub, chatInput.trim() || (stagedFile ? `📎 ${stagedFile.name}` : ''), stagedFile ?? undefined);
                                          }
                                        }}
                                        disabled={!chatInput.trim() && !stagedFile}
                                        className="p-1.5 text-white bg-zinc-900 rounded-md hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                      >
                                        <Send size={11} />
                                      </button>
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* -- Dispatch Configuration Modal -------------------------------- */}
      {dispatchTarget && (() => {
        const selectedCount = dispatchConfig.selectedBrandIds.length;
        const selectedItemCount = dispatchTarget.readySubmissions
          .filter(s => dispatchConfig.selectedBrandIds.includes(s.brandId))
          .reduce((acc, s) => acc + s.items.filter(it => it.itemStatus !== 'DISCARDED').length, 0);

        const MENU_COL_OPTIONS: { key: keyof ColumnSelection; label: string; icon: React.ReactNode }[] = [
          { key: 'itemName', label: 'Item EN', icon: <Tag size={11} /> },
          { key: 'itemNameAR', label: 'Item AR', icon: <Tag size={11} /> },
          { key: 'description', label: 'Description EN', icon: <FileText size={11} /> },
          { key: 'descriptionAR', label: 'Description AR', icon: <FileText size={11} /> },
          { key: 'optionGroupsEN', label: 'Option Groups EN', icon: <Layers size={11} /> },
          { key: 'optionGroupsAR', label: 'Option Groups AR', icon: <Layers size={11} /> },
          { key: 'originalPrice', label: 'Original Price', icon: <DollarSign size={11} /> },
          { key: 'discountedPrice', label: 'After Discount', icon: <Percent size={11} /> },
          { key: 'internalBrandId', label: 'Brand ID', icon: <Building2 size={11} /> },
          { key: 'itemId', label: 'Item ID', icon: <Hash size={11} /> },
        ];

        const READINESS_COL_OPTIONS: { key: keyof ReadinessColumnSelection; label: string; icon: React.ReactNode }[] = [
          { key: 'brandName', label: 'Brand Name', icon: <Building2 size={11} /> },
          { key: 'branches', label: 'Branches', icon: <MapPin size={11} /> },
          { key: 'itemCount', label: 'Item Count', icon: <Hash size={11} /> },
          { key: 'target', label: 'Target', icon: <Target size={11} /> },
          { key: 'balance', label: 'Balance', icon: <BarChart3 size={11} /> },
          { key: 'status', label: 'Status', icon: <CheckCircle2 size={11} /> },
          { key: 'currentStage', label: 'Current Stage', icon: <Layers size={11} /> },
          { key: 'submittedBy', label: 'Lifecycle Chain', icon: <Users size={11} /> },
        ];

        const AUDIT_COL_OPTIONS: { key: keyof AuditColumnSelection; label: string; icon: React.ReactNode }[] = [
          { key: 'brand', label: 'Brand', icon: <Building2 size={11} /> },
          { key: 'itemName', label: 'Item Name', icon: <Tag size={11} /> },
          { key: 'event', label: 'Event', icon: <Zap size={11} /> },
          { key: 'reviewer', label: 'Reviewer', icon: <Users size={11} /> },
          { key: 'adminNote', label: 'Admin Note', icon: <FileText size={11} /> },
          { key: 'department', label: 'Department', icon: <Layers size={11} /> },
          { key: 'timestamp', label: 'Timestamp', icon: <Clock size={11} /> },
        ];

        const menuColCount = Object.values(dispatchConfig.columns).filter(Boolean).length;
        const readinessColCount = Object.values(dispatchConfig.readinessColumns).filter(Boolean).length;
        const auditColCount = Object.values(dispatchConfig.auditColumns).filter(Boolean).length;
        const showMenuCols = dispatchConfig.includeApprovedMenu || dispatchConfig.includeBrandSheets;
        const showReadinessCols = dispatchConfig.includeBrandReadiness;
        const showAuditCols = dispatchConfig.includeAuditHistory;
        const showColumnConfig = showMenuCols || showReadinessCols || showAuditCols;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { if (!generating) setDispatchTarget(null); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-900 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-emerald-500 rounded-xl flex items-center justify-center text-white"><Package size={18} /></div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Configure Dispatch Package</h3>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      "{dispatchTarget.campaign.name}" · {selectedCount} brand(s) · {selectedItemCount} items
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {/* -- Template Selection ------------------------- */}
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2">Export Template</p>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { key: 'full' as const, icon: <Package size={14} />, accent: 'bg-emerald-500', activeBg: 'bg-emerald-50', activeBorder: 'border-emerald-300', activeRing: 'ring-emerald-200', title: 'Template 1', sub: 'Full Package · All sheets · Images' },
                      { key: 'summary' as const, icon: <FileText size={14} />, accent: 'bg-amber-500', activeBg: 'bg-amber-50', activeBorder: 'border-amber-300', activeRing: 'ring-amber-200', title: 'Template 2', sub: 'Summary · Core columns only' },
                      { key: 'custom' as const, icon: <Settings size={14} />, accent: 'bg-violet-500', activeBg: 'bg-violet-50', activeBorder: 'border-violet-300', activeRing: 'ring-violet-200', title: 'Custom', sub: 'Pick sheets & columns' },
                    ] as const).map(t => {
                      const isActive = dispatchTemplate === t.key;
                      return (
                        <button
                          key={t.key}
                          onClick={() => applyDispatchTemplate(t.key)}
                          className={cn(
                            "relative flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-center transition-all",
                            isActive
                              ? `${t.activeBg} ${t.activeBorder} ring-1 ${t.activeRing}`
                              : "bg-white border-zinc-200 hover:border-zinc-300 opacity-70 hover:opacity-100"
                          )}
                        >
                          <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center text-white", t.accent)}>
                            {t.icon}
                          </div>
                          <p className="text-[10px] font-bold text-zinc-800">{t.title}</p>
                          <p className="text-[8px] text-zinc-400 leading-tight">{t.sub}</p>
                          {isActive && (
                            <div className={cn("absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center text-white", t.accent)}>
                              <Check size={9} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* -- Brand Selection -------------------------------- */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Brand Selection</p>
                    <button
                      onClick={() => {
                        const allIds = dispatchTarget.readySubmissions.map(s => s.brandId);
                        const allSelected = allIds.every(id => dispatchConfig.selectedBrandIds.includes(id));
                        setDispatchConfig(prev => ({ ...prev, selectedBrandIds: allSelected ? [] : allIds }));
                      }}
                      className="text-[9px] font-bold text-violet-600 hover:text-violet-700"
                    >
                      {dispatchTarget.readySubmissions.every(s => dispatchConfig.selectedBrandIds.includes(s.brandId)) ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {dispatchTarget.readySubmissions.map(sub => {
                      const isSelected = dispatchConfig.selectedBrandIds.includes(sub.brandId);
                      return (
                        <button
                          key={sub.brandId}
                          onClick={() => setDispatchConfig(prev => ({
                            ...prev,
                            selectedBrandIds: isSelected
                              ? prev.selectedBrandIds.filter(id => id !== sub.brandId)
                              : [...prev.selectedBrandIds, sub.brandId],
                          }))}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all",
                            isSelected
                              ? "bg-violet-50 border-violet-300 ring-1 ring-violet-200"
                              : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60"
                          )}
                        >
                          <div className={cn(
                            "w-4 h-4 rounded-md flex items-center justify-center shrink-0 border transition-all",
                            isSelected ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-300"
                          )}>
                            {isSelected && <Check size={9} />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-bold text-zinc-900 truncate">{sub.brandName}</p>
                            <p className="text-[8px] text-zinc-400">{sub.items.filter(it => it.itemStatus !== 'DISCARDED').length} items</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* -- Sheet Tabs & Options (centered) · custom mode only -- */}
                {dispatchTemplate === 'custom' && (<>
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2 text-center">Sheets &amp; Options</p>
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    {([
                      { key: 'includeOverview' as const, label: 'Campaign Overview', icon: <FileText size={11} /> },
                      { key: 'includeBrandReadiness' as const, label: 'Brand Readiness', icon: <CheckCircle2 size={11} /> },
                      { key: 'includeApprovedMenu' as const, label: 'Master Menu (All Brands)', icon: <Layers size={11} /> },
                      { key: 'includeBrandSheets' as const, label: 'Brand-Specific Sheets', icon: <Building2 size={11} /> },
                      { key: 'includeAuditHistory' as const, label: 'Audit History', icon: <History size={11} /> },
                      { key: 'includeImages' as const, label: 'Include Images', icon: <ImageIcon size={11} /> },
                    ] as const).map(opt => {
                      const isOn = dispatchConfig[opt.key];
                      return (
                        <button
                          key={opt.key}
                          onClick={() => setDispatchConfig(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))}
                          className={cn(
                            "flex items-center gap-1.5 px-3 py-2 rounded-xl border text-left transition-all",
                            isOn
                              ? "bg-violet-50 border-violet-300 ring-1 ring-violet-200"
                              : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60"
                          )}
                        >
                          <div className={cn(
                            "w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 border transition-all",
                            isOn ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-300"
                          )}>
                            {isOn && <Check size={8} />}
                          </div>
                          <span className="text-zinc-400">{opt.icon}</span>
                          <p className="text-[10px] font-bold text-zinc-700">{opt.label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* -- Column Configuration Tabs (conditional) ------- */}
                {showColumnConfig && (
                  <div>
                    <div className="flex items-center gap-1 mb-2 flex-wrap">
                      <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mr-auto">Column Configuration</p>
                      {showReadinessCols && (
                        <button
                          onClick={() => setDispatchTab('readiness')}
                          className={cn(
                            "px-2.5 py-1 rounded-lg text-[9px] font-bold transition-all border",
                            dispatchTab === 'readiness'
                              ? "bg-slate-800 text-white border-slate-700"
                              : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:bg-zinc-100"
                          )}
                        >Brand Readiness ({readinessColCount})</button>
                      )}
                      {showMenuCols && (
                        <button
                          onClick={() => setDispatchTab('menu')}
                          className={cn(
                            "px-2.5 py-1 rounded-lg text-[9px] font-bold transition-all border",
                            dispatchTab === 'menu'
                              ? "bg-emerald-600 text-white border-emerald-500"
                              : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:bg-zinc-100"
                          )}
                        >Menu Items ({menuColCount})</button>
                      )}
                      {showAuditCols && (
                        <button
                          onClick={() => setDispatchTab('audit')}
                          className={cn(
                            "px-2.5 py-1 rounded-lg text-[9px] font-bold transition-all border",
                            dispatchTab === 'audit'
                              ? "bg-amber-600 text-white border-amber-500"
                              : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:bg-zinc-100"
                          )}
                        >Audit History ({auditColCount})</button>
                      )}
                    </div>

                    {dispatchTab === 'readiness' && showReadinessCols ? (
                      <div className="grid grid-cols-2 gap-1.5">
                        {READINESS_COL_OPTIONS.map(col => {
                          const isOn = dispatchConfig.readinessColumns[col.key];
                          return (
                            <button
                              key={col.key}
                              onClick={() => setDispatchConfig(prev => ({
                                ...prev,
                                readinessColumns: { ...prev.readinessColumns, [col.key]: !prev.readinessColumns[col.key] },
                              }))}
                              className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all",
                                isOn
                                  ? "bg-slate-50 border-slate-300 ring-1 ring-slate-200"
                                  : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60"
                              )}
                            >
                              <div className={cn(
                                "w-4 h-4 rounded-md flex items-center justify-center shrink-0 border transition-all",
                                isOn ? "bg-slate-700 border-slate-700 text-white" : "bg-white border-zinc-300"
                              )}>
                                {isOn && <Check size={9} />}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-zinc-400">{col.icon}</span>
                                <p className="text-[10px] font-bold text-zinc-700">{col.label}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : dispatchTab === 'audit' && showAuditCols ? (
                      <div className="grid grid-cols-2 gap-1.5">
                        {AUDIT_COL_OPTIONS.map(col => {
                          const isOn = dispatchConfig.auditColumns[col.key];
                          return (
                            <button
                              key={col.key}
                              onClick={() => setDispatchConfig(prev => ({
                                ...prev,
                                auditColumns: { ...prev.auditColumns, [col.key]: !prev.auditColumns[col.key] },
                              }))}
                              className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all",
                                isOn
                                  ? "bg-amber-50 border-amber-300 ring-1 ring-amber-200"
                                  : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60"
                              )}
                            >
                              <div className={cn(
                                "w-4 h-4 rounded-md flex items-center justify-center shrink-0 border transition-all",
                                isOn ? "bg-amber-600 border-amber-600 text-white" : "bg-white border-zinc-300"
                              )}>
                                {isOn && <Check size={9} />}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-zinc-400">{col.icon}</span>
                                <p className="text-[10px] font-bold text-zinc-700">{col.label}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : showMenuCols ? (
                      <div className="grid grid-cols-2 gap-1.5">
                        {MENU_COL_OPTIONS.map(col => {
                          const isOn = dispatchConfig.columns[col.key];
                          return (
                            <button
                              key={col.key}
                              onClick={() => setDispatchConfig(prev => ({
                                ...prev,
                                columns: { ...prev.columns, [col.key]: !prev.columns[col.key] },
                              }))}
                              className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all",
                                isOn
                                  ? "bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200"
                                  : "bg-white border-zinc-200 hover:border-zinc-300 opacity-60"
                              )}
                            >
                              <div className={cn(
                                "w-4 h-4 rounded-md flex items-center justify-center shrink-0 border transition-all",
                                isOn ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-zinc-300"
                              )}>
                                {isOn && <Check size={9} />}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-zinc-400">{col.icon}</span>
                                <p className="text-[10px] font-bold text-zinc-700">{col.label}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                )}
                </>)}

                {/* Info bar */}
                <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                  <Info size={12} className="text-slate-500 mt-0.5 shrink-0" />
                  <p className="text-[10px] text-slate-600">
                    {dispatchTemplate === 'full'
                      ? 'Full Package · ZIP with all sheets, audit history, lifecycle chain, AR/EN columns, and images.'
                      : dispatchTemplate === 'summary'
                      ? 'Summary · Standalone .xlsx with Overview, Readiness, and core menu columns only.'
                      : dispatchConfig.includeImages
                      ? 'ZIP package · Excel + /images folder. Filenames match item names.'
                      : 'Standalone .xlsx with your selected columns and brands.'
                    }
                    {dispatchTemplate === 'custom' && ' Unchecked columns are fully removed from the exported file.'}
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50/50 shrink-0">
                <button
                  onClick={() => setDispatchTarget(null)}
                  disabled={generating}
                  className="px-4 py-2 text-[10px] font-bold text-zinc-600 bg-white border border-zinc-200 hover:bg-zinc-100 rounded-xl transition-all disabled:opacity-40"
                >Cancel</button>
                <button
                  onClick={handleGenerateDispatch}
                  disabled={generating || selectedCount === 0}
                  className="flex items-center gap-2 px-4 py-2 text-[10px] font-bold text-white bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 rounded-xl transition-all disabled:opacity-60 shadow-lg shadow-slate-300/50"
                >
                  {generating ? (
                    <><RotateCcw size={11} className="animate-spin" /> Generating·</>
                  ) : (
                    <><Download size={11} /> Generate &amp; Download</>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* -- Launch Confirmation Dialog ----------------------------------- */}
      {launchConfirmTarget && (() => {
        const cr = launchConfirmTarget;
        const missingBrands = cr.brands.filter(b => b.status === 'MISSING');
        const inReviewBrands = cr.brands.filter(b => b.status === 'IN_REVIEW');
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setLaunchConfirmTarget(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-6 py-5 bg-amber-50 border-b border-amber-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center text-white"><AlertTriangle size={20} /></div>
                  <div>
                    <h3 className="text-base font-bold text-zinc-900">Partial Launch Warning</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">"{cr.campaign.name}"</p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 space-y-3">
                <p className="text-sm text-zinc-700">
                  Only <strong>{cr.readyCount}</strong> of <strong>{cr.brands.length}</strong> brand(s) are ready.
                  Launching now will exclude incomplete brands from this deployment.
                </p>
                {missingBrands.length > 0 && (
                  <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-[10px] font-bold text-red-600 uppercase mb-1">Missing ({missingBrands.length})</p>
                    <p className="text-xs text-red-700">{missingBrands.map(b => b.brandName).join(', ')}</p>
                  </div>
                )}
                {inReviewBrands.length > 0 && (
                  <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-[10px] font-bold text-amber-600 uppercase mb-1">Still In Review ({inReviewBrands.length})</p>
                    <p className="text-xs text-amber-700">{inReviewBrands.map(b => `${b.brandName} @ ${b.currentStage ?? 'Review'}`).join(', ')}</p>
                  </div>
                )}
                {(() => {
                  const target = cr.campaign.requiredItemCount ?? 0;
                  if (target <= 0) return null;
                  const partialBrands = cr.brands.filter(b => b.status === 'READY' && b.itemCount < target);
                  if (partialBrands.length === 0) return null;
                  return (
                    <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-[10px] font-bold text-orange-600 uppercase mb-1">Partial Items ({partialBrands.length})</p>
                      <p className="text-xs text-orange-700">
                        {partialBrands.map(b => `${b.brandName} (${b.itemCount}/${target})`).join(', ')}
                      </p>
                    </div>
                  );
                })()}
              </div>
              <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => setLaunchConfirmTarget(null)}
                  className="px-4 py-2 text-xs font-bold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-xl transition-all"
                >Cancel</button>
                <button
                  onClick={() => { setLaunchConfirmTarget(null); handleLaunchAll(cr); }}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-violet-600 hover:bg-violet-700 rounded-xl transition-all"
                >
                  <Rocket size={13} /> Launch {cr.readyCount} Brand{cr.readyCount !== 1 ? 's' : ''} Anyway
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* -- Full Data Review Drawer ----------------------------------------- */}
      {fullDataDrawer && (() => {
        const cr = fullDataDrawer;
        const camp = cr.campaign;
        return (
          <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={() => setFullDataDrawer(null)}>
            <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-zinc-900">Campaign Master Record</h3>
                  <p className="text-xs text-zinc-500">{camp.name} · {cr.readyCount} brand(s) ready</p>
                </div>
                <button onClick={() => setFullDataDrawer(null)} className="p-2 hover:bg-zinc-100 rounded-xl transition-all">
                  <X size={20} className="text-zinc-500" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Campaign Overview */}
                <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-5 grid grid-cols-2 gap-4">
                  <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Campaign</p><p className="text-sm font-bold text-zinc-900 mt-0.5">{camp.name}</p></div>
                  <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Discount</p><p className="text-sm font-bold text-zinc-900 mt-0.5">{camp.discountValue}{camp.discountType === 'PERCENTAGE' ? '%' : ' QAR'}</p></div>
                  <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Period</p><p className="text-sm font-bold text-zinc-900 mt-0.5">{fmtDate(camp.validFrom)} · {fmtDate(camp.validTo)}</p></div>
                  <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Funding</p>
                    <p className="text-sm font-bold text-zinc-900 mt-0.5">{camp.fundingType === 'CO_FUNDED' ? `Co-Funded (${camp.sterlingSharePct ?? 0}% / ${camp.partnerSharePct ?? 0}%)` : camp.fundingType === 'FULLY_FUNDED' ? 'Fully Funded' : 'No Funding'}</p>
                  </div>
                  <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Aggregators</p>
                    <div className="flex flex-wrap gap-1 mt-1">{camp.aggregators.map(a => <span key={a} className="px-2 py-0.5 bg-white/60 rounded-full text-[10px] font-bold text-violet-700 border border-violet-200">{a}</span>)}</div>
                  </div>
                  {camp.importantRequirements && (
                    <div className="col-span-2"><p className="text-[10px] font-bold text-zinc-400 uppercase">Requirements</p><p className="text-sm text-zinc-700 mt-0.5">{camp.importantRequirements}</p></div>
                  )}
                </div>

                {/* Brand Readiness Summary */}
                <div className="space-y-3">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Brand Status</p>
                  <div className="space-y-2">
                    {cr.brands.map(b => (
                      <div key={b.brandId} className={cn("flex items-center gap-3 px-4 py-3 rounded-xl border",
                        b.status === 'READY' && "bg-emerald-50 border-emerald-200",
                        b.status === 'IN_REVIEW' && "bg-amber-50 border-amber-200",
                        b.status === 'MISSING' && "bg-red-50 border-red-200",
                      )}>
                        {b.status === 'READY' ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> : b.status === 'IN_REVIEW' ? <Clock size={14} className="text-amber-600 shrink-0" /> : <AlertCircle size={14} className="text-red-500 shrink-0" />}
                        <span className="text-sm font-bold text-zinc-900 flex-1">{b.brandName}</span>
                        <span className={cn("text-xs font-bold",
                          b.status === 'READY' && "text-emerald-600",
                          b.status === 'IN_REVIEW' && "text-amber-600",
                          b.status === 'MISSING' && "text-red-500",
                        )}>
                          {b.status === 'READY' ? `${b.itemCount} items` : b.status === 'IN_REVIEW' ? `@ ${b.currentStage ?? 'Review'}` : 'No Data'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Full items from all ready brands */}
                <div className="space-y-3">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Approved Items · All Ready Brands ({cr.totalItems})</p>
                  <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-[40px_1fr_1fr_100px_100px] gap-2 px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 text-[9px] font-bold text-zinc-400 uppercase tracking-widest">
                      <span>#</span><span>Brand / Item</span><span>Description</span><span className="text-right">Original</span><span className="text-right">Discounted</span>
                    </div>
                    {(() => {
                      let row = 0;
                      return cr.readySubmissions.flatMap(sub =>
                        getApprovedItems(sub).map(item => {
                          row++;
                          const disc = camp.discountType === 'PERCENTAGE'
                            ? Math.round(item.price * (1 - camp.discountValue / 100) * 100) / 100
                            : Math.max(0, Math.round((item.price - camp.discountValue) * 100) / 100);
                          return (
                            <div key={`${sub.id}-${item.id}`} className="grid grid-cols-[40px_1fr_1fr_100px_100px] gap-2 px-4 py-2.5 items-center border-b border-zinc-100 last:border-b-0 text-xs">
                              <span className="text-[10px] font-bold text-zinc-400">{row}</span>
                              <div className="min-w-0">
                                <p className="font-semibold text-zinc-900 truncate">{item.name}</p>
                                <p className="text-[10px] text-zinc-400">{sub.brandName}</p>
                              </div>
                              <span className="text-zinc-500 truncate">{item.description || '·'}</span>
                              <span className="text-right font-mono text-zinc-600">{item.price.toFixed(2)}</span>
                              <span className="text-right font-mono font-bold text-emerald-700">{disc.toFixed(2)}</span>
                            </div>
                          );
                        })
                      );
                    })()}
                  </div>
                </div>

                {/* Review History across all subs */}
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Review History</p>
                  {cr.readySubmissions.flatMap(sub =>
                    sub.reviewHistory.map((action, i) => (
                      <div key={`${sub.id}-${i}`} className={cn(
                        "flex items-start gap-3 px-3 py-2.5 rounded-lg border text-xs",
                        action.action === 'APPROVE' ? "bg-emerald-50 border-emerald-200 text-emerald-700" : action.action === 'REJECT' ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"
                      )}>
                        {action.action === 'APPROVE' ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : action.action === 'REJECT' ? <XCircle size={14} className="mt-0.5 shrink-0" /> : <Unlock size={14} className="mt-0.5 shrink-0" />}
                        <div>
                          <p className="font-bold">[{sub.brandName}] [{action.departmentName}] {action.action === 'APPROVE' ? 'Approved' : action.action === 'REJECT' ? 'Rejected' : 'Unlocked'} by {action.reviewerName}</p>
                          {action.note && <p className="opacity-80 mt-0.5">{action.note}</p>}
                          <p className="text-zinc-400 mt-0.5">{new Date(action.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* -- Brand Submission Detail Drawer (Golden Record) ------------------ */}
      {brandDetailDrawer && (() => {
        const { campaign: bCamp, submission: bSub } = brandDetailDrawer;
        const bApproved = getApprovedItems(bSub);
        const bTarget = bCamp.requiredItemCount ?? 0;
        const bComparison = bTarget > 0 ? compareTarget(bApproved.length, bTarget) : null;
        const bColors = bComparison ? TARGET_STATUS_COLORS[bComparison.status] : null;

        return (
          <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={() => setBrandDetailDrawer(null)}>
            <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-zinc-900">{bSub.brandName} · Golden Record</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-zinc-500">{bCamp.name}</p>
                    {bComparison && (
                      <span className={cn("px-2 py-0.5 rounded-full text-[9px] font-bold border", bColors!.bg, bColors!.text, bColors!.border)}>
                        Items: {bComparison.label}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => setBrandDetailDrawer(null)} className="p-2 hover:bg-zinc-100 rounded-xl transition-all">
                  <X size={20} className="text-zinc-500" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Target summary */}
                {bComparison && (
                  <div className={cn("flex items-center gap-3 px-4 py-3 rounded-xl border", bColors!.bg, bColors!.border)}>
                    <Hash size={16} className={bColors!.text} />
                    <div>
                      <p className={cn("text-xs font-bold", bColors!.text)}>
                        {bComparison.status === 'PARTIAL' ? 'Incomplete Submission' : bComparison.status === 'OVER_SUBMITTED' ? 'Extra Items Submitted' : 'Complete Submission'}
                      </p>
                      <p className={cn("text-[10px] mt-0.5", bColors!.text.replace('700', '600'))}>
                        {bApproved.length} item(s) submitted vs. {bTarget} target
                      </p>
                    </div>
                  </div>
                )}

                {/* Items list */}
                <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-[40px_1fr_1fr_100px_100px] gap-2 px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 text-[9px] font-bold text-zinc-400 uppercase tracking-widest">
                    <span>#</span><span>Item Name</span><span>Description</span><span className="text-right">Original</span><span className="text-right">Discounted</span>
                  </div>
                  {bApproved.map((item, idx) => {
                    const disc = bCamp.discountType === 'PERCENTAGE'
                      ? Math.round(item.price * (1 - bCamp.discountValue / 100) * 100) / 100
                      : Math.max(0, Math.round((item.price - bCamp.discountValue) * 100) / 100);
                    return (
                      <div key={item.id} className="grid grid-cols-[40px_1fr_1fr_100px_100px] gap-2 px-4 py-3 items-center border-b border-zinc-100 last:border-b-0 text-xs">
                        <span className="text-[10px] font-bold text-zinc-400">{idx + 1}</span>
                        <div className="min-w-0 flex items-center gap-2">
                          {item.imageUrl && <img src={getMediaUrl(item.imageUrl)} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0 border border-zinc-200" />}
                          <p className="font-semibold text-zinc-900 truncate">{item.name}</p>
                        </div>
                        <span className="text-zinc-500 truncate">{item.description || '·'}</span>
                        <span className="text-right font-mono text-zinc-600">{item.price.toFixed(2)}</span>
                        <span className="text-right font-mono font-bold text-emerald-700">{disc.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Submission metadata */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-zinc-50 rounded-xl p-4">
                    <p className="text-[10px] font-bold text-zinc-400 uppercase">Submitted By</p>
                    <p className="text-sm font-bold text-zinc-900 mt-1">{bSub.submitterName}</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">{new Date(bSub.createdAt).toLocaleString()}</p>
                  </div>
                  <div className="bg-zinc-50 rounded-xl p-4">
                    <p className="text-[10px] font-bold text-zinc-400 uppercase">Review Status</p>
                    <p className="text-sm font-bold text-zinc-900 mt-1">{bSub.status}</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">{bSub.reviewHistory.length} review action(s)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* -- Postpone Dialog ------------------------------------------------ */}
      {postponeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setPostponeTarget(null); setPostponeReason(''); setPostponeUntil(''); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
              <div>
                <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><Clock size={18} className="text-orange-500" /> Postpone Launch</h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {postponeTarget.mode === 'campaign'
                    ? `${postponeTarget.campaign.name} · ${(postponeTarget.subs ?? []).filter(s => s.status === SubmissionStatus.DEPLOYMENT_READY).length} brand(s)`
                    : `${postponeTarget.sub?.campaignName} · ${postponeTarget.sub?.brandName}`}
                </p>
              </div>
              <button onClick={() => { setPostponeTarget(null); setPostponeReason(''); setPostponeUntil(''); }} className="p-2 hover:bg-zinc-100 rounded-xl"><X size={18} className="text-zinc-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-zinc-700 mb-1.5 block">Reason for Postponement *</label>
                <textarea
                  value={postponeReason}
                  onChange={e => setPostponeReason(e.target.value)}
                  placeholder="e.g. Launch delayed due to menu image approval pending, aggregator setup incomplete..."
                  rows={3}
                  className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 focus:bg-white resize-none transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-700 mb-1.5 block">Postpone Until (optional)</label>
                <DatePicker
                  value={postponeUntil}
                  onChange={v => setPostponeUntil(v)}
                  placeholder="Select date·"
                />
                <p className="text-[10px] text-zinc-400 mt-1">Leave empty if no target date is known yet.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={() => { setPostponeTarget(null); setPostponeReason(''); setPostponeUntil(''); }}
                className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 rounded-xl transition-all">
                Cancel
              </button>
              <button
                onClick={handlePostpone}
                disabled={!postponeReason.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Clock size={14} /> Postpone
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Live Campaigns Tab · Read-Only Running Monitor -------------------------

function LiveOperationsTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'aggregator', 'live', 'admin');
  const isSuperAdmin = isAdmin;
  const repo = getRepository();
  const toast = useToast();
  const [submissions, setSubmissions] = useState<CampaignSubmission[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [summaryDrawer, setSummaryDrawer] = useState<CampaignSubmission | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [injectionLogTarget, setInjectionLogTarget] = useState<CampaignSubmission | null>(null);

  // -- Search & Filters --
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [aggregatorFilter, setAggregatorFilter] = useState('');
  const [dateFrom, setLiveDateFrom] = useState('');
  const [dateTo, setLiveDateTo] = useState('');

  // -- Quick date presets --
  const applyDatePreset = (preset: 'this_month' | 'last_month' | 'last_year') => {
    const now = new Date();
    let from: Date, to: Date;
    switch (preset) {
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last_month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_year':
        from = new Date(now.getFullYear() - 1, 0, 1);
        to = new Date(now.getFullYear() - 1, 11, 31);
        break;
    }
    setLiveDateFrom(from.toISOString().slice(0, 10));
    setLiveDateTo(to.toISOString().slice(0, 10));
  };

  const load = async () => {
    const [s, c, d, b] = await Promise.all([repo.getCampaignSubmissions(), repo.getCampaigns(), repo.getDepartments(), repo.getBrands()]);

    // Auto-close expired campaigns · if validTo has passed and campaign is still ACTIVE/PLANNED
    const now = Date.now();
    for (const camp of c) {
      if ((camp.status === CampaignStatus.ACTIVE || camp.status === CampaignStatus.PLANNED) && camp.validTo < now) {
        const closed: Campaign = { ...camp, status: CampaignStatus.CLOSED };
        await repo.saveCampaign(closed);
        camp.status = CampaignStatus.CLOSED; // update local ref
        await repo.saveSystemLog({
          id: crypto.randomUUID(),
          userId: 'SYSTEM',
          userName: 'System',
          action: `Auto-closed expired campaign "${camp.name}" · period ended ${fmtDate(camp.validTo)}`,
          timestamp: now,
          moduleId: 'aggregator',
        });
      }
    }

    const isolated = isAdmin ? s : s.filter(sub => sub.submittedBy === user?.id);
    setSubmissions(isolated.filter(sub => sub.status === SubmissionStatus.COMPLETED));
    setCampaigns(c);
    setDepartments(d);
    setBrands(b.filter(br => br.enabledModules?.includes('aggregator')));
  };
  useEffect(() => { load(); const h = (e: any) => { if (import.meta.env.DEV && e && e.type === 'PULSE_UPDATE') console.log('💓 Heartbeat: Data Refreshed (Aggregator) ' + e.type); load(); }; window.addEventListener('PULSE_UPDATE', h); return () => window.removeEventListener('PULSE_UPDATE', h); }, []);

  // Dynamic department stages
  const dynamicStages = (() => {
    const campaignDepts = departments.filter(d => (d.linkedModules ?? []).includes('aggregator'));
    if (campaignDepts.length > 0) return campaignDepts.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(d => d.name);
    return [...DEPARTMENT_STAGES];
  })();

  // Revoke Completion · reset back to IN_REVIEW at first department stage
  const handleRevokeCompletion = async (sub: CampaignSubmission) => {
    if (!user) return;
    setRevoking(sub.id);
    const firstStage = dynamicStages[0] ?? 'F&B';
    const updated: CampaignSubmission = {
      ...sub,
      status: SubmissionStatus.IN_REVIEW,
      currentStage: firstStage,
      reviewHistory: [...sub.reviewHistory, {
        departmentId: '',
        departmentName: 'SYSTEM',
        action: 'UNLOCK' as const,
        reviewerId: user.id,
        reviewerName: user.name,
        note: `Completion revoked · submission returned to ${firstStage} for re-review.`,
        timestamp: Date.now(),
      }],
      updatedAt: Date.now(),
    };
    await repo.saveCampaignSubmission(updated);

    // -- Revoke Hook: Remove injected campaign items from all linked Menu Profiles --
    let menuItemsRemoved = 0;
    try {
      menuItemsRemoved = await revokeCampaignItems(sub.campaignId);
    } catch (err) {
      console.error('Menu revoke cleanup failed:', err);
    }

    await repo.saveSystemLog({
      id: crypto.randomUUID(),
      userId: user.id,
      userName: user.name,
      action: `Revoked completion for "${sub.campaignName}" (${sub.brandName}) · returned to ${firstStage}${menuItemsRemoved > 0 ? ` · ${menuItemsRemoved} menu item(s) removed` : ''}`,
      timestamp: Date.now(),
      moduleId: 'aggregator',
    });
    toast.success('Completion Revoked', `"${sub.campaignName}" (${sub.brandName}) returned to ${firstStage} for re-review.${menuItemsRemoved > 0 ? ` ${menuItemsRemoved} promo item(s) removed from menus.` : ''}`);
    setRevoking(null);
    setSummaryDrawer(null);
    await load();
  };

  const campaignFor = (id: string) => campaigns.find(c => c.id === id);
  const totalItems = submissions.reduce((acc, s) => acc + getApprovedItems(s).length, 0);

  // Days remaining helper
  const daysRemaining = (camp: Campaign) => {
    const diff = Math.ceil((camp.validTo - Date.now()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // -- Filtered live submissions --
  const filteredLive = React.useMemo(() => {
    let result = [...submissions];

    // Text search: campaign name, brand name, submitter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(s =>
        s.campaignName.toLowerCase().includes(q) ||
        s.brandName.toLowerCase().includes(q) ||
        s.submitterName.toLowerCase().includes(q)
      );
    }

    // Brand filter
    if (brandFilter) {
      result = result.filter(s => s.brandId === brandFilter);
    }

    // Aggregator filter
    if (aggregatorFilter) {
      result = result.filter(s => {
        const camp = campaignFor(s.campaignId);
        return camp?.aggregators.includes(aggregatorFilter);
      });
    }

    // Date range (by campaign period)
    if (dateFrom) {
      const from = new Date(dateFrom).getTime();
      result = result.filter(s => {
        const camp = campaignFor(s.campaignId);
        return camp ? camp.validFrom >= from : true;
      });
    }
    if (dateTo) {
      const to = new Date(dateTo).getTime() + 86400000 - 1;
      result = result.filter(s => {
        const camp = campaignFor(s.campaignId);
        return camp ? camp.validTo <= to : true;
      });
    }

    return result;
  }, [submissions, searchQuery, brandFilter, aggregatorFilter, dateFrom, dateTo, campaigns]);

  // -- Unique brands and aggregators for dropdown filters --
  const uniqueBrands = React.useMemo(() => {
    const map = new Map<string, string>();
    submissions.forEach(s => map.set(s.brandId, s.brandName));
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [submissions]);

  const uniqueAggregators = React.useMemo(() => {
    const aggs = new Set<string>();
    submissions.forEach(s => {
      const camp = campaignFor(s.campaignId);
      camp?.aggregators.forEach(a => aggs.add(a));
    });
    return Array.from(aggs).sort();
  }, [submissions, campaigns]);

  // -- Excel Export (Professional Theme) --
  const handleExportExcel = () => {
    if (filteredLive.length === 0) {
      toast.error('No Data', 'No live campaigns to export.');
      return;
    }

    const wb = XLSX.utils.book_new();

    // Count unique campaigns and brands
    const uniqueCampIds = new Set(filteredLive.map(s => s.campaignId));
    const uniqueBrandIds = new Set(filteredLive.map(s => s.brandId));

    // Build overview sheet
    const overviewData = [
      { Field: 'Report', Value: 'Live Campaigns Export' },
      { Field: 'Generated', Value: new Date().toLocaleString() },
      { Field: 'Total Submissions', Value: String(filteredLive.length) },
      { Field: 'Unique Campaigns', Value: String(uniqueCampIds.size) },
      { Field: 'Unique Brands', Value: String(uniqueBrandIds.size) },
      { Field: 'Total Items', Value: String(filteredLive.reduce((acc, s) => acc + getApprovedItems(s).length, 0)) },
      ...(dateFrom ? [{ Field: 'Date From', Value: dateFrom }] : []),
      ...(dateTo ? [{ Field: 'Date To', Value: dateTo }] : []),
      ...(brandFilter ? [{ Field: 'Brand Filter', Value: uniqueBrands.find(b => b.id === brandFilter)?.name ?? brandFilter }] : []),
      ...(aggregatorFilter ? [{ Field: 'Aggregator Filter', Value: aggregatorFilter }] : []),
    ];
    const ws1 = XLSX.utils.json_to_sheet(overviewData);
    ws1['!cols'] = [{ wch: 22 }, { wch: 40 }];
    ProfessionalExcelFormatter.applyOverviewStyle(ws1);
    XLSX.utils.book_append_sheet(wb, ws1, 'Overview');

    // Build main data sheet with all submissions
    const mainColCount = 17;
    const mainData = filteredLive.map((sub, idx) => {
      const camp = campaignFor(sub.campaignId);
      const approvedItems = getApprovedItems(sub);
      const days = camp ? daysRemaining(camp) : 0;
      const allAggs = camp?.aggregators ?? [];
      const liveAggs = sub.liveAggregators ?? [];
      const pendingAggs = allAggs.filter(a => !liveAggs.includes(a));
      const allLive = allAggs.length > 0 && allAggs.every(a => liveAggs.includes(a));
      return {
        '#': idx + 1,
        'Campaign': sub.campaignName,
        'Brand': sub.brandName,
        'Manager': sub.submitterName,
        'All Aggregators': allAggs.join(', '),
        'Live Aggregators': liveAggs.join(', '),
        'Pending Aggregators': pendingAggs.join(', '),
        'Aggregator Status': allLive ? 'ALL LIVE' : liveAggs.length > 0 ? `${liveAggs.length}/${allAggs.length} LIVE` : 'LIVE',
        'Approved Items': approvedItems.length,
        'Period Start': camp ? fmtDate(camp.validFrom) : '',
        'Period End': camp ? fmtDate(camp.validTo) : '',
        'Days Remaining': days,
        'Discount': camp ? (camp.discountType === DiscountType.PERCENTAGE ? `${camp.discountValue}%` : `QAR ${camp.discountValue}`) : '',
        'Funding': camp ? (camp.fundingType === FundingType.CO_FUNDED ? `Co-Funded (${camp.sterlingSharePct ?? 0}/${camp.partnerSharePct ?? 0})` : camp.fundingType === FundingType.FULLY_FUNDED ? 'Fully Funded' : 'None') : '',
        'Submitted': new Date(sub.createdAt).toLocaleDateString('en-GB'),
        'Launched': new Date(sub.updatedAt).toLocaleDateString('en-GB'),
        'Status': 'LIVE',
      };
    });
    const ws2 = XLSX.utils.json_to_sheet(mainData);
    ws2['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws2);
    ProfessionalExcelFormatter.applyHeaderRow(ws2, mainColCount);
    ProfessionalExcelFormatter.applyDataBorders(ws2);
    ws2['!autofilter'] = { ref: `A1:${String.fromCharCode(64 + mainColCount)}${mainData.length + 1}` };
    XLSX.utils.book_append_sheet(wb, ws2, 'Live Campaigns');

    // Items detail sheet · one row per item
    const itemColCount = 11;
    const itemRows: Record<string, unknown>[] = [];
    let itemIdx = 1;
    filteredLive.forEach(sub => {
      const camp = campaignFor(sub.campaignId);
      const liveAggs = sub.liveAggregators ?? [];
      getApprovedItems(sub).forEach(item => {
        const discountedPrice = camp
          ? camp.discountType === DiscountType.PERCENTAGE
            ? Math.round(item.price * (1 - camp.discountValue / 100) * 100) / 100
            : Math.max(0, Math.round((item.price - camp.discountValue) * 100) / 100)
          : item.price;
        itemRows.push({
          '#': itemIdx++,
          'Campaign': sub.campaignName,
          'Brand': sub.brandName,
          'Manager': sub.submitterName,
          'Item Name': item.name,
          'Description': item.description ?? '',
          'Original Price (QAR)': item.price,
          'Discounted Price (QAR)': discountedPrice,
          'Period': camp ? `${fmtDate(camp.validFrom)} · ${fmtDate(camp.validTo)}` : '',
          'Live On': liveAggs.join(', '),
          'Item Status': item.itemStatus ?? 'ACTIVE',
        });
      });
    });
    if (itemRows.length > 0) {
      const ws3 = XLSX.utils.json_to_sheet(itemRows);
      ws3['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws3);
      ProfessionalExcelFormatter.applyHeaderRow(ws3, itemColCount);
      ProfessionalExcelFormatter.applyDataBorders(ws3);
      ws3['!autofilter'] = { ref: `A1:K${itemRows.length + 1}` };
      XLSX.utils.book_append_sheet(wb, ws3, 'Menu Items');
    }

    // Aggregator summary sheet · aggregator-level breakdown
    const aggSummaryMap: Record<string, { live: number; pending: number; brands: Set<string>; items: number }> = {};
    filteredLive.forEach(sub => {
      const camp = campaignFor(sub.campaignId);
      const liveAggs = sub.liveAggregators ?? [];
      (camp?.aggregators ?? []).forEach(agg => {
        if (!aggSummaryMap[agg]) aggSummaryMap[agg] = { live: 0, pending: 0, brands: new Set(), items: 0 };
        aggSummaryMap[agg].brands.add(sub.brandName);
        aggSummaryMap[agg].items += getApprovedItems(sub).length;
        if (liveAggs.includes(agg)) aggSummaryMap[agg].live++; else aggSummaryMap[agg].pending++;
      });
    });
    const aggSummaryRows = Object.entries(aggSummaryMap).sort((a, b) => a[0].localeCompare(b[0])).map(([agg, d], i) => ({
      '#': i + 1,
      'Aggregator': agg,
      'Live Brands': d.live,
      'Pending Brands': d.pending,
      'Total Brands': d.brands.size,
      'Total Items': d.items,
      'Status': d.pending === 0 ? 'ALL LIVE' : `${d.live}/${d.live + d.pending} LIVE`,
    }));
    if (aggSummaryRows.length > 0) {
      const ws4 = XLSX.utils.json_to_sheet(aggSummaryRows);
      ws4['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws4);
      ProfessionalExcelFormatter.applyHeaderRow(ws4, 7);
      ProfessionalExcelFormatter.applyDataBorders(ws4);
      XLSX.utils.book_append_sheet(wb, ws4, 'Aggregator Summary');
    }

    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    XLSX.writeFile(wb, `Live_Campaigns_${dateStr}.xlsx`);
    toast.success('Excel Exported', `${filteredLive.length} live campaigns exported successfully.`);
  };

  return (
    <div className="space-y-0">
      {/* -- Header -- */}
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Live Campaigns</h2>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin ? 'Read-only monitor · campaigns currently active in the field.' : 'Your campaigns currently running live.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs font-bold text-emerald-700">
            <Radio size={14} className="animate-pulse" /> {filteredLive.length} Live
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-xl text-xs font-bold text-teal-700">
            <Package size={14} /> {filteredLive.reduce((acc, s) => acc + getApprovedItems(s).length, 0)} Items
          </div>
          {isAdmin && (
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-all"
            >
              <Download size={14} /> Export Excel
            </button>
          )}
        </div>
      </div>

      {/* -- Search & Filter Utility Bar -- */}
      <div className="bg-white border border-zinc-200 rounded-xl px-4 py-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Global Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search campaign, brand, or manager·"
              className="w-full pl-9 pr-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-zinc-400 focus:bg-white transition-all"
            />
          </div>

          {/* Brand Filter */}
          <div className="flex items-center gap-1.5">
            <Building2 size={13} className="text-zinc-400" />
            <CustomSelect value={brandFilter} onChange={v => setBrandFilter(v)} placeholder="All Brands"
              options={[{ value: '', label: 'All Brands' }, ...uniqueBrands.map(b => ({ value: b.id, label: b.name }))]} />
          </div>

          {/* Aggregator Filter */}
          <div className="flex items-center gap-1.5">
            <Globe size={13} className="text-zinc-400" />
            <CustomSelect value={aggregatorFilter} onChange={v => setAggregatorFilter(v)} placeholder="All Aggregators"
              options={[{ value: '', label: 'All Aggregators' }, ...uniqueAggregators.map(a => ({ value: a, label: a }))]} />
          </div>
        </div>

        {/* Date Range + Quick Presets */}
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-zinc-100">
          <div className="flex items-center gap-2">
            <Calendar size={13} className="text-zinc-400 shrink-0" />
            <DatePicker value={dateFrom} onChange={v => setLiveDateFrom(v)}
              placeholder="From date" />
            <span className="text-xs text-zinc-400">to</span>
            <DatePicker value={dateTo} onChange={v => setLiveDateTo(v)}
              placeholder="To date" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mr-1">Quick</span>
            {([
              { key: 'this_month' as const, label: 'This Month' },
              { key: 'last_month' as const, label: 'Last Month' },
              { key: 'last_year' as const,  label: 'Last Year' },
            ]).map(p => (
              <button key={p.key} onClick={() => applyDatePreset(p.key)}
                className="px-2.5 py-1 bg-zinc-100 border border-zinc-200 rounded-lg text-[10px] font-semibold text-zinc-600 hover:bg-zinc-200 hover:text-zinc-800 transition-all">
                {p.label}
              </button>
            ))}
            {(dateFrom || dateTo) && (
              <button onClick={() => { setLiveDateFrom(''); setLiveDateTo(''); }}
                className="px-2 py-1 text-[10px] font-bold text-red-500 hover:bg-red-50 rounded-lg transition-all">
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* -- Live Submissions Table -- */}
      {filteredLive.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
          <Radio size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm font-semibold text-zinc-400">
            {submissions.length === 0
              ? 'No live campaigns'
              : 'No campaigns match your filters'
            }
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            {submissions.length === 0
              ? 'Campaigns will appear here after they are launched from Launch Readiness.'
              : 'Try adjusting your search or filter criteria.'
            }
          </p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Campaign / Brand</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Manager</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Aggregators</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest text-center">Items</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Period</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest text-center">Remaining</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest text-center">Status</th>
                <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filteredLive.map(sub => {
                const camp = campaignFor(sub.campaignId);
                const days = camp ? daysRemaining(camp) : 0;
                return (
                  <tr key={sub.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center text-white shrink-0"><Radio size={14} /></div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-zinc-900 truncate">{sub.campaignName}</p>
                          <span className="flex items-center gap-1 text-[11px] text-zinc-400"><Building2 size={10} /> {sub.brandName}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-zinc-600">{sub.submitterName}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(camp?.aggregators ?? []).map(agg => {
                          const isLive = (sub.liveAggregators ?? []).includes(agg);
                          return (
                            <span key={agg} className={cn(
                              "px-2 py-0.5 rounded-full text-[9px] font-bold border truncate max-w-[100px]",
                              isLive
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                            )} title={`${agg} · ${isLive ? 'LIVE' : 'PENDING'}`}>
                              <span className={cn("inline-block w-1.5 h-1.5 rounded-full mr-1", isLive ? "bg-emerald-500" : "bg-amber-500")} />
                              {agg}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-bold text-zinc-900">{getApprovedItems(sub).length}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-[11px] text-zinc-600 whitespace-nowrap">{camp ? `${fmtDate(camp.validFrom)} · ${fmtDate(camp.validTo)}` : '·'}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {camp && (
                        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border",
                          days > 7 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : days > 0 ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-red-50 text-red-600 border-red-200"
                        )}>
                          {days > 0 ? `${days}d` : 'Ended'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const allAggs = camp?.aggregators ?? [];
                        const liveAggs = sub.liveAggregators ?? [];
                        const allLive = allAggs.length > 0 && allAggs.every(a => liveAggs.includes(a));
                        const partialLive = liveAggs.length > 0 && !allLive;
                        return (
                          <span className={cn("px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border",
                            allLive ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : partialLive ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          )}>
                            <span className={cn("inline-block w-1.5 h-1.5 rounded-full mr-1", allLive ? "bg-emerald-500 animate-pulse" : partialLive ? "bg-amber-500 animate-pulse" : "bg-emerald-500 animate-pulse")} />
                            {allLive ? 'RUNNING' : partialLive ? `${liveAggs.length}/${allAggs.length} RUNNING` : 'RUNNING'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setInjectionLogTarget(sub)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg text-[11px] font-bold hover:bg-violet-100 transition-all">
                          <History size={12} /> Injection Log
                        </button>
                        <button onClick={() => setSummaryDrawer(sub)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-lg text-[11px] font-bold hover:bg-zinc-200 transition-all">
                          <Eye size={12} /> Summary
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* -- Summary Drawer (Read-Only) -------------------------------------- */}
      {summaryDrawer && (() => {
        const camp = campaignFor(summaryDrawer.campaignId);
        return (
          <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={() => setSummaryDrawer(null)}>
            <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                    <Radio size={18} className="text-emerald-600" /> Live Campaign Summary
                  </h3>
                  <p className="text-xs text-zinc-500">{summaryDrawer.campaignName} · {summaryDrawer.brandName}</p>
                </div>
                <button onClick={() => setSummaryDrawer(null)} className="p-2 hover:bg-zinc-100 rounded-xl transition-all">
                  <X size={20} className="text-zinc-500" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Campaign Info */}
                {camp && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 grid grid-cols-2 gap-4">
                    <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Campaign</p><p className="text-sm font-bold text-zinc-900 mt-0.5">{camp.name}</p></div>
                    <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Brand</p><p className="text-sm font-bold text-zinc-900 mt-0.5">{summaryDrawer.brandName}</p></div>
                    <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Period</p><p className="text-sm font-bold text-zinc-900 mt-0.5">{fmtDate(camp.validFrom)} · {fmtDate(camp.validTo)}</p></div>
                    <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Discount</p><p className="text-sm font-bold text-zinc-900 mt-0.5">{camp.discountValue}{camp.discountType === 'PERCENTAGE' ? '%' : ' QAR'}</p></div>
                    <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Aggregators</p>
                      <div className="flex flex-wrap gap-1 mt-1">{camp.aggregators.map(a => {
                        const isLive = (summaryDrawer.liveAggregators ?? []).includes(a);
                        return (
                          <span key={a} className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold border",
                            isLive ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"
                          )}>
                            <span className={cn("inline-block w-1.5 h-1.5 rounded-full mr-1", isLive ? "bg-emerald-500" : "bg-amber-400")} />{a}
                          </span>
                        );
                      })}</div>
                    </div>
                    <div><p className="text-[10px] font-bold text-zinc-400 uppercase">Submitted By</p><p className="text-sm font-bold text-zinc-900 mt-0.5">{summaryDrawer.submitterName}</p></div>
                  </div>
                )}

                {/* Items */}
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Approved Items ({getApprovedItems(summaryDrawer).length})</p>
                  <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                    {getApprovedItems(summaryDrawer).map((item, idx) => (
                      <div key={item.id} className={cn("flex items-center gap-3 px-4 py-3", idx > 0 && "border-t border-zinc-100")}>
                        <CheckCircle2 size={14} className="text-teal-600 shrink-0" />
                        {item.imageUrl ? (
                          <img src={getMediaUrl(item.imageUrl)} alt={item.name} className="w-10 h-10 rounded-lg object-cover border border-zinc-200 shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0"><ImageIcon size={14} className="text-zinc-400" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-zinc-900 truncate">{item.name}</p>
                          {item.description && <p className="text-[11px] text-zinc-400 truncate">{item.description}</p>}
                        </div>
                        <p className="text-sm font-bold text-zinc-900 shrink-0">QAR {item.price.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Review History */}
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Review History</p>
                  {summaryDrawer.reviewHistory.map((action, i) => (
                    <div key={i} className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg border text-xs",
                      action.action === 'APPROVE' ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"
                    )}>
                      {action.action === 'APPROVE' ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                      <span className="font-bold">[{action.departmentName}]</span>
                      <span>{action.reviewerName}</span>
                      <span className="ml-auto text-zinc-400 text-[10px]">{new Date(action.timestamp).toLocaleString()}</span>
                    </div>
                  ))}
                </div>

                {/* Revoke Completion · Admin Only */}
                {isAdmin && (
                  <div className="border-t border-zinc-200 pt-4">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="text-xs font-bold text-red-700 mb-1">Revoke Completion</p>
                      <p className="text-[10px] text-red-600 mb-3">This will reset the submission back to the first review stage. All departments must re-approve.</p>
                      <button
                        onClick={() => handleRevokeCompletion(summaryDrawer)}
                        disabled={revoking === summaryDrawer.id}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 disabled:opacity-50 transition-all"
                      >
                        {revoking === summaryDrawer.id ? <RotateCcw size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                        {revoking === summaryDrawer.id ? 'Revoking·' : 'Revoke & Return to Review'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* -- Injection Log Popup ----------------------------------------- */}
      {injectionLogTarget && (() => {
        const sub = injectionLogTarget;
        const deployActions = (sub.reviewHistory ?? []).filter(r => r.departmentName === 'DEPLOYMENT');
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setInjectionLogTarget(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 bg-gradient-to-r from-violet-600 to-violet-700 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-white"><History size={18} /></div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Injection Log</h3>
                    <p className="text-[10px] text-violet-200 mt-0.5">{sub.campaignName} · {sub.brandName}</p>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {deployActions.length === 0 ? (
                  <div className="text-center py-8">
                    <History size={28} className="mx-auto text-zinc-300 mb-2" />
                    <p className="text-sm text-zinc-400">No deployment events recorded.</p>
                  </div>
                ) : (
                  deployActions.map((action, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-xl">
                      <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                        <Rocket size={13} className="text-emerald-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold text-zinc-900">{action.note || 'Deployment action'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-zinc-500">{action.reviewerName}</span>
                          <span className="text-[10px] text-zinc-300">·</span>
                          <span className="text-[10px] text-zinc-400">{fmtDate(action.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {(sub.liveAggregators ?? []).length > 0 && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl mt-4">
                    <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-widest mb-2">Live Aggregators</p>
                    <div className="flex flex-wrap gap-1.5">
                      {sub.liveAggregators!.map(agg => (
                        <span key={agg} className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold border border-emerald-200">
                          ?? {agg}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="px-5 py-3 border-t border-zinc-200 flex justify-end bg-zinc-50/50 shrink-0">
                <button onClick={() => setInjectionLogTarget(null)}
                  className="px-4 py-2 text-[11px] font-bold text-zinc-600 bg-white border border-zinc-200 hover:bg-zinc-100 rounded-xl transition-all">
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// --- Analytics Dashboard Tab -------------------------------------------------

function AnalyticsDashboardTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'aggregator', 'analytics', 'admin');
  const isSuperAdmin = isAdmin;
  const repo = getRepository();
  const toast = useToast();
  const [submissions, setSubmissions] = useState<CampaignSubmission[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [analyticsTab, setAnalyticsTab] = usePersistentState<'brand' | 'aggregator' | 'campaign' | 'status' | 'full' | 'bi-export'>('view', 'brand');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = usePersistentState('search');
  const [brandFilter, setBrandFilter] = useState('');
  const [aggregatorFilter, setAggregatorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    Promise.all([repo.getCampaignSubmissions(), repo.getCampaigns(), repo.getBrands()]).then(([s, c, b]) => {
      const isolated = isAdmin ? s : s.filter(sub => sub.submittedBy === user?.id);
      setSubmissions(isolated);
      setCampaigns(c);
      setBrands(b.filter(br => br.enabledModules?.includes('aggregator')));
    });
  }, []);

  const campaignFor = (id: string) => campaigns.find(c => c.id === id);

  const filtered = React.useMemo(() => {
    let list = submissions;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        s.campaignName.toLowerCase().includes(q) || s.brandName.toLowerCase().includes(q) || s.submitterName?.toLowerCase().includes(q)
      );
    }
    if (brandFilter) list = list.filter(s => s.brandId === brandFilter);
    if (aggregatorFilter) {
      list = list.filter(s => {
        const camp = campaignFor(s.campaignId);
        return camp?.aggregators.includes(aggregatorFilter);
      });
    }
    if (statusFilter) list = list.filter(s => s.status === statusFilter);
    return list;
  }, [submissions, searchQuery, brandFilter, aggregatorFilter, statusFilter, campaigns]);

  const uniqueBrands = React.useMemo(() => {
    const seen = new Map<string, string>();
    submissions.forEach(s => seen.set(s.brandId, s.brandName));
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [submissions]);

  const uniqueAggregators = React.useMemo(() => {
    const aggs = new Set<string>();
    submissions.forEach(s => {
      const camp = campaignFor(s.campaignId);
      camp?.aggregators.forEach(a => aggs.add(a));
    });
    return Array.from(aggs).sort();
  }, [submissions, campaigns]);

  // -- Analytics by Brand --
  const brandAnalytics = React.useMemo(() => {
    const map = new Map<string, { brandId: string; brandName: string; subs: CampaignSubmission[]; campaignIds: Set<string>; aggs: Set<string>; items: number }>();
    filtered.forEach(sub => {
      const existing = map.get(sub.brandId);
      const e = existing ?? { brandId: sub.brandId, brandName: sub.brandName, subs: [] as CampaignSubmission[], campaignIds: new Set<string>(), aggs: new Set<string>(), items: 0 };
      e.subs.push(sub);
      e.campaignIds.add(sub.campaignId);
      const camp = campaignFor(sub.campaignId);
      if (camp) camp.aggregators.forEach(a => e.aggs.add(a));
      e.items += getApprovedItems(sub).length;
      map.set(sub.brandId, e);
    });
    return Array.from(map.values()).sort((a, b) => b.items - a.items);
  }, [filtered, campaigns]);

  // -- Analytics by Aggregator --
  const aggregatorAnalytics = React.useMemo(() => {
    const map = new Map<string, { aggregator: string; campaignIds: Set<string>; brandIds: Set<string>; items: number; subs: CampaignSubmission[] }>();
    filtered.forEach(sub => {
      const camp = campaignFor(sub.campaignId);
      (camp?.aggregators ?? []).forEach(agg => {
        const existing = map.get(agg);
        const e = existing ?? { aggregator: agg, campaignIds: new Set<string>(), brandIds: new Set<string>(), items: 0, subs: [] as CampaignSubmission[] };
        e.campaignIds.add(sub.campaignId);
        e.brandIds.add(sub.brandId);
        e.items += getApprovedItems(sub).length;
        if (!existing) e.subs.push(sub); else if (!e.subs.find(s => s.id === sub.id)) e.subs.push(sub);
        map.set(agg, e);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.items - a.items);
  }, [filtered, campaigns]);

  // -- Analytics by Campaign --
  const campaignAnalyticsData = React.useMemo(() => {
    const map = new Map<string, { campaignId: string; campaignName: string; camp: Campaign | undefined; brandNames: Map<string, string>; items: number; subs: CampaignSubmission[] }>();
    filtered.forEach(sub => {
      const existing = map.get(sub.campaignId);
      const e = existing ?? { campaignId: sub.campaignId, campaignName: sub.campaignName, camp: campaignFor(sub.campaignId), brandNames: new Map<string, string>(), items: 0, subs: [] as CampaignSubmission[] };
      e.brandNames.set(sub.brandId, sub.brandName);
      e.items += getApprovedItems(sub).length;
      e.subs.push(sub);
      map.set(sub.campaignId, e);
    });
    return Array.from(map.values()).sort((a, b) => b.items - a.items);
  }, [filtered, campaigns]);

  // -- Analytics by Status --
  const statusAnalytics = React.useMemo(() => {
    const statusMap: Record<string, { status: string; label: string; color: string; subs: CampaignSubmission[]; items: number }> = {};
    const statusLabels: Record<string, { label: string; color: string }> = {
      [SubmissionStatus.PENDING]: { label: 'Pending', color: 'bg-amber-500' },
      [SubmissionStatus.IN_REVIEW]: { label: 'In Review', color: 'bg-blue-500' },
      [SubmissionStatus.APPROVED]: { label: 'Approved', color: 'bg-emerald-500' },
      [SubmissionStatus.DEPLOYMENT_READY]: { label: 'Deployment Ready', color: 'bg-cyan-500' },
      [SubmissionStatus.COMPLETED]: { label: 'Completed / Live', color: 'bg-green-600' },
      [SubmissionStatus.POSTPONED]: { label: 'Postponed', color: 'bg-orange-500' },
      [SubmissionStatus.REJECTED]: { label: 'Rejected', color: 'bg-red-500' },
    };
    filtered.forEach(sub => {
      if (!statusMap[sub.status]) {
        const meta = statusLabels[sub.status] ?? { label: sub.status, color: 'bg-zinc-400' };
        statusMap[sub.status] = { status: sub.status, label: meta.label, color: meta.color, subs: [], items: 0 };
      }
      statusMap[sub.status].subs.push(sub);
      statusMap[sub.status].items += getApprovedItems(sub).length;
    });
    return Object.values(statusMap).sort((a, b) => b.subs.length - a.subs.length);
  }, [filtered]);

  // -- Global summary stats --
  const totalItems = React.useMemo(() => filtered.reduce((a, s) => a + getApprovedItems(s).length, 0), [filtered]);

  // -- Export --
  const handleExportAnalytics = () => {
    if (filtered.length === 0) { toast.error('No Data', 'No data to export.'); return; }
    const wb = XLSX.utils.book_new();
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const viewLbl = analyticsTab.charAt(0).toUpperCase() + analyticsTab.slice(1);
    const genDate = new Date().toLocaleString();

    // -- Compute spend by campaign (reused by multiple exports) --
    const spendByCampaign = campaignAnalyticsData.map(c => {
      let orig = 0;
      c.subs.forEach(sub => { getApprovedItems(sub).forEach(it => { orig += it.price; }); });
      return { label: c.campaignName, value: Math.round(orig), pct: '' };
    }).sort((a, b) => b.value - a.value);
    const spendTotal = spendByCampaign.reduce((s, x) => s + x.value, 0) || 1;
    spendByCampaign.forEach(s => { s.pct = `${((s.value / spendTotal) * 100).toFixed(1)}%`; });

    // -- Weekly trend data for sparklines --
    const weeklyMap = new Map<string, number>();
    filtered.forEach(sub => {
      const d = new Date(sub.createdAt);
      const wk = new Date(d); wk.setDate(d.getDate() - d.getDay());
      const key = wk.toISOString().slice(0, 10);
      weeklyMap.set(key, (weeklyMap.get(key) ?? 0) + 1);
    });
    const weeklyTrend = Array.from(weeklyMap.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([, v]) => v);

    if (analyticsTab === 'brand') {
      // -- Brand Summary · Visual First Layout --
      const colCount = 12;
      const rows = brandAnalytics.map((b, i) => {
        const statuses: Record<string, number> = {};
        b.subs.forEach(s => { statuses[s.status] = (statuses[s.status] ?? 0) + 1; });
        const brandWeekly = new Map<string, number>();
        b.subs.forEach(sub => {
          const d = new Date(sub.createdAt);
          const wk = new Date(d); wk.setDate(d.getDate() - d.getDay());
          const key = wk.toISOString().slice(0, 10);
          brandWeekly.set(key, (brandWeekly.get(key) ?? 0) + 1);
        });
        const trend = Array.from(brandWeekly.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([, v]) => v);
        return {
          '#': i + 1, Brand: b.brandName, Campaigns: b.campaignIds.size, Submissions: b.subs.length, 'Total Items': b.items,
          Aggregators: Array.from(b.aggs).join(', '),
          'Live': statuses[SubmissionStatus.COMPLETED] ?? 0,
          'In Review': statuses[SubmissionStatus.IN_REVIEW] ?? 0,
          'Approved': statuses[SubmissionStatus.APPROVED] ?? 0,
          'Postponed': statuses[SubmissionStatus.POSTPONED] ?? 0,
          'Rejected': statuses[SubmissionStatus.REJECTED] ?? 0,
          'Trend': ProfessionalExcelFormatter.sparkline(trend.length > 0 ? trend : weeklyTrend),
        };
      });

      // Build sheet with visual dashboard header
      const ws = XLSX.utils.aoa_to_sheet([[]]);
      ws['!ref'] = 'A1';
      const dataStart = ProfessionalExcelFormatter.applyDashboardHeader(ws, colCount,
        '??  BRAND ANALYTICS REPORT',
        `Generated ${genDate} · ${filtered.length} submissions · ${totalItems} items`,
        [
          { label: 'Total Brands', value: String(brandAnalytics.length) },
          { label: 'Total Submissions', value: String(filtered.length) },
          { label: 'Total Items', value: String(totalItems) },
          { label: 'Avg Items/Brand', value: brandAnalytics.length > 0 ? (totalItems / brandAnalytics.length).toFixed(1) : '0' },
        ],
      );

      // Spend distribution legend
      const pieEnd = ProfessionalExcelFormatter.applySpendPieLegend(ws, dataStart, spendByCampaign, colCount);

      // Bar chart · all brands
      const brandBars = brandAnalytics.sort((a, b) => b.items - a.items).map(b => ({ label: b.brandName, value: b.items }));
      const barEnd = ProfessionalExcelFormatter.applyBarChart(ws, pieEnd, 'ALL BRANDS BY ITEMS', brandBars, colCount);

      // Now add data table starting at barEnd
      const headerRow = barEnd;
      const headers = ['#', 'Brand', 'Campaigns', 'Submissions', 'Total Items', 'Aggregators', 'Live', 'In Review', 'Approved', 'Postponed', 'Rejected', 'Trend'];
      headers.forEach((h, c) => {
        const addr = XLSX.utils.encode_cell({ r: headerRow, c });
        ws[addr] = { v: h, t: 's' };
      });
      rows.forEach((row, ri) => {
        const vals = [row['#'], row.Brand, row.Campaigns, row.Submissions, row['Total Items'], row.Aggregators, row.Live, row['In Review'], row.Approved, row.Postponed, row.Rejected, row.Trend];
        vals.forEach((v, c) => {
          const addr = XLSX.utils.encode_cell({ r: headerRow + 1 + ri, c });
          ws[addr] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
        });
      });
      // Update range
      ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: headerRow + rows.length, c: colCount - 1 } });
      ws['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws);
      ProfessionalExcelFormatter.applyCardTable(ws, colCount, headerRow, headerRow + 1);
      XLSX.utils.book_append_sheet(wb, ws, 'Brand Summary');
      XLSX.writeFile(wb, `Analytics_Brand_${dateStr}.xlsx`);

    } else if (analyticsTab === 'aggregator') {
      // -- Aggregator Summary · Visual First Layout --
      const colCount = 9;
      const rows = aggregatorAnalytics.map((a, i) => {
        const liveSubs = a.subs?.filter((s: CampaignSubmission) => (s.liveAggregators ?? []).includes(a.aggregator)).length ?? 0;
        const aggWeekly = new Map<string, number>();
        (a.subs ?? []).forEach((sub: CampaignSubmission) => {
          const d = new Date(sub.createdAt);
          const wk = new Date(d); wk.setDate(d.getDate() - d.getDay());
          const key = wk.toISOString().slice(0, 10);
          aggWeekly.set(key, (aggWeekly.get(key) ?? 0) + 1);
        });
        const trend = Array.from(aggWeekly.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([, v]) => v);
        return {
          '#': i + 1, Aggregator: a.aggregator, Campaigns: a.campaignIds.size, Brands: a.brandIds.size,
          Submissions: a.subs?.length ?? 0, 'Total Items': a.items,
          'Live Brands': liveSubs,
          'Pending Brands': (a.subs?.length ?? 0) - liveSubs,
          'Trend': ProfessionalExcelFormatter.sparkline(trend.length > 0 ? trend : weeklyTrend),
        };
      });

      const ws = XLSX.utils.aoa_to_sheet([[]]);
      ws['!ref'] = 'A1';
      const dataStart = ProfessionalExcelFormatter.applyDashboardHeader(ws, colCount,
        '??  AGGREGATOR ANALYTICS REPORT',
        `Generated ${genDate} · ${aggregatorAnalytics.length} aggregators`,
        [
          { label: 'Total Aggregators', value: String(aggregatorAnalytics.length) },
          { label: 'Total Brands', value: String(new Set(filtered.map(s => s.brandId)).size) },
          { label: 'Total Items', value: String(totalItems) },
          { label: 'Submissions', value: String(filtered.length) },
        ],
      );

      const aggBars = aggregatorAnalytics.sort((a, b) => b.items - a.items).map(a => ({ label: a.aggregator, value: a.items }));
      const barEnd = ProfessionalExcelFormatter.applyBarChart(ws, dataStart, 'ALL AGGREGATORS BY ITEMS', aggBars, colCount);

      const headerRow = barEnd;
      const headers = ['#', 'Aggregator', 'Campaigns', 'Brands', 'Submissions', 'Total Items', 'Live Brands', 'Pending Brands', 'Trend'];
      headers.forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: headerRow, c })] = { v: h, t: 's' }; });
      rows.forEach((row, ri) => {
        const vals = [row['#'], row.Aggregator, row.Campaigns, row.Brands, row.Submissions, row['Total Items'], row['Live Brands'], row['Pending Brands'], row.Trend];
        vals.forEach((v, c) => {
          ws[XLSX.utils.encode_cell({ r: headerRow + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
        });
      });
      ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: headerRow + rows.length, c: colCount - 1 } });
      ws['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws);
      ProfessionalExcelFormatter.applyCardTable(ws, colCount, headerRow, headerRow + 1);
      XLSX.utils.book_append_sheet(wb, ws, 'Aggregator Summary');
      XLSX.writeFile(wb, `Analytics_Aggregator_${dateStr}.xlsx`);

    } else if (analyticsTab === 'campaign') {
      // -- Campaign · Visual First + multi-sheet --
      const colCount = 14;
      const rows = campaignAnalyticsData.map((c, i) => {
        const statusBreakdown: Record<string, number> = {};
        c.subs.forEach(s => { statusBreakdown[s.status] = (statusBreakdown[s.status] ?? 0) + 1; });
        const campWeekly = new Map<string, number>();
        c.subs.forEach((sub: CampaignSubmission) => {
          const d = new Date(sub.createdAt);
          const wk = new Date(d); wk.setDate(d.getDate() - d.getDay());
          const key = wk.toISOString().slice(0, 10);
          campWeekly.set(key, (campWeekly.get(key) ?? 0) + 1);
        });
        const trend = Array.from(campWeekly.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([, v]) => v);
        return {
          '#': i + 1, Campaign: c.campaignName, Brands: c.brandNames.size, Submissions: c.subs.length, 'Total Items': c.items,
          Discount: c.camp ? (c.camp.discountType === DiscountType.PERCENTAGE ? `${c.camp.discountValue}%` : `QAR ${c.camp.discountValue}`) : '',
          Funding: c.camp ? (c.camp.fundingType === FundingType.CO_FUNDED ? `Co-Funded (${c.camp.sterlingSharePct ?? 0}/${c.camp.partnerSharePct ?? 0})` : c.camp.fundingType === FundingType.FULLY_FUNDED ? 'Fully Funded' : 'None') : '',
          Period: c.camp ? `${fmtDate(c.camp.validFrom)} · ${fmtDate(c.camp.validTo)}` : '',
          'Days Remaining': c.camp ? Math.max(0, Math.ceil((c.camp.validTo - Date.now()) / 86400000)) : 0,
          Aggregators: c.camp?.aggregators.join(', ') ?? '',
          'Live': statusBreakdown[SubmissionStatus.COMPLETED] ?? 0,
          'Postponed': statusBreakdown[SubmissionStatus.POSTPONED] ?? 0,
          'Rejected': statusBreakdown[SubmissionStatus.REJECTED] ?? 0,
          'Trend': ProfessionalExcelFormatter.sparkline(trend.length > 0 ? trend : weeklyTrend),
        };
      });

      const ws = XLSX.utils.aoa_to_sheet([[]]);
      ws['!ref'] = 'A1';
      const totalSpend = spendByCampaign.reduce((s, x) => s + x.value, 0);
      const dataStart = ProfessionalExcelFormatter.applyDashboardHeader(ws, colCount,
        '??  CAMPAIGN ANALYTICS REPORT',
        `Generated ${genDate} · ${campaignAnalyticsData.length} campaigns · QAR ${totalSpend.toLocaleString()} total spend`,
        [
          { label: 'Campaigns', value: String(campaignAnalyticsData.length) },
          { label: 'Total Spend', value: `QAR ${totalSpend.toLocaleString()}` },
          { label: 'Total Items', value: String(totalItems) },
          { label: 'Avg Items/Campaign', value: campaignAnalyticsData.length > 0 ? (totalItems / campaignAnalyticsData.length).toFixed(1) : '0' },
          { label: 'Submissions', value: String(filtered.length) },
          { label: 'Brands', value: String(uniqueBrands.length) },
        ],
      );

      const pieEnd = ProfessionalExcelFormatter.applySpendPieLegend(ws, dataStart, spendByCampaign, colCount);
      const campBars = campaignAnalyticsData.sort((a, b) => b.items - a.items).map(c => ({ label: c.campaignName, value: c.items }));
      const barEnd = ProfessionalExcelFormatter.applyBarChart(ws, pieEnd, 'ALL CAMPAIGNS BY ITEMS', campBars, colCount);

      const headerRow = barEnd;
      const headers = ['#', 'Campaign', 'Brands', 'Submissions', 'Total Items', 'Discount', 'Funding', 'Period', 'Days Remaining', 'Aggregators', 'Live', 'Postponed', 'Rejected', 'Trend'];
      headers.forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: headerRow, c })] = { v: h, t: 's' }; });
      rows.forEach((row, ri) => {
        const vals = [row['#'], row.Campaign, row.Brands, row.Submissions, row['Total Items'], row.Discount, row.Funding, row.Period, row['Days Remaining'], row.Aggregators, row.Live, row.Postponed, row.Rejected, row.Trend];
        vals.forEach((v, c) => {
          ws[XLSX.utils.encode_cell({ r: headerRow + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
        });
      });
      ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: headerRow + rows.length, c: colCount - 1 } });
      ws['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws);
      ProfessionalExcelFormatter.applyCardTable(ws, colCount, headerRow, headerRow + 1);
      XLSX.utils.book_append_sheet(wb, ws, 'Campaign Summary');

      // Brand breakdown sheet
      const brandRows2 = brandAnalytics.map((b, i) => {
        const statuses: Record<string, number> = {};
        b.subs.forEach(s => { statuses[s.status] = (statuses[s.status] ?? 0) + 1; });
        return {
          '#': i + 1, Brand: b.brandName, Campaigns: b.campaignIds.size, Submissions: b.subs.length, Items: b.items,
          Aggregators: Array.from(b.aggs).join(', '),
          'Live': statuses[SubmissionStatus.COMPLETED] ?? 0,
          'Postponed': statuses[SubmissionStatus.POSTPONED] ?? 0,
          'Rejected': statuses[SubmissionStatus.REJECTED] ?? 0,
        };
      });
      if (brandRows2.length > 0) {
        const bColCount = 9;
        const ws2 = XLSX.utils.aoa_to_sheet([[]]);
        ws2['!ref'] = 'A1';
        const dStart2 = ProfessionalExcelFormatter.applyDashboardHeader(ws2, bColCount,
          '??  CAMPAIGN · BRAND BREAKDOWN',
          `${brandRows2.length} brands`,
          [
            { label: 'Brands', value: String(brandRows2.length) },
            { label: 'Total Submissions', value: String(brandRows2.reduce((s, r) => s + r.Submissions, 0)) },
            { label: 'Total Items', value: String(brandRows2.reduce((s, r) => s + r.Items, 0)) },
          ],
        );
        const hdr2 = dStart2;
        const bHeaders = ['#', 'Brand', 'Campaigns', 'Submissions', 'Items', 'Aggregators', 'Live', 'Postponed', 'Rejected'];
        bHeaders.forEach((h, c) => { ws2[XLSX.utils.encode_cell({ r: hdr2, c })] = { v: h, t: 's' }; });
        brandRows2.forEach((row, ri) => {
          const vals = [row['#'], row.Brand, row.Campaigns, row.Submissions, row.Items, row.Aggregators, row.Live, row.Postponed, row.Rejected];
          vals.forEach((v, c) => {
            ws2[XLSX.utils.encode_cell({ r: hdr2 + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
          });
        });
        ws2['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: hdr2 + brandRows2.length, c: bColCount - 1 } });
        ws2['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws2);
        ProfessionalExcelFormatter.applyCardTable(ws2, bColCount, hdr2, hdr2 + 1);
        XLSX.utils.book_append_sheet(wb, ws2, 'By Brand');
      }
      // Status breakdown sheet
      const totalSubsC = filtered.length || 1;
      const statusRows2 = statusAnalytics.map((s, i) => ({
        '#': i + 1, Status: s.label, Submissions: s.subs.length,
        '% of Total': `${((s.subs.length / totalSubsC) * 100).toFixed(1)}%`,
        Items: s.items, Brands: new Set(s.subs.map(sub => sub.brandId)).size,
        Campaigns: new Set(s.subs.map(sub => sub.campaignId)).size,
      }));
      if (statusRows2.length > 0) {
        const sColCount = 7;
        const ws3 = XLSX.utils.aoa_to_sheet([[]]);
        ws3['!ref'] = 'A1';
        const dStart3 = ProfessionalExcelFormatter.applyDashboardHeader(ws3, sColCount,
          '??  CAMPAIGN · STATUS BREAKDOWN',
          `${statusRows2.length} statuses`,
          [
            { label: 'Statuses', value: String(statusRows2.length) },
            { label: 'Total Submissions', value: String(filtered.length) },
            { label: 'Total Items', value: String(totalItems) },
          ],
        );
        const hdr3 = dStart3;
        const sHeaders = ['#', 'Status', 'Submissions', '% of Total', 'Items', 'Brands', 'Campaigns'];
        sHeaders.forEach((h, c) => { ws3[XLSX.utils.encode_cell({ r: hdr3, c })] = { v: h, t: 's' }; });
        statusRows2.forEach((row, ri) => {
          const vals = [row['#'], row.Status, row.Submissions, row['% of Total'], row.Items, row.Brands, row.Campaigns];
          vals.forEach((v, c) => {
            ws3[XLSX.utils.encode_cell({ r: hdr3 + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
          });
        });
        ws3['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: hdr3 + statusRows2.length, c: sColCount - 1 } });
        ws3['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws3);
        ProfessionalExcelFormatter.applyCardTable(ws3, sColCount, hdr3, hdr3 + 1);
        XLSX.utils.book_append_sheet(wb, ws3, 'By Status');
      }
      // All items detail sheet
      const allItemsC: Record<string, unknown>[] = [];
      let idxC = 1;
      filtered.forEach(sub => {
        const campX = campaignFor(sub.campaignId);
        const liveAggs = sub.liveAggregators ?? [];
        getApprovedItems(sub).forEach(item => {
          const dp = campX
            ? campX.discountType === DiscountType.PERCENTAGE
              ? Math.round(item.price * (1 - campX.discountValue / 100) * 100) / 100
              : Math.max(0, Math.round((item.price - campX.discountValue) * 100) / 100)
            : item.price;
          allItemsC.push({
            '#': idxC++, Campaign: sub.campaignName, Brand: sub.brandName, Manager: sub.submitterName,
            Status: sub.status, 'Item Name': item.name, Description: item.description ?? '',
            'Original (QAR)': item.price, 'Discounted (QAR)': dp,
            Aggregators: campX?.aggregators.join(', ') ?? '',
            'Live On': liveAggs.join(', '),
          });
        });
      });
      if (allItemsC.length > 0) {
        const aiColCount = 11;
        const ws4 = XLSX.utils.aoa_to_sheet([[]]);
        ws4['!ref'] = 'A1';
        const dStart4 = ProfessionalExcelFormatter.applyDashboardHeader(ws4, aiColCount,
          '??  CAMPAIGN · ALL ITEMS',
          `${allItemsC.length} items across all campaigns`,
          [
            { label: 'Total Items', value: String(allItemsC.length) },
            { label: 'Campaigns', value: String(new Set(allItemsC.map(r => r.Campaign)).size) },
            { label: 'Brands', value: String(new Set(allItemsC.map(r => r.Brand)).size) },
          ],
        );
        const hdr4 = dStart4;
        const aiHeaders = ['#', 'Campaign', 'Brand', 'Manager', 'Status', 'Item Name', 'Description', 'Original (QAR)', 'Discounted (QAR)', 'Aggregators', 'Live On'];
        aiHeaders.forEach((h, c) => { ws4[XLSX.utils.encode_cell({ r: hdr4, c })] = { v: h, t: 's' }; });
        allItemsC.forEach((row, ri) => {
          const vals = [row['#'], row.Campaign, row.Brand, row.Manager, row.Status, row['Item Name'], row.Description, row['Original (QAR)'], row['Discounted (QAR)'], row.Aggregators, row['Live On']];
          vals.forEach((v, c) => {
            ws4[XLSX.utils.encode_cell({ r: hdr4 + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
          });
        });
        ws4['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: hdr4 + allItemsC.length, c: aiColCount - 1 } });
        ws4['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws4);
        ProfessionalExcelFormatter.applyCardTable(ws4, aiColCount, hdr4, hdr4 + 1);
        XLSX.utils.book_append_sheet(wb, ws4, 'All Items');
      }
      XLSX.writeFile(wb, `Analytics_Campaign_${dateStr}.xlsx`);

    } else if (analyticsTab === 'status') {
      // -- Status Summary · Visual First Layout --
      const colCount = 8;
      const totalSubs = filtered.length || 1;
      const rows = statusAnalytics.map((s, i) => ({
        '#': i + 1, Status: s.label, Submissions: s.subs.length,
        '% of Total': `${((s.subs.length / totalSubs) * 100).toFixed(1)}%`,
        Items: s.items, Brands: new Set(s.subs.map(sub => sub.brandId)).size,
        Campaigns: new Set(s.subs.map(sub => sub.campaignId)).size,
        'Trend': ProfessionalExcelFormatter.sparkline(weeklyTrend),
      }));

      const ws = XLSX.utils.aoa_to_sheet([[]]);
      ws['!ref'] = 'A1';
      const approved = filtered.filter(s => [SubmissionStatus.APPROVED, SubmissionStatus.DEPLOYMENT_READY, SubmissionStatus.COMPLETED].includes(s.status as SubmissionStatus)).length;
      const dataStart = ProfessionalExcelFormatter.applyDashboardHeader(ws, colCount,
        '??  STATUS ANALYTICS REPORT',
        `Generated ${genDate} · ${filtered.length} submissions`,
        [
          { label: 'Total Submissions', value: String(filtered.length) },
          { label: 'Approval Rate', value: `${filtered.length > 0 ? Math.round((approved / filtered.length) * 100) : 0}%` },
          { label: 'Total Items', value: String(totalItems) },
          { label: 'Unique Brands', value: String(new Set(filtered.map(s => s.brandId)).size) },
        ],
      );

      const statusBars = statusAnalytics.sort((a, b) => b.subs.length - a.subs.length).map(s => ({ label: s.label, value: s.subs.length }));
      const barEnd = ProfessionalExcelFormatter.applyBarChart(ws, dataStart, 'SUBMISSIONS BY STATUS', statusBars, colCount);

      const headerRow = barEnd;
      const headers = ['#', 'Status', 'Submissions', '% of Total', 'Items', 'Brands', 'Campaigns', 'Trend'];
      headers.forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: headerRow, c })] = { v: h, t: 's' }; });
      rows.forEach((row, ri) => {
        const vals = [row['#'], row.Status, row.Submissions, row['% of Total'], row.Items, row.Brands, row.Campaigns, row.Trend];
        vals.forEach((v, c) => {
          ws[XLSX.utils.encode_cell({ r: headerRow + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
        });
      });
      ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: headerRow + rows.length, c: colCount - 1 } });
      ws['!cols'] = ProfessionalExcelFormatter.autoFitColumns(ws);
      ProfessionalExcelFormatter.applyCardTable(ws, colCount, headerRow, headerRow + 1);
      XLSX.utils.book_append_sheet(wb, ws, 'Status Summary');
      XLSX.writeFile(wb, `Analytics_Status_${dateStr}.xlsx`);

    } else {
      // ----------------------------------------------------------
      // DEEP DIVE · High-Impact Visuals: Heatmaps, Arrows, Bars
      // ----------------------------------------------------------

      // -- Sheet 1: Price Analytics with heatmap + data bars --
      const priceColCount = 10;
      const priceRows: Array<Record<string, unknown>> = [];
      let pIdx = 1;
      let maxItemPrice = 0;
      filtered.forEach(sub => {
        const camp = campaignFor(sub.campaignId);
        getApprovedItems(sub).forEach(item => {
          const dp = camp
            ? camp.discountType === DiscountType.PERCENTAGE
              ? Math.round(item.price * (1 - camp.discountValue / 100) * 100) / 100
              : Math.max(0, Math.round((item.price - camp.discountValue) * 100) / 100)
            : item.price;
          const saving = Math.round((item.price - dp) * 100) / 100;
          const discPct = item.price > 0 ? Math.round(((item.price - dp) / item.price) * 1000) / 10 : 0;
          if (item.price > maxItemPrice) maxItemPrice = item.price;
          priceRows.push({
            '#': pIdx++, Campaign: sub.campaignName, Brand: sub.brandName,
            Item: item.name, 'Original (QAR)': item.price, 'Discounted (QAR)': dp,
            'Saving (QAR)': saving, 'Discount %': discPct,
            'Status': sub.status, 'Price Bar': '',
          });
        });
      });
      if (priceRows.length > 0) {
        const pWs = XLSX.utils.aoa_to_sheet([[]]);
        pWs['!ref'] = 'A1';
        const totalOrig = priceRows.reduce((s, r) => s + (r['Original (QAR)'] as number), 0);
        const totalDisc = priceRows.reduce((s, r) => s + (r['Discounted (QAR)'] as number), 0);
        const totalSav = totalOrig - totalDisc;
        const dStart = ProfessionalExcelFormatter.applyDashboardHeader(pWs, priceColCount,
          '??  DEEP DIVE · PRICE ANALYTICS',
          `Generated ${genDate} · ${priceRows.length} items analyzed`,
          [
            { label: 'Total Original', value: `QAR ${totalOrig.toFixed(0)}`, sub: `${priceRows.length} items` },
            { label: 'Total After Discount', value: `QAR ${totalDisc.toFixed(0)}`, sub: `${((totalSav / (totalOrig || 1)) * 100).toFixed(1)}% saved` },
            { label: 'Total Savings', value: `QAR ${totalSav.toFixed(0)}` },
            { label: 'Avg Item Price', value: `QAR ${priceRows.length > 0 ? (totalOrig / priceRows.length).toFixed(2) : '0'}` },
          ],
        );

        const pieEnd = ProfessionalExcelFormatter.applySpendPieLegend(pWs, dStart, spendByCampaign, priceColCount);
        const headerRow = pieEnd;
        const headers = ['#', 'Campaign', 'Brand', 'Item', 'Original (QAR)', 'Discounted (QAR)', 'Saving (QAR)', 'Discount %', 'Status', 'Price Bar'];
        headers.forEach((h, c) => { pWs[XLSX.utils.encode_cell({ r: headerRow, c })] = { v: h, t: 's' }; });
        priceRows.forEach((row, ri) => {
          const vals = [row['#'], row.Campaign, row.Brand, row.Item, row['Original (QAR)'], row['Discounted (QAR)'], row['Saving (QAR)'], row['Discount %'], row.Status, row['Price Bar']];
          vals.forEach((v, c) => {
            pWs[XLSX.utils.encode_cell({ r: headerRow + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
          });
        });
        pWs['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: headerRow + priceRows.length, c: priceColCount - 1 } });
        pWs['!cols'] = ProfessionalExcelFormatter.autoFitColumns(pWs);
        // Ensure Price Bar col is wide enough
        if (pWs['!cols'] && pWs['!cols'][9]) pWs['!cols'][9] = { wch: 22 };
        ProfessionalExcelFormatter.applyCardTable(pWs, priceColCount, headerRow, headerRow + 1);

        // Apply heatmap to Saving column (col 6)
        ProfessionalExcelFormatter.applyHeatmapColumn(pWs, 6, headerRow + 1);
        // Apply heatmap to Discount % column (col 7)
        ProfessionalExcelFormatter.applyHeatmapColumn(pWs, 7, headerRow + 1);
        // Apply performance arrows to Discount % (threshold: 15% is "good")
        ProfessionalExcelFormatter.applyPerformanceArrows(pWs, 7, headerRow + 1, 15);
        // Apply data bars for Original Price relative to max (col 4 ? col 9)
        ProfessionalExcelFormatter.applyInlineDataBars(pWs, 4, 9, headerRow + 1, maxItemPrice);
        // Apply status colouring
        ProfessionalExcelFormatter.applyStatusColouring(pWs, 8);

        XLSX.utils.book_append_sheet(wb, pWs, 'Price Analytics');
      }

      // -- Sheet 2: Brand Performance with heatmaps + arrows --
      const bpColCount = 11;
      const bpRows = brandAnalytics.map((b, i) => {
        const approved = b.subs.filter(s => [SubmissionStatus.APPROVED, SubmissionStatus.DEPLOYMENT_READY, SubmissionStatus.COMPLETED].includes(s.status as SubmissionStatus)).length;
        const rejected = b.subs.filter(s => s.status === SubmissionStatus.REJECTED).length;
        const avgItems = b.subs.length > 0 ? Math.round(b.items / b.subs.length) : 0;
        const avgPrice = b.items > 0 ? Math.round(b.subs.reduce((a, s) => a + getApprovedItems(s).reduce((x, it) => x + it.price, 0), 0) / b.items * 100) / 100 : 0;
        const rate = b.subs.length > 0 ? Math.round((approved / b.subs.length) * 100) : 0;
        const avgResponse = b.subs.length > 0 ? Math.round(b.subs.reduce((a, s) => a + Math.ceil((s.updatedAt - s.createdAt) / 86400000), 0) / b.subs.length) : 0;
        return {
          '#': i + 1, Brand: b.brandName, Campaigns: b.campaignIds.size,
          'Total Subs': b.subs.length, 'Total Items': b.items, 'Avg Items/Sub': avgItems,
          'Avg Price (QAR)': avgPrice, 'Approved': approved, 'Rejected': rejected,
          'Approval Rate %': rate, 'Avg Response (days)': avgResponse,
        };
      });
      if (bpRows.length > 0) {
        const bWs = XLSX.utils.aoa_to_sheet([[]]);
        bWs['!ref'] = 'A1';
        const overallRate = filtered.length > 0 ? Math.round((filtered.filter(s => [SubmissionStatus.APPROVED, SubmissionStatus.DEPLOYMENT_READY, SubmissionStatus.COMPLETED].includes(s.status as SubmissionStatus)).length / filtered.length) * 100) : 0;
        const dStart = ProfessionalExcelFormatter.applyDashboardHeader(bWs, bpColCount,
          '??  DEEP DIVE · BRAND PERFORMANCE',
          `Generated ${genDate} · ${brandAnalytics.length} brands scored`,
          [
            { label: 'Total Brands', value: String(brandAnalytics.length) },
            { label: 'Overall Approval', value: `${overallRate}%` },
            { label: 'Total Items', value: String(totalItems) },
            { label: 'Avg Submissions', value: filtered.length > 0 && brandAnalytics.length > 0 ? (filtered.length / brandAnalytics.length).toFixed(1) : '0' },
          ],
        );

        const brandApprBars = bpRows.sort((a, b) => (b['Approval Rate %'] as number) - (a['Approval Rate %'] as number)).map(b => ({ label: b.Brand as string, value: b['Approval Rate %'] as number }));
        const barEnd = ProfessionalExcelFormatter.applyBarChart(bWs, dStart, 'ALL BRANDS · APPROVAL RATE %', brandApprBars, bpColCount);

        const headerRow = barEnd;
        const headers = ['#', 'Brand', 'Campaigns', 'Total Subs', 'Total Items', 'Avg Items/Sub', 'Avg Price (QAR)', 'Approved', 'Rejected', 'Approval Rate %', 'Avg Response (days)'];
        headers.forEach((h, c) => { bWs[XLSX.utils.encode_cell({ r: headerRow, c })] = { v: h, t: 's' }; });
        bpRows.forEach((row, ri) => {
          const vals = [row['#'], row.Brand, row.Campaigns, row['Total Subs'], row['Total Items'], row['Avg Items/Sub'], row['Avg Price (QAR)'], row.Approved, row.Rejected, row['Approval Rate %'], row['Avg Response (days)']];
          vals.forEach((v, c) => {
            bWs[XLSX.utils.encode_cell({ r: headerRow + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
          });
        });
        bWs['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: headerRow + bpRows.length, c: bpColCount - 1 } });
        bWs['!cols'] = ProfessionalExcelFormatter.autoFitColumns(bWs);
        ProfessionalExcelFormatter.applyCardTable(bWs, bpColCount, headerRow, headerRow + 1);

        // Heatmap on Approval Rate % (col 9) · light-to-deep
        ProfessionalExcelFormatter.applyHeatmapColumn(bWs, 9, headerRow + 1);
        // Performance arrows on Approval Rate (threshold: 70%)
        ProfessionalExcelFormatter.applyPerformanceArrows(bWs, 9, headerRow + 1, 70);
        // Performance arrows on Avg Response days (lower is better · threshold 5, invert logic handled by arrows)
        ProfessionalExcelFormatter.applyPerformanceArrows(bWs, 10, headerRow + 1, 5);
        // Heatmap on Total Items (col 4)
        ProfessionalExcelFormatter.applyHeatmapColumn(bWs, 4, headerRow + 1);

        XLSX.utils.book_append_sheet(wb, bWs, 'Brand Performance');
      }

      // -- Sheet 3: Aggregator Coverage with heatmap --
      const allAggs = new Set<string>();
      filtered.forEach(s => (s.liveAggregators ?? []).forEach(a => allAggs.add(a)));
      const aggList = Array.from(allAggs).sort();
      const acRows: Record<string, unknown>[] = [];
      let acIdx = 1;
      brandAnalytics.forEach(b => {
        const row: Record<string, unknown> = { '#': acIdx++, Brand: b.brandName };
        let liveCount = 0;
        aggList.forEach(a => {
          const live = b.subs.some(s => (s.liveAggregators ?? []).includes(a));
          row[a] = live ? '? LIVE' : '?';
          if (live) liveCount++;
        });
        row['Coverage'] = aggList.length > 0 ? Math.round((liveCount / aggList.length) * 100) : 0;
        acRows.push(row);
      });
      if (acRows.length > 0) {
        const acColCount = 3 + aggList.length;
        const aWs = XLSX.utils.aoa_to_sheet([[]]);
        aWs['!ref'] = 'A1';
        const avgCov = acRows.length > 0 ? Math.round(acRows.reduce((s, r) => s + (r.Coverage as number), 0) / acRows.length) : 0;
        const acStart = ProfessionalExcelFormatter.applyDashboardHeader(aWs, acColCount,
          '??  DEEP DIVE · AGGREGATOR COVERAGE',
          `${brandAnalytics.length} brands · ${aggList.length} aggregators`,
          [
            { label: 'Brands', value: String(brandAnalytics.length) },
            { label: 'Aggregators', value: String(aggList.length) },
            { label: 'Avg Coverage', value: `${avgCov}%` },
          ],
        );
        const acHdr = acStart;
        const acHeaders = ['#', 'Brand', ...aggList, 'Coverage'];
        acHeaders.forEach((h, c) => { aWs[XLSX.utils.encode_cell({ r: acHdr, c })] = { v: h, t: 's' }; });
        acRows.forEach((row, ri) => {
          const vals: unknown[] = [row['#'], row.Brand, ...aggList.map(a => row[a]), row.Coverage];
          vals.forEach((v, c) => {
            aWs[XLSX.utils.encode_cell({ r: acHdr + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
          });
        });
        aWs['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: acHdr + acRows.length, c: acColCount - 1 } });
        aWs['!cols'] = ProfessionalExcelFormatter.autoFitColumns(aWs);
        ProfessionalExcelFormatter.applyCardTable(aWs, acColCount, acHdr, acHdr + 1);
        // Heatmap on coverage column
        ProfessionalExcelFormatter.applyHeatmapColumn(aWs, acColCount - 1, acHdr + 1);
        // Colour LIVE/? cells
        for (let r = acHdr + 1; r <= acHdr + acRows.length; r++) {
          for (let c = 2; c < acColCount - 1; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            if (!aWs[addr]) continue;
            const val = String(aWs[addr].v ?? '');
            if (val.includes('LIVE')) {
              aWs[addr].s = { ...aWs[addr].s, font: { bold: true, sz: 10, color: { rgb: '10B981' } }, fill: { patternType: 'solid' as const, fgColor: { rgb: 'D1FAE5' } } };
            } else if (val === '?') {
              aWs[addr].s = { ...aWs[addr].s, font: { bold: true, sz: 10, color: { rgb: 'EF4444' } }, fill: { patternType: 'solid' as const, fgColor: { rgb: 'FEE2E2' } } };
            }
          }
        }
        XLSX.utils.book_append_sheet(wb, aWs, 'Aggregator Coverage');
      }

      // -- Sheet 4: Review Cycles with arrows + heatmap --
      const rcColCount = 10;
      const rcRows = filtered.sort((a, b) => b.reviewHistory.length - a.reviewHistory.length).map((sub, i) => {
        const rounds = sub.reviewHistory.length;
        const daysSinceCreate = Math.ceil((Date.now() - sub.createdAt) / 86400000);
        const lastAction = sub.reviewHistory.length > 0 ? sub.reviewHistory[sub.reviewHistory.length - 1] : null;
        return {
          '#': i + 1, Campaign: sub.campaignName, Brand: sub.brandName, Manager: sub.submitterName,
          Status: sub.status, 'Review Rounds': rounds, 'Days Since Submit': daysSinceCreate,
          'Last Action': lastAction ? lastAction.action : '·',
          'Last Reviewer': lastAction ? lastAction.reviewerName : '·',
          'Trend': ProfessionalExcelFormatter.sparkline(sub.reviewHistory.map((_, idx) => idx + 1)),
        };
      });
      if (rcRows.length > 0) {
        const rWs = XLSX.utils.aoa_to_sheet([[]]);
        rWs['!ref'] = 'A1';
        const avgRounds = filtered.length > 0 ? (filtered.reduce((a, s) => a + s.reviewHistory.length, 0) / filtered.length).toFixed(1) : '0';
        const avgDays = filtered.length > 0 ? Math.round(filtered.reduce((a, s) => a + Math.ceil((s.updatedAt - s.createdAt) / 86400000), 0) / filtered.length) : 0;
        const dStart = ProfessionalExcelFormatter.applyDashboardHeader(rWs, rcColCount,
          '??  DEEP DIVE · REVIEW CYCLES',
          `Generated ${genDate} · ${filtered.length} submissions · Avg ${avgRounds} rounds · ${avgDays}d avg resolve`,
          [
            { label: 'Submissions', value: String(filtered.length) },
            { label: 'Avg Review Rounds', value: avgRounds },
            { label: 'Avg Days to Resolve', value: `${avgDays}d` },
            { label: 'Max Rounds', value: String(Math.max(...filtered.map(s => s.reviewHistory.length), 0)) },
          ],
        );

        const headerRow = dStart;
        const headers = ['#', 'Campaign', 'Brand', 'Manager', 'Status', 'Review Rounds', 'Days Since Submit', 'Last Action', 'Last Reviewer', 'Trend'];
        headers.forEach((h, c) => { rWs[XLSX.utils.encode_cell({ r: headerRow, c })] = { v: h, t: 's' }; });
        rcRows.forEach((row, ri) => {
          const vals = [row['#'], row.Campaign, row.Brand, row.Manager, row.Status, row['Review Rounds'], row['Days Since Submit'], row['Last Action'], row['Last Reviewer'], row.Trend];
          vals.forEach((v, c) => {
            rWs[XLSX.utils.encode_cell({ r: headerRow + 1 + ri, c })] = { v: v ?? '', t: typeof v === 'number' ? 'n' : 's' };
          });
        });
        rWs['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: headerRow + rcRows.length, c: rcColCount - 1 } });
        rWs['!cols'] = ProfessionalExcelFormatter.autoFitColumns(rWs);
        ProfessionalExcelFormatter.applyCardTable(rWs, rcColCount, headerRow, headerRow + 1);

        // Heatmap on Review Rounds (col 5) · more rounds = hotter
        ProfessionalExcelFormatter.applyHeatmapColumn(rWs, 5, headerRow + 1);
        // Heatmap on Days Since Submit (col 6)
        ProfessionalExcelFormatter.applyHeatmapColumn(rWs, 6, headerRow + 1);
        // Performance arrows on Review Rounds (threshold: 2 = healthy)
        ProfessionalExcelFormatter.applyPerformanceArrows(rWs, 5, headerRow + 1, 2);
        // Performance arrows on Days Since Submit (threshold: 7)
        ProfessionalExcelFormatter.applyPerformanceArrows(rWs, 6, headerRow + 1, 7);
        // Status colouring
        ProfessionalExcelFormatter.applyStatusColouring(rWs, 4);

        XLSX.utils.book_append_sheet(wb, rWs, 'Review Cycles');
      }
      XLSX.writeFile(wb, `Analytics_DeepDive_${dateStr}.xlsx`);
    }
    toast.success('Analytics Exported', 'Report exported successfully.');
  };

  return (
    <div className="space-y-4 bg-[#121212] rounded-2xl p-6">
      {/* -- Header -- */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-white">Analytics Dashboard</h2>
          <p className="text-sm text-zinc-400 mt-1">Comprehensive analytics across all campaigns and submission statuses.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-950/50 border border-indigo-800 rounded-xl text-xs font-bold text-indigo-400">
            <BarChart3 size={14} /> {filtered.length} Submissions
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-950/50 border border-teal-800 rounded-xl text-xs font-bold text-teal-400">
            <Package size={14} /> {totalItems} Items
          </div>
        </div>
      </div>

      {/* -- Filters -- */}
      <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 space-y-3 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search campaigns, brands, managers·"
              className="w-full pl-9 pr-3 py-2 bg-[#2D2D2D] border border-[#3D3D3D] rounded-lg text-sm text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:border-[#3B82F6] transition-all" />
          </div>
          <CustomSelect value={brandFilter} onChange={v => setBrandFilter(v)} placeholder="All Brands"
            options={[{ value: '', label: 'All Brands' }, ...uniqueBrands.map(b => ({ value: b.id, label: b.name }))]} />
          <CustomSelect value={aggregatorFilter} onChange={v => setAggregatorFilter(v)} placeholder="All Aggregators"
            options={[{ value: '', label: 'All Aggregators' }, ...uniqueAggregators.map(a => ({ value: a, label: a }))]} />
          <CustomSelect value={statusFilter} onChange={v => setStatusFilter(v)} placeholder="All Statuses"
            options={[
              { value: '', label: 'All Statuses' },
              { value: SubmissionStatus.PENDING, label: 'Pending' },
              { value: SubmissionStatus.IN_REVIEW, label: 'In Review' },
              { value: SubmissionStatus.APPROVED, label: 'Approved' },
              { value: SubmissionStatus.DEPLOYMENT_READY, label: 'Deployment Ready' },
              { value: SubmissionStatus.COMPLETED, label: 'Completed / Live' },
              { value: SubmissionStatus.POSTPONED, label: 'Postponed' },
              { value: SubmissionStatus.REJECTED, label: 'Rejected' },
            ]} />
          {(searchQuery || brandFilter || aggregatorFilter || statusFilter) && (
            <button onClick={() => { setSearchQuery(''); setBrandFilter(''); setAggregatorFilter(''); setStatusFilter(''); }}
              className="px-3 py-2 text-xs font-bold text-red-400 hover:bg-red-900/20 rounded-lg transition-all">
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* -- View Tabs + Export -- */}
      <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-3 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
        <div className="flex items-center gap-1 bg-[#161616] rounded-lg p-0.5">
          {([
            { key: 'brand' as const, label: 'By Brand', Icon: Building2 },
            { key: 'aggregator' as const, label: 'By Aggregator', Icon: Globe },
            { key: 'campaign' as const, label: 'By Campaign', Icon: Megaphone },
            { key: 'status' as const, label: 'By Status', Icon: Layers },
            { key: 'full' as const, label: 'Deep Dive', Icon: BarChart3 },
            { key: 'bi-export' as const, label: 'BI Export', Icon: FileText },
          ]).map(t => (
            <button key={t.key} onClick={() => { setAnalyticsTab(t.key); setExpandedRow(null); }}
              className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all",
                analyticsTab === t.key ? "bg-[#2D2D2D] text-blue-400 border-2 border-[#3B82F6] shadow-[0_0_12px_rgba(59,130,246,0.3)]" : "text-zinc-400 hover:text-zinc-200"
              )}>
              <t.Icon size={13} /> {t.label}
            </button>
          ))}
        </div>
        <button onClick={handleExportAnalytics}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 shadow-[0_0_12px_rgba(59,130,246,0.2)] transition-all">
          <Download size={14} /> {(() => {
              const labels: Record<string, string> = {
                brand: 'Download Brand Report',
                aggregator: 'Download Aggregator Report',
                campaign: 'Download Campaign Report',
                status: 'Download Status Report',
                full: 'Download Deep Dive Report',
              };
              return labels[analyticsTab] ?? 'Download Report';
            })()}
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-12 text-center">
          <BarChart3 size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm font-semibold text-zinc-400">No data available for analysis</p>
          <p className="text-xs text-zinc-400 mt-1">Try adjusting your filters or wait for submissions to arrive.</p>
        </div>
      ) : (
        <>
          {/* -- Brand View -- */}
          {analyticsTab === 'brand' && (
            <div className="space-y-3">
              {brandAnalytics.length > 0 && (
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Items Distribution by Brand</p>
                  <div className="space-y-2">
                    {(() => {
                      const maxI = Math.max(...brandAnalytics.map(x => x.items), 1);
                      return brandAnalytics.map(b => (
                        <div key={b.brandId} className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold text-zinc-300 w-28 truncate" title={b.brandName}>{b.brandName}</span>
                          <div className="flex-1 h-6 bg-[#2D2D2D] rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full transition-all flex items-center justify-end pr-2" style={{ width: `${(b.items / maxI) * 100}%` }}>
                              {b.items > 0 && <span className="text-[9px] font-bold text-white">{b.items}</span>}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.4)]">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#252525] border-b border-[#333]">
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Brand</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Campaigns</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Submissions</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Items</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Aggregators</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brandAnalytics.map(b => (
                      <React.Fragment key={b.brandId}>
                        <tr onClick={() => setExpandedRow(expandedRow === b.brandId ? null : b.brandId)} className={cn("border-b border-[#2A2A2A] hover:bg-[#2D2D2D] cursor-pointer transition-all duration-300", expandedRow !== null && expandedRow !== b.brandId && "opacity-30")}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              {expandedRow === b.brandId ? <ChevronUp size={12} className="text-zinc-400" /> : <ChevronDown size={12} className="text-zinc-400" />}
                              <span className="text-xs font-semibold text-zinc-100">{b.brandName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-zinc-300 text-center">{b.campaignIds.size}</td>
                          <td className="px-4 py-2.5 text-xs text-zinc-300 text-center">{b.subs.length}</td>
                          <td className="px-4 py-2.5 text-xs font-bold text-indigo-400 text-center">{b.items}</td>
                          <td className="px-4 py-2.5"><div className="flex flex-wrap gap-1">{Array.from(b.aggs).map(a => <span key={a} className="px-1.5 py-0.5 bg-[#2D2D2D] text-[10px] font-semibold text-zinc-400 rounded">{a}</span>)}</div></td>
                        </tr>
                        {expandedRow === b.brandId && b.subs.map(sub => getApprovedItems(sub).map(item => (
                          <tr key={`${sub.id}-${item.id}`} className="bg-[#161616] border-b border-[#2A2A2A] animate-[analytics-row-fade_0.25s_ease-out]">
                            <td className="px-4 py-2 text-xs text-zinc-400 pl-10">{sub.campaignName}</td>
                            <td className="px-4 py-2 text-xs text-zinc-400 text-center"><span className={cn("px-1.5 py-0.5 rounded text-[10px] font-bold", STATUS_COLOR[sub.status])}>{sub.status}</span></td>
                            <td className="px-4 py-2"><div className="flex items-center gap-2">{item.imageUrl ? <img src={getMediaUrl(item.imageUrl)} alt="" className="w-6 h-6 rounded object-cover" /> : <div className="w-6 h-6 rounded bg-[#2D2D2D] flex items-center justify-center"><ImageIcon size={8} className="text-zinc-400" /></div>}<span className="text-xs font-semibold text-zinc-200">{item.name}</span></div></td>
                            <td className="px-4 py-2 text-xs text-zinc-300 text-right">QAR {item.price.toFixed(2)}</td>
                            <td className="px-4 py-2 text-xs text-zinc-400">{item.description ?? ''}</td>
                          </tr>
                        )))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* -- Aggregator View -- */}
          {analyticsTab === 'aggregator' && (
            <div className="space-y-3">
              {aggregatorAnalytics.length > 0 && (
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Items Distribution by Aggregator</p>
                  <div className="space-y-2">
                    {(() => {
                      const maxI = Math.max(...aggregatorAnalytics.map(x => x.items), 1);
                      return aggregatorAnalytics.map(a => (
                        <div key={a.aggregator} className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold text-zinc-300 w-28 truncate">{a.aggregator}</span>
                          <div className="flex-1 h-6 bg-[#2D2D2D] rounded-full overflow-hidden">
                            <div className="h-full bg-violet-500 rounded-full transition-all flex items-center justify-end pr-2" style={{ width: `${(a.items / maxI) * 100}%` }}>
                              {a.items > 0 && <span className="text-[9px] font-bold text-white">{a.items}</span>}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.4)]">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#252525] border-b border-[#333]">
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Aggregator</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Campaigns</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Brands</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregatorAnalytics.map(a => (
                      <React.Fragment key={a.aggregator}>
                        <tr onClick={() => setExpandedRow(expandedRow === a.aggregator ? null : a.aggregator)} className={cn("border-b border-[#2A2A2A] hover:bg-[#2D2D2D] cursor-pointer transition-all duration-300", expandedRow !== null && expandedRow !== a.aggregator && "opacity-30")}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              {expandedRow === a.aggregator ? <ChevronUp size={12} className="text-zinc-400" /> : <ChevronDown size={12} className="text-zinc-400" />}
                              <Globe size={14} className="text-violet-500" />
                              <span className="text-xs font-semibold text-zinc-100">{a.aggregator}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-zinc-300 text-center">{a.campaignIds.size}</td>
                          <td className="px-4 py-2.5 text-xs text-zinc-300 text-center">{a.brandIds.size}</td>
                          <td className="px-4 py-2.5 text-xs font-bold text-violet-400 text-center">{a.items}</td>
                        </tr>
                        {expandedRow === a.aggregator && a.subs.map(sub => getApprovedItems(sub).map(item => (
                          <tr key={`${sub.id}-${item.id}`} className="bg-[#161616] border-b border-[#2A2A2A] animate-[analytics-row-fade_0.25s_ease-out]">
                            <td className="px-4 py-2 text-xs text-zinc-400 pl-10">{sub.brandName}</td>
                            <td className="px-4 py-2 text-xs text-zinc-400">{sub.campaignName}</td>
                            <td className="px-4 py-2"><div className="flex items-center gap-2">{item.imageUrl ? <img src={getMediaUrl(item.imageUrl)} alt="" className="w-6 h-6 rounded object-cover" /> : <div className="w-6 h-6 rounded bg-[#2D2D2D] flex items-center justify-center"><ImageIcon size={8} className="text-zinc-400" /></div>}<span className="text-xs font-semibold text-zinc-200">{item.name}</span></div></td>
                            <td className="px-4 py-2 text-xs text-zinc-300 text-right">QAR {item.price.toFixed(2)}</td>
                          </tr>
                        )))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* -- Campaign View -- */}
          {/* -- Campaign View (Master Analytics) -- */}
          {analyticsTab === 'campaign' && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 text-center shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                  <p className="text-2xl font-bold text-indigo-400">{uniqueBrands.length}</p>
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">Brands</p>
                </div>
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 text-center shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                  <p className="text-2xl font-bold text-violet-400">{uniqueAggregators.length}</p>
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">Aggregators</p>
                </div>
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 text-center shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                  <p className="text-2xl font-bold text-amber-400">{new Set(filtered.map(s => s.campaignId)).size}</p>
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">Campaigns</p>
                </div>
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 text-center shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                  <p className="text-2xl font-bold text-teal-400">{totalItems}</p>
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">Total Items</p>
                </div>
              </div>

              {/* Status breakdown */}
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Status Breakdown</p>
                <div className="flex flex-wrap gap-3">
                  {statusAnalytics.map(s => (
                    <div key={s.status} className="flex items-center gap-2 px-3 py-1.5 bg-[#252525] border border-[#333] rounded-lg">
                      <div className={cn("w-2.5 h-2.5 rounded-full", s.color)} />
                      <span className="text-xs font-semibold text-zinc-200">{s.label}</span>
                      <span className="text-xs font-bold text-zinc-900">{s.subs.length}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Distribution chart */}
              {campaignAnalyticsData.length > 0 && (
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Items Distribution by Campaign</p>
                  <div className="space-y-2">
                    {(() => {
                      const maxI = Math.max(...campaignAnalyticsData.map(x => x.items), 1);
                      return campaignAnalyticsData.map(c => (
                        <div key={c.campaignId} className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold text-zinc-300 w-36 truncate" title={c.campaignName}>{c.campaignName}</span>
                          <div className="flex-1 h-6 bg-[#2D2D2D] rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full transition-all flex items-center justify-end pr-2" style={{ width: `${(c.items / maxI) * 100}%` }}>
                              {c.items > 0 && <span className="text-[9px] font-bold text-white">{c.items}</span>}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {/* Campaign table with expandable detail */}
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#252525] border-b border-[#333]">
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest w-8"></th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Campaign</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Status</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Period</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Duration</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Brands</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignAnalyticsData.map(c => {
                      const isExp = expandedRow === c.campaignId;
                      const camp = c.camp;
                      const totalDays = camp ? Math.ceil((camp.validTo - camp.validFrom) / (1000 * 60 * 60 * 24)) : 0;
                      const elapsed = camp ? Math.ceil((Math.min(Date.now(), camp.validTo) - camp.validFrom) / (1000 * 60 * 60 * 24)) : 0;
                      const remaining = camp ? Math.max(0, Math.ceil((camp.validTo - Date.now()) / (1000 * 60 * 60 * 24))) : 0;
                      const campStatus = camp?.status ?? CampaignStatus.DRAFT;
                      const st = STATUS_STYLES[campStatus];

                      return (
                        <React.Fragment key={c.campaignId}>
                          <tr onClick={() => setExpandedRow(isExp ? null : c.campaignId)}
                            className={cn("border-b border-[#2A2A2A] hover:bg-[#2D2D2D] cursor-pointer transition-all duration-300",
                              isExp && "bg-[#1A2332] border-l-2 border-l-blue-500",
                              expandedRow !== null && !isExp && "opacity-30"
                            )}>
                            <td className="px-3 py-2.5 text-center">
                              {isExp ? <ChevronUp size={13} className="text-blue-500" /> : <ChevronDown size={13} className="text-zinc-400" />}
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="text-xs font-bold text-zinc-100">{c.campaignName}</span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border", st.pill)}>
                                <span className={cn("w-1.5 h-1.5 rounded-full", st.dot)} />
                                {st.label}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-zinc-400">{camp ? `${fmtDate(camp.validFrom)} · ${fmtDate(camp.validTo)}` : '·'}</td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded",
                                remaining <= 0 ? "bg-[#2D2D2D] text-zinc-400" :
                                remaining <= 3 ? "bg-red-900/30 text-red-400" :
                                remaining <= 7 ? "bg-amber-900/30 text-amber-400" :
                                "bg-emerald-900/30 text-emerald-400"
                              )}>
                                {campStatus === CampaignStatus.CLOSED ? `${totalDays}d total` :
                                 remaining <= 0 ? 'Ended' : `${remaining}d left`}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-zinc-300 text-center">{c.brandNames.size}</td>
                            <td className="px-4 py-2.5 text-xs font-bold text-amber-400 text-center">{c.items}</td>
                          </tr>

                          {/* -- Expanded Campaign Detail Panel -- */}
                          {isExp && (
                            <tr>
                              <td colSpan={7} className="p-0">
                                <div className="border-t border-[#333] bg-[#161616] animate-[analytics-expand_0.3s_ease-out]">
                                  {/* Campaign Summary Bar */}
                                  <div className="px-5 py-3 border-b border-[#2A2A2A] flex items-center gap-4 flex-wrap text-[10px]">
                                    <span className="flex items-center gap-1 text-zinc-400"><Calendar size={10} /> {totalDays} days total</span>
                                    <span className="flex items-center gap-1 text-zinc-400"><Clock size={10} /> {elapsed} days elapsed</span>
                                    {remaining > 0 && <span className={cn("font-bold px-1.5 py-0.5 rounded", remaining <= 3 ? "bg-red-900/30 text-red-400" : "bg-emerald-900/30 text-emerald-400")}>{remaining}d remaining</span>}
                                    {camp && <span className="flex items-center gap-1 font-bold text-teal-400"><Percent size={10} /> {camp.discountType === DiscountType.PERCENTAGE ? `${camp.discountValue}%` : `QAR ${camp.discountValue}`}</span>}
                                    <span className={cn("px-1.5 py-0.5 rounded font-bold uppercase tracking-wider", camp ? FUNDING_STYLES[camp.fundingType] : 'bg-[#2D2D2D] text-zinc-400')}>{camp ? FUNDING_LABELS[camp.fundingType] : '·'}</span>
                                    {camp?.requiredItemCount && camp.requiredItemCount > 0 && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-900/30 text-indigo-400 border border-indigo-800 rounded-full font-bold">
                                        <Target size={9} /> Target: {camp.requiredItemCount} items / brand
                                      </span>
                                    )}
                                    {camp?.aggregators && camp.aggregators.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        {camp.aggregators.map(a => (
                                          <span key={a} className="px-1.5 py-0.5 bg-indigo-900/30 text-indigo-400 rounded border border-indigo-800 text-[9px] font-semibold">{a}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* Brand-wise Submission Breakdown */}
                                  <div className="px-5 py-3 space-y-3">
                                    <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">Brand Submissions ({c.subs.length})</p>
                                    {c.subs.map(sub => {
                                      const liveItems = sub.items.filter(it => it.itemStatus !== 'DISCARDED');
                                      const subSc = STATUS_COLOR[sub.status];
                                      const subCreated = new Date(sub.createdAt);
                                      const daysSinceSubmit = Math.ceil((Date.now() - sub.createdAt) / (1000 * 60 * 60 * 24));
                                      const lastComment = [...sub.reviewHistory].reverse().find(r => r.note);

                                      return (
                                        <div key={sub.id} className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-lg overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                                          {/* Brand header */}
                                          <div className="px-4 py-2.5 flex items-center gap-3 flex-wrap border-b border-[#2A2A2A]">
                                            <div className="w-7 h-7 rounded-lg bg-zinc-600 text-white flex items-center justify-center text-[9px] font-bold shrink-0">
                                              {sub.brandName.slice(0, 2).toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-xs font-bold text-zinc-100">{sub.brandName}</p>
                                              <div className="flex items-center gap-2 text-[10px] text-zinc-400">
                                                <span className="flex items-center gap-1"><Users size={9} /> {sub.submitterName}</span>
                                                <span className="flex items-center gap-1"><Clock size={9} /> {subCreated.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                                <span>{daysSinceSubmit}d ago</span>
                                              </div>
                                            </div>
                                            <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border", subSc.pill)}>
                                              <span className={cn("w-1.5 h-1.5 rounded-full", subSc.dot)} />
                                              {sub.status}
                                            </span>
                                            <span className="text-[10px] font-bold text-zinc-400">{liveItems.length} items</span>
                                          </div>

                                          {/* Items table */}
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                              <thead>
                                                <tr className="bg-[#252525]">
                                                  <th className="px-4 py-1.5 text-[9px] font-bold text-zinc-400 uppercase">Item</th>
                                                  <th className="px-4 py-1.5 text-[9px] font-bold text-zinc-400 uppercase text-right">Price</th>
                                                  <th className="px-4 py-1.5 text-[9px] font-bold text-zinc-400 uppercase text-right">After Discount</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {liveItems.map(item => {
                                                  const dp = camp
                                                    ? camp.discountType === DiscountType.PERCENTAGE
                                                      ? Math.round(item.price * (1 - camp.discountValue / 100) * 100) / 100
                                                      : Math.max(0, Math.round((item.price - camp.discountValue) * 100) / 100)
                                                    : item.price;
                                                  return (
                                                    <tr key={item.id} className="border-t border-[#2A2A2A]">
                                                      <td className="px-4 py-1.5">
                                                        <div className="flex items-center gap-2">
                                                          {item.imageUrl
                                                            ? <img src={getMediaUrl(item.imageUrl)} alt="" className="w-6 h-6 rounded object-cover" />
                                                            : <div className="w-6 h-6 rounded bg-[#2D2D2D] flex items-center justify-center"><ImageIcon size={8} className="text-zinc-400" /></div>
                                                          }
                                                          <div className="min-w-0">
                                                            <span className="text-[11px] font-semibold text-zinc-200 block truncate">{item.name}</span>
                                                            {item.nameAR && <span className="text-[9px] text-zinc-400 block truncate">{item.nameAR}</span>}
                                                          </div>
                                                        </div>
                                                      </td>
                                                      <td className="px-4 py-1.5 text-[11px] text-zinc-300 text-right">QAR {item.price.toFixed(2)}</td>
                                                      <td className="px-4 py-1.5 text-[11px] font-bold text-emerald-400 text-right">QAR {dp.toFixed(2)}</td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                              <tfoot>
                                                <tr className="border-t-2 border-[#444] bg-[#1A1A1A]">
                                                  <td className="px-4 py-2 text-[11px] font-bold text-zinc-200">Total ({liveItems.length} items)</td>
                                                  <td className="px-4 py-2 text-[11px] font-bold text-zinc-200 text-right">
                                                    QAR {liveItems.reduce((s, it) => s + it.price, 0).toFixed(2)}
                                                  </td>
                                                  <td className="px-4 py-2 text-[11px] font-bold text-emerald-400 text-right">
                                                    QAR {liveItems.reduce((s, it) => {
                                                      const d = camp
                                                        ? camp.discountType === DiscountType.PERCENTAGE
                                                          ? Math.round(it.price * (1 - camp.discountValue / 100) * 100) / 100
                                                          : Math.max(0, Math.round((it.price - camp.discountValue) * 100) / 100)
                                                        : it.price;
                                                      return s + d;
                                                    }, 0).toFixed(2)}
                                                  </td>
                                                </tr>
                                              </tfoot>
                                            </table>
                                          </div>

                                          {/* Review history + last comment */}
                                          {(sub.reviewHistory.length > 0 || lastComment) && (
                                            <div className="px-4 py-2 border-t border-[#2A2A2A] flex items-center gap-2 flex-wrap">
                                              {sub.reviewHistory.slice(-3).map((rh, i) => (
                                                <span key={i} className={cn("px-1.5 py-0.5 rounded text-[8px] font-bold",
                                                  rh.action === 'APPROVE' ? 'bg-emerald-900/30 text-emerald-400' :
                                                  rh.action === 'REJECT' ? 'bg-red-900/30 text-red-400' :
                                                  rh.action === 'UNLOCK' ? 'bg-amber-900/30 text-amber-400' :
                                                  'bg-blue-900/30 text-blue-400'
                                                )}>
                                                  {rh.action} · {rh.reviewerName}
                                                </span>
                                              ))}
                                              {lastComment && (
                                                <span className="flex items-center gap-1 text-[10px] text-zinc-400 italic ml-auto">
                                                  <MessageSquare size={9} /> "{lastComment.note}"
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* -- Status View -- */}
          {analyticsTab === 'status' && (
            <div className="space-y-3">
              {statusAnalytics.length > 0 && (
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Submissions Distribution by Status</p>
                  <div className="space-y-2">
                    {(() => {
                      const maxS = Math.max(...statusAnalytics.map(x => x.subs.length), 1);
                      return statusAnalytics.map(s => (
                        <div key={s.status} className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold text-zinc-300 w-36 truncate">{s.label}</span>
                          <div className="flex-1 h-6 bg-[#2D2D2D] rounded-full overflow-hidden">
                            <div className={cn("h-full rounded-full transition-all flex items-center justify-end pr-2", s.color)} style={{ width: `${(s.subs.length / maxS) * 100}%` }}>
                              {s.subs.length > 0 && <span className="text-[9px] font-bold text-white">{s.subs.length}</span>}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
              <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.4)]">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#252525] border-b border-[#333]">
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Status</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Submissions</th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusAnalytics.map(s => (
                      <React.Fragment key={s.status}>
                        <tr onClick={() => setExpandedRow(expandedRow === s.status ? null : s.status)} className={cn("border-b border-[#2A2A2A] hover:bg-[#2D2D2D] cursor-pointer transition-all duration-300", expandedRow !== null && expandedRow !== s.status && "opacity-30")}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              {expandedRow === s.status ? <ChevronUp size={12} className="text-zinc-400" /> : <ChevronDown size={12} className="text-zinc-400" />}
                              <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold text-white", s.color)}>{s.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-xs font-bold text-zinc-200 text-center">{s.subs.length}</td>
                          <td className="px-4 py-2.5 text-xs font-bold text-zinc-300 text-center">{s.items}</td>
                        </tr>
                        {expandedRow === s.status && s.subs.map(sub => (
                          <tr key={sub.id} className="bg-[#161616] border-b border-[#2A2A2A] animate-[analytics-row-fade_0.25s_ease-out]">
                            <td className="px-4 py-2 text-xs text-zinc-300 pl-10">{sub.campaignName} · <span className="text-zinc-400">{sub.brandName}</span></td>
                            <td className="px-4 py-2 text-xs text-zinc-400 text-center">{sub.submitterName ?? '·'}</td>
                            <td className="px-4 py-2 text-xs font-semibold text-zinc-200 text-center">{getApprovedItems(sub).length} items</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* -- Deep Dive View -- */}
          {analyticsTab === 'full' && (() => {
            // Pre-compute deep-dive analytics
            const allItems = filtered.flatMap(sub => {
              const camp = campaigns.find(c => c.id === sub.campaignId);
              return getApprovedItems(sub).map(item => {
                const dp = camp
                  ? camp.discountType === DiscountType.PERCENTAGE
                    ? Math.round(item.price * (1 - camp.discountValue / 100) * 100) / 100
                    : Math.max(0, Math.round((item.price - camp.discountValue) * 100) / 100)
                  : item.price;
                return { ...item, discountedPrice: dp, saving: Math.round((item.price - dp) * 100) / 100, brandName: sub.brandName, campaignName: sub.campaignName, campaignId: sub.campaignId, submissionStatus: sub.status };
              });
            });
            const totalOriginal = allItems.reduce((s, it) => s + it.price, 0);
            const totalDiscounted = allItems.reduce((s, it) => s + it.discountedPrice, 0);
            const totalSaving = totalOriginal - totalDiscounted;
            const avgPrice = allItems.length > 0 ? totalOriginal / allItems.length : 0;
            const avgDiscount = allItems.length > 0 ? totalDiscounted / allItems.length : 0;
            const maxPrice = allItems.length > 0 ? Math.max(...allItems.map(i => i.price)) : 0;
            const minPrice = allItems.length > 0 ? Math.min(...allItems.map(i => i.price)) : 0;

            // Brand performance
            const brandPerf = brandAnalytics.map(b => {
              const approved = b.subs.filter(s => [SubmissionStatus.APPROVED, SubmissionStatus.DEPLOYMENT_READY, SubmissionStatus.COMPLETED].includes(s.status as SubmissionStatus)).length;
              const rejected = b.subs.filter(s => s.status === SubmissionStatus.REJECTED).length;
              const avgResponseDays = b.subs.length > 0
                ? Math.round(b.subs.reduce((a, s) => a + Math.ceil((s.updatedAt - s.createdAt) / (1000 * 60 * 60 * 24)), 0) / b.subs.length)
                : 0;
              const brandItems = b.subs.flatMap(s => getApprovedItems(s));
              const brandAvgPrice = brandItems.length > 0 ? brandItems.reduce((a, it) => a + it.price, 0) / brandItems.length : 0;
              return { ...b, approved, rejected, rate: b.subs.length > 0 ? Math.round((approved / b.subs.length) * 100) : 0, avgResponseDays, avgPrice: brandAvgPrice };
            });

            // Aggregator coverage
            const aggSet = new Set<string>();
            filtered.forEach(s => (s.liveAggregators ?? []).forEach(a => aggSet.add(a)));
            campaigns.forEach(c => c.aggregators.forEach(a => aggSet.add(a)));
            const allAggs = Array.from(aggSet).sort();

            // Submission timeline (by week)
            const weekMap = new Map<string, { week: string; count: number; items: number }>();
            filtered.forEach(sub => {
              const d = new Date(sub.createdAt);
              const weekStart = new Date(d);
              weekStart.setDate(d.getDate() - d.getDay());
              const wk = weekStart.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
              const e = weekMap.get(wk) ?? { week: wk, count: 0, items: 0 };
              e.count++;
              e.items += getApprovedItems(sub).length;
              weekMap.set(wk, e);
            });
            const timeline = Array.from(weekMap.values());

            // Review cycle stats
            const avgReviewRounds = filtered.length > 0 ? (filtered.reduce((a, s) => a + s.reviewHistory.length, 0) / filtered.length).toFixed(1) : '0';
            const avgDaysToResolve = filtered.length > 0
              ? Math.round(filtered.reduce((a, s) => a + Math.ceil((s.updatedAt - s.createdAt) / (1000 * 60 * 60 * 24)), 0) / filtered.length)
              : 0;

            return (
              <div className="space-y-4">
                {/* -- KPI Cards Row -- */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {[
                    { label: 'Total Original', value: `QAR ${totalOriginal.toFixed(0)}`, color: 'text-zinc-100', sub: `${allItems.length} items` },
                    { label: 'Total After Discount', value: `QAR ${totalDiscounted.toFixed(0)}`, color: 'text-emerald-400', sub: `${((totalSaving / (totalOriginal || 1)) * 100).toFixed(1)}% off` },
                    { label: 'Total Savings', value: `QAR ${totalSaving.toFixed(0)}`, color: 'text-red-400', sub: 'campaign savings' },
                    { label: 'Avg Item Price', value: `QAR ${avgPrice.toFixed(2)}`, color: 'text-blue-700', sub: `${minPrice.toFixed(0)} · ${maxPrice.toFixed(0)} range` },
                    { label: 'Avg Review Rounds', value: avgReviewRounds, color: 'text-amber-400', sub: `${avgDaysToResolve}d avg resolve` },
                    { label: 'Approval Rate', value: `${filtered.length > 0 ? Math.round((filtered.filter(s => [SubmissionStatus.APPROVED, SubmissionStatus.DEPLOYMENT_READY, SubmissionStatus.COMPLETED].includes(s.status as SubmissionStatus)).length / filtered.length) * 100) : 0}%`, color: 'text-violet-400', sub: `${filtered.filter(s => s.status === SubmissionStatus.REJECTED).length} rejected` },
                  ].map((kpi, i) => (
                    <div key={i} className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                      <p className={cn("text-xl font-bold", kpi.color)}>{kpi.value}</p>
                      <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">{kpi.label}</p>
                      <p className="text-[10px] text-zinc-400 mt-0.5">{kpi.sub}</p>
                    </div>
                  ))}
                </div>

                {/* -- Submission Timeline -- */}
                {timeline.length > 0 && (
                  <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Submission Activity by Week</p>
                    <div className="space-y-2">
                      {(() => {
                        const maxC = Math.max(...timeline.map(t => t.count), 1);
                        return timeline.map((t, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-[11px] font-semibold text-zinc-300 w-20 shrink-0">{t.week}</span>
                            <div className="flex-1 h-7 bg-[#2D2D2D] rounded-full overflow-hidden relative">
                              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all flex items-center justify-end pr-2" style={{ width: `${(t.count / maxC) * 100}%` }}>
                                <span className="text-[9px] font-bold text-white">{t.count} subs</span>
                              </div>
                            </div>
                            <span className="text-[10px] font-bold text-zinc-400 w-16 text-right">{t.items} items</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* -- Price Distribution Analysis -- */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Price Impact by Campaign</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-[#252525] border-b border-[#333]">
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Campaign</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-right">Items</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-right">Original Total</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-right">After Discount</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-right">Savings</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-right">Avg Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const campMap = new Map<string, { name: string; items: number; orig: number; disc: number }>();
                          allItems.forEach(it => {
                            const e = campMap.get(it.campaignId) ?? { name: it.campaignName, items: 0, orig: 0, disc: 0 };
                            e.items++; e.orig += it.price; e.disc += it.discountedPrice;
                            campMap.set(it.campaignId, e);
                          });
                          return Array.from(campMap.entries()).sort((a, b) => b[1].orig - a[1].orig).map(([id, v]) => (
                            <tr key={id} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                              <td className="px-4 py-2 text-xs font-semibold text-zinc-100">{v.name}</td>
                              <td className="px-4 py-2 text-xs text-zinc-300 text-right">{v.items}</td>
                              <td className="px-4 py-2 text-xs text-zinc-300 text-right">QAR {v.orig.toFixed(2)}</td>
                              <td className="px-4 py-2 text-xs font-bold text-emerald-400 text-right">QAR {v.disc.toFixed(2)}</td>
                              <td className="px-4 py-2 text-xs font-bold text-red-400 text-right">QAR {(v.orig - v.disc).toFixed(2)}</td>
                              <td className="px-4 py-2 text-xs text-zinc-400 text-right">QAR {v.items > 0 ? (v.orig / v.items).toFixed(2) : '0.00'}</td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-[#444] bg-[#1A1A1A]">
                          <td className="px-4 py-2 text-xs font-bold text-zinc-200">Grand Total</td>
                          <td className="px-4 py-2 text-xs font-bold text-zinc-200 text-right">{allItems.length}</td>
                          <td className="px-4 py-2 text-xs font-bold text-zinc-200 text-right">QAR {totalOriginal.toFixed(2)}</td>
                          <td className="px-4 py-2 text-xs font-bold text-emerald-400 text-right">QAR {totalDiscounted.toFixed(2)}</td>
                          <td className="px-4 py-2 text-xs font-bold text-red-400 text-right">QAR {totalSaving.toFixed(2)}</td>
                          <td className="px-4 py-2 text-xs font-bold text-zinc-400 text-right">QAR {avgPrice.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* -- Brand Performance Matrix -- */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Brand Performance Scorecard</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-[#252525] border-b border-[#333]">
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Brand</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Subs</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Items</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-right">Avg Price</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Approval Rate</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Avg Response</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Health</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brandPerf.sort((a, b) => b.rate - a.rate).map(b => (
                          <tr key={b.brandId} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-md bg-zinc-600 text-white flex items-center justify-center text-[8px] font-bold shrink-0">
                                  {b.brandName.slice(0, 2).toUpperCase()}
                                </div>
                                <span className="text-xs font-semibold text-zinc-100">{b.brandName}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-zinc-300 text-center">{b.subs.length}</td>
                            <td className="px-4 py-2.5 text-xs font-bold text-zinc-200 text-center">{b.items}</td>
                            <td className="px-4 py-2.5 text-xs text-zinc-300 text-right">QAR {b.avgPrice.toFixed(2)}</td>
                            <td className="px-4 py-2.5 text-center">
                              <div className="inline-flex items-center gap-1.5">
                                <div className="w-16 h-2 bg-[#2D2D2D] rounded-full overflow-hidden">
                                  <div className={cn("h-full rounded-full", b.rate >= 80 ? "bg-emerald-500" : b.rate >= 50 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${b.rate}%` }} />
                                </div>
                                <span className="text-[10px] font-bold text-zinc-300">{b.rate}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded",
                                b.avgResponseDays <= 2 ? "bg-emerald-900/30 text-emerald-400" :
                                b.avgResponseDays <= 5 ? "bg-amber-900/30 text-amber-400" :
                                "bg-red-900/30 text-red-400"
                              )}>{b.avgResponseDays}d</span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={cn("text-xs font-bold",
                                b.rate >= 80 && b.avgResponseDays <= 3 ? "text-emerald-600" :
                                b.rate >= 50 ? "text-amber-600" :
                                "text-red-400"
                              )}>
                                {b.rate >= 80 && b.avgResponseDays <= 3 ? '? Excellent' :
                                 b.rate >= 50 ? '? Good' : '? Needs Attention'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* -- Aggregator Coverage Heatmap -- */}
                {allAggs.length > 0 && (
                  <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Aggregator Coverage Matrix</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-[#252525] border-b border-[#333]">
                            <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest sticky left-0 bg-[#252525] z-10">Brand</th>
                            {allAggs.map(a => (
                              <th key={a} className="px-3 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">{a}</th>
                            ))}
                            <th className="px-3 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Coverage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brandAnalytics.map(b => {
                            const liveCount = allAggs.filter(a => b.subs.some(s => (s.liveAggregators ?? []).includes(a))).length;
                            return (
                              <tr key={b.brandId} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                                <td className="px-4 py-2 text-xs font-semibold text-zinc-100 sticky left-0 bg-[#1E1E1E] z-10">{b.brandName}</td>
                                {allAggs.map(a => {
                                  const isLive = b.subs.some(s => (s.liveAggregators ?? []).includes(a));
                                  return (
                                    <td key={a} className="px-3 py-2 text-center">
                                      {isLive
                                        ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-900/40 text-emerald-400"><CheckCircle2 size={12} /></span>
                                        : <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2D2D2D] text-zinc-400"><XCircle size={12} /></span>
                                      }
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 text-center">
                                  <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full",
                                    liveCount === allAggs.length ? "bg-emerald-900/40 text-emerald-400" :
                                    liveCount > 0 ? "bg-amber-900/40 text-amber-400" :
                                    "bg-red-900/40 text-red-400"
                                  )}>{liveCount}/{allAggs.length}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* -- Review Cycle Analytics -- */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(255,255,255,0.05)]">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Review Cycle Breakdown</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-[#252525] border-b border-[#333]">
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Campaign / Brand</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Status</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Review Rounds</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Days to Resolve</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Last Action</th>
                          <th className="px-4 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Manager</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.sort((a, b) => b.reviewHistory.length - a.reviewHistory.length).map(sub => {
                          const sc = STATUS_COLOR[sub.status];
                          const days = Math.ceil((sub.updatedAt - sub.createdAt) / (1000 * 60 * 60 * 24));
                          const last = sub.reviewHistory.length > 0 ? sub.reviewHistory[sub.reviewHistory.length - 1] : null;
                          return (
                            <tr key={sub.id} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                              <td className="px-4 py-2">
                                <span className="text-xs font-semibold text-zinc-100">{sub.campaignName}</span>
                                <span className="text-[10px] text-zinc-400 ml-1.5">· {sub.brandName}</span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border", sc.pill)}>
                                  <span className={cn("w-1.5 h-1.5 rounded-full", sc.dot)} />
                                  {sub.status}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className={cn("text-xs font-bold", sub.reviewHistory.length > 3 ? "text-red-400" : sub.reviewHistory.length > 1 ? "text-amber-600" : "text-zinc-300")}>
                                  {sub.reviewHistory.length}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded",
                                  days <= 2 ? "bg-emerald-900/30 text-emerald-400" :
                                  days <= 7 ? "bg-amber-900/30 text-amber-400" :
                                  "bg-red-900/30 text-red-400"
                                )}>{days}d</span>
                              </td>
                              <td className="px-4 py-2 text-xs text-zinc-300">{last ? `${last.action} by ${last.reviewerName}` : '·'}</td>
                              <td className="px-4 py-2 text-xs text-zinc-400">{sub.submitterName}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* -- BI Export -- */}
          {analyticsTab === 'bi-export' && (() => {
            const computeDiscountedPrice = (price: number, camp: Campaign | undefined) => {
              if (!camp) return price;
              return camp.discountType === DiscountType.PERCENTAGE
                ? Math.round(price * (1 - camp.discountValue / 100) * 100) / 100
                : Math.max(0, Math.round((price - camp.discountValue) * 100) / 100);
            };

            // Sheet 1: Full Submission Details
            const biSheet1 = filtered.map(sub => {
              const camp = campaignFor(sub.campaignId);
              const items = getApprovedItems(sub);
              const origTotal = items.reduce((s, it) => s + it.price, 0);
              const discTotal = items.reduce((s, it) => s + computeDiscountedPrice(it.price, camp), 0);
              const days = Math.ceil((sub.updatedAt - sub.createdAt) / (1000 * 60 * 60 * 24));
              const lastReview = sub.reviewHistory.length > 0 ? sub.reviewHistory[sub.reviewHistory.length - 1] : null;
              return {
                campaign: sub.campaignName, brand: sub.brandName,
                aggregators: (camp?.aggregators ?? []).join(', '),
                status: sub.status, stage: sub.currentStage,
                items: items.length, origTotal, discTotal, savings: origTotal - discTotal,
                submittedBy: sub.submitterName || sub.submittedBy,
                reviewRounds: sub.reviewHistory.length, daysToResolve: days,
                lastAction: lastReview ? `${lastReview.action} by ${lastReview.reviewerName}` : '·',
                liveAggregators: (sub.liveAggregators ?? []).join(', '),
                rejectionReason: sub.rejectionReason ?? '',
                postponeReason: sub.postponeReason ?? '',
                funding: camp ? FUNDING_LABELS[camp.fundingType] : '·',
                discount: camp ? (camp.discountType === DiscountType.PERCENTAGE ? `${camp.discountValue}%` : `QAR ${camp.discountValue}`) : '·',
                date: new Date(sub.createdAt).toLocaleDateString(),
                updated: new Date(sub.updatedAt).toLocaleDateString(),
              };
            });

            // Sheet 2: Item-Level Detail
            const biSheet2 = filtered.flatMap(sub => {
              const camp = campaignFor(sub.campaignId);
              return getApprovedItems(sub).map(item => {
                const dp = computeDiscountedPrice(item.price, camp);
                return {
                  campaign: sub.campaignName, brand: sub.brandName,
                  itemName: item.name, nameAR: item.nameAR ?? '',
                  price: item.price, discountedPrice: dp, saving: item.price - dp,
                  description: item.description ?? '',
                  itemStatus: item.itemStatus ?? 'ACTIVE',
                  menuLinked: item.menuLinked ? 'Yes' : 'No',
                  menuProfile: item.menuProfileName ?? '',
                  locked: item.locked ? 'Locked' : 'Unlocked',
                  adminNote: item.adminNote ?? '',
                  submissionStatus: sub.status,
                  aggregators: (camp?.aggregators ?? []).join(', '),
                };
              });
            });

            // Sheet 3: Brand Performance (full)
            const biSheet3 = brandAnalytics.map(b => {
              const statusCounts: Record<string, number> = {};
              b.subs.forEach(s => { statusCounts[s.status] = (statusCounts[s.status] ?? 0) + 1; });
              const approvedCount = (statusCounts[SubmissionStatus.APPROVED] ?? 0) + (statusCounts[SubmissionStatus.COMPLETED] ?? 0) + (statusCounts[SubmissionStatus.DEPLOYMENT_READY] ?? 0);
              const rate = b.subs.length > 0 ? Math.round((approvedCount / b.subs.length) * 100) : 0;
              let origTotal = 0, discTotal = 0;
              b.subs.forEach(sub => {
                const camp = campaignFor(sub.campaignId);
                getApprovedItems(sub).forEach(it => { origTotal += it.price; discTotal += computeDiscountedPrice(it.price, camp); });
              });
              const avgDays = b.subs.length > 0 ? Math.round(b.subs.reduce((s, sub) => s + Math.ceil((sub.updatedAt - sub.createdAt) / (1000 * 60 * 60 * 24)), 0) / b.subs.length) : 0;
              return {
                brand: b.brandName, campaigns: b.campaignIds.size,
                submissions: b.subs.length, items: b.items,
                aggregators: Array.from(b.aggs).join(', '),
                pending: statusCounts[SubmissionStatus.PENDING] ?? 0,
                inReview: statusCounts[SubmissionStatus.IN_REVIEW] ?? 0,
                approved: statusCounts[SubmissionStatus.APPROVED] ?? 0,
                deployReady: statusCounts[SubmissionStatus.DEPLOYMENT_READY] ?? 0,
                live: statusCounts[SubmissionStatus.COMPLETED] ?? 0,
                postponed: statusCounts[SubmissionStatus.POSTPONED] ?? 0,
                rejected: statusCounts[SubmissionStatus.REJECTED] ?? 0,
                approvalRate: rate, origTotal, discTotal, savings: origTotal - discTotal,
                avgResponseDays: avgDays,
              };
            });

            // Sheet 4: Aggregator Coverage (full)
            const biSheet4 = aggregatorAnalytics.map(a => {
              const statusCounts: Record<string, number> = {};
              a.subs.forEach(s => { statusCounts[s.status] = (statusCounts[s.status] ?? 0) + 1; });
              let origTotal = 0, discTotal = 0;
              a.subs.forEach(sub => {
                const camp = campaignFor(sub.campaignId);
                getApprovedItems(sub).forEach(it => { origTotal += it.price; discTotal += computeDiscountedPrice(it.price, camp); });
              });
              const brandNames = Array.from(a.brandIds).map(bId => {
                const brand = brands.find(b => b.id === bId);
                return brand?.name ?? bId;
              });
              return {
                aggregator: a.aggregator, campaigns: a.campaignIds.size,
                brands: a.brandIds.size, brandList: brandNames.join(', '),
                submissions: a.subs.length, items: a.items,
                pending: statusCounts[SubmissionStatus.PENDING] ?? 0,
                inReview: statusCounts[SubmissionStatus.IN_REVIEW] ?? 0,
                approved: statusCounts[SubmissionStatus.APPROVED] ?? 0,
                live: statusCounts[SubmissionStatus.COMPLETED] ?? 0,
                rejected: statusCounts[SubmissionStatus.REJECTED] ?? 0,
                origTotal, discTotal, savings: origTotal - discTotal,
              };
            });

            // Sheet 5: Campaign Performance (full)
            const biSheet5 = campaignAnalyticsData.map(c => {
              const camp = c.camp;
              let origTotal = 0, discTotal = 0;
              c.subs.forEach(sub => {
                getApprovedItems(sub).forEach(it => { origTotal += it.price; discTotal += computeDiscountedPrice(it.price, camp); });
              });
              const statusCounts: Record<string, number> = {};
              c.subs.forEach(s => { statusCounts[s.status] = (statusCounts[s.status] ?? 0) + 1; });
              const totalDays = camp ? Math.ceil((camp.validTo - camp.validFrom) / (1000 * 60 * 60 * 24)) : 0;
              const remaining = camp ? Math.ceil((camp.validTo - Date.now()) / (1000 * 60 * 60 * 24)) : 0;
              return {
                campaign: c.campaignName, status: camp?.status ?? '·',
                brands: c.brandNames.size, brandList: Array.from(c.brandNames.values()).join(', '),
                aggregators: (camp?.aggregators ?? []).join(', '),
                submissions: c.subs.length, items: c.items,
                origTotal, discTotal, savings: origTotal - discTotal,
                discountType: camp?.discountType ?? '·',
                discountValue: camp ? (camp.discountType === DiscountType.PERCENTAGE ? `${camp.discountValue}%` : `QAR ${camp.discountValue}`) : '·',
                funding: camp ? FUNDING_LABELS[camp.fundingType] : '·',
                sterlingShare: camp?.sterlingSharePct != null ? `${camp.sterlingSharePct}%` : '·',
                partnerShare: camp?.partnerSharePct != null ? `${camp.partnerSharePct}%` : '·',
                duration: `${totalDays}d`, daysRemaining: remaining > 0 ? `${remaining}d` : 'Ended',
                validFrom: camp ? fmtDate(camp.validFrom) : '·',
                validTo: camp ? fmtDate(camp.validTo) : '·',
                target: camp?.requiredItemCount ?? '·',
                pending: statusCounts[SubmissionStatus.PENDING] ?? 0,
                approved: statusCounts[SubmissionStatus.APPROVED] ?? 0,
                live: statusCounts[SubmissionStatus.COMPLETED] ?? 0,
                rejected: statusCounts[SubmissionStatus.REJECTED] ?? 0,
              };
            });

            // Sheet 6: Review Cycle & Status
            const biSheet6 = filtered.map(sub => {
              const days = Math.ceil((sub.updatedAt - sub.createdAt) / (1000 * 60 * 60 * 24));
              const reviews = sub.reviewHistory;
              const approvals = reviews.filter(r => r.action === 'APPROVE').length;
              const rejections = reviews.filter(r => r.action === 'REJECT').length;
              const unlocks = reviews.filter(r => r.action === 'UNLOCK').length;
              const lastReview = reviews.length > 0 ? reviews[reviews.length - 1] : null;
              const departments = [...new Set(reviews.map(r => r.departmentName))];
              return {
                campaign: sub.campaignName, brand: sub.brandName,
                status: sub.status, stage: sub.currentStage,
                totalRounds: reviews.length, approvals, rejections, unlocks,
                daysToResolve: days,
                departments: departments.join(', '),
                lastAction: lastReview ? lastReview.action : '·',
                lastReviewer: lastReview ? lastReview.reviewerName : '·',
                lastNote: lastReview?.note ?? '',
                lastDate: lastReview ? new Date(lastReview.timestamp).toLocaleDateString() : '·',
                discussionCount: (sub.discussion ?? []).length,
                rejectionReason: sub.rejectionReason ?? '',
                postponeReason: sub.postponeReason ?? '',
              };
            });

            const downloadBIExport = () => {
              const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
              let tsv = 'AGGREGATOR BI EXPORT · FULL DETAILS\n';
              tsv += `Generated: ${new Date().toLocaleString()} · ${filtered.length} submissions · ${totalItems} items\n\n`;

              tsv += '=== SHEET 1: SUBMISSION DETAILS ===\n';
              tsv += 'Campaign\tBrand\tAggregators\tStatus\tStage\tItems\tOriginal (QAR)\tDiscounted (QAR)\tSavings (QAR)\tSubmitted By\tReview Rounds\tDays to Resolve\tLast Action\tLive Aggregators\tRejection Reason\tPostpone Reason\tFunding\tDiscount\tDate\tLast Updated\n';
              biSheet1.forEach(r => {
                tsv += `${r.campaign}\t${r.brand}\t${r.aggregators}\t${r.status}\t${r.stage}\t${r.items}\t${r.origTotal.toFixed(2)}\t${r.discTotal.toFixed(2)}\t${r.savings.toFixed(2)}\t${r.submittedBy}\t${r.reviewRounds}\t${r.daysToResolve}\t${r.lastAction}\t${r.liveAggregators}\t${r.rejectionReason}\t${r.postponeReason}\t${r.funding}\t${r.discount}\t${r.date}\t${r.updated}\n`;
              });

              tsv += '\n=== SHEET 2: ITEM-LEVEL DETAIL ===\n';
              tsv += 'Campaign\tBrand\tItem Name\tName (AR)\tPrice (QAR)\tDiscounted (QAR)\tSaving (QAR)\tDescription\tItem Status\tMenu Linked\tMenu Profile\tLocked\tAdmin Note\tSubmission Status\tAggregators\n';
              biSheet2.forEach(r => {
                tsv += `${r.campaign}\t${r.brand}\t${r.itemName}\t${r.nameAR}\t${r.price.toFixed(2)}\t${r.discountedPrice.toFixed(2)}\t${r.saving.toFixed(2)}\t${r.description}\t${r.itemStatus}\t${r.menuLinked}\t${r.menuProfile}\t${r.locked}\t${r.adminNote}\t${r.submissionStatus}\t${r.aggregators}\n`;
              });

              tsv += '\n=== SHEET 3: BRAND PERFORMANCE ===\n';
              tsv += 'Brand\tCampaigns\tSubmissions\tItems\tAggregators\tPending\tIn Review\tApproved\tDeploy Ready\tLive\tPostponed\tRejected\tApproval Rate\tOriginal (QAR)\tDiscounted (QAR)\tSavings (QAR)\tAvg Response Days\n';
              biSheet3.forEach(r => {
                tsv += `${r.brand}\t${r.campaigns}\t${r.submissions}\t${r.items}\t${r.aggregators}\t${r.pending}\t${r.inReview}\t${r.approved}\t${r.deployReady}\t${r.live}\t${r.postponed}\t${r.rejected}\t${r.approvalRate}%\t${r.origTotal.toFixed(2)}\t${r.discTotal.toFixed(2)}\t${r.savings.toFixed(2)}\t${r.avgResponseDays}\n`;
              });

              tsv += '\n=== SHEET 4: AGGREGATOR COVERAGE ===\n';
              tsv += 'Aggregator\tCampaigns\tBrands\tBrand Names\tSubmissions\tItems\tPending\tIn Review\tApproved\tLive\tRejected\tOriginal (QAR)\tDiscounted (QAR)\tSavings (QAR)\n';
              biSheet4.forEach(r => {
                tsv += `${r.aggregator}\t${r.campaigns}\t${r.brands}\t${r.brandList}\t${r.submissions}\t${r.items}\t${r.pending}\t${r.inReview}\t${r.approved}\t${r.live}\t${r.rejected}\t${r.origTotal.toFixed(2)}\t${r.discTotal.toFixed(2)}\t${r.savings.toFixed(2)}\n`;
              });

              tsv += '\n=== SHEET 5: CAMPAIGN PERFORMANCE ===\n';
              tsv += 'Campaign\tStatus\tBrands\tBrand Names\tAggregators\tSubmissions\tItems\tOriginal (QAR)\tDiscounted (QAR)\tSavings (QAR)\tDiscount\tFunding\tSterling Share\tPartner Share\tDuration\tDays Remaining\tValid From\tValid To\tTarget Items\tPending\tApproved\tLive\tRejected\n';
              biSheet5.forEach(r => {
                tsv += `${r.campaign}\t${r.status}\t${r.brands}\t${r.brandList}\t${r.aggregators}\t${r.submissions}\t${r.items}\t${r.origTotal.toFixed(2)}\t${r.discTotal.toFixed(2)}\t${r.savings.toFixed(2)}\t${r.discountValue}\t${r.funding}\t${r.sterlingShare}\t${r.partnerShare}\t${r.duration}\t${r.daysRemaining}\t${r.validFrom}\t${r.validTo}\t${r.target}\t${r.pending}\t${r.approved}\t${r.live}\t${r.rejected}\n`;
              });

              tsv += '\n=== SHEET 6: REVIEW CYCLE & STATUS ===\n';
              tsv += 'Campaign\tBrand\tStatus\tStage\tTotal Rounds\tApprovals\tRejections\tUnlocks\tDays to Resolve\tDepartments\tLast Action\tLast Reviewer\tLast Note\tLast Date\tDiscussions\tRejection Reason\tPostpone Reason\n';
              biSheet6.forEach(r => {
                tsv += `${r.campaign}\t${r.brand}\t${r.status}\t${r.stage}\t${r.totalRounds}\t${r.approvals}\t${r.rejections}\t${r.unlocks}\t${r.daysToResolve}\t${r.departments}\t${r.lastAction}\t${r.lastReviewer}\t${r.lastNote}\t${r.lastDate}\t${r.discussionCount}\t${r.rejectionReason}\t${r.postponeReason}\n`;
              });

              const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `Aggregator_BI_Export_Full_${dateStr}.tsv`;
              document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
            };

            return (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-blue-400" />
                    <span className="text-sm font-bold text-zinc-100">BI Export · Full Details</span>
                    <span className="text-[10px] text-zinc-500 ml-1">{filtered.length} submissions · {totalItems} items · {biSheet2.length} line items</span>
                  </div>
                  <button onClick={downloadBIExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors">
                    <Download size={12} /> Download Full TSV
                  </button>
                </div>

                {/* Sheet 1: Submission Details */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                  <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 1 · Submission Details</p>
                    <span className="text-[10px] text-zinc-600">{biSheet1.length} rows</span>
                  </div>
                  <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-[#252525] border-b border-[#333]">
                          {['Camp','Brand','Aggs','Status','Stage','#','Orig','Disc','Save','By','Rev','Days','Action','Live','Fund','Disc%','Date','Upd'].map(h => (
                            <th key={h} className="px-1.5 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {biSheet1.length === 0 ? (
                          <tr><td colSpan={18} className="px-4 py-8 text-center text-xs text-zinc-500">No submissions match current filters</td></tr>
                        ) : biSheet1.map((r, i) => (
                          <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                            <td className="px-1.5 py-1.5 text-[11px] font-semibold text-zinc-100 max-w-[90px] truncate">{r.campaign}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 max-w-[80px] truncate">{r.brand}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[70px] truncate">{r.aggregators}</td>
                            <td className="px-1.5 py-1.5">
                              <span className={cn("text-[8px] font-bold px-1 py-0.5 rounded-full",
                                r.status === SubmissionStatus.APPROVED || r.status === SubmissionStatus.COMPLETED ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-800' :
                                r.status === SubmissionStatus.REJECTED ? 'bg-red-900/30 text-red-400 border border-red-800' :
                                r.status === SubmissionStatus.IN_REVIEW ? 'bg-blue-900/30 text-blue-400 border border-blue-800' :
                                'bg-amber-900/30 text-amber-400 border border-amber-800'
                              )}>{r.status}</span>
                            </td>
                            <td className="px-1.5 py-1.5 text-[10px] text-zinc-500">{r.stage}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-zinc-200 text-right">{r.items}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 text-right">{r.origTotal.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-emerald-400 text-right">{r.discTotal.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-red-400 text-right">{r.savings.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[70px] truncate">{r.submittedBy}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 text-center">{r.reviewRounds}</td>
                            <td className="px-1.5 py-1.5"><span className={cn("text-[9px] font-bold px-1 py-0.5 rounded", r.daysToResolve <= 2 ? "bg-emerald-900/30 text-emerald-400" : r.daysToResolve <= 7 ? "bg-amber-900/30 text-amber-400" : "bg-red-900/30 text-red-400")}>{r.daysToResolve}d</span></td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-500">{r.lastAction}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[60px] truncate">{r.liveAggregators || '·'}</td>
                            <td className="px-1.5 py-1.5"><span className="text-[8px] font-bold px-1 py-0.5 rounded bg-[#2D2D2D] text-zinc-300">{r.funding}</span></td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-teal-400">{r.discount}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-500">{r.date}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-500">{r.updated}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sheet 2: Item-Level Detail */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                  <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 2 · Item-Level Detail</p>
                    <span className="text-[10px] text-zinc-600">{biSheet2.length} items</span>
                  </div>
                  <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-[#252525] border-b border-[#333]">
                          {['Camp','Brand','Item','AR','Price','Disc','Save','Status','Menu','Profile','Lock','Note','Sub','Aggs'].map(h => (
                            <th key={h} className="px-1.5 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {biSheet2.length === 0 ? (
                          <tr><td colSpan={14} className="px-4 py-8 text-center text-xs text-zinc-500">No items available</td></tr>
                        ) : biSheet2.map((r, i) => (
                          <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[80px] truncate">{r.campaign}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 max-w-[70px] truncate">{r.brand}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-semibold text-zinc-100 max-w-[90px] truncate">{r.itemName}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[70px] truncate" dir="rtl">{r.nameAR || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 text-right">{r.price.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-emerald-400 text-right">{r.discountedPrice.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-red-400 text-right">{r.saving.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5"><span className={cn("text-[8px] font-bold px-1 py-0.5 rounded", r.itemStatus === 'ACTIVE' ? "bg-emerald-900/30 text-emerald-400" : "bg-red-900/30 text-red-400")}>{r.itemStatus}</span></td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 text-center">{r.menuLinked}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-500 max-w-[60px] truncate">{r.menuProfile || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-500">{r.locked}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-500 max-w-[80px] truncate">{r.adminNote || '·'}</td>
                            <td className="px-1.5 py-1.5">
                              <span className={cn("text-[8px] font-bold px-1 py-0.5 rounded-full",
                                r.submissionStatus === SubmissionStatus.APPROVED || r.submissionStatus === SubmissionStatus.COMPLETED ? 'bg-emerald-900/30 text-emerald-400' :
                                r.submissionStatus === SubmissionStatus.REJECTED ? 'bg-red-900/30 text-red-400' :
                                'bg-amber-900/30 text-amber-400'
                              )}>{r.submissionStatus}</span>
                            </td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[60px] truncate">{r.aggregators}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sheet 3: Brand Performance */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                  <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 3 · Brand Performance</p>
                    <span className="text-[10px] text-zinc-600">{biSheet3.length} brands</span>
                  </div>
                  <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-[#252525] border-b border-[#333]">
                          {['Brand','Camp','Subs','#','Aggs','Pend','Rev','Appr','Dep','Live','Post','Rej','Rate','Orig','Disc','Save','Days'].map(h => (
                            <th key={h} className="px-1.5 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {biSheet3.length === 0 ? (
                          <tr><td colSpan={17} className="px-4 py-8 text-center text-xs text-zinc-500">No brand data available</td></tr>
                        ) : biSheet3.map((r, i) => (
                          <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                            <td className="px-1.5 py-1.5">
                              <div className="flex items-center gap-1.5">
                                <div className="w-5 h-5 rounded bg-zinc-600 text-white flex items-center justify-center text-[7px] font-bold shrink-0">{r.brand.slice(0, 2).toUpperCase()}</div>
                                <span className="text-[11px] font-semibold text-zinc-100 max-w-[70px] truncate">{r.brand}</span>
                              </div>
                            </td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 text-center">{r.campaigns}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 text-center">{r.submissions}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-zinc-200 text-center">{r.items}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[60px] truncate">{r.aggregators}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-amber-400 text-center">{r.pending || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-blue-400 text-center">{r.inReview || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-emerald-400 text-center">{r.approved || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-violet-400 text-center">{r.deployReady || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-green-400 text-center">{r.live || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-orange-400 text-center">{r.postponed || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-red-400 text-center">{r.rejected || '·'}</td>
                            <td className="px-1.5 py-1.5">
                              <div className="flex items-center gap-1">
                                <div className="w-8 h-1.5 bg-[#2D2D2D] rounded-full overflow-hidden"><div className={cn("h-full rounded-full", r.approvalRate >= 80 ? "bg-emerald-500" : r.approvalRate >= 50 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${r.approvalRate}%` }} /></div>
                                <span className="text-[9px] font-bold text-zinc-300">{r.approvalRate}%</span>
                              </div>
                            </td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 text-right">{r.origTotal.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-emerald-400 text-right">{r.discTotal.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-red-400 text-right">{r.savings.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5"><span className={cn("text-[9px] font-bold px-1 py-0.5 rounded", r.avgResponseDays <= 2 ? "bg-emerald-900/30 text-emerald-400" : r.avgResponseDays <= 5 ? "bg-amber-900/30 text-amber-400" : "bg-red-900/30 text-red-400")}>{r.avgResponseDays}d</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sheet 4: Aggregator Coverage */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                  <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 4 · Aggregator Coverage</p>
                    <span className="text-[10px] text-zinc-600">{biSheet4.length} aggregators</span>
                  </div>
                  <div className="overflow-x-auto max-h-[260px] overflow-y-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-[#252525] border-b border-[#333]">
                          {['Agg','Camp','Brands','Brand Names','Subs','#','Pend','Rev','Appr','Live','Rej','Orig','Disc','Save'].map(h => (
                            <th key={h} className="px-1.5 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {biSheet4.length === 0 ? (
                          <tr><td colSpan={14} className="px-4 py-8 text-center text-xs text-zinc-500">No aggregator data</td></tr>
                        ) : biSheet4.map((r, i) => (
                          <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-zinc-100">{r.aggregator}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 text-center">{r.campaigns}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-zinc-200 text-center">{r.brands}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[120px] truncate">{r.brandList}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 text-center">{r.submissions}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-zinc-200 text-center">{r.items}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-amber-400 text-center">{r.pending || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-blue-400 text-center">{r.inReview || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-emerald-400 text-center">{r.approved || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-green-400 text-center">{r.live || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-red-400 text-center">{r.rejected || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 text-right">{r.origTotal.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-emerald-400 text-right">{r.discTotal.toFixed(0)}</td>
                            <td className="px-1.5 py-1.5 text-[11px] font-bold text-red-400 text-right">{r.savings.toFixed(0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sheet 5: Campaign Performance */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                  <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 5 · Campaign Performance</p>
                    <span className="text-[10px] text-zinc-600">{biSheet5.length} campaigns</span>
                  </div>
                  <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-[#252525] border-b border-[#333]">
                          {['Camp','Sts','Br','Aggs','Sub','#','Orig','Disc','Save','Disc%','Fund','St%','Pt%','Dur','Rem','From','To','Tgt','Pend','Appr','Live','Rej'].map(h => (
                            <th key={h} className="px-1 py-1.5 text-[8px] font-bold text-zinc-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {biSheet5.length === 0 ? (
                          <tr><td colSpan={22} className="px-4 py-8 text-center text-xs text-zinc-500">No campaign data</td></tr>
                        ) : biSheet5.map((r, i) => (
                          <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                            <td className="px-1 py-1.5 text-[10px] font-semibold text-zinc-100 max-w-[80px] truncate">{r.campaign}</td>
                            <td className="px-1 py-1.5"><span className={cn("text-[7px] font-bold px-1 py-0.5 rounded uppercase", r.status === CampaignStatus.ACTIVE ? "bg-emerald-900/30 text-emerald-400" : r.status === CampaignStatus.PLANNED ? "bg-blue-900/30 text-blue-400" : r.status === CampaignStatus.CLOSED ? "bg-zinc-700 text-zinc-400" : "bg-amber-900/30 text-amber-400")}>{r.status}</span></td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-300 text-center">{r.brands}</td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-400 max-w-[50px] truncate">{r.aggregators}</td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-300 text-center">{r.submissions}</td>
                            <td className="px-1 py-1.5 text-[10px] font-bold text-zinc-200 text-center">{r.items}</td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-300 text-right">{r.origTotal.toFixed(0)}</td>
                            <td className="px-1 py-1.5 text-[10px] font-bold text-emerald-400 text-right">{r.discTotal.toFixed(0)}</td>
                            <td className="px-1 py-1.5 text-[10px] font-bold text-red-400 text-right">{r.savings.toFixed(0)}</td>
                            <td className="px-1 py-1.5 text-[10px] font-bold text-teal-400">{r.discountValue}</td>
                            <td className="px-1 py-1.5"><span className="text-[7px] font-bold px-1 py-0.5 rounded bg-[#2D2D2D] text-zinc-300">{r.funding}</span></td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-400 text-center">{r.sterlingShare}</td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-400 text-center">{r.partnerShare}</td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-400 text-center">{r.duration}</td>
                            <td className="px-1 py-1.5"><span className={cn("text-[9px] font-bold px-1 py-0.5 rounded", r.daysRemaining === 'Ended' ? "bg-[#2D2D2D] text-zinc-400" : "bg-emerald-900/30 text-emerald-400")}>{r.daysRemaining}</span></td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-500">{r.validFrom}</td>
                            <td className="px-1 py-1.5 text-[10px] text-zinc-500">{r.validTo}</td>
                            <td className="px-1 py-1.5 text-[10px] text-indigo-400 text-center font-bold">{r.target}</td>
                            <td className="px-1 py-1.5 text-[10px] text-amber-400 text-center">{r.pending || '·'}</td>
                            <td className="px-1 py-1.5 text-[10px] text-emerald-400 text-center">{r.approved || '·'}</td>
                            <td className="px-1 py-1.5 text-[10px] text-green-400 text-center">{r.live || '·'}</td>
                            <td className="px-1 py-1.5 text-[10px] text-red-400 text-center">{r.rejected || '·'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sheet 6: Review Cycle & Status */}
                <div className="bg-[#1E1E1E] border border-[#2D2D2D] rounded-xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                  <div className="px-4 py-2.5 bg-[#161616] border-b border-[#2D2D2D] flex items-center justify-between">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Sheet 6 · Review Cycle & Status</p>
                    <span className="text-[10px] text-zinc-600">{biSheet6.length} rows</span>
                  </div>
                  <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-[#252525] border-b border-[#333]">
                          {['Camp','Brand','Sts','Stage','Rnd','Appr','Rej','Unlk','Days','Depts','Action','Reviewer','Note','Date','Disc','Rej Reason','Post Reason'].map(h => (
                            <th key={h} className="px-1.5 py-1.5 text-[9px] font-bold text-zinc-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {biSheet6.length === 0 ? (
                          <tr><td colSpan={17} className="px-4 py-8 text-center text-xs text-zinc-500">No review data</td></tr>
                        ) : biSheet6.map((r, i) => (
                          <tr key={i} className="border-b border-[#2A2A2A] hover:bg-[#2D2D2D] transition-colors">
                            <td className="px-1.5 py-1.5 text-[11px] font-semibold text-zinc-100 max-w-[80px] truncate">{r.campaign}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300 max-w-[70px] truncate">{r.brand}</td>
                            <td className="px-1.5 py-1.5">
                              <span className={cn("text-[8px] font-bold px-1 py-0.5 rounded-full",
                                r.status === SubmissionStatus.APPROVED || r.status === SubmissionStatus.COMPLETED ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-800' :
                                r.status === SubmissionStatus.REJECTED ? 'bg-red-900/30 text-red-400 border border-red-800' :
                                r.status === SubmissionStatus.IN_REVIEW ? 'bg-blue-900/30 text-blue-400 border border-blue-800' :
                                'bg-amber-900/30 text-amber-400 border border-amber-800'
                              )}>{r.status}</span>
                            </td>
                            <td className="px-1.5 py-1.5 text-[10px] text-zinc-500">{r.stage}</td>
                            <td className="px-1.5 py-1.5 text-center"><span className={cn("text-[11px] font-bold", r.totalRounds > 3 ? "text-red-400" : r.totalRounds > 1 ? "text-amber-400" : "text-zinc-300")}>{r.totalRounds}</span></td>
                            <td className="px-1.5 py-1.5 text-[11px] text-emerald-400 text-center">{r.approvals}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-red-400 text-center">{r.rejections}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-amber-400 text-center">{r.unlocks}</td>
                            <td className="px-1.5 py-1.5"><span className={cn("text-[9px] font-bold px-1 py-0.5 rounded", r.daysToResolve <= 2 ? "bg-emerald-900/30 text-emerald-400" : r.daysToResolve <= 7 ? "bg-amber-900/30 text-amber-400" : "bg-red-900/30 text-red-400")}>{r.daysToResolve}d</span></td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[70px] truncate">{r.departments}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-300">{r.lastAction}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 max-w-[70px] truncate">{r.lastReviewer}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-500 max-w-[80px] truncate">{r.lastNote || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-500">{r.lastDate}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-zinc-400 text-center">{r.discussionCount}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-red-400 max-w-[80px] truncate">{r.rejectionReason || '·'}</td>
                            <td className="px-1.5 py-1.5 text-[11px] text-orange-400 max-w-[80px] truncate">{r.postponeReason || '·'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

        </>
      )}
    </div>
  );
}

// --- Campaign Archive Tab ----------------------------------------------------

function CampaignArchiveTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'aggregator', 'archive', 'admin');
  const repo = getRepository();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [submissions, setSubmissions] = useState<CampaignSubmission[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([repo.getCampaigns(), repo.getCampaignSubmissions(), repo.getBrands()]).then(([c, s, b]) => {
      setCampaigns(c.filter(camp => camp.status === CampaignStatus.CLOSED));
      const isolated = isAdmin ? s : s.filter(sub => sub.submittedBy === user?.id);
      setSubmissions(isolated);
      setBrands(b.filter(br => br.enabledModules?.includes('aggregator')));
    });
  }, []);

  const subsFor = (campId: string) => submissions.filter(s => s.campaignId === campId);
  const brandName = (id: string) => brands.find(b => b.id === id)?.name ?? id;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Campaign Archive</h2>
          <p className="text-sm text-slate-500 mt-1">Closed and expired campaigns with their submission history.</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded-xl text-xs font-bold text-zinc-500">
          <Archive size={14} /> {campaigns.length} Archived
        </div>
      </div>

      {campaigns.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
          <Archive size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm font-semibold text-zinc-400">No archived campaigns</p>
          <p className="text-xs text-zinc-400 mt-1">Campaigns move here automatically when their period ends or they are manually closed.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.sort((a, b) => b.validTo - a.validTo).map(camp => {
            const subs = subsFor(camp.id);
            const totalItems = subs.reduce((acc, s) => acc + getApprovedItems(s).length, 0);
            const expanded = expandedId === camp.id;
            const daysSinceEnd = Math.ceil((Date.now() - camp.validTo) / (1000 * 60 * 60 * 24));
            return (
              <div key={camp.id} className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
                {/* Header row */}
                <button
                  onClick={() => setExpandedId(expanded ? null : camp.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-zinc-200 rounded-xl flex items-center justify-center text-zinc-500 shrink-0">
                    <Archive size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-zinc-900 truncate">{camp.name}</p>
                    <p className="text-[11px] text-zinc-400 truncate">
                      {fmtDate(camp.validFrom)} · {fmtDate(camp.validTo)} · {camp.targetAllBrands ? 'All Brands' : camp.brandIds.map(brandName).join(', ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border bg-zinc-100 text-zinc-500 border-zinc-200">
                      Closed
                    </span>
                    <span className="text-[10px] font-bold text-zinc-400">{daysSinceEnd}d ago</span>
                    <span className="text-xs font-bold text-zinc-500">{subs.length} submissions · {totalItems} items</span>
                    {expanded ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
                  </div>
                </button>

                {/* Expanded detail */}
                {expanded && (
                  <div className="border-t border-zinc-100 px-5 py-4 space-y-4 bg-zinc-50/50">
                    {/* Campaign details */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-zinc-400 font-bold uppercase text-[10px]">Discount</span><p className="text-zinc-900 font-semibold mt-0.5">{camp.discountValue}{camp.discountType === 'PERCENTAGE' ? '%' : ' QAR'}</p></div>
                      <div><span className="text-zinc-400 font-bold uppercase text-[10px]">Aggregators</span>
                        <div className="flex flex-wrap gap-1 mt-1">{camp.aggregators.map(a => <span key={a} className="px-2 py-0.5 bg-white rounded-full text-[9px] font-bold text-teal-700 border border-teal-200">{a}</span>)}</div>
                      </div>
                      <div><span className="text-zinc-400 font-bold uppercase text-[10px]">Submissions</span><p className="text-zinc-900 font-semibold mt-0.5">{subs.length}</p></div>
                      <div><span className="text-zinc-400 font-bold uppercase text-[10px]">Total Items</span><p className="text-zinc-900 font-semibold mt-0.5">{totalItems}</p></div>
                    </div>

                    {/* Submission list */}
                    {subs.length > 0 && (
                      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                        <div className="grid grid-cols-[1fr_100px_80px_100px] gap-2 px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                          <span>Brand / Submitter</span>
                          <span className="text-center">Items</span>
                          <span className="text-center">Status</span>
                          <span className="text-right">Submitted</span>
                        </div>
                        {subs.map(sub => (
                          <div key={sub.id} className="grid grid-cols-[1fr_100px_80px_100px] gap-2 px-4 py-3 items-center border-b border-zinc-100 last:border-b-0">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-zinc-900 truncate">{sub.brandName}</p>
                              <p className="text-[10px] text-zinc-400 truncate">{sub.submitterName}</p>
                            </div>
                            <span className="text-sm font-bold text-zinc-900 text-center">{getApprovedItems(sub).length}</span>
                            <span className="flex justify-center">
                              <span className={cn(
                                "px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border",
                                sub.status === SubmissionStatus.COMPLETED ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                sub.status === SubmissionStatus.REJECTED ? "bg-red-50 text-red-700 border-red-200" :
                                "bg-amber-50 text-amber-700 border-amber-200"
                              )}>
                                {sub.status === SubmissionStatus.COMPLETED ? 'Done' : sub.status === SubmissionStatus.REJECTED ? 'Rejected' : 'Pending'}
                              </span>
                            </span>
                            <span className="text-[11px] text-zinc-400 text-right">{fmtDate(sub.createdAt)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- Department Management Tab (Aggregator-owned) ----------------------------

function DepartmentManagementTab() {
  const repo = getRepository();
  const toast = useToast();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [newName, setNewName] = useState('');
  const [newLinkedModules, setNewLinkedModules] = useState<string[]>([]);
  const [editDept, setEditDept] = useState<Department | null>(null);
  const [editName, setEditName] = useState('');
  const [editLinkedModules, setEditLinkedModules] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);

  const INSTALLABLE_MODULES = PLATFORM_MODULES.filter(m => m.id !== MODULE_IDS.SYSTEM);

  const load = async () => setDepartments(await repo.getDepartments());
  useEffect(() => { load(); const h = (e: any) => { if (import.meta.env.DEV && e && e.type === 'PULSE_UPDATE') console.log('💓 Heartbeat: Data Refreshed (Aggregator) ' + e.type); load(); }; window.addEventListener('PULSE_UPDATE', h); return () => window.removeEventListener('PULSE_UPDATE', h); }, []);

  const toggleNewModule = (modId: string) => {
    setNewLinkedModules(prev => prev.includes(modId) ? prev.filter(m => m !== modId) : [...prev, modId]);
  };

  const toggleEditModule = (modId: string) => {
    setEditLinkedModules(prev => prev.includes(modId) ? prev.filter(m => m !== modId) : [...prev, modId]);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const nextName = newName.trim().toUpperCase();
    if (departments.some(d => d.name.trim().toUpperCase() === nextName)) {
      toast.error('Duplicate Department', 'A department with this name already exists.');
      return;
    }
    await repo.saveDepartment({
      id: `dept-${crypto.randomUUID()}`,
      name: nextName,
      linkedModules: [...newLinkedModules],
    });
    setNewName('');
    setNewLinkedModules([]);
    await load();
  };

  const handleEditSave = async () => {
    if (!editDept || !editName.trim()) return;
    const nextName = editName.trim().toUpperCase();
    if (departments.some(d => d.id !== editDept.id && d.name.trim().toUpperCase() === nextName)) {
      toast.error('Duplicate Department', 'A department with this name already exists.');
      return;
    }
    await repo.updateDepartment({ ...editDept, name: nextName, linkedModules: [...editLinkedModules] });
    setEditDept(null); setEditName(''); setEditLinkedModules([]);
    await load();
  };

  const handleDelete = async (id: string) => {
    setDeleteConfirm({
      message: 'Remove this department? This cannot be undone.',
      onConfirm: async () => { await repo.deleteDepartment(id); await load(); },
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Department Registry</h2>
          <p className="text-sm text-slate-500 mt-1">Manage departments and map them to specific application modules.</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-2">
          <Layers size={18} className="text-zinc-400" />
          <h3 className="font-bold text-zinc-900">Departments</h3>
          <span className="ml-auto text-xs text-zinc-400">{departments.length} department{departments.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Add new department form */}
        <form onSubmit={handleAdd} className="space-y-3 bg-zinc-50 border border-zinc-200 rounded-xl p-4">
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">New Department</p>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Department name (e.g. F&B, SALES)"
              className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900"
            />
            <button
              type="submit"
              disabled={!newName.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-xl text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 transition-all"
            >
              <Plus size={16} /> Add
            </button>
          </div>
          {/* Module association checkboxes */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Link to Modules</p>
            <div className="flex flex-wrap gap-2">
              {INSTALLABLE_MODULES.map(mod => {
                const checked = newLinkedModules.includes(mod.id);
                return (
                  <button
                    key={mod.id}
                    type="button"
                    onClick={() => toggleNewModule(mod.id)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                      checked
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"
                    )}
                  >
                    <div className={cn(
                      "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all",
                      checked ? "bg-white border-white" : "border-zinc-300"
                    )}>
                      {checked && <Check size={10} className="text-indigo-600" />}
                    </div>
                    {mod.name}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-zinc-400 italic">Select which app modules this department belongs to.</p>
          </div>
        </form>

        {/* Department list */}
        <div className="space-y-2">
          {departments.length === 0 && (
            <div className="py-8 text-center text-zinc-400 italic text-sm">No departments defined yet.</div>
          )}
          {departments.map(dept => {
            const isEditing = editDept?.id === dept.id;
            const linked = dept.linkedModules ?? [];
            return (
              <div key={dept.id} className={cn(
                "bg-zinc-50 rounded-xl border border-zinc-100 group transition-all",
                isEditing && "border-indigo-200 bg-indigo-50/30"
              )}>
                <div className="flex items-center gap-3 p-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 shrink-0">
                    {dept.name.slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEditSave(); } if (e.key === 'Escape') { setEditDept(null); setEditLinkedModules([]); } }}
                        className="w-full px-2 py-1 bg-white border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-zinc-900"
                        placeholder="Department name"
                      />
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-zinc-900">{dept.name}</p>
                        <p className="text-[10px] font-mono text-zinc-400">{dept.id}</p>
                      </>
                    )}
                  </div>
                  {/* Linked module badges (read-only view) */}
                  {!isEditing && linked.length > 0 && (
                    <div className="hidden sm:flex items-center gap-1">
                      {linked.map(modId => {
                        const mod = PLATFORM_MODULES.find(m => m.id === modId);
                        return (
                          <span key={modId} className="px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded text-[9px] font-semibold text-indigo-600 uppercase tracking-wider">
                            {mod?.name ?? modId}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {!isEditing && linked.length === 0 && (
                    <span className="hidden sm:inline px-2 py-0.5 bg-amber-50 border border-amber-100 rounded text-[9px] font-semibold text-amber-600 uppercase tracking-wider">
                      No Module
                    </span>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isEditing ? (
                      <>
                        <button onClick={handleEditSave} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"><Check size={14} /></button>
                        <button onClick={() => { setEditDept(null); setEditLinkedModules([]); }} className="p-1.5 text-zinc-400 hover:bg-zinc-100 rounded-lg transition-all"><X size={14} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setEditDept(dept); setEditName(dept.name); setEditLinkedModules(dept.linkedModules ?? []); }} className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"><Edit2 size={14} /></button>
                        <button onClick={() => handleDelete(dept.id)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                </div>

                {/* Edit mode: module association checkboxes */}
                {isEditing && (
                  <div className="px-4 pb-4 space-y-1.5 border-t border-zinc-200 pt-3 mt-1">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Linked Modules</p>
                    <div className="flex flex-wrap gap-2">
                      {INSTALLABLE_MODULES.map(mod => {
                        const checked = editLinkedModules.includes(mod.id);
                        return (
                          <button
                            key={mod.id}
                            type="button"
                            onClick={() => toggleEditModule(mod.id)}
                            className={cn(
                              "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                              checked
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"
                            )}
                          >
                            <div className={cn(
                              "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all",
                              checked ? "bg-white border-white" : "border-zinc-300"
                            )}>
                              {checked && <Check size={10} className="text-indigo-600" />}
                            </div>
                            {mod.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}

// --- Campaign Settings Tab (Admin) -------------------------------------------

function CampaignSettingsTab({ user }: { user: User | null }) {
  const canEdit = hasTabVariant(user, 'aggregator', 'settings', 'admin');
  const repo = getRepository();
  const toast = useToast();
  const [settings, setSettings] = useState<CampaignSettings>({ brands: [], aggregators: [...DEFAULT_AGGREGATORS] });
  const [newAgg, setNewAgg] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const settingsSnap = React.useRef<CampaignSettings | null>(null);
  const settingsDirty = React.useMemo(() => settingsSnap.current ? hasDataChanged(settingsSnap.current, settings) : false, [settings]);
  const aggLogoRef = React.useRef<HTMLInputElement>(null);
  const [logoTarget, setLogoTarget] = useState<string | null>(null);

  useEffect(() => {
    repo.getCampaignSettings().then(s => { setSettings(s); settingsSnap.current = JSON.parse(JSON.stringify(s)); });
    repo.getDepartments().then(setDepartments);
  }, []);

  const addAggregator = () => {
    const name = newAgg.trim();
    if (!name || settings.aggregators.includes(name)) return;
    setSettings(prev => ({ ...prev, aggregators: [...prev.aggregators, name] }));
    setNewAgg('');
  };

  const removeAggregator = (agg: string) => {
    setSettings(prev => ({
      ...prev,
      aggregators: prev.aggregators.filter(a => a !== agg),
      aggregatorLogos: (() => { const l = { ...(prev.aggregatorLogos ?? {}) }; delete l[agg]; return l; })(),
    }));
  };

  const handleLogoFile = (file: File, aggName: string) => {
    if (file.size > 200 * 1024) { toast.error('Too Large', 'Logo must be under 200 KB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUri = reader.result as string;
      setSettings(prev => ({ ...prev, aggregatorLogos: { ...(prev.aggregatorLogos ?? {}), [aggName]: dataUri } }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = (aggName: string) => {
    setSettings(prev => {
      const logos = { ...(prev.aggregatorLogos ?? {}) };
      delete logos[aggName];
      return { ...prev, aggregatorLogos: logos };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    await repo.saveCampaignSettings(settings);
    settingsSnap.current = JSON.parse(JSON.stringify(settings));
    setSaving(false);
    setSaved(true);
    toast.success('Settings Saved!', 'Campaign settings updated successfully.');
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Campaign Settings</h2>
        <p className="text-sm text-slate-500 mt-1">Manage aggregators and other campaign configuration.</p>
      </div>

      {saved && (
        <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
          <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
          <p className="text-sm font-semibold text-emerald-700">Settings saved successfully.</p>
        </div>
      )}

      {/* Empty Department Warning */}
      {departments.filter(d => (d.linkedModules ?? []).includes('aggregator')).length === 0 && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-700">No Departments Linked to Aggregator Operations</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Aggregator review workflow requires at least one department linked to "Aggregator Operations" in the Global Department Registry.
              Go to <span className="font-bold">System Settings ? Departments</span> and link departments to the Aggregator Operations module.
              Until then, the legacy fallback stages (F&B, F&P, SALES) will be used.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm">
        <div className="p-5 space-y-6">

          {/* Aggregator Management */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Aggregator Partners</p>
              <div className="flex-1 h-px bg-zinc-100" />
            </div>
            <p className="text-xs text-zinc-500">
              Manage the list of aggregator partners available for campaign selection.
              Changes apply to all future campaigns.
            </p>

            {/* Current aggregators as cards with logos */}
            <input ref={aggLogoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f && logoTarget) handleLogoFile(f, logoTarget); e.target.value = ''; setLogoTarget(null); }} />
            <div className="flex flex-wrap gap-3">
              {settings.aggregators.map(agg => {
                const logo = (settings.aggregatorLogos ?? {})[agg];
                return (
                  <div key={agg} className="flex flex-col items-center gap-1.5 p-3 bg-teal-50 border border-teal-200 rounded-xl min-w-[100px]">
                    {/* Logo area */}
                    <div className="relative w-12 h-12 rounded-lg border border-dashed border-teal-300 bg-white flex items-center justify-center overflow-hidden group">
                      {logo ? (
                        <>
                          <img src={logo} alt={agg} className="w-full h-full object-contain" />
                          {canEdit && (
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1 transition-opacity">
                              <button type="button" onClick={() => { setLogoTarget(agg); aggLogoRef.current?.click(); }} className="p-1 bg-white rounded-md"><Upload size={10} /></button>
                              <button type="button" onClick={() => removeLogo(agg)} className="p-1 bg-white rounded-md text-red-500"><Trash2 size={10} /></button>
                            </div>
                          )}
                        </>
                      ) : (
                        canEdit ? (
                          <button type="button" onClick={() => { setLogoTarget(agg); aggLogoRef.current?.click(); }} className="p-1 hover:bg-teal-100 rounded-md transition-colors" title="Upload logo">
                            <Upload size={14} className="text-teal-400" />
                          </button>
                        ) : (
                          <ImageIcon size={16} className="text-teal-300" />
                        )
                      )}
                    </div>
                    {/* Name + remove */}
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-semibold text-teal-700">{agg}</span>
                      {canEdit && (
                        <button type="button" onClick={() => removeAggregator(agg)} className="p-0.5 hover:bg-teal-200 rounded-md transition-colors" title={`Remove ${agg}`}>
                          <X size={10} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {(settings.aggregators ?? []).length === 0 && (
                <p className="text-xs text-zinc-400 italic">No aggregators configured. Add at least one below.</p>
              )}
            </div>

            {/* Add new aggregator */}
            {canEdit && (
              <div className="flex items-center gap-2">
                <input
                  value={newAgg}
                  onChange={e => setNewAgg(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAggregator(); } }}
                  placeholder="New aggregator name·"
                  className="flex-1 max-w-xs px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-zinc-900"
                />
                <button
                  type="button"
                  onClick={addAggregator}
                  disabled={!newAgg.trim()}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
            )}
          </div>

          {/* Manual Entry Control */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Submission Controls</p>
              <div className="flex-1 h-px bg-zinc-100" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl">
              <div>
                <p className="text-sm font-semibold text-zinc-900">Allow Manual Item Entry</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {settings.allowManualEntry !== false
                    ? 'Managers can add items manually or import from Menu.'
                    : 'Managers must use "Add from Menu" only · manual entry is disabled.'}
                </p>
              </div>
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => setSettings(prev => ({ ...prev, allowManualEntry: prev.allowManualEntry === false ? true : false }))}
                  className="shrink-0"
                >
                  {settings.allowManualEntry !== false
                    ? <ToggleRight size={32} className="text-emerald-500" />
                    : <ToggleLeft size={32} className="text-zinc-300" />}
                </button>
              ) : (
                settings.allowManualEntry !== false
                  ? <ToggleRight size={32} className="text-emerald-500 opacity-50" />
                  : <ToggleLeft size={32} className="text-zinc-300 opacity-50" />
              )}
            </div>
          </div>

          {/* Save Button */}
          {canEdit && (
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-100">
              {!settingsDirty && !saving && <span className="text-[10px] text-zinc-400">No changes detected</span>}
              <button
                onClick={handleSave}
                disabled={saving || !settingsDirty}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <Check size={14} /> {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
